/**
 * TradingChart — reusable candlestick chart using lightweight-charts v5.
 * Supports overlays: Bollinger Envelope, EMA 200.
 * Accepts height as number (px) or "100%" to fill parent.
 *
 * KEY FIX: Always renders the chart container div (never conditionally unmounts it).
 * Loading / empty states are overlaid on top so the chart instance persists.
 */
import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import {
  createChart,
  CandlestickSeries,
  LineSeries,
  HistogramSeries,
  BaselineSeries,
  ColorType,
  CrosshairMode,
  IChartApi,
  ISeriesApi,
  CandlestickData,
  LineData,
  HistogramData,
  Time,
  createSeriesMarkers,
} from 'lightweight-charts';
import { joinCrosshairGroup, type CrosshairHandle, type CrosshairPos } from '../../stores/crosshairSyncStore';
import { useAppTheme } from '../../hooks/useAppTheme';
import { useChartSettings } from '../../contexts/ChartSettingsContext';
import { OVERVIEW_ZONE_COLORS, ZONE_LABELS, hexToRgba, zoneForScore, type OverviewZone } from '../../theme/overviewColors';
import {
  computeEnvelope, computeCombinedEnvelope, computeCpr, computePocPerPeriod,
  computeProjectedCpr, computeProjectedPoc,
  computeBbrReversals, computeBbrSeries, computeVolumeSpikes, atrSeries,
  sessionStarts,
  ema as emaNullable,
  sma as smaNullable,
  type CprPeriod,
} from '../../utils/dailyBlastIndicators';
import {
  computeLRHamaBand, computeFLLHHSignals, computeFBOSignals, computeWaveSignals, computeLRCSignals,
  computeASHSignals, computeBlastSignals, computeWaveSeries,
} from '../../utils/signalIndicators';
import { detectBlastEvents, blastActiveWindows, closedPeriodCandles, type BlastWindow } from '../../utils/blast';
import { computeBwTrades } from '../../utils/bbrWaveTrades';
import { computeVob } from '../../utils/vob';
import { computeBollingerBands, type BollingerVariant } from '../../utils/bollingerVariants';
import { OrderBlockPrimitive, type ObBox } from './primitives/OrderBlockPrimitive';
import { linreg as linregOf, transformCandles, ema as emaOf, dropFormingHtfBars } from '../../utils/candleTransforms';
import { computeBolev, type BolevPeriod } from '../../utils/bolev';
import { computeVpaMarkers, VPA_MIN_BARS, VPA_TOGGLE_DEFAULTS, vpaDefsForMarkerText, type VpaMarker } from '../../utils/vpaIndicators';
import { rsiWilder, vwmaSeries, emaSeries } from '../../utils/jhm';
import { wrPctPair, resolveWrPct, wrPctZoneAt, WRPCT_OVERBOUGHT, WRPCT_OVERSOLD, WRPCT_MID } from '../../utils/wrPct';
import { computeWVol } from '../../utils/wvol';
import { hmaSeries } from '../../utils/hma';
import { BandPrimitive, type BandPoint } from './primitives/BandPrimitive';
import { VerticalLinePrimitive } from './primitives/VerticalLinePrimitive';
import { BarLevelPrimitive, BarLevelPoint } from './primitives/BarLevelPrimitive';
import { BarBandPrimitive } from './primitives/BarBandPrimitive';
import type { TradeLevelSegment } from '../../utils/strategies/types';
import { GANDIV_DEFAULTS } from '../../utils/strategies/gandiv';
import { VerticalZonesPrimitive, type VerticalZonePoint } from './primitives/VerticalZonesPrimitive';
import { HorizontalLevelsPrimitive, type HLevel } from './primitives/HorizontalLevelsPrimitive';
import { HorizontalZonesPrimitive, type HZone } from './primitives/HorizontalZonesPrimitive';
import { PnlBadgePrimitive } from './primitives/PnlBadgePrimitive';
import { PaneLegendPrimitive, type PaneLegendItem } from './primitives/PaneLegendPrimitive';
import { type FnoBar, fmtExpiry } from '../../utils/fnoBars';
import FnoExpiryMenu, { type FnoSelectProps } from './FnoExpiryMenu';
import { RectangleZonePrimitive, type RectZone } from './primitives/RectangleZonePrimitive';
import { LeftLabeledLevelsPrimitive, type LLevel } from './primitives/LeftLabeledLevelsPrimitive';
import { SrBandPrimitive, type SrBand } from './primitives/SrBandPrimitive';
import { RealOhlcPrimitive, type RealOhlcPoint } from './primitives/RealOhlcPrimitive';
import { LrBodyPrimitive, type LrBodyPoint } from './primitives/LrBodyPrimitive';
import EmptyState from '../common/EmptyState';
import { CardSkeleton } from '../common/Skeletons';
import { CH_TYPE, chOverlaySurface, chNumeric } from '../../styles/appUi';
import { computeClnBars, CLN_DEFAULT_LENGTH } from '../../utils/clnCandles';
import { computeAvBandSeries } from '../../utils/avBand';
import { computeClnHtfStamps } from '../../utils/clnHtfBand';
import { computeProfitZones, type ProfitZonePoint, type ProfitZoneGap } from '../../utils/profitZone';
import { colourHtfStamps } from '../../utils/bandStepColour';
import { computeRegimeStack, StackCandleType } from '../../utils/regimeStack';
import { computeClhBandLevels, stampHtfLevels, stepDirections, ClhLevelPoint } from '../../utils/clhBand';
import {
  computeClnBreakSeries, resolveClnBreakOptions, CLN_BREAK_BUY_LINE, CLN_BREAK_SHORT_LINE,
} from '../../utils/strategies/clnBreak';
import { indicatorSourceCandles, type IndicatorSource } from '../../utils/indicatorSource';
import { buildHtfBarsForOsc, stampOscHtf, htfBarCloseTime, type OscHtfStamp } from '../../utils/oscHtf';
import { OptionProfilePrimitive, type OptionProfileData } from './primitives/OptionProfilePrimitive';
import { computeHlDiffSr, buildRunningBand, computeSrBreakRespectEvents, type SrLevel } from '../../utils/srLevels';
import { CrosshairInfoBox, type CrosshairCtx } from './CrosshairInfoBox';
import ChartTitleAccountStats from './ChartTitleAccountStats';
import { interpretFnoAtPrice, scoreFno, type FnoInterpretInputs } from '../../utils/fnoInterpret';
import { projectToBarClose } from '../../utils/projectSeries';
import { intervalLabel as catalogIntervalLabel } from '../../utils/intervalCatalog';
import { tickDecimals } from '../../utils/decimalPrecision';
// ArrowMarkerPrimitive (round 31) is no longer used — LRC now renders
// via createSeriesMarkers like the other signal indicators so its
// BUY/SHORT arrows are equally visible.

/* ───── types ───── */
export interface CandleRow {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

/** What the chart needs to draw "important fraction" candles (see utils/candleFraction.ts). */
export interface FractionView {
  /** The label shown in the OHLC legend, so a fraction chart is never mistaken for a normal one. */
  label: string;
  /** The CLOSED fraction bars (O/H/L/V from the window, the real close), joined on time. */
  bars: CandleRow[];
  /** Closed bars left normal because no finer data covers them: drawn faded. */
  missing: Set<number>;
  /** Plain-English legend notes: snapped window, history start, forming bar, unavailable. */
  notes: string[];
  /** The fraction is set but this pane draws normal candles (option panes, a timeframe
   *  too short for it, synthetics): the legend shows only the label and the notes. */
  inactive?: boolean;
}

export interface Overlays {
  bollingerEnvelope?: boolean;
  ema200?: boolean;
  entryZones?: boolean;
  /** Options payoff-zones overlay — draws the open/under-review basket's
   *  underlying-price levels (break-even / max-profit / −50%-margin) on the
   *  underlying (spot) pane. 4-quad options context only. */
  payoffZones?: boolean;
  /** FNO overlay — option-derived levels (Max Pain, GEX flip/walls, OI S/R)
   *  drawn as running step-lines per expiry tier. Delta BTC/ETH only. Config in
   *  Chart Settings → FNO. */
  fno?: boolean;
  cpr?: boolean;
  poc?: boolean;
  /** CLH Band — the REAL price at which the next CLH body would break out of
   *  the current one (up / down), plus an optional colour-flip level. Drawn on
   *  raw candles whatever the chart is displaying, optionally for the mapped
   *  higher timeframe too. Config in Chart Settings -> CLH Band. */
  clhBand?: boolean;
  /** CLN_Break levels — the two stepped prices the CLN_Break rule trades: the
   *  max of the previous N linear-regression body highs and the min of their
   *  lows. Drawn from the SAME engine call the CLN_Break strategy chip marks
   *  from, so a line and a mark can never disagree about where the level was.
   *  Config in Chart Settings -> CLN Break. */
  clnBreakLevels?: boolean;
  /** HTF Band — the CLN higher-timeframe context band (was a Chart Settings
   *  switch until 2026-09-25). Undefined = not clicked yet: falls back to the
   *  legacy `settings.candle.clnHtfBand`, so a template that had it on keeps it. */
  clnHtfBand?: boolean;
  /** Linear-regression (LR) body drawn over ANY candle type — the CLN body as a
   *  switch. The "CLN" candle type is the preset Regular + LR + ghost; this lets the
   *  same body sit over HA / CLH / CSH / CSL / Regular (2026-11-01). Regression of
   *  the RAW opens/closes on every type, so it is the same object everywhere. */
  lrBody?: boolean;
  /** P-Zone — the Dynamic Profit Zone as a reference (utils/profitZone.ts):
   *  Zone 1 / Zone 2 beyond the HTF Band, set at each HTF close. Not a signal. */
  profitZone?: boolean;
  /* Advanced (DailyBlast-derived, strategy-independent) */
  /** Round 34 — was `combinedEnvelope` (Combined Envelope outer-BB band).
   *  Now renders the dynamic cloud between Avg band edge and Combined Midline
   *  per Pine's "Show Cloud: Avg Band ↔ Combined Mid". */
  avgCloud?: boolean;
  combinedMidline?: boolean;
  ctfPoc?: boolean;
  htfPoc?: boolean;
  bbrColor?: boolean;
  spikeColor?: boolean;
  /* MM (Market Maker) — for each SOURCE candle (type set in Chart Settings → MM,
   * independent of the chart candle type) that is a BBR reversal OR a Vol spike,
   * mark its body (Open/Close) and join consecutive marks into a staircase band,
   * filled bull when the stair steps up vs the previous, bear when down. */
  mm?: boolean;
  /** Round 34 — was `lrBnd` (CSL open/close band only).
   *  Now renders the average of SLR (CSL) and HAMA open/close clouds. */
  lrHama?: boolean;
  /* Signal indicators — only BUY/SHORT plotshapes are rendered. */
  fllhh?: boolean;
  fbo?: boolean;
  wave?: boolean;
  /* LRC — LRBand crosses CPR. Round 30. */
  lrc?: boolean;
  /* AS — Absolute Strength Histogram (jh ASH v3.1) BUY/SHORT signals. */
  as?: boolean;
  /* BBR / Wave as OSCILLATOR CURVES, separate from bbrColor (candle tint) and
   * wave (▲▼ arrows) — those keep doing exactly what they did. Both can render
   * in the shared lower pane or overlaid on price, per settings.bbr.pane /
   * settings.wave.pane. When both are on they are drawn on pinned scales so
   * BBR's ±100 and Wave's ±60 land on the same row and can actually be compared. */
  bbrOsc?: boolean;
  waveOsc?: boolean;
  /* Blast — Inside (HA) / Engulfing (Regular) candles on Daily & Weekly draw a
   * forward-projected band (detected candle's High/Low, +2 periods). While a
   * band is visible, a fresh close-cross of the C-POC band fires BUY/SHORT.
   * Computed on auxCandles.daily / auxCandles.weekly, independent of the chart's
   * interval / candle type. Band colour + opacity in Chart Settings → Blast. */
  blast?: boolean;
  /* VPA — Volume Spread Analysis signal markers (karthikmarar VPA 5.0). Master
   * toggle; which of the ~20 VSA signals render is chosen in Chart Settings →
   * VPA. Computed on raw closed candles, snapped to display bars (candle-type
   * independent). */
  vpa?: boolean;
  /* HLDiff (R134) — EMA of Highs + EMA of Lows of a selectable candle type
   * with a translucent fill between them. Candle type / period / colours in
   * Chart Settings → HLDiff. */
  hlDiff?: boolean;
  /* S/R (R137) — Support/Resistance indicator. A container for multiple S/R
   * source methods that all plot horizontal levels on the price pane
   * simultaneously. First method: "HLDiff SR" — levels at the local peaks of
   * the HLDiff histogram (dark-green peak → Resistance at that bar's high;
   * dark-red peak → Support at that bar's low), computed on BOTH the chart TF
   * and the Strategies-mapped HTF. Methods / styling in Chart Settings → S/R. */
  sr?: boolean;
  /* RelStrength — ratio of LinReg(close,14) of chart symbol vs benchmark. */
  relStrength?: boolean;
  /* Synthiya (synthetic-symbols plan §5) — staircase PCR / ATM-IV / RelStrength
   * of the UNDERLYING in the lower pane, up/down step-coloured. Data via the
   * `synthiya` prop; colours in Chart Settings → Synthiya. */
  synthiya?: boolean;
  /* DbCrZone — Debit/Credit option-market regime vertical background zones on the underlying.
   * Data via the `dbcrZones` prop; colours/opacity in Chart Settings → DbCrZone. */
  dbcrZone?: boolean;
  /* 24HCng% — running 24-hour change percentage. */
  dayChange?: boolean;
  /* MACD — EMA(fast)−EMA(slow) with SMA signal; plotted as a directional band
   * (Round 94). */
  macd?: boolean;
  /* Funding — Delta perpetual funding rate (% per 8h), forward-filled onto each
   * candle's start time (flat between the 8h settlements). Lower-pane baseline
   * line, sign-coloured around zero. Delta symbols only. Data via fundingSeries. */
  funding?: boolean;
  /* Flow — historical Order Flow per-candle bias (Delta only). Rendered as a
   * coloured strip beneath candles and/or triangle markers per bar; absorption
   * is shown as a small ★ marker. Data source: orderFlowAggregator buckets,
   * fetched by the page via chartFlowService.getHistory. */
  flow?: boolean;
  /* JHM — JSR Hilega-Milega oscillator (RSI → VWMA + EMA of RSI), own 0–100
   * pane strip with 50 midline, zone shading and EMA/VWMA fill (Round 103). */
  jhm?: boolean;
  /* WR% — paired Williams %R EMAs (Round 142). Fast %R + EMA, slow %R + EMA;
   * crossover of fast over slow = bull, fast under slow = bear. Fixed -100..0
   * pane scale with -20/-80 OB/OS bands and a configurable bull/bear band
   * fill between the two EMAs. */
  wrPct?: boolean;
  /* BOLev_D / BOLev_W (Round 112) — breakout-level projector from Daily / Weekly
   * CLH candles (2-completed-candle high/low), with lookout/zone states. Works
   * on any chart timeframe + candle type; levels are non-repainting step lines. */
  bolevD?: boolean;
  bolevW?: boolean;
  /* BO_4H — same breakout-level projector on 4-hour candles. */
  bolev4h?: boolean;
  /* Vol — Weighted Volume (volume × close), LinReg(7)-smoothed, EMA(3)/EMA(21)
   * fast/slow lines with a bull/bear band fill, and spike bars (raw wVol >
   * slow × 2). Lower pane. Toggled in a cycle with Flow on the same chip.
   * (Displayed as "HVol" — High Volume — in the toolbar.) */
  vol?: boolean;
  /* Volume — plain raw-volume histogram of EVERY bar, coloured green/red by the
   * regular candle's up/down (computed from raw candles, so it's candle-type
   * independent). Lower pane; cycles on the same chip after HVol. */
  volume?: boolean;
  /* AV_Band — Anchored Volume Band. From WVol spike bars it holds a High/Low
   * zone; an "Effective Line" picks the zone's upper/lower/mid edge by the
   * candle's chart-TF OverView score (>20 → high, <-20 → low, else mid); two
   * HMAs (fast/slow) of that line form a bull/bear band on the price pane. */
  avBand?: boolean;
  /* VOB — Volume-Trend Order Block Engine. Custom supertrend cloud
   * (hl2 ± mult·SMA(high−low)) + volume-split order-block boxes drawn on
   * confirmed swing pivots (top "Sell %" / bottom "Buy %"), plus retest crosses.
   * Computed on closed raw candles, candle-type independent. Config in Chart
   * Settings → VOB. */
  vob?: boolean;
}

/** Per-candle Order Flow signal — produced by `chartFlowService.getHistory`
 *  and aligned to the chart's display-bar times by the parent page. */
export interface FlowCandleSignal {
  time: number;
  windowBias: 'STRONG_BUY' | 'BUY' | 'NEUTRAL' | 'SELL' | 'STRONG_SELL';
  bias: 'BULL' | 'BEAR' | 'NEUTRAL';
  absorption: 'NONE' | 'SUPPORT_ABSORPTION' | 'RESISTANCE_ABSORPTION';
  buyPct: number;
  /** Round 69a — taker buy/sell volume for the bar. Used to scale histogram
   *  bar height proportional to trading activity. Optional for back-compat
   *  with callers that supplied only the bias fields. */
  buyVol?: number;
  sellVol?: number;
}

/* Higher-timeframe candles used by Combined Envelope / CPR / POC overlays.
   Fetched lazily by the page and passed down — keeps the chart dumb. */
export interface AuxCandles {
  ctf?: CandleRow[];
  htf?: CandleRow[];
  /* RAW Daily / Weekly candles (IST-shifted) for the BOLev_D / BOLev_W
   * breakout-level overlays — independent of the chart's own interval. */
  daily?: CandleRow[];
  weekly?: CandleRow[];
  /* RAW 4-hour candles for the BO_4H breakout-level overlay. */
  h4?: CandleRow[];
  /* S/R indicator HTF candles (R137) — candles at the Strategies → Timeframe
   * Mapping HTF for this pane's interval, used by HLDiff SR (and future S/R
   * methods) to draw the HTF support/resistance stream. Independent of the
   * ladder-based `htf` above, which feeds AvgCloud / Combined Midline. */
  srHtf?: CandleRow[];
  /* Minutes of the S/R HTF interval (for the level labels, e.g. "60m"). */
  srHtfMinutes?: number;
  /* CPR base period. 'D' for intraday LTF, 'W' for hourly+, 'M' for daily+. */
  cprPeriod?: CprPeriod;
  /* Minutes of this pane's LTF interval — used to size CPR lookback for POC. */
  ltfMinutes?: number;
  /* Exchange — threaded to computeCpr so period boundaries align with session
   * open (09:15 IST NSE, 09:00 IST MCX, 05:30 IST DELTA) not midnight IST. */
  exchange?: string;
}

export interface PriceLevel {
  type: string;
  /** Stable id when multiple levels can share a `type` (HVN supports multiple
   *  rows per symbol). Pass-through to drag and delete callbacks so the parent
   *  knows which row to update. Trigger-types (NBUY/NSHORT/...) are unique
   *  per chart and may omit this. */
  id?: string | number;
  price: number;
  color: string;
  lineStyle?: number; // 0=solid, 1=dotted, 2=dashed, 3=large-dashed
  /** When false, the level is rendered read-only (no drag handle).
   *  Defaults to true. FNO-derived levels (MaxPain, GEX Flip, R/S)
   *  pass false so users can't accidentally move them. */
  draggable?: boolean;
  /** Optional override for the right-axis title. Defaults to `${type} @ ${price}`.
   *  Used by the active-trade SL line to surface what's driving the level
   *  (e.g. "SL [TSL] @ 0.0543" so the user knows the trail is the breach). */
  axisLabel?: string;
  /** Draw the line only — NO axis tag / on-line text (the colour+style convey the
   *  meaning). Used by the FNO GEX walls so they don't stamp text on the chart. */
  noAxisLabel?: boolean;
  /** When true, the right-axis handle exposes a small × icon on hover
   *  that fires `onPriceLevelDelete`. Used for HVN levels. */
  deletable?: boolean;
}

export interface StrategyOverlay {
  candleColors?: { time: number; upColor: string; downColor: string }[];
  markers?: { time: number; position: 'aboveBar' | 'belowBar'; text: string; color: string; shape: 'arrowUp' | 'arrowDown' | 'circle' }[];
  lines?: { name: string; data: { time: number; value: number }[]; color: string; lineWidth: number; lineStyle?: number }[];
  /** Per-bar levels a strategy trades against (Gandiv) — see strategies/types.ts. */
  tradeLevels?: TradeLevelSegment[];
  /** R115 Stage 2 — per-bar fresh signal + entry condition (RockBand). The
   *  unified crosshair tooltip uses this to annotate signal candles with the
   *  firing Cond ("BUY Cond 2", etc.). */
  signalsByTime?: Map<number, { signal: 'BUY' | 'SHORT' | 'EXIT_LONG' | 'EXIT_SHORT'; strength: 'HIGH' | 'MEDIUM' | 'LOW' | null; pos: 'LONG' | 'SHORT' | 'FLAT' }>;
  /** R115 Stage 2 — per-bar running RockBand position so the tooltip can show
   *  LONG / SHORT / FLAT at the hovered bar even on bars without a fresh signal. */
  positionByTime?: Map<number, 'LONG' | 'SHORT' | 'FLAT'>;
  /** Informational, non-trade marks for a bar — OM_Simple's W / C. An ARRAY,
   *  because several can share a bar and one can share it with a BUY/SHORT.
   *  Mirrors `StrategyOverlayResult.notesByTime` in utils/strategies/types.ts. */
  notesByTime?: Map<number, Array<{ code: string; label: string; tip: string; color: string; score?: number }>>;
}

interface Props {
  candles: CandleRow[];
  /** Raw OHLC for signal-indicator computations (FLLHH / FBO / Wave).
   *  Should always be the closed RAW candles (pre-transform), so signals
   *  remain identical regardless of the chart's display candle type. The
   *  chart maps each signal's raw bar time to the corresponding display
   *  bar time (identity for Regular/HA/CSL/CLH).
   *  Falls back to `candles` when omitted (legacy callers). */
  rawCandles?: CandleRow[];
  overlays?: Overlays;
  auxCandles?: AuxCandles;
  /** Benchmark candles (same interval, ideally time-aligned) for RelStrength. */
  benchmarkCandles?: CandleRow[];
  /** Benchmark symbol/exchange for the chart legend (informational only). */
  benchmarkLabel?: string;
  loading?: boolean;
  height?: number | string;
  latestCandle?: CandleRow;
  /** RAW (Normal) forming/live bar — same time as `latestCandle` but with the
   *  untransformed OHLC. `latestCandle` is candle-type-transformed for display; this
   *  lets the OHLC legend + crosshair read the CURRENT bar's regular-candle values
   *  (and a candle-type-independent change %), not the smoothed transform. */
  rawLatestCandle?: CandleRow;
  /** "Important fraction" candles (utils/candleFraction.ts). Present only while the
   *  fraction is not 1; `candles` / `latestCandle` are then already built from fraction
   *  bars. Stable between bar closes: the per-tick forming bar is `fractionLatest`. */
  fraction?: FractionView;
  /** The forming bar as drawn under a fraction: the real bar until its window opens,
   *  then the fraction bar. Feeds CLN's layers and the legend. */
  fractionLatest?: CandleRow;
  priceLevels?: PriceLevel[];
  /** FNO level lines on the price pane (call / put OI walls, max pain, γ flip, GEX walls),
   *  stepped, one value per candle as of its close. A null value = no FNO data for that
   *  candle: the line BREAKS there instead of being drawn flat across the hole. */
  fnoLevels?: Array<{ key: string; label: string; color: string; lineStyle: number; lineWidth: number; data: { time: number; value: number | null }[] }>;
  /** Latest FNO structure for the crosshair interpreter (hovered-price read). Null = off. */
  fnoInterp?: FnoInterpretInputs | null;
  /** FNO view (2026-11-03): the per-candle FNO state and how to draw it — PCR, ATM IV and
   *  OI change each in their OWN labelled lower pane, collector gaps shaded, expiry rolls
   *  marked, optional OI-range / expected-move bands and γ-regime tint. Null = off. */
  fnoView?: FnoViewProps | null;
  /** The FNO expiry switch (rolling D0/W0/M0 or a pinned date), shown in the legend. */
  fnoSelect?: FnoSelectProps | null;
  /** Synthiya — pre-aligned staircase series (PCR / IV% / RelStrength of the
   *  underlying); per-step up/down colours applied here from settings.synthiya. */
  synthiya?: { pcr: { time: number; value: number }[]; iv: { time: number; value: number }[]; rs: { time: number; value: number }[] } | null;
  /** Shaded horizontal payoff zones (profit / max-profit / −50% danger) on spot. */
  priceZones?: HZone[];
  /** Live basket P&L badge pinned to the pane's top-left (payoff overlay). Null = off. */
  pnlBadge?: { value: number; sub?: string; lines?: string[]; warn?: string } | null;
  /** Click handler for the bottom-left P&L badge (opens the basket adjustment dialog). */
  onPnlBadgeClick?: () => void;
  /** Payoff levels (TP/SL/BE/Max-P) drawn as lines with LEFT-pinned labels so the
   *  right-side option-profile rail isn't blocked. */
  leftLevels?: LLevel[];
  onChartClick?: (price: number) => void;
  strategyOverlay?: StrategyOverlay;
  /* Cross-pane crosshair mirroring. Panes sharing a group key mirror each other; a pane
     with no group (single layout) never touches its own crosshair.

     This USED to be a pair of props — `onCrosshairMove` up to ChartsPage state and
     `syncCrosshair` back down — which meant every mouse move re-rendered an 8,900-line
     component and, through it, re-ran this file's whole data effect. Measured at 83–100ms
     per frame during a drag in split. The receiving side is purely imperative
     (`chart.setCrosshairPosition`), so it never needed React: see stores/crosshairSyncStore. */
  crosshairGroup?: string;
  /* Called after user drags an axis handle and releases. Gives the new price
     for the price-level identified by `type` (e.g. 'NBUY'). When the level
     supplied an `id`, it is forwarded so multi-row types (HVN) can be matched. */
  onPriceLevelChange?: (type: string, price: number, id?: string | number) => void;
  /* Called when the user clicks the × on a deletable handle (HVN). */
  onPriceLevelDelete?: (type: string, id?: string | number) => void;
  /* Called whenever a drag of a price-level handle (SL / Target / HVN /
   *  trigger) starts (true) or ends (false). The parent uses this to pause
   *  any periodic state replacement (e.g. the activeTrade poll) so the
   *  drag gesture isn't visibly reset mid-move. */
  onDragStateChange?: (dragging: boolean) => void;
  /* Hide-Candles mode (HC candle type). When true, the candle series is
   *  painted fully transparent — the series remains attached so the
   *  price-scale still uses its high/low to position indicators, but
   *  no candle bodies / wicks render. Indicators / signals / overlays
   *  continue to compute on the raw OHLC data underneath. */
  hideCandles?: boolean;
  /* Candle close countdown (TradingView-style) — requires interval in minutes. */
  intervalMinutes?: number;
  /* Lazy-load older candles when user pans the chart left past the first bar.
     Called with the oldest candle's `time` (UNIX sec, IST-shifted). Parent must
     fetch and prepend older candles — the chart will not re-fit after update. */
  /** `reason` distinguishes a deliberate drag past the left edge ('pan') from
   *  the merely-near-the-edge heuristic ('edge'), which also fires whenever a
   *  pane simply holds few bars. Callers that surface UI must only react to
   *  'pan', or they fire on their own without the user doing anything. */
  onLoadMore?: (oldestTime: number, reason?: 'pan' | 'edge') => void;
  /* Optional Delta-perpetual mark price — drawn as a dotted horizontal line.
     Hidden when null/undefined or when the symbol isn't a Delta perpetual. */
  markPrice?: number | null;
  markPriceColor?: string;
  /* Decimal precision for Y-axis price labels and crosshair price. Defaults to 2. */
  decimalPrecision?: number;
  /* Optional minimum price increment — used to snap dragged SL/Target/trigger
   * lines to a valid exchange tick. Falls back to 10^-decimalPrecision when
   * not provided. */
  tickSize?: number;
  /* Active OPEN position info — when set, a P&L pill is rendered on the right
   * edge near the LTP label showing live unrealised P&L vs the entry price.
   * Hidden when null/undefined. */
  positionPnl?: {
    side: 'LONG' | 'SHORT';
    entryPrice: number;
    quantity?: number;
    /** PAPER chart trade — the pill says so, so a simulated P&L is never read as real. */
    paper?: boolean;
  } | null;
  /* The ACTUAL last-traded price for this symbol — independent of the chart's
   * candle type. The LTP line and countdown are anchored here so HA / CSH /
   * CSL / CLH transforms (which derive synthetic close values for visual
   * smoothing) do NOT shift the LTP indicator away from the real market price.
   * Falls back to latestCandle?.close when not provided. */
  actualLtp?: number | null;
  /* Optional unrealised P&L (INR or native currency) for an open position on
   * this symbol. Rendered next to the LTP indicator alongside the countdown
   * and % change so the user can read entry → P&L at a glance. */
  unrealizedPnl?: number | null;
  pnlCurrency?: 'INR' | 'USD';
  /* When provided, the LTP overlay pill renders a small ✕ button that calls
   * this back so the user can close the open position straight from the chart.
   * The parent should confirm before calling the close API. */
  onCloseTrade?: () => void;
  /* Initial visible-bar count — drives the chart's first-load zoom. With
   * fitContent (default), 2 candles stretched across the full pane look like
   * bands not candles; this prop lets the caller enforce a sensible default
   * visible window so newly-listed symbols sit at the right of the pane with
   * empty space on the left, just like TradingView. */
  defaultVisibleBars?: number;
  /** Candle wick visibility — toggled live from the Charts toolbar (after the
   *  candle-type buttons). When false, candles render body-only (no high/low
   *  shadows). Applied via applyOptions so toggling doesn't rebuild the chart. */
  showWick?: boolean;
  /** Left inset (px) for chart-anchored overlays that would otherwise be drawn
   *  underneath a floating panel owned by the parent (the OverView panel). The
   *  bar-close countdown pill is positioned by PRICE, so it lands wherever the
   *  last close is — which for most of a session is inside the OverView panel.
   *  Undefined = no panel, sit at the default left edge. */
  overlayLeftInset?: number;
  /** The chart's selected candle type. Only CLN changes behaviour here: it
   *  turns on the linear-regression body layer and makes LR direction the
   *  sole colour source. Every other value renders exactly as before. */
  candleType?: string;
  /** Right-docked Option Profile (item 3) — per-strike OI/ΔOI/GEX bars aligned to
   *  the price Y-axis. Null/undefined = not shown. */
  optionProfile?: OptionProfileData | null;
  /** Reports the user's chosen zoom as a visible-candle COUNT (debounced),
   *  whenever they scroll-zoom. The parent persists it per layout and passes it
   *  back as `defaultVisibleBars` so the next symbol opens at the same zoom.
   *  Programmatic range changes (first-fit / auto-refresh restore) are excluded. */
  onVisibleBarsChange?: (bars: number) => void;
  /* Left-anchored pills attached to the SL and Target lines for an open
   * position, showing the INR risk/gain numbers from Trades Management. The
   * chart computes Y from `priceToCoordinate(price)` so the pills float at
   * the exact SL / Target line position regardless of zoom or pan. */
  tradeAmountPills?: Array<{
    type: 'SL' | 'TARGET';
    price: number;
    amountInr: number;
    profitLocked?: boolean;
  }>;
  /** Per-candle order-flow signals, one entry per display bar where flow data
   *  exists. Times are seconds-since-epoch and should already match the chart
   *  bar times (caller is responsible for any snapping). Hidden unless the
   *  `flow` overlay flag is on. */
  flowCandles?: FlowCandleSignal[];
  /** Funding-rate series for the `funding` overlay — { time (IST-shifted secs,
   *  matching candle.time), rate (% per 8h) }. Forward-filled onto candles. */
  fundingSeries?: Array<{ time: number; rate: number }>;
  /** Bar Replay (Round 68a). When set, the chart treats every series, overlay,
   *  signal and marker as if no candle with `time > replayMaxTime` exists.
   *  Indicators recompute step-by-step as the parent advances `replayMaxTime`
   *  so the user sees an honest historical view rather than a slideshow.
   *  Live-only decorations (LTP, mark price, latestCandle merge) are
   *  suppressed while this prop is set. */
  replayMaxTime?: number;
  /** Round 68b — fires on any chart click with the candle TIME under the
   *  cursor (in addition to onChartClick which gives the price). Used by
   *  Bar Replay to let the user click anywhere on the chart to seek the
   *  playhead. Time is the chart's display time (IST-shifted for symbols
   *  whose candles are IST-shifted). */
  onChartTimeClick?: (time: number) => void;
  /** Round 92 — when set, overrides the chart's layout background color (used
   *  by the "Bkground" trend-tint toggle). Falls back to the theme default. */
  backgroundColor?: string;
  /** Round 96 — per-bar OverView score series for the crosshair tooltip. When
   *  provided (OverView on), the tooltip summarises the hovered candle's
   *  OverView for the chart timeframe (overviewTf, exact bar) and its HTF
   *  (overviewHtf, the HTF bar containing the hovered time). */
  overviewTf?: Array<{ time: number; score: number; zone: OverviewZone; up: number; down: number; na: number }>;
  overviewHtf?: Array<{ time: number; score: number; zone: OverviewZone; up: number; down: number; na: number }>;
  overviewTfLabel?: string;
  overviewHtfLabel?: string;
  /** Round 98 — when true, overlay the chart-TF (thin) and HTF (thick) OverView
   *  trend score lines on the price pane via a hidden secondary scale. Uses
   *  overviewTf / overviewHtf for data and settings.trend for styling. */
  showTrendBand?: boolean;
  /** R110 — "Bkground" indicator: per-candle vertical background zones on the
   *  price pane, bullish when the combined TF+HTF trend score > 0 else bearish.
   *  Consumes overviewTf / overviewHtf; colours/opacity from settings.bkground. */
  showBkground?: boolean;
  /** R115 Stage 2 — symbol displayed in the unified crosshair tooltip header. */
  symbol?: string;
  /** R115 Stage 2 — running RockBand strategy id (passed through to the
   *  unified crosshair tooltip so it shows the position when relevant). */
  activeStrategy?: string;
  /** When the chart is a SYNTHETIC symbol, its constituent option legs — surfaced
   *  in the OHLC legend so the user sees exactly which contracts are being traded. */
  syntheticLegs?: Array<{ symbol?: string | null; action: 'BUY' | 'SELL'; ratio?: number }>;
  /** Live per-leg marks (symbol → LTP) — shown beside each constituent in the legend
   *  so the user sees which leg contributes how much of the combined premium. */
  syntheticLegMarks?: Record<string, number>;
  /** Alias resolution note (e.g. "→ GOLD26AUGFUT · MCX") — the actual contract an
   *  IDXN alias chart is showing, rendered right after the symbol in the legend. */
  symbolNote?: string | null;
  /** Append the account figures (open P&L, day P&L, portfolio, margin, liquidation) to the
   *  end of the OHLC legend. PRIMARY SPOT PANE ONLY — they are account-level, so repeating
   *  them on four quad panes or on every option leg would be noise dressed as data. */
  showAccountStats?: boolean;
  /** Rectangle zone annotations (item 3) — user-drawn time+price boxes, persisted
   *  per symbol by the parent. Pane 0 only. NOT clamped to replayMaxTime. */
  rectZones?: RectZone[];
  /** Two-click rectangle creation mode (corner 1 → corner 2; Esc cancels). */
  rectDrawMode?: boolean;
  /** Highlighted zone (brighter border + resize handles). */
  selectedRectId?: string | null;
  /** Commit a zones change (create / move / resize / delete). */
  onRectZonesChange?: (zones: RectZone[]) => void;
  /** A zone was clicked (select) — pos is in CLIENT px for the editor popover.
   *  Called with null when clicking empty space deselects. */
  onRectZoneSelect?: (id: string | null, pos?: { x: number; y: number }) => void;
  /** Draw mode finished (zone created or Esc) — parent disarms the toolbar chip. */
  onRectDrawComplete?: () => void;
  /** DbCrZone — Debit/Credit option-market regime change-points (epoch MS). Rendered as vertical
   *  background zones on the underlying (forward-filled between change-points). Colours/opacity
   *  from settings.dbcr. Presence (non-empty) enables the render. */
  dbcrZones?: Array<{ ts: number; zone: 'DEBIT' | 'CREDIT' | 'NEUTRAL'; score?: number | null }>;
}

/* ───── Order Flow ribbon state classifier (Round 67h) ─────
 * Maps a (buyPct, prevBuyPct) pair into a 7-state colour so a glance at
 * the ribbon tells you the regime: who has the order book, and whether
 * their grip is tightening or fading. State labels intentionally use
 * plain trader vocabulary — no Greek letters, no jargon.
 */
type FlowState =
  | 'BULLS_IN_CONTROL'   // strong buying, getting stronger
  | 'BULLS_HOLDING'      // strong buying, steady
  | 'BULLS_FADING'       // strong buying, but easing off
  | 'NEUTRAL'            // balanced — no clear bias
  | 'BEARS_FADING'       // strong selling, but easing off
  | 'BEARS_HOLDING'      // strong selling, steady
  | 'BEARS_IN_CONTROL';  // strong selling, getting stronger

const FLOW_STATE_COLORS: Record<FlowState, string> = {
  BULLS_IN_CONTROL: '#00c853', // deep green
  BULLS_HOLDING:    '#66bb6a', // medium green
  BULLS_FADING:     '#c5e1a5', // pale green
  NEUTRAL:          '#9e9e9e', // gray
  BEARS_FADING:     '#ffab91', // pale red/orange
  BEARS_HOLDING:    '#ef5350', // medium red
  BEARS_IN_CONTROL: '#c62828', // deep red
};

/** Pick the FlowState for one bar from its buyPct and the previous bar's
 *  buyPct. dir = sign of (buyPct - 0.5); trend = sign of delta vs prev. */
function classifyFlowState(buyPct: number, prevBuyPct: number | null): FlowState {
  if (!Number.isFinite(buyPct)) return 'NEUTRAL';
  const dir = buyPct - 0.5;
  // Within ±10 % of even → neutral regardless of trend
  if (Math.abs(dir) < 0.10) return 'NEUTRAL';
  const delta = prevBuyPct != null ? (buyPct - prevBuyPct) : 0;
  // "Fading" requires a clear move against the current direction — at
  // least 4 % swing — otherwise call it holding.
  const FADE_THRESHOLD = 0.04;
  if (dir > 0) {
    if (delta < -FADE_THRESHOLD) return 'BULLS_FADING';
    if (delta > FADE_THRESHOLD || dir > 0.20) return 'BULLS_IN_CONTROL';
    return 'BULLS_HOLDING';
  }
  if (delta > FADE_THRESHOLD) return 'BEARS_FADING';
  if (delta < -FADE_THRESHOLD || dir < -0.20) return 'BEARS_IN_CONTROL';
  return 'BEARS_HOLDING';
}

/* ───── indicator helpers ───── */
function sma(data: number[], p: number): (number | null)[] {
  const r: (number | null)[] = [];
  for (let i = 0; i < data.length; i++) {
    if (i < p - 1) { r.push(null); continue; }
    let s = 0; for (let j = i - p + 1; j <= i; j++) s += data[j];
    r.push(s / p);
  }
  return r;
}
function stdDev(data: number[], avg: (number | null)[], p: number): (number | null)[] {
  const r: (number | null)[] = [];
  for (let i = 0; i < data.length; i++) {
    if (avg[i] === null) { r.push(null); continue; }
    let sq = 0; for (let j = i - p + 1; j <= i; j++) sq += (data[j] - avg[i]!) ** 2;
    r.push(Math.sqrt(sq / p));
  }
  return r;
}
/** Convert `#rrggbb` (or already-rgba) into an rgba() string with the
 *  supplied alpha. Opacity 0..1. Returns the input untouched if it already
 *  contains rgba(), so user-supplied rgba strings pass through. */
/** CLN — how the REAL candle recedes behind the regression body.
 *
 *  Tuned against screenshots, and the first pass got it wrong in an instructive
 *  way: a 30% fill under a FULL-STRENGTH border reads as a HOLLOW candle, which
 *  already means something else to a trader. The real bar has to read as a
 *  ghost, not as a hollow bar — so the edge is softened along with the fill, and
 *  both sit well below the opaque regression body that is meant to dominate.
 *
 *  History of the pair, so nobody re-walks it: 0.22/0.55 read well on light but
 *  went dim on the near-black dark ground → 0.28/0.60. That was still too faint
 *  to read the real bar's body against the LR body, so both were raised TOGETHER
 *  to 0.44/0.80. Raising the fill alone brings back the hollow-candle read;
 *  raising the edge alone brings it back harder. Move them as a pair or not at all. */
const CLN_REAL_BODY_ALPHA = 0.44;
/** Border AND wick of the real candle. Must stay clearly below full strength —
 *  at 1.0 the real bar competes with the opaque regression body instead of
 *  sitting behind it, which inverts the whole point of the candle type. */
const CLN_REAL_EDGE_ALPHA = 0.80;
/** "Important fraction": a bar with no finer data stays a NORMAL candle at this
 *  opacity, so it can never be read as a fraction candle. */
const FRACTION_MISSING_ALPHA = 0.35;

/** AV_Band slow HMA — the line is coloured by where the CLN linear-regression
 *  close sits relative to the slow HMA *and* which way the slow HMA is sloping,
 *  so a single flat colour threw that information away. Agreement in either
 *  direction gets the directional hue; price on one side with the slope on the
 *  other — or a flat/unknown slope — gets the neutral hue, which is the state
 *  worth seeing (a trend losing its slope while price is still on the right side).
 *
 *  Deliberately NOT user-configurable: the colour is derived, so a picker for it
 *  would only let the user break the mapping. The old `avBand.slowColor` setting
 *  and its Chart Settings field are gone; the key is left in the payload schema
 *  because removing it buys nothing. */
const AV_SLOW_UP = '#26a69a';
const AV_SLOW_DOWN = '#ef5350';
const AV_SLOW_NEUTRAL = '#fbc02d';

function withAlpha(color: string, alpha: number): string {
  const a = Math.max(0, Math.min(1, alpha));
  if (!color) return `rgba(0,0,0,${a})`;
  if (color.startsWith('rgba')) return color;
  if (color.startsWith('rgb(')) {
    const inner = color.slice(4, -1);
    return `rgba(${inner},${a})`;
  }
  let hex = color.replace('#', '');
  if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
  if (hex.length !== 6) return color;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

function calcEma(data: number[], p: number): (number | null)[] {
  const r: (number | null)[] = []; const k = 2 / (p + 1); let prev: number | null = null;
  for (let i = 0; i < data.length; i++) {
    if (i < p - 1) { r.push(null); continue; }
    if (prev === null) { let s = 0; for (let j = i - p + 1; j <= i; j++) s += data[j]; prev = s / p; }
    else { prev = data[i] * k + prev * (1 - k); }
    r.push(prev);
  }
  return r;
}

/* ───── component ───── */
// ── Trend-line smoothing (Round 102) — turn the stepped −100..+100 score into a
//    smooth curve. All operate on a plain number[] and return the same length. ──
function emaSmooth(v: number[], p: number): number[] {
  if (p <= 1 || v.length === 0) return v.slice();
  const k = 2 / (p + 1); const out: number[] = new Array(v.length); let prev = v[0];
  for (let i = 0; i < v.length; i++) { prev = i === 0 ? v[0] : v[i] * k + prev * (1 - k); out[i] = prev; }
  return out;
}
/** LSMA — value of the least-squares regression line at the current bar. */
function lsmaSmooth(v: number[], p: number): number[] {
  if (p <= 1 || v.length === 0) return v.slice();
  const out: number[] = new Array(v.length);
  for (let i = 0; i < v.length; i++) {
    const n = Math.min(p, i + 1);
    let sx = 0, sy = 0, sxy = 0, sxx = 0;
    for (let j = 0; j < n; j++) { const x = j, y = v[i - n + 1 + j]; sx += x; sy += y; sxy += x * y; sxx += x * x; }
    const denom = n * sxx - sx * sx;
    if (denom === 0) { out[i] = v[i]; continue; }
    const slope = (n * sxy - sx * sy) / denom;
    const intercept = (sy - slope * sx) / n;
    out[i] = intercept + slope * (n - 1);
  }
  return out;
}
function wmaSmooth(v: number[], p: number): number[] {
  if (p <= 1 || v.length === 0) return v.slice();
  const out: number[] = new Array(v.length);
  for (let i = 0; i < v.length; i++) {
    const n = Math.min(p, i + 1); let num = 0, den = 0;
    for (let j = 0; j < n; j++) { const w = j + 1; num += v[i - n + 1 + j] * w; den += w; }
    out[i] = num / den;
  }
  return out;
}
/** Hull MA = WMA(2·WMA(p/2) − WMA(p), √p) — very smooth + responsive. */
function hullSmooth(v: number[], p: number): number[] {
  if (p <= 1 || v.length === 0) return v.slice();
  const half = Math.max(1, Math.floor(p / 2)), sq = Math.max(1, Math.round(Math.sqrt(p)));
  const w1 = wmaSmooth(v, half), w2 = wmaSmooth(v, p);
  const diff = v.map((_, i) => 2 * w1[i] - w2[i]);
  return wmaSmooth(diff, sq);
}
function smoothScores(values: number[], method: string, period: number): number[] {
  switch (method) {
    case 'ema': return emaSmooth(values, period);
    case 'hull': return hullSmooth(values, period);
    case 'none': return values.slice();
    case 'linreg': default: return lsmaSmooth(values, period);
  }
}

// ── Trend divergence (Round 106) ──────────────────────────────────────────
// Pivot-based regular + hidden divergence between an oscillator (the Trend's
// HMA — smooth, so its pivots are clean) and price. A pivot is a strict local
// extreme over [i-L, i+R]; each pivot is compared ONLY with the previous
// same-type pivot within [minBars, maxBars] to stay clutter-free.
//   • Regular Bullish:  price Lower-Low   + osc Higher-Low   → reversal up
//   • Regular Bearish:  price Higher-High + osc Lower-High   → reversal down
//   • Hidden  Bullish:  price Higher-Low  + osc Lower-Low    → uptrend continuation
//   • Hidden  Bearish:  price Lower-High  + osc Higher-High  → downtrend continuation
// Markers: arrows = regular, circles = hidden; below troughs (bull) / above
// peaks (bear). A pivot is only confirmed R bars after it forms (so the last
// R bars never carry a marker — standard, avoids look-ahead).
interface DivMarker {
  time: Time;
  position: 'aboveBar' | 'belowBar';
  color: string;
  shape: 'arrowUp' | 'arrowDown' | 'circle';
  size: number;
  text?: string;
}
function detectTrendDivergences(
  times: number[], osc: number[], priceHigh: number[], priceLow: number[],
  opts: { pivotL: number; pivotR: number; minBars: number; maxBars: number; bull: string; bear: string; includeHidden: boolean },
): DivMarker[] {
  const n = osc.length;
  const { pivotL: L, pivotR: R, minBars, maxBars, bull, bear, includeHidden } = opts;
  if (n < L + R + 2) return [];
  const lows: number[] = []; const highs: number[] = [];
  for (let i = L; i < n - R; i++) {
    const v = osc[i];
    if (!Number.isFinite(v)) continue;
    let isLow = true, isHigh = true;
    for (let j = i - L; j <= i + R; j++) {
      if (j === i) continue;
      if (!(osc[j] > v)) isLow = false;   // strict: every neighbour > v
      if (!(osc[j] < v)) isHigh = false;  // strict: every neighbour < v
      if (!isLow && !isHigh) break;
    }
    if (isLow) lows.push(i);
    if (isHigh) highs.push(i);
  }
  const out: DivMarker[] = [];
  const inRange = (p: number, q: number) => (p - q) >= minBars && (p - q) <= maxBars;
  for (let k = 1; k < lows.length; k++) {
    const q = lows[k - 1], p = lows[k];
    if (!inRange(p, q)) continue;
    const oUp = osc[p] > osc[q], oDn = osc[p] < osc[q];
    const pUp = priceLow[p] > priceLow[q], pDn = priceLow[p] < priceLow[q];
    if (pDn && oUp) out.push({ time: times[p] as Time, position: 'belowBar', color: bull, shape: 'arrowUp', size: 1 });
    else if (includeHidden && pUp && oDn) out.push({ time: times[p] as Time, position: 'belowBar', color: bull, shape: 'circle', size: 1 });
  }
  for (let k = 1; k < highs.length; k++) {
    const q = highs[k - 1], p = highs[k];
    if (!inRange(p, q)) continue;
    const oUp = osc[p] > osc[q], oDn = osc[p] < osc[q];
    const pUp = priceHigh[p] > priceHigh[q], pDn = priceHigh[p] < priceHigh[q];
    if (pUp && oDn) out.push({ time: times[p] as Time, position: 'aboveBar', color: bear, shape: 'arrowDown', size: 1 });
    else if (includeHidden && pDn && oUp) out.push({ time: times[p] as Time, position: 'aboveBar', color: bear, shape: 'circle', size: 1 });
  }
  // createSeriesMarkers requires markers sorted by time ascending.
  out.sort((a, b) => (a.time as number) - (b.time as number));
  return out;
}

// JHM (JSR Hilega-Milega) helpers (rsiWilder / vwmaSeries / emaSeries) now live
// in utils/jhm.ts so the chart oscillator and the OverView JHM voter stay in sync.

/** Bar Replay: truncate a time-keyed series at the playhead, memoised.
 *
 *  `cut == null` (not replaying) returns the original array UNCHANGED — same
 *  reference in, same reference out — so nothing downstream can tell this hook
 *  is here when replay is off. When replaying, the result changes identity only
 *  when the source array or the playhead changes, which is what keeps the
 *  chart's effects from re-running on every mouse move. */
function useReplayCut<T extends { time: any }>(rows: T[] | undefined, cut: number | null): T[] | undefined {
  return useMemo(
    () => (cut == null || !rows ? rows : rows.filter(r => (r.time as number) <= cut)),
    [rows, cut],
  );
}

/* ── Right-side empty space ────────────────────────────────────────────────
 *
 * The gap past the last candle is a FRACTION OF THE VIEW, not a bar count.
 * It used to be a user setting in bars (`rightOffsetBars`, default 20), which
 * meant one number had wildly different meanings per interval: on a 300-bar 4H
 * chart 20 bars is a tidy 6% of the pane, but on a 34-bar MONTHLY chart it is
 * 20 months — measured at 520px of a 1420px pane, 37% of the chart given over
 * to nothing. A fraction is the same amount of air everywhere, so the setting
 * had nothing left to configure and was removed.
 *
 * `want` and `gap` are derived together and used in three places that must
 * agree exactly — the whitespace series, the first-fit logical range, and the
 * remember-zoom subscriber — so they come from here and nowhere else.
 */
const RIGHT_GAP_FRACTION = 0.10;
const MIN_GAP_BARS = 2;
const MAX_GAP_BARS = 60;

/** Empty bar-slots to leave to the right of the last candle for a given view width. */
function gapBarsFor(visibleBars: number): number {
  const g = Math.round(Math.max(0, visibleBars) * RIGHT_GAP_FRACTION);
  return Math.max(MIN_GAP_BARS, Math.min(MAX_GAP_BARS, g));
}

/** The first-load geometry: how many real candles to show, and how much air after them. */
function plannedFit(count: number, remembered?: number): { want: number; gap: number } {
  // Never reserve more bar-slots than there are bars, or a sparse series gets
  // squeezed into a sliver at the right edge with hundreds of empty slots to
  // its left. The floor of 20 keeps the original intent — a 2-bar history
  // still must not be stretched across the whole pane.
  const want = Math.max(20, Math.min(remembered || count || 20, count || 20));
  return { want, gap: gapBarsFor(want) };
}

/** Inverse of the above: real candles implied by an observed visible span.
 *  Exact, so re-opening a chart reproduces the zoom instead of drifting a bar
 *  smaller on every round-trip (span = want + gap - 1, and gap is clamped, so
 *  dividing by 1 + fraction is only right in the unclamped middle). */
function realBarsFromSpan(span: number): number {
  let lo = 1, hi = 4000;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (mid + gapBarsFor(mid) - 1 < span) lo = mid + 1; else hi = mid;
  }
  return lo;
}

/* Set a logical range and PROVE it landed, re-applying until it does.
 *
 * lightweight-charts silently drops a setVisibleLogicalRange issued in the same synchronous
 * block as setData. Until this was hoisted, only the FIRST-FIT path verified; the
 * CONTINUATION restore — which runs on every refresh, every replay step and every rebuild —
 * was a bare set, and so was the jump-to-latest button. When such a set is dropped the chart
 * keeps the library default (barSpacing 6, last bar flush right), and the NEXT pass
 * snapshots that as "the user's range" and faithfully restores it: the wrong view latches
 * and compounds.
 *
 * MEASURED on the live app, BTCUSD split 8H/15m, 2026-09-10, two runs differing only in
 * poll cadence:
 *   liveUpdateSec 15 -> pane 0 opened at pxPerBar 4.096 with a 15-bar (9%) right gap
 *   liveUpdateSec  5 -> pane 0 opened at pxPerBar 5.97  with a  2-bar (1.8%) gap
 * 5.97 is the library default of 6 and 2 is MIN_GAP_BARS, i.e. at the faster cadence the
 * app's zoom and gap never reached the chart at all. Tripling the poll rate did not create
 * the bug; it made it likely enough to notice.
 *
 * `rafRef` is shared so a newer request cancels a pending retry instead of racing it.
 * `onSettled(landed)` reports whether the chart actually accepted the range — callers use
 * that to decide whether the fit may be marked done.
 */
function applyRangeVerified(
  ts: ReturnType<IChartApi['timeScale']>,
  target: { from: number; to: number },
  rafRef: React.MutableRefObject<number | null>,
  onSettled: (landed: boolean) => void,
): void {
  if (rafRef.current != null) {
    try { cancelAnimationFrame(rafRef.current); } catch { /* ignore */ }
    rafRef.current = null;
  }
  // Already there? Then do not touch the time scale at all. This is the difference between
  // "leave a settled view alone" and "stop correcting it": the check is against what the
  // chart is ACTUALLY showing right now, not against whether our arithmetic changed.
  try {
    const cur: any = ts.getVisibleLogicalRange();
    if (cur
      && Math.abs(Number(cur.from) - target.from) < 0.51
      && Math.abs(Number(cur.to) - target.to) < 0.51) { onSettled(true); return; }
  } catch { /* fall through and set it */ }
  const step = (attempt: number) => {
    try {
      ts.setVisibleLogicalRange(target as any);
    } catch {
      // The API rejected the range (e.g. degenerate data). Leave whatever the chart
      // settled on rather than fighting it.
      onSettled(false);
      return;
    }
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      let cur: any = null;
      try { cur = ts.getVisibleLogicalRange(); } catch { /* chart disposed */ }
      const landed = !!cur
        && Math.abs(Number(cur.from) - target.from) <= 1
        && Math.abs(Number(cur.to) - target.to) <= 1;
      // Bounded: a range the chart legitimately cannot honour must not spin forever.
      if (landed || attempt >= 4) { onSettled(landed); return; }
      step(attempt + 1);
    });
  };
  step(0);
}

export interface FnoViewProps {
  bars: FnoBar[];
  /** Legend title, e.g. "FNO · D0 → 03 Nov" */
  title: string;
  colors: { call: string; put: string; maxPain: string; gexFlip: string; pcr: string; iv: string; ivEma: string; pcrEma: string };
  pcr?: { line: boolean; ema: { time: number; value: number | null }[] | null } | null;
  iv?: { line: boolean; ema: { time: number; value: number | null }[] | null } | null;
  oi?: { mode: 'net' | 'total' } | null;
  /** Bar times (chart time) the expiry rolled at, with the label to print. */
  rolls?: Array<{ time: number; text: string }>;
  /** Bar times inside a collector outage — shaded so a hole reads as a hole. */
  gapTimes?: number[];
  /** Stepped fills on the price pane, one entry per contiguous run (they break at gaps). */
  bands?: Array<{ key: string; color: string; points: Array<{ time: number; upper: number; lower: number }> }>;
  /** γ-regime tint per bar: + = long gamma (pinning), − = short gamma (trending). */
  regime?: Array<{ time: number; sign: 1 | -1 }> | null;
}

/** A LineSeries point list with gaps: lightweight-charts JOINS a line across whitespace,
 *  so a missing value is drawn as the previous level in a transparent colour (and the
 *  point before the hole too), which ends the visible line at the last real value and
 *  restarts it at the next one. */
function gapLine(data: Array<{ time: number; value: number | null }>, color: string) {
  let last: number | null = null;
  for (const d of data) if (d.value != null && Number.isFinite(d.value)) { last = d.value; break; }
  const out: any[] = [];
  for (let i = 0; i < data.length; i++) {
    const v = data[i].value;
    if (v == null || !Number.isFinite(v)) {
      out.push(last == null ? { time: data[i].time as Time } : { time: data[i].time as Time, value: last, color: 'rgba(0,0,0,0)' });
      continue;
    }
    last = v;
    const nx = data[i + 1]?.value;
    const holeNext = i < data.length - 1 && (nx == null || !Number.isFinite(nx));
    out.push({ time: data[i].time as Time, value: v, color: holeNext ? 'rgba(0,0,0,0)' : color });
  }
  return out;
}

const TradingChart: React.FC<Props> = ({ candles, rawCandles, overlays = {}, auxCandles, benchmarkCandles, benchmarkLabel, loading, height = 500, latestCandle, rawLatestCandle, fraction, fractionLatest, priceLevels, fnoLevels, priceZones, pnlBadge, onPnlBadgeClick, leftLevels, onChartClick, strategyOverlay, crosshairGroup, onPriceLevelChange, onPriceLevelDelete, onDragStateChange, hideCandles, candleType, overlayLeftInset, intervalMinutes, onLoadMore, markPrice, markPriceColor, decimalPrecision = 2, tickSize, positionPnl, actualLtp, unrealizedPnl, pnlCurrency, onCloseTrade, defaultVisibleBars, onVisibleBarsChange, showWick, optionProfile, tradeAmountPills, flowCandles, fundingSeries, replayMaxTime, onChartTimeClick, backgroundColor, overviewTf, overviewHtf, overviewTfLabel, overviewHtfLabel, showTrendBand, showBkground, symbol, activeStrategy, syntheticLegs, syntheticLegMarks, symbolNote, showAccountStats, rectZones, rectDrawMode, selectedRectId, onRectZonesChange, onRectZoneSelect, onRectDrawComplete, dbcrZones, fnoInterp, fnoView, fnoSelect, synthiya }) => {
  // ── Bar Replay (Round 68a) ────────────────────────────────────────────
  // When replayMaxTime is set we treat the chart as if no candle with
  // time > replayMaxTime exists. Truncating the inputs at the top means
  // every downstream derivation (series, overlays, signals, markers,
  // first-fit zoom) automatically sees the historical view — no need to
  // sprinkle replay-aware branches through 1500 lines of effects.
  //
  // The MEMOISATION below is load-bearing, not tidiness. These filters used to
  // run inline on every render, so every consumer got a NEW array/object
  // identity each time. Outside replay the same props are stable memos from
  // ChartsPage and the effects keyed on them sit idle; under replay they
  // changed on every render — and the crosshair handler setStates on every
  // mouse move, so a single mouse move re-ran: the whole applyData rebuild, the
  // crosshair-sync effect (which called clearCrosshairPosition() and WIPED the
  // crosshair out from under the pointer), the visible-range restore (which
  // snapped any pan/zoom back to the playhead), the LTP price line and the
  // bar-close countdown interval. Measured on BTCUSD 4H: one mouse move cost
  // 75ms live vs 140ms replaying, with 4x the long tasks. Keying each memo on
  // [prop, replayCut] means identity now changes only when the playhead
  // actually moves — which is exactly when a rebuild IS wanted.
  const replayCut = (replayMaxTime != null && Number.isFinite(replayMaxTime)) ? replayMaxTime : null;
  const candlesCut = useReplayCut(candles, replayCut) as CandleRow[];
  const rawCandlesCut = useReplayCut(rawCandles, replayCut);
  const benchmarkCut = useReplayCut(benchmarkCandles, replayCut);
  const flowCut = useReplayCut(flowCandles, replayCut);
  const fundingCut = useReplayCut(fundingSeries, replayCut);
  const overviewTfCut = useReplayCut(overviewTf, replayCut);
  const overviewHtfCut = useReplayCut(overviewHtf, replayCut);
  // Fraction bars are a separate series, so they need their own cut. A closed bar's
  // fraction row ends at that bar's close, so the cut alone keeps replay honest; the
  // forming bar does not exist in replay.
  const fractionBarsCut = useReplayCut(fraction?.bars, replayCut);
  // Aux candles (CTF/HTF used by CPR/POC/AvgCloud) also need clamping so the
  // period boundaries don't pull in future data.
  const auxCut = useMemo<AuxCandles | undefined>(() => {
    if (replayCut == null || !auxCandles) return auxCandles;
    return {
      ...auxCandles,
      ctf: auxCandles.ctf?.filter(c => (c.time as number) <= replayCut),
      htf: auxCandles.htf?.filter(c => (c.time as number) <= replayCut),
    };
  }, [auxCandles, replayCut]);
  // Strategy trail/extra lines must also stop at the playhead (markers are
  // already clipped by the candleTimes inRange filter; lines are not).
  const strategyCut = useMemo<StrategyOverlay | undefined>(() => {
    if (replayCut == null || !strategyOverlay?.lines?.length) return strategyOverlay;
    return {
      ...strategyOverlay,
      lines: strategyOverlay.lines.map(l => ({ ...l, data: l.data.filter(d => d.time <= replayCut) })),
    };
  }, [strategyOverlay, replayCut]);

  const preReplayCandleCount = candles.length;
  /* This pane has bars, but ALL of them are newer than the playhead - pane 0
     drives replayMaxTime for every pane, so a shorter-history pane can filter
     to nothing. Without this the pane showed the generic "nothing came back"
     empty state, which reads as a data failure rather than a replay position. */
  const replayEmptiedPane = replayCut != null && preReplayCandleCount > 0 && candlesCut.length === 0;
  candles = candlesCut;
  rawCandles = rawCandlesCut;
  benchmarkCandles = benchmarkCut;
  flowCandles = flowCut;
  fundingSeries = fundingCut;
  overviewTf = overviewTfCut;
  overviewHtf = overviewHtfCut;
  auxCandles = auxCut;
  strategyOverlay = strategyCut;
  if (replayCut != null) {
    // Live decorations don't make sense in a frozen historical window.
    latestCandle = undefined;
    fractionLatest = undefined;
    markPrice = null;
    actualLtp = null;
  }

  const wrapperRef = useRef<HTMLDivElement>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const realOhlcPrimitiveRef = useRef<RealOhlcPrimitive | null>(null);
  const lrBodyPrimitiveRef = useRef<LrBodyPrimitive | null>(null);
  const optionProfilePrimitiveRef = useRef<OptionProfilePrimitive | null>(null);
  const pnlBadgePrimRef = useRef<PnlBadgePrimitive | null>(null);
  /* Rectangle zone annotations (item 3) — primitive + which series it's attached to
     (the big build effect recreates the series; we re-attach when that happens). */
  const rectPrimRef = useRef<RectangleZonePrimitive | null>(null);
  const rectAttachedToRef = useRef<any>(null);
  const rectDraftRef = useRef<{ time: number; price: number } | null>(null);
  const rectLiveRef = useRef<RectZone[] | null>(null); // zones during an in-flight drag
  const overlaySeriesRefs = useRef<ISeriesApi<'Line'>[]>([]);
  const primitivesRef = useRef<Array<BandPrimitive | VerticalLinePrimitive | VerticalZonesPrimitive | HorizontalLevelsPrimitive | HorizontalZonesPrimitive | SrBandPrimitive | OrderBlockPrimitive | PnlBadgePrimitive | LeftLabeledLevelsPrimitive>>([]);
  const markersPluginRef = useRef<any>(null);
  const priceLinesRef = useRef<any[]>([]);
  const roRef = useRef<ResizeObserver | null>(null);
  const firstFitDoneRef = useRef(false);
  // True only while WE set the visible range programmatically (first-fit /
  // auto-refresh restore), so the "remember zoom" subscriber ignores those
  // events and captures genuine user scroll-zoom only.
  const programmaticRangeRef = useRef(false);
  // Identifies which applyData pass currently owns the programmatic guard. A
  // verified first-fit releases it several frames later, by which time a newer
  // pass may have taken over — the token stops the stale one releasing it early.
  const programmaticTokenRef = useRef(0);
  // rAF handle of an in-flight verified first-fit, so a new pass cancels the old.
  const fitRafRef = useRef<number | null>(null);
  // False until a first-fit has finished proving itself. The remember-zoom
  // subscriber refuses to persist while this is false — before the fix it
  // captured lightweight-charts' OWN default view (238 bars) and wrote it into
  // chart_visibleBars_v1 as if the user had chosen it, silently overwriting the
  // real remembered zoom on charts nobody had touched.
  const fitSettledRef = useRef(false);
  // R105 — remembered STRETCH FACTOR of the shared lower indicator pane (pane 1),
  // so a user's drag-resize survives data refreshes and chart rebuilds. Stretch
  // factors are relative weights (not pixels), so capturing + restoring them is
  // idempotent — unlike setHeight/getHeight, which don't round-trip cleanly and
  // caused the pane to creep taller on every refresh.
  const lowerPaneStretchRef = useRef<number | null>(null);
  // FNO lower panes (PCR / IV / OI): the series that anchors each one, the user's
  // remembered stretch per pane, and the crosshair hook that refreshes their labels.
  const fnoPaneSeriesRef = useRef<Record<string, ISeriesApi<any>>>({});
  const fnoPaneStretchRef = useRef<Record<string, number>>({});
  const fnoPaneLegendRef = useRef<((t: number | null) => void) | null>(null);
  const fnoBarByTime = useMemo(() => new Map((fnoView?.bars || []).map(b => [b.time, b] as const)), [fnoView]);
  const fnoGapSet = useMemo(() => new Set(fnoView?.gapTimes || []), [fnoView]);
  // Round 42 — last-candle time from the previous applyData run. Used to tell a
  // genuine continuation (auto-refresh / lazy-load of the same chart) apart from
  // a fresh load (new symbol / interval), so the visible range is preserved on
  // the former and re-fitted on the latter.
  const prevLastCandleTimeRef = useRef<number | null>(null);
  // Round 44 — candle count from the previous applyData run. Lets first-fit
  // tell a "settled" small dataset (genuinely short history → safe to lock)
  // apart from a still-growing one (initial load mid-flight → don't lock yet).
  const prevCandleCountRef = useRef<number>(0);
  // First-candle time from the previous applyData run. Bar Replay steps BACK by
  // shrinking the array, which the last-candle continuation test reads as fresh
  // data; matching on the FIRST bar identifies it as the same series.
  const prevFirstCandleTimeRef = useRef<number | null>(null);
  /* Round 32 — callback ref pointing at the latest data-application closure.
   * The data effect updates this ref every time it runs (capturing current
   * candles/overlays/etc. in its closure). The chart-creation effect calls
   * applyDataRef.current?.() right after building a fresh chart instance,
   * which guarantees the new chart immediately receives current data —
   * even if the data effect's deps haven't changed and React would
   * therefore skip re-running it this commit.
   *
   * Why this matters: prior to round 32 the chart could be re-created for
   * any reason (theme flip, settings template save) without the data
   * effect re-running, leaving a freshly-built but visually empty chart.
   * The decimalPrecision fix removed the most common cause, but this ref
   * is the architectural belt-and-braces — any future trigger that
   * re-runs chart-creation will still result in a populated chart. */
  const applyDataRef = useRef<(() => void) | null>(null);
  // Declared up-front so the data-clear branch of the main effect (which runs
  // when candles becomes empty mid-symbol-switch) can detach these without
  // TDZ errors. Bodies stay further down where they are managed.
  const ltpPriceLineRef = useRef<any>(null);
  const markPriceLineRef = useRef<any>(null);
  /** Last crosshair time applied from the parent's sync, so the sync effect can
   *  tell a genuine "partner left the chart" (clear) from a no-op re-run. */
  const lastSyncTimeRef = useRef<number | null>(null);
  /** Live handle into the crosshair mirroring group; null when this pane has no group. */
  const crosshairHandleRef = useRef<CrosshairHandle | null>(null);
  /** The right-side whitespace series. PERSISTENT across data passes on purpose — see the
   *  block that writes it. `wsKeyRef` is its current (bars|lastBarTime|spacing) signature so
   *  the data is only re-written when it actually differs. */
  const wsSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const wsKeyRef = useRef<string>('');
  /** Consecutive first-fit passes whose range never landed. Bounds the re-fit retry. */
  const fitFailsRef = useRef(0);
  /** Deferred "first fit is done" commit — run only once the fit has proven it landed. */
  const pendingFitLockRef = useRef<((landed: boolean) => void) | null>(null);
  const { C, isDark } = useAppTheme();
  const { settings } = useChartSettings();

  /* ── CLN (Candle + Linear regressioN) ──────────────────────────────────
   * The regression bodies for the LR layer, and the per-bar LR direction the
   * candle colours are driven from. Computed from the RAW bars — CLN's own
   * transform is a passthrough, so `candles` and `rawCandles` agree here, but
   * reading raw keeps this consistent with every other indicator in this file.
   * Empty (and therefore free) for every other candle type. */
  // The LR body is ON for the CLN candle type (a preset) or via the LR chip on any
  // type. "Ghost" = the CLN look: the candle series fades and takes the LR hue, and
  // strategy candle colours are suppressed. Under the LR chip it is a setting
  // (candle.lrGhost, default on); under CLN it is always on, as before.
  const isClnType = candleType === 'CLN';
  const lrOn = isClnType || overlays.lrBody === true;
  const lrGhost = isClnType || (lrOn && settings.candle?.lrGhost !== false);
  const clnUp = settings.candle.risingColor || C.chartBull;
  const clnDown = settings.candle.fallingColor || C.chartBear;
  const clnBars = useMemo(() => {
    if (!lrOn) return [];
    // Under an "important fraction" BOTH CLN layers follow it (owner, 2026-09-30):
    // the ghost is the fraction bar (it is the display series) and the regression
    // runs over the fraction bars' opens and closes.
    const base = fraction ? (fractionBarsCut || []) : ((rawCandles && rawCandles.length) ? rawCandles : candles);
    // Replay: never fold in the LIVE forming bar — it lies past the playhead and would
    // draw a future regression body (the P-Zone memo below guards the same way).
    const latest = fraction ? fractionLatest : (replayCut == null ? rawLatestCandle : null);
    if (!base || !base.length) return [];
    // Fold in the forming bar so the right-edge regression body updates live
    // instead of stopping one bar short of the market.
    const src = (latest && Number.isFinite(latest.time as number)
      && (base[base.length - 1].time as number) < (latest.time as number))
      ? [...base, latest] : base;
    return computeClnBars(src, Number(settings.candle?.clnLength) || CLN_DEFAULT_LENGTH);
  }, [lrOn, candles, rawCandles, rawLatestCandle, replayCut, fraction, fractionBarsCut, fractionLatest, settings.candle?.clnLength]);

  /* P-Zone (utils/profitZone.ts). Computed from the REAL bars (never the display
   * candle type or an important-fraction bar) and the mapped HTF series. Each bar
   * reads only the HTF bar closed by its own open, so the un-cut aux HTF series is
   * safe under replay: the playhead bar cannot see a later HTF close. */
  const profitZones = useMemo(() => {
    if (!overlays.profitZone) return null;
    const base = (rawCandles && rawCandles.length) ? rawCandles : candles;
    if (!base || !base.length) return null;
    const bars = (replayCut == null && rawLatestCandle && Number.isFinite(rawLatestCandle.time as number)
      && (base[base.length - 1].time as number) < (rawLatestCandle.time as number)) ? [...base, rawLatestCandle] : base;
    const pz = (settings as any).profitZone || {};
    const res = computeProfitZones(bars as CandleRow[], Number(intervalMinutes) || 0,
      (auxCandles?.srHtf || []) as CandleRow[], Number(auxCandles?.srHtfMinutes) || 0,
      { clnLength: Number(settings.candle?.clnLength) || CLN_DEFAULT_LENGTH, m1: Number(pz.m1), m2: Number(pz.m2) });
    const byTime = new Map<number, ProfitZonePoint | ProfitZoneGap>();
    res.points.forEach((p, i) => byTime.set(bars[i].time as number, p));
    return { points: res.points, byTime, bars };
  }, [overlays.profitZone, rawCandles, candles, rawLatestCandle, replayCut, auxCandles, intervalMinutes, settings]);

  /* The candles BBR and Wave COMPUTE on — deliberately NOT the chart's display
   * candle type.
   *
   * Switching the chart to Heikin-Ashi to read the trend used to silently
   * re-compute both oscillators on synthetic prices: levels moved and marks
   * appeared or vanished for a reason that had nothing to do with the market.
   * This file already states that intent for signal markers ("computed on raw
   * OHLC so positions & values are stable across display candle types"); the
   * oscillator block was the one place that did not honour it.
   *
   * ONE source feeds all four renderings — the BBR curve, the Wave curve, the
   * BW/BD/X marks and the BBR candle tint — so they can never disagree.
   *
   * Prices are NOT taken from here. The trade walk is still priced on real
   * bars with a real ATR, because a stop measured in Heikin-Ashi points is not
   * a stop anyone can place. */
  const bwSrcType = String((settings as any)?.bbrWave?.srcCandle || 'Regular') as IndicatorSource;
  const bwClnLen = Number(settings.candle?.clnLength) || CLN_DEFAULT_LENGTH;

  /* ALIGNED BY TIME, NOT BY INDEX.
   *
   * `candles` is the capped tail of the display series with the live bar
   * appended; `rawCandles` is the full closed history. Their LENGTHS never
   * match, so the first version of this - which fell back to `candles` unless
   * the two arrays were the same length - fell back every single time and
   * quietly disabled the whole feature. The probe caught it: changing the
   * source from Regular to CLN moved exactly 0.00% of the pane's pixels while
   * changing the CHART's candle type still moved 10.5% of them.
   *
   * A defensive fallback that hides the thing it is guarding is worse than no
   * guard, so the join is on bar TIME and the window is the one the chart
   * actually draws. */
  const bwRawByTime = useMemo(() => {
    const m = new Map<number, CandleRow>();
    const base = (rawCandles && rawCandles.length) ? rawCandles : candles;
    for (const c of base || []) m.set(c.time as number, c);
    // The forming bar too, so the right edge is the same geometry as the rest
    // of the series instead of reverting to the display candle for one bar.
    if (rawLatestCandle && Number.isFinite(rawLatestCandle.time as number)) {
      m.set(rawLatestCandle.time as number, rawLatestCandle as unknown as CandleRow);
    }
    return m;
  }, [candles, rawCandles, rawLatestCandle]);

  /** Real market bars for the window on screen. PRICES come from here. */
  const bwRealBars = useMemo(
    () => (candles || []).map(c => bwRawByTime.get(c.time as number) || c),
    [candles, bwRawByTime],
  );

  /** The same window as the indicator's chosen source. LEVELS come from here. */
  const bwSource = useMemo(
    () => (bwSrcType === 'Regular' ? bwRealBars : indicatorSourceCandles(bwRealBars, bwSrcType, bwClnLen)),
    [bwRealBars, bwSrcType, bwClnLen],
  );

  /** LR direction by bar time — the colour source for BOTH CLN layers. */
  const clnDirByTime = useMemo(() => {
    const m = new Map<number, boolean>();
    for (const b of clnBars) m.set(b.time, b.up);
    return m;
  }, [clnBars]);

  const isFillHeight = height === '100%';

  /* Destroy chart cleanly */
  const destroyChart = useCallback(() => {
    if (roRef.current) { roRef.current.disconnect(); roRef.current = null; }
    overlaySeriesRefs.current = [];
    candleSeriesRef.current = null;
    // The whitespace series outlives a data pass but NOT the chart it belongs to.
    wsSeriesRef.current = null;
    wsKeyRef.current = '';
    if (chartRef.current) { try { chartRef.current.remove(); } catch {} chartRef.current = null; }
  }, []);

  /* Create chart on mount + theme change. NO setTimeout — refs are always present. */
  useEffect(() => {
    const el = chartContainerRef.current;
    if (!el) return;
    destroyChart();

    const w = el.clientWidth || 600;
    const h = isFillHeight
      ? (wrapperRef.current?.clientHeight || 400)
      : (typeof height === 'number' ? height : 500);

    const prec = Math.max(0, Math.min(8, Math.floor(decimalPrecision)));
    const chart = createChart(el, {
      width: w,
      height: h,
      layout: {
        background: { type: ColorType.Solid, color: backgroundColor || (isDark ? '#0D1117' : '#FFFFFF') },
        textColor: isDark ? '#C9D1D9' : '#333333',
        // R105 — lower indicator pane: draggable separator so the user can
        // resize the sub-pane that holds Trend / RelStrength / ChgN% / MACD / JHM.
        panes: {
          enableResize: true,
          separatorColor: isDark ? '#2b3947' : '#cfd8dc',
          separatorHoverColor: isDark ? 'rgba(120,144,156,0.45)' : 'rgba(120,144,156,0.3)',
        },
      },
      grid: { vertLines: { visible: false }, horzLines: { visible: false } },
      // Crosshair — the library default is a pale dotted grey that is hard to spot on a
      // dense chart. Use a high-contrast line + a solid label chip in both themes.
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color: isDark ? '#d1d9e6' : '#1c2733',
          width: 1, style: 2, labelVisible: true,
          labelBackgroundColor: isDark ? '#33415a' : '#1c2733',
        },
        horzLine: {
          color: isDark ? '#d1d9e6' : '#1c2733',
          width: 1, style: 2, labelVisible: true,
          labelBackgroundColor: isDark ? '#33415a' : '#1c2733',
        },
      },
      rightPriceScale: { borderColor: isDark ? '#1e2d3d' : '#e0e0e0' },
      timeScale: {
        borderColor: isDark ? '#1e2d3d' : '#e0e0e0',
        timeVisible: true, secondsVisible: false,
        // Start with NO gap; applyData sets the real one (plannedFit) once it
        // knows how many candles there are, since the gap is a fraction of the
        // view rather than a fixed bar count.
        // The right-hand empty space is materialised as WHITESPACE BARS on a hidden
        // series (see `wsSeriesRef` below) rather than as a bare `rightOffset` gap.
        // lightweight-charts only draws the crosshair's time-axis label over bars that
        // exist on the time scale, so with a plain rightOffset the date/time label
        // vanished the moment the crosshair passed the last candle. Whitespace bars give
        // the scale real time points (they draw nothing) and the label follows.
        rightOffset: 0,
        // Round 39 — keep the user's pan/zoom position when new bars arrive.
        // Without this, lightweight-charts auto-shifts the visible range to
        // the right whenever cs.setData / cs.update appends a bar past the
        // current right edge — including the WhitespaceData entries the
        // projection block appends. The user complaint "I drag left to see
        // history, the chart jumps back to the live edge on next refresh"
        // was caused by this default behaviour.
        shiftVisibleRangeOnNewBar: false,
      },
      localization: {
        priceFormatter: (p: number) => p.toFixed(prec),
        // Crosshair time-axis label — include the DAY OF WEEK alongside date/time.
        // Chart times are IST-shifted unix seconds (getUTC* reads IST wall-clock);
        // daily candles may arrive as a {year,month,day} business-day object.
        timeFormatter: (t: any) => {
          let d: Date | null = null;
          if (typeof t === 'number') d = new Date(t * 1000);
          else if (t && typeof t === 'object' && t.year) d = new Date(Date.UTC(t.year, (t.month || 1) - 1, t.day || 1));
          if (!d || isNaN(d.getTime())) return String(t);
          const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getUTCDay()];
          const MO = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getUTCMonth()];
          const dd = String(d.getUTCDate()).padStart(2, '0');
          const isIntraday = typeof t === 'number';
          const hhmm = `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
          return isIntraday ? `${WD} ${dd} ${MO} ${hhmm}` : `${WD} ${dd} ${MO} ${d.getUTCFullYear()}`;
        },
      },
    });
    chartRef.current = chart;

    const rising = settings.candle.risingColor || C.chartBull;
    const falling = settings.candle.fallingColor || C.chartBear;
    // When borderMatchesFill / wickMatchesFill are false, use a neutral slate
    // outline so hollow/muted candles remain readable on either theme.
    const neutralEdge = isDark ? '#90a4ae' : '#546e7a';
    const borderUp = settings.candle.borderMatchesFill ? rising : neutralEdge;
    const borderDown = settings.candle.borderMatchesFill ? falling : neutralEdge;
    const wickUp = settings.candle.wickMatchesFill ? rising : neutralEdge;
    const wickDown = settings.candle.wickMatchesFill ? falling : neutralEdge;
    const minMove = Math.pow(10, -prec);
    const cs = chart.addSeries(CandlestickSeries, {
      upColor: rising, downColor: falling,
      borderUpColor: borderUp, borderDownColor: borderDown,
      wickUpColor: wickUp, wickDownColor: wickDown,
      // Candle wicks toggle (Charts toolbar). When OFF, only the bodies render.
      // Default ON. Live toggling is applied via applyOptions below (no rebuild).
      wickVisible: showWick !== false,
      // The built-in last-value price line tracks the last data point's close
      // — which for HA / CSH / CSL / CLH is a SMOOTHED value, not the real LTP.
      // We hide it and draw a dedicated manual price line below that anchors
      // to actualLtp regardless of candle type.
      priceLineVisible: false,
      lastValueVisible: false,
      priceFormat: { type: 'price', precision: prec, minMove },
    });
    candleSeriesRef.current = cs;

    // Real-OHLC marks for smoothed candle types (HA/CSH/CSL/CLH) — faint true-range
    // hairline + real-close tick. Part of the wick layer (hidden when wicks off).
    try {
      const range = isDark ? 'rgba(207,216,220,0.30)' : 'rgba(55,71,79,0.28)';
      const cap = isDark ? 'rgba(236,239,241,0.92)' : 'rgba(38,50,56,0.85)';
      const realOhlc = new RealOhlcPrimitive({ range, cap, up: rising, down: falling });
      (cs as any).attachPrimitive(realOhlc);
      realOhlcPrimitiveRef.current = realOhlc;
    } catch { /* primitive optional */ }

    // CLN — the linear-regression body layer. Attached always, fed only when the
    // CLN candle type is selected (setData([]) otherwise), so switching candle
    // types never rebuilds the chart. Body-only by construction: this primitive
    // has no code path that draws a wick.
    try {
      const lrBody = new LrBodyPrimitive({
        up: rising,
        down: falling,
        // Contradiction accent — deliberately a neutral outline rather than a
        // third hue, so it cannot be misread as a direction.
        accent: isDark ? 'rgba(236,239,241,0.85)' : 'rgba(38,50,56,0.80)',
      });
      (cs as any).attachPrimitive(lrBody);
      lrBodyPrimitiveRef.current = lrBody;
    } catch { /* primitive optional */ }

    // Option Profile rail (item 3) — per-strike OI/ΔOI/GEX bars on the right.
    try {
      const prof = new OptionProfilePrimitive({
        ce: isDark ? '#ef5350' : '#e53935', pe: rising, gexPos: rising, gexNeg: falling,
        build: rising, unwind: falling, text: isDark ? '#cfd8dc' : '#37474f',
        // Near-opaque panel so the OI/ΔOI/GEX profiles stand out clearly against the
        // candles behind (theme-aware). A subtle left divider frames the rail.
        railBg: isDark ? 'rgba(13,17,23,0.86)' : 'rgba(245,247,250,0.90)',
        maxPain: '#ffb300', flip: '#ab47bc',
      });
      (cs as any).attachPrimitive(prof);
      optionProfilePrimitiveRef.current = prof;
    } catch { /* primitive optional */ }

    // ResizeObserver for responsive sizing
    const ro = new ResizeObserver(() => {
      if (!chartRef.current || !chartContainerRef.current) return;
      const nw = chartContainerRef.current.clientWidth || w;
      const nh = isFillHeight ? (wrapperRef.current?.clientHeight || h) : h;
      chartRef.current.applyOptions({ width: nw, height: nh });
    });
    ro.observe(el);
    if (isFillHeight && wrapperRef.current) ro.observe(wrapperRef.current);
    roRef.current = ro;

    // After the fresh chart instance is fully wired up (series + RO), apply
    // whatever data the parent currently has. Without this call a chart
    // re-created for reasons unrelated to the data effect's deps would sit
    // empty until the next prop change happened to retrigger that effect.
    try { applyDataRef.current?.(); } catch { /* the data effect will retry */ }

    return () => destroyChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDark, settings.candle, settings.ltp]);

  /* Candle wick toggle — apply live via applyOptions so flipping it from the
     Charts toolbar is INSTANT and never rebuilds the chart/series. */
  useEffect(() => {
    try { candleSeriesRef.current?.applyOptions({ wickVisible: showWick !== false }); } catch { /* ignore */ }
  }, [showWick]);

  /* Real-OHLC marks (item 16): for smoothed candle types the drawn candle hides the
   * real low/high (when a wick is absent) and the real close (always). Feed the
   * primitive the REAL bar OHLC for bars whose display differs from raw; clear when
   * wicks are off or on a Regular chart (display == raw → nothing hidden). */
  /* Option Profile data → primitive (item 3). */
  useEffect(() => {
    try { optionProfilePrimitiveRef.current?.setData(optionProfile || null); } catch { /* ignore */ }
  }, [optionProfile]);

  /* CLN — feed the linear-regression body layer. HC (Hide Candles) blanks it
   * too: "hide candles" has to mean BOTH layers, or HC would still paint a
   * chart full of regression bodies. */
  useEffect(() => {
    const prim = lrBodyPrimitiveRef.current;
    if (!prim) return;
    try { prim.setShowAccent(settings.candle?.clnVolumeAccent === true); } catch { /* ignore */ }
    if (!lrOn || hideCandles || !clnBars.length) { prim.setData([]); return; }
    const pts: LrBodyPoint[] = clnBars.map(b => ({
      time: b.time, lrOpen: b.lrOpen, lrClose: b.lrClose, up: b.up, contradiction: b.volContradiction,
    }));
    prim.setData(pts);
  }, [lrOn, clnBars, hideCandles, settings.candle?.clnVolumeAccent]);

  useEffect(() => {
    const prim = realOhlcPrimitiveRef.current;
    if (!prim) return;
    // CLN draws the REAL candle as its base series, so nothing is hidden and
    // the real-OHLC caps/close-ticks would be pure duplication - unless a fraction
    // is on, when the base series is the fraction bar and the real range is hidden.
    if (isClnType && !fraction) { prim.setData([]); return; }
    if (showWick === false || !rawCandles || !rawCandles.length) { prim.setData([]); return; }
    const byTime = new Map<number, CandleRow>();
    for (const d of candles) byTime.set(d.time as number, d);
    const pts: RealOhlcPoint[] = [];
    for (const r of rawCandles) {
      const d = byTime.get(r.time as number);
      if (!d) continue;
      if (Number(d.high) === Number(r.high) && Number(d.low) === Number(r.low) && Number(d.close) === Number(r.close)) continue; // Regular bar — nothing hidden
      // De-clutter (item): the DRAWN (smoothed) candle's wick already reveals an
      // extreme, so only mark the real High/Low on the side that has NO wick.
      // Both wicks → only the Close tick. Always show the Close (HA never shows it).
      const dHasUpper = Number(d.high) > Math.max(Number(d.open), Number(d.close));
      const dHasLower = Number(d.low) < Math.min(Number(d.open), Number(d.close));
      pts.push({
        time: r.time as number, high: Number(r.high), low: Number(r.low), close: Number(r.close),
        up: Number(r.close) >= Number(r.open),
        showHigh: !dHasUpper, showLow: !dHasLower,
      });
    }
    prim.setData(pts);
  }, [candles, rawCandles, showWick, isClnType, fraction]);

  /* Round 32 — apply decimalPrecision changes WITHOUT recreating the chart.
   *
   * Previously `decimalPrecision` was in the chart-creation effect's deps.
   * That caused intermittent blank-charts after a symbol click: the data
   * effect would land first (chart shows candles), then the parent's async
   * instrument-detail fetch would resolve and call setSpotDecimals(N) —
   * which destroyed + recreated the chart instance. The data effect's deps
   * (`candles`, `overlays`, …) hadn't changed, so it didn't re-run, and the
   * fresh chart sat empty until the user pressed Refresh.
   *
   * The price formatter and per-series priceFormat are the only things that
   * actually need updating when decimalPrecision changes; both can be
   * re-applied via applyOptions on the existing chart/series, no destroy
   * needed. */
  useEffect(() => {
    const chart = chartRef.current;
    const cs = candleSeriesRef.current;
    if (!chart || !cs) return;
    const prec = Math.max(0, Math.min(8, Math.floor(decimalPrecision)));
    const minMove = Math.pow(10, -prec);
    try { chart.applyOptions({ localization: { priceFormatter: (p: number) => p.toFixed(prec) } }); } catch { /* ignore */ }
    try { cs.applyOptions({ priceFormat: { type: 'price', precision: prec, minMove } }); } catch { /* ignore */ }
  }, [decimalPrecision]);

  /* Round 92 — live-apply the background tint (Bkground toggle) without
   *  recreating the chart. Falls back to the theme default when unset. */
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    try {
      chart.applyOptions({
        layout: { background: { type: ColorType.Solid, color: backgroundColor || (isDark ? '#0D1117' : '#FFFFFF') } },
      });
    } catch { /* ignore */ }
  }, [backgroundColor, isDark]);

  /* Apply candle data + overlays whenever candles, overlays, or theme colors change.
   * Both useEffects run in declaration order within the same commit,
   * so on mount the chart is created first, then data is applied.
   *
   * Round 32 — the body is wrapped in `applyData()` and exposed via
   * applyDataRef so the chart-creation effect can re-apply data immediately
   * after rebuilding the chart (e.g. on theme flip or settings save). */
  useEffect(() => {
    const applyData = () => {
    const chart = chartRef.current;
    const cs = candleSeriesRef.current;
    if (!chart || !cs) return;

    // R105 — Lower indicator pane. The five indicators (Trend / RelStrength /
    // ChgN% / MACD / JHM) can each render either on the price pane (overlay,
    // their classic behaviour) or in a shared lower pane (index 1) so they no
    // longer sit inside the candles. `cfgScale` configures a series' price
    // scale pane-aware: in-pane → a visible axis spanning most of the lower
    // pane (so each indicator keeps its OWN axis column); overlay → the
    // caller's original visible flag + scaleMargins. Capture the user's current
    // lower-pane height first so the drag-resize survives this rebuild.
    const PANE_INDEX = 1;
    // Filled by the BBR/Wave oscillator block, consumed by the markers block
    // further down - the conjunction has to reach pushSignal, which lives there.
    let bbrWaveSignals: {
      buys: number[]; shorts: number[];
      exits: Array<{ time: number; side: 'BUY' | 'SHORT'; why: 'level' | 'stop' | 'time' }>;
    } | null = null;
    let bbrOscSeriesRef: ISeriesApi<'Line'> | null = null;
    // Whether the lower pane already exists at the START of this pass. On a
    // plain auto-refresh it does (and carries the user's dragged height); only
    // when it's freshly created (new chart / re-enabled) do we (re)apply a
    // height at the end — re-applying every refresh makes lightweight-charts
    // drift the size. We still snapshot the live height here so a later rebuild
    // can restore it.
    let hadLowerPane = false;
    try {
      const ps0 = chart.panes();
      hadLowerPane = ps0.length > 1;
      // Snapshot the user's current lower-pane STRETCH (relative weight) so a
      // rebuild that resets pane proportions can restore it. Stretch round-trips
      // exactly, so this never drifts.
      // Only when pane 1 really is the shared indicator pane: with no other lower-pane
      // indicator on, pane 1 is an FNO pane, whose height is remembered separately.
      if (hadLowerPane) {
        const fnoS = new Set<any>(Object.values(fnoPaneSeriesRef.current));
        const own = ps0[1].getSeries().some(s => !fnoS.has(s as any));
        if (own) { const sf = ps0[1].getStretchFactor(); if (Number.isFinite(sf) && sf > 0) lowerPaneStretchRef.current = sf; }
      }
    } catch { /* ignore */ }
    const cfgScale = (
      series: ISeriesApi<any>, inPane: boolean,
      overlayVisible: boolean, overlayMargins: { top: number; bottom: number }, borderColor: string,
    ) => {
      try {
        series.priceScale().applyOptions({
          visible: inPane ? true : overlayVisible,
          borderColor,
          scaleMargins: inPane ? { top: 0.12, bottom: 0.12 } : overlayMargins,
        });
      } catch { /* lightweight-charts may reject if scale not yet ready */ }
    };

    // Round 42 — preserve the user's pan/zoom ONLY across genuine
    // continuations (auto-refresh / lazy-load of the SAME chart), and
    // force a fresh first-fit when the chart's data fundamentally changes
    // (new symbol, new interval, full reload).
    //
    // Round 41 saved/restored the visible range unconditionally whenever
    // firstFitDoneRef was true. That broke the fresh-open case: opening a
    // new symbol kept firstFitDoneRef=true (it only resets on an empty
    // candles array, and symbol switches don't always pass through empty),
    // so the previous symbol's stale range was restored onto the new
    // data — the chart "pointed at the first candle".
    //
    // Continuation test: the PREVIOUS render's last-candle time still
    // falls within the NEW data's [first, last] span. Auto-refresh
    // (appends a bar) and lazy-load (prepends bars) both keep the old
    // last candle inside the new span; a fresh symbol does not.
    const newFirstTime = candles.length ? (candles[0].time as number) : null;
    const newLastTime  = candles.length ? (candles[candles.length - 1].time as number) : null;
    const prevLast     = prevLastCandleTimeRef.current;
    const isContinuation = prevLast != null && newFirstTime != null && newLastTime != null
      && prevLast >= newFirstTime && prevLast <= newLastTime;
    // Bar Replay: the candle array is a PREFIX of the same series, so stepping
    // BACKWARD makes it shorter and the test above reads the result as fresh
    // data — which re-fitted the zoom on every backward step while a forward
    // step preserved it. Same series (same first bar) is the same chart.
    const isReplayContinuation = replayCut != null
      && prevFirstCandleTimeRef.current != null && newFirstTime != null
      && prevFirstCandleTimeRef.current === newFirstTime;
    if (!isContinuation && !isReplayContinuation) {
      // Fresh data — let the first-fit block re-position the chart.
      firstFitDoneRef.current = false;
    }
    // Snapshot the LOGICAL visible range for restore at the end of applyData.
    // Logical (bar-index) range — unlike the time range — natively captures any
    // right-side whitespace the user panned in (its `to` can exceed the last bar
    // index). We restore it shifted by the number of bars added so the user's
    // exact pan/zoom AND their extra empty space survive an auto-refresh.
    const savedLogical = ((isContinuation || isReplayContinuation) && firstFitDoneRef.current)
      ? (() => { try { return chart.timeScale().getVisibleLogicalRange(); } catch { return null; } })()
      : null;

    // Remember each FNO pane's stretch (the user may have dragged it) before its series go.
    for (const [k, s] of Object.entries(fnoPaneSeriesRef.current)) {
      try { const sf = s.getPane().getStretchFactor(); if (Number.isFinite(sf) && sf > 0) fnoPaneStretchRef.current[k] = sf; } catch { /* gone */ }
    }
    fnoPaneSeriesRef.current = {};
    fnoPaneLegendRef.current = null;
    // Remove old overlays
    overlaySeriesRefs.current.forEach(s => { try { chart.removeSeries(s); } catch {} });
    overlaySeriesRefs.current = [];
    // Detach old primitives (translucent fills, session lines)
    primitivesRef.current.forEach(p => { try { cs.detachPrimitive(p as any); } catch {} });
    primitivesRef.current = [];

    if (!candles.length) {
      cs.setData([]);
      // Force the price scale + time scale to forget the previous symbol's
      // range, otherwise switching symbols while a fetch is in flight leaves
      // the price axis locked on the old symbol's values (e.g. BTCUSD 78k
      // labels still showing while HANAUSD ~0.04 is loading).
      try { chart.priceScale('right').applyOptions({ autoScale: true }); } catch { /* ignore */ }
      try { chart.timeScale().resetTimeScale(); } catch { /* ignore */ }
      // Drop any custom price lines we added (LTP, Mark, position lines) so
      // they don't keep painting at the prior symbol's prices.
      if (ltpPriceLineRef.current) {
        try { cs.removePriceLine(ltpPriceLineRef.current); } catch { /* ignore */ }
        ltpPriceLineRef.current = null;
      }
      if (markPriceLineRef.current) {
        try { cs.removePriceLine(markPriceLineRef.current); } catch { /* ignore */ }
        markPriceLineRef.current = null;
      }
      firstFitDoneRef.current = false; // reset so next non-empty load fits
      return;
    }

    // Build candle color map from strategy
    const colorMap = new Map<number, { upColor: string; downColor: string }>();
    // Most-recent strategy colour — used for the appended LIVE/forming bar, which
    // isn't in the (closed-bar) overlay. Without it the forming bar would use the
    // series default up/down colour, which is display-candle-type dependent (e.g.
    // green in Heikin-Ashi vs red in CLH for the same strategy state).
    let lastStratColor: { upColor: string; downColor: string } | undefined;
    if (strategyOverlay?.candleColors) {
      for (const cc of strategyOverlay.candleColors) {
        colorMap.set(cc.time, { upColor: cc.upColor, downColor: cc.downColor });
      }
      if (strategyOverlay.candleColors.length) {
        lastStratColor = strategyOverlay.candleColors[strategyOverlay.candleColors.length - 1];
      }
    }

    /* ── BBR / Volume-spike candle-color OVERRIDE ──
       Higher priority than strategy colors. User-customisable via ChartSettings. */
    const bbrUp   = settings.bbr.oversoldColor;   // oversold reversal up
    const bbrDown = settings.bbr.overboughtColor; // overbought reversal down
    const spike   = settings.vspk.color;          // volume spike
    let bbr: { overboughtAt: Set<number>; oversoldAt: Set<number> } | null = null;
    let spikes: Set<number> | null = null;
    // Same source as the BBR curve and the BW marks, so the tint can never
    // disagree with the oscillator that is supposed to explain it.
    if (overlays.bbrColor) bbr = computeBbrReversals(bwSource);
    // Under a fraction the display bars carry the WINDOW's volume; the spike tint is
    // an indicator and stays on the real volume (the forming bar keeps its own).
    if (overlays.spikeColor) {
      let volSrc = candles;
      if (fraction && rawCandles && rawCandles.length) {
        const realVol = new Map<number, number>();
        for (const r of rawCandles) realVol.set(r.time as number, Number(r.volume) || 0);
        volSrc = candles.map(c => (realVol.has(c.time as number) ? { ...c, volume: realVol.get(c.time as number) } : c));
      }
      spikes = computeVolumeSpikes(volSrc);
    }

    const overrideColor = (time: number): string | null => {
      if (bbr?.overboughtAt.has(time)) return bbrDown;
      if (bbr?.oversoldAt.has(time))   return bbrUp;
      if (spikes?.has(time))           return spike;
      return null;
    };

    // Set candle data (per-candle color priority: BBR/spike > strategy > default).
    // HC (Hide Candles) overrides everything — paint each bar with fully
    // transparent body / border / wick. The series remains attached, so the
    // price scale still uses its OHLC values to position indicators, but
    // no candles render visibly.
    const TRANSPARENT = 'rgba(0,0,0,0)';
    const fracMissing = fraction && fraction.missing.size ? fraction.missing : null;
    cs.setData(candles.map(c => {
      if (hideCandles) {
        return {
          time: c.time as Time, open: c.open, high: c.high, low: c.low, close: c.close,
          color: TRANSPARENT, borderColor: TRANSPARENT, wickColor: TRANSPARENT,
        } as CandlestickData<Time>;
      }
      const ov = overrideColor(c.time);
      if (ov) {
        return {
          time: c.time as Time, open: c.open, high: c.high, low: c.low, close: c.close,
          color: ov, borderColor: ov, wickColor: ov,
        } as CandlestickData<Time>;
      }
      /* CLN — the hard invariant: LR DIRECTION colours BOTH layers. A bearish
       * real bar inside a rising regression prints bullish; that disagreement is
       * the information the candle type exists to show, so the bar's own
       * open/close direction must not be consulted, and an active strategy's
       * candleColors are deliberately SUPPRESSED here (its markers, signals and
       * tooltips are untouched). The body is drawn translucent so the opaque
       * regression body sitting on top of it stays the dominant read. */
      if (lrGhost) {
        const up = clnDirByTime.get(c.time as number);
        const hue = (up === undefined ? c.close >= c.open : up) ? clnUp : clnDown;
        const fade = fracMissing?.has(c.time as number) ? 0.5 : 1;
        return {
          time: c.time as Time, open: c.open, high: c.high, low: c.low, close: c.close,
          color: withAlpha(hue, CLN_REAL_BODY_ALPHA * fade),
          borderColor: withAlpha(hue, CLN_REAL_EDGE_ALPHA * fade),
          wickColor: withAlpha(hue, CLN_REAL_EDGE_ALPHA * fade),
        } as CandlestickData<Time>;
      }
      // Forming bar (appended live bar, time beyond the closed-bar overlay) →
      // continue the most-recent strategy colour so it isn't display-type dependent.
      const sc = colorMap.get(c.time) ?? lastStratColor;
      // "Important fraction": a bar no finer data covers stays a normal candle, faded
      // so it cannot pass for a fraction candle.
      if (fracMissing?.has(c.time as number)) {
        const upBar = c.close >= c.open;
        const hue = sc ? (upBar ? sc.upColor : sc.downColor)
          : (upBar ? (settings.candle.risingColor || C.chartBull) : (settings.candle.fallingColor || C.chartBear));
        const faded = withAlpha(hue, FRACTION_MISSING_ALPHA);
        return {
          time: c.time as Time, open: c.open, high: c.high, low: c.low, close: c.close,
          color: faded, borderColor: faded, wickColor: faded,
        } as CandlestickData<Time>;
      }
      return {
        time: c.time as Time, open: c.open, high: c.high, low: c.low, close: c.close,
        ...(sc ? { color: c.close >= c.open ? sc.upColor : sc.downColor, borderColor: c.close >= c.open ? sc.upColor : sc.downColor, wickColor: c.close >= c.open ? sc.upColor : sc.downColor } : {}),
      } as CandlestickData<Time>;
    }));

    // Round 40 — bar geometry for projection rendering. Projection bands sit
    // PAST the last real candle, where lightweight-charts' timeToCoordinate
    // returns null. BandPrimitive falls back to bar-spacing arithmetic using
    // these two values, so we never have to append whitespace data to the
    // candle series (which broke live updates and the user's scroll position).
    const lastBarTime = candles.length ? (candles[candles.length - 1].time as number) : 0;
    const barSpacingSec = candles.length > 1
      ? Math.max(1, (candles[candles.length - 1].time as number) - (candles[candles.length - 2].time as number))
      : 60;

    // ── Right-side whitespace (crosshair time labels past the last candle) ──
    // A plain `timeScale.rightOffset` gap has no time points, so lightweight-charts
    // hides the crosshair's date/time label the moment the pointer leaves the last bar.
    // Materialise the same gap as WHITESPACE bars on a dedicated, value-less series:
    // they render nothing, they don't touch the candle series (appending whitespace
    // THERE breaks `cs.update()` on the live bar), and the time scale now has real
    // times to label.
    //
    // PERSISTENT, unlike every other overlay here. It used to be removed with the rest at
    // the top of this function and re-added below, which means that on every single pass —
    // and this function runs on every live tick — the chart's rightmost data point briefly
    // became the last candle and the logical index space shrank by `wsBars`, then grew back.
    // Any visible range referring to that space had to be re-clamped in between. Keeping one
    // series alive and only re-writing its data when its INPUTS change removes a whole class
    // of "the view moved and nobody touched it".
    const wsBars = plannedFit(candles.length, defaultVisibleBars).gap;
    if (wsBars > 0 && lastBarTime > 0) {
      if (!wsSeriesRef.current) {
        wsSeriesRef.current = chart.addSeries(LineSeries, {
          priceScaleId: '', visible: false, lastValueVisible: false, priceLineVisible: false,
          crosshairMarkerVisible: false,
        } as any);
        wsKeyRef.current = '';
      }
      const key = wsBars + '|' + lastBarTime + '|' + barSpacingSec;
      if (wsKeyRef.current !== key) {
        wsKeyRef.current = key;
        try {
          wsSeriesRef.current.setData(
            Array.from({ length: wsBars }, (_, k) => ({ time: (lastBarTime + (k + 1) * barSpacingSec) as Time })) as any,
          );
        } catch { /* chart disposed mid-pass */ }
      }
    } else if (wsSeriesRef.current) {
      // No gap wanted (empty series). Blank it rather than destroying it, so the series
      // identity — and the logical space it owns — stays stable.
      try { wsSeriesRef.current.setData([] as any); } catch { /* ignore */ }
      wsKeyRef.current = '';
    }

    const closes = candles.map(c => c.close);

    // R81 — RelStrength + ChgN% should always read from the SAME source
    // regardless of the chart's display candle type. We derive a CSH-
    // transformed close array from rawCandles (or, when rawCandles wasn't
    // supplied, from the current display candles — degraded but stable for
    // legacy callers). Indexed by the same `i` because transformCandles
    // preserves the input length + ordering.
    const sourceForCsh: CandleRow[] = (rawCandles && rawCandles.length) ? rawCandles : candles;
    const cshCandles = transformCandles(sourceForCsh, 'CSH');
    const cshCloseByTime = new Map<number, number>();
    for (const c of cshCandles) {
      if (Number.isFinite(c.close)) cshCloseByTime.set(c.time as number, c.close);
    }
    // Aligned to the display candles[] indexes so existing per-i loops in the
    // ChgN% block can swap `closes[i]` for `cshClosesAligned[i]` without
    // changing the loop bounds.
    const cshClosesAligned: number[] = candles.map(c => {
      const v = cshCloseByTime.get(c.time as number);
      return (typeof v === 'number' && Number.isFinite(v)) ? v : NaN;
    });

    // Bollinger Bands — four computation variants (Traditional / Liner / Custom
    // Liner / Custom Liner Weighted) sharing one set of look controls. Config in
    // Chart Settings → Bollinger Bands (BB); defaults preserve the classic 20/2σ.
    if (overlays.bollingerEnvelope) {
      const bb = (settings as any)?.bollinger || {};
      const variant = (['traditional', 'liner', 'customLiner', 'customLinerWeighted'].includes(bb.variant) ? bb.variant : 'traditional') as BollingerVariant;
      const period = Math.max(2, Math.floor(Number(bb.period) || 20));
      const stdMult = Number.isFinite(Number(bb.stdMult)) ? Number(bb.stdMult) : 2;
      const bands = computeBollingerBands(candles, sourceForCsh, { variant, period, stdMult });
      const upperD: LineData<Time>[] = [], midD: LineData<Time>[] = [], lowerD: LineData<Time>[] = [];
      const fillPts: BandPoint[] = [];
      for (let i = 0; i < candles.length; i++) {
        const t = candles[i].time as Time;
        const u = bands.upper[i], mv = bands.middle[i], l = bands.lower[i];
        if (mv != null) midD.push({ time: t, value: mv });
        if (u != null) upperD.push({ time: t, value: u });
        if (l != null) lowerD.push({ time: t, value: l });
        if (u != null && l != null) fillPts.push({ time: candles[i].time as number, upper: u, lower: l });
      }
      if (upperD.length > 1) {
        const stair = bb.lineMode === 'staircase';
        const lineType = stair ? 1 : 0; // 0 = Simple, 1 = WithSteps (staircase)
        const lw = Math.max(1, Math.min(4, Number(bb.lineWidth) || 1)) as 1 | 2 | 3 | 4;
        const bandColor = bb.bandColor || '#2962ff';
        const middleColor = bb.middleColor || '#ff6d00';
        const showMiddle = bb.showMiddle !== false;
        const fillEnabled = bb.fillEnabled !== false;
        const fillOp = Math.max(0, Math.min(1, Number(bb.fillOpacity ?? 0.08)));

        // Fill FIRST (behind the lines) via a transparent anchor so z-order is clean.
        if (fillEnabled && fillOp > 0 && fillPts.length > 1) {
          const anchor = chart.addSeries(LineSeries, { color: 'rgba(0,0,0,0)', lineWidth: 1, lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false });
          anchor.setData(showMiddle && midD.length ? midD : upperD);
          const bp = new BandPrimitive(fillPts, { fillColor: withAlpha(bb.fillColor || bandColor, fillOp), step: stair });
          (anchor as any).attachPrimitive(bp);
          primitivesRef.current.push(bp);
          overlaySeriesRefs.current.push(anchor);
        }

        const u = chart.addSeries(LineSeries, { color: bandColor, lineWidth: lw, lineType, lastValueVisible: false, priceLineVisible: false });
        u.setData(upperD);
        overlaySeriesRefs.current.push(u);
        if (showMiddle && midD.length) {
          const mid = chart.addSeries(LineSeries, { color: middleColor, lineWidth: lw, lineType, lineStyle: 2, lastValueVisible: false, priceLineVisible: false });
          mid.setData(midD);
          overlaySeriesRefs.current.push(mid);
        }
        const lo = chart.addSeries(LineSeries, { color: bandColor, lineWidth: lw, lineType, lastValueVisible: false, priceLineVisible: false });
        lo.setData(lowerD);
        overlaySeriesRefs.current.push(lo);
      }
    }

    // EMAs — up to 4 independently-configurable EMAs over LinReg(close, 14).
    // Periods, colors, and per-EMA visibility are read from Chart Settings
    // (defaults: 7 / 21 / 51 / 200). Line width scales with rank so longer
    // averages stay visually dominant in trending periods.
    if (overlays.ema200) {
      // R120.4 — LinReg over RAW closes (sourceForCsh), matching the EMA OverView
      // voter (which runs on the raw, candle-type-independent series). On a
      // transformed candle type, display closes diverge from raw, so the drawn
      // EMA3/EMA4 wouldn't line up with the bar colour the EMA voter produces.
      const lrSrc = linregOf(sourceForCsh.map(c => Number(c.close)), 14);
      const cfg = (settings as any)?.emas;
      const widthFor = (p: number): 1 | 2 | 3 | 4 => (p >= 150 ? 3 : p >= 50 ? 2 : 1);
      const EMA_DEFS: Array<{ period: number; color: string; width: 1 | 2 | 3 | 4 } | null> = cfg ? [
        cfg.ema1?.enabled !== false ? { period: Math.max(1, Number(cfg.ema1?.period) || 7),   color: cfg.ema1?.color || '#ff7043', width: widthFor(Number(cfg.ema1?.period) || 7) }   : null,
        cfg.ema2?.enabled !== false ? { period: Math.max(1, Number(cfg.ema2?.period) || 21),  color: cfg.ema2?.color || '#ffd54f', width: widthFor(Number(cfg.ema2?.period) || 21) }  : null,
        cfg.ema3?.enabled !== false ? { period: Math.max(1, Number(cfg.ema3?.period) || 51),  color: cfg.ema3?.color || '#26c6da', width: widthFor(Number(cfg.ema3?.period) || 51) }  : null,
        cfg.ema4?.enabled !== false ? { period: Math.max(1, Number(cfg.ema4?.period) || 200), color: cfg.ema4?.color || '#ab47bc', width: widthFor(Number(cfg.ema4?.period) || 200) } : null,
      ] : [
        { period: 7,   color: '#ff7043', width: 1 },
        { period: 21,  color: '#ffd54f', width: 1 },
        { period: 51,  color: '#26c6da', width: 2 },
        { period: 200, color: '#ab47bc', width: 3 },
      ];
      for (const def of EMA_DEFS) {
        if (!def) continue;
        const arr = calcEma(lrSrc, def.period);
        const data: LineData<Time>[] = [];
        for (let i = 0; i < sourceForCsh.length; i++) {
          if (arr[i] === null) continue;
          data.push({ time: sourceForCsh[i].time as Time, value: arr[i]! });
        }
        const s = chart.addSeries(LineSeries, {
          color: def.color, lineWidth: def.width,
          lastValueVisible: false, priceLineVisible: false,
        });
        s.setData(data);
        overlaySeriesRefs.current.push(s);
      }
    }

    // 24HCng% — running 24-hour percentage change of close vs close 24h ago.
    // Lookback = ceil(1440 / intervalMinutes) bars. Plotted on its own price
    // scale ('pctRight') so it doesn't compress the candlestick axis.
    // ChgN% — rolling N-candle percentage change. R73: lookback comes from
    // settings (default 24 bars) — replaces the old ceil(1440/intervalMinutes)
    // formula so the indicator is meaningful at any timeframe. EMA(period)
    // is drawn over the change line and a translucent band fills the gap
    // between them so direction + momentum read at a glance.
    if (overlays.dayChange && cshClosesAligned.length > 1) {
      // R81 — ChgN% now ALWAYS reads CSH-transformed close (not the chart's
      // display candle close). Stable across user candle-type switches.
      const dcCfg = (settings as any)?.dayChange || {};
      const inPane = dcCfg.pane !== false; const paneIdx = inPane ? PANE_INDEX : 0;
      const dcColor = dcCfg.color || '#00897b';
      const dcWidth = Math.max(1, Math.min(4, Number(dcCfg.lineWidth) || 2)) as 1 | 2 | 3 | 4;
      const showZero = dcCfg.showZeroLine !== false;
      const lookback = Math.max(1, Math.floor(Number(dcCfg.lookbackBars) || 24));
      const emaPeriod = Math.max(1, Math.floor(Number(dcCfg.emaPeriod) || 7));
      const bandFill = dcCfg.bandFillColor || dcColor;
      const bandOp = Math.max(0, Math.min(1, Number(dcCfg.bandOpacity ?? 0.20)));
      // R94 — directional band: green above EMA, red below.
      const bandUp = dcCfg.bandUpColor || '#26a69a';
      const bandDown = dcCfg.bandDownColor || '#ef5350';

      const data: LineData<Time>[] = [];
      const rawPct: Array<number | null> = new Array(candles.length).fill(null);
      for (let i = lookback; i < candles.length; i++) {
        const prev = cshClosesAligned[i - lookback];
        const cur = cshClosesAligned[i];
        if (prev > 0 && Number.isFinite(prev) && Number.isFinite(cur)) {
          const pct = ((cur - prev) / prev) * 100;
          data.push({ time: candles[i].time as Time, value: Number(pct.toFixed(3)) });
          rawPct[i] = pct;
        }
      }
      if (data.length > 0) {
        // Main change line on its own price scale ('pctRight').
        const s = chart.addSeries(LineSeries, {
          color: dcColor,
          lineWidth: dcWidth,
          priceScaleId: 'pctRight',
          lastValueVisible: true,
          priceLineVisible: false,
          priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
          title: '',
        }, paneIdx);
        s.setData(data);
        cfgScale(s, inPane, true, { top: 0.05, bottom: 0.05 }, dcColor);
        if (showZero) {
          try {
            s.createPriceLine({
              price: 0, color: dcColor, lineWidth: 1, lineStyle: 2, // dashed
              axisLabelVisible: false, title: '',
            });
          } catch { /* ignore */ }
        }
        overlaySeriesRefs.current.push(s);

        // EMA(period) of the change line. Same price scale so they overlay.
        // R73-fix — use the null-aware ema() helper (waits for `period`
        // consecutive non-null values before seeding). The local calcEma
        // can't handle nulls cleanly: mapping null→NaN poisoned its seed
        // and propagated NaN through the recursion, so the EMA series
        // (and the band that depends on it) never rendered.
        const emaArr = emaNullable(rawPct, emaPeriod);
        const emaData: LineData<Time>[] = [];
        const bandPts: { time: number; upper: number; lower: number; color?: string }[] = [];
        for (let i = 0; i < candles.length; i++) {
          const e = emaArr[i];
          const v = rawPct[i];
          if (e == null || !Number.isFinite(e) || v == null || !Number.isFinite(v)) continue;
          emaData.push({ time: candles[i].time as Time, value: Number(e.toFixed(3)) });
          bandPts.push({
            time: candles[i].time as number,
            upper: Math.max(v, e),
            lower: Math.min(v, e),
            color: withAlpha(v >= e ? bandUp : bandDown, bandOp),
          });
        }
        if (emaData.length > 0) {
          const emaS = chart.addSeries(LineSeries, {
            color: dcColor,
            lineWidth: 1,
            lineStyle: 2, // dashed — visually subordinate to the main line
            priceScaleId: 'pctRight',
            lastValueVisible: false,
            priceLineVisible: false,
            priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
            title: '',
          }, paneIdx);
          emaS.setData(emaData);
          overlaySeriesRefs.current.push(emaS);
          if (bandOp > 0 && bandPts.length >= 2) {
            const bp = new BandPrimitive(bandPts, { fillColor: withAlpha(bandFill, bandOp) });
            // Attach to the main change-line series so the primitive uses
            // pctRight for coordinate conversion.
            (s as any).attachPrimitive(bp);
            primitivesRef.current.push(bp);
          }
        }
      }
    }

    // HLDiff (R134/R136) — EMA of the Highs + EMA of the Lows of a selectable
    // candle type. Always computed from RAW candles + the configured transform
    // (independent of the display candle type); the chosen transforms preserve
    // bar times so the values map 1:1 onto the displayed bars.
    // Two display modes: 'line' = both EMA lines + translucent band overlaid
    // on the price chart; 'histogram' = High−Low EMA spread bars in the shared
    // lower pane (mandatory — the spread is not on the price scale), coloured
    // by the slope of the two EMAs vs the previous bar.
    if (overlays.hlDiff) {
      const hcfg = (settings as any)?.hlDiff || {};
      const ctype = String(hcfg.candleType || 'HA');
      const period = Math.max(1, Math.min(500, Math.floor(Number(hcfg.emaPeriod) || 21)));
      const histMode = hcfg.mode !== 'line';
      const srcRaw: CandleRow[] = (rawCandles && rawCandles.length) ? rawCandles : candles;
      const tc = ctype === 'Regular' ? srcRaw : transformCandles(srcRaw, ctype as any);
      if (tc.length > period + 2) {
        const highsE = emaOf(tc.map(c => c.high), period);
        const lowsE = emaOf(tc.map(c => c.low), period);
        if (histMode) {
          const cBothRise = hcfg.histBothRiseColor || '#26a69a';
          const cBothFall = hcfg.histBothFallColor || '#ef5350';
          const cHiRiseLoFall = hcfg.histHiRiseLoFallColor || '#ef9a9a';
          const cHiFallLoRise = hcfg.histHiFallLoRiseColor || '#a5d6a7';
          const hOp = Math.max(0, Math.min(1, Number(hcfg.histOpacity ?? 0.8)));
          const histData: HistogramData<Time>[] = [];
          // Skip the EMA warm-up region (the helper echoes raw values there);
          // start one bar later so the slope vs the previous bar is defined.
          for (let i = period + 1; i < tc.length; i++) {
            const h = highsE[i], l = lowsE[i], ph = highsE[i - 1], pl = lowsE[i - 1];
            if (h == null || l == null || ph == null || pl == null) continue;
            if (!Number.isFinite(h) || !Number.isFinite(l) || !Number.isFinite(ph) || !Number.isFinite(pl)) continue;
            const hiUp = h > ph, loUp = l > pl;
            const col = hiUp && loUp ? cBothRise
              : !hiUp && !loUp ? cBothFall
              : hiUp ? cHiRiseLoFall : cHiFallLoRise;
            histData.push({ time: tc[i].time as Time, value: h - l, color: withAlpha(col, hOp) });
          }
          if (histData.length >= 2) {
            const hs = chart.addSeries(HistogramSeries, {
              priceScaleId: 'hlDiffRight', base: 0,
              lastValueVisible: false, priceLineVisible: false,
              title: '',
            }, PANE_INDEX);
            hs.setData(histData);
            overlaySeriesRefs.current.push(hs as any);
            cfgScale(hs, true, false, { top: 0.1, bottom: 0.1 }, cBothRise);
          }
        } else {
          const hiColor = hcfg.highColor || '#26a69a';
          const loColor = hcfg.lowColor || '#ef5350';
          const lw = Math.max(1, Math.min(4, Math.floor(Number(hcfg.lineWidth) || 1))) as 1 | 2 | 3 | 4;
          const fillColor = hcfg.fillColor || '#90a4ae';
          const fillOp = Math.max(0, Math.min(1, Number(hcfg.fillOpacity ?? 0.15)));
          const hiData: LineData<Time>[] = [];
          const loData: LineData<Time>[] = [];
          const bandPts: { time: number; upper: number; lower: number }[] = [];
          // Skip the EMA warm-up region (the helper echoes raw values there).
          for (let i = period; i < tc.length; i++) {
            const h = highsE[i], l = lowsE[i];
            if (h == null || l == null || !Number.isFinite(h) || !Number.isFinite(l)) continue;
            hiData.push({ time: tc[i].time as Time, value: h });
            loData.push({ time: tc[i].time as Time, value: l });
            bandPts.push({ time: tc[i].time as number, upper: Math.max(h, l), lower: Math.min(h, l) });
          }
          if (hiData.length >= 2) {
            const hs = chart.addSeries(LineSeries, {
              color: hiColor, lineWidth: lw, priceScaleId: 'right',
              lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false,
              title: '',
            }, 0);
            hs.setData(hiData);
            overlaySeriesRefs.current.push(hs);
            const ls2 = chart.addSeries(LineSeries, {
              color: loColor, lineWidth: lw, priceScaleId: 'right',
              lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false,
              title: '',
            }, 0);
            ls2.setData(loData);
            overlaySeriesRefs.current.push(ls2);
            if (fillOp > 0 && bandPts.length >= 2) {
              const bp = new BandPrimitive(bandPts, { fillColor: withAlpha(fillColor, fillOp) });
              (hs as any).attachPrimitive(bp);
              primitivesRef.current.push(bp);
            }
          }
        }
      }
    }

    // S/R (R137) — Support/Resistance indicator. A container for one or more
    // source methods that all plot horizontal levels on the price pane. Each
    // enabled method contributes SrLevel[]; we render them together as a single
    // HorizontalLevelsPrimitive on the candle series. HTF lines are thicker
    // than CTF; the single active level per stream is deep, the rest faint.
    if (overlays.sr) {
      const scfg = (settings as any)?.sr || {};
      const hcfg = (settings as any)?.hlDiff || {};
      const fmtTf = catalogIntervalLabel;
      const styleName = (scfg.lineStyle === 1 ? 'dotted' : scfg.lineStyle === 0 ? 'solid' : 'dashed') as HLevel['style'];
      const rColor = scfg.resistanceColor || '#ef5350';
      const sColor = scfg.supportColor || '#26a69a';
      const ctfW = Math.max(1, Math.floor(Number(scfg.ctfWidth) || 1));
      const htfW = Math.max(1, Math.floor(Number(scfg.htfWidth) || 2));
      const actOp = Math.max(0.05, Math.min(1, Number(scfg.activeOpacity ?? 0.95)));
      const inactOp = Math.max(0.05, Math.min(1, Number(scfg.inactiveOpacity ?? 0.3)));
      const showLabels = scfg.showLabels !== false;

      const bandMode = scfg.displayMode === 'band';
      // Per-stream level lists. computeHlDiffSr returns ALL levels (uncapped),
      // last one flagged active; we cap in levels-mode below.
      let ctfLevels: SrLevel[] = [];
      let htfLevels: SrLevel[] = [];
      const m1 = scfg.methods?.hlDiff;
      // NO LOOK-AHEAD: drop any level whose confirmation time is still in the
      // future relative to the latest CHART bar (this hides peaks "confirmed"
      // only by a not-yet-closed HTF/CTF bar), then re-flag the most-recent
      // CONFIRMED level as active (computeHlDiffSr flagged the raw last one).
      const lastBarTime = candles.length ? candles[candles.length - 1].time : 0;
      const confirmFilter = (a: SrLevel[]): SrLevel[] => {
        const f = a.filter(l => l.formedAt <= lastBarTime);
        f.forEach((l, idx) => { l.active = (idx === f.length - 1); });
        return f;
      };
      if (m1?.enabled !== false) {
        const srCfg = {
          candleType: String(hcfg.candleType || 'HA'),
          emaPeriod: Math.max(1, Math.min(500, Math.floor(Number(hcfg.emaPeriod) || 21))),
        };
        if (m1?.useCurrentTf !== false) {
          const ctfSrc = (rawCandles && rawCandles.length) ? rawCandles : candles;
          ctfLevels = confirmFilter(computeHlDiffSr(ctfSrc as any, srCfg, intervalMinutes || 0, 'CTF'));
        }
        if (m1?.useHtf !== false && auxCandles?.srHtf && auxCandles.srHtf.length) {
          htfLevels = confirmFilter(computeHlDiffSr(auxCandles.srHtf as any, srCfg, auxCandles.srHtfMinutes || 0, 'HTF'));
        }
      }

      if (bandMode) {
        // Running channel per stream: stepped most-recent R / most-recent S
        // lines with a fill between, sampled on the chart's bar times.
        const barTimes = candles.map(c => c.time);
        const fillCol = withAlpha(scfg.bandFillColor || '#90a4ae', Math.max(0, Math.min(1, Number(scfg.bandFillOpacity ?? 0.12))));
        const bands: SrBand[] = [];
        const streams: Array<{ levels: SrLevel[]; width: number; tf: number }> = [
          { levels: ctfLevels, width: ctfW, tf: intervalMinutes || 0 },
          { levels: htfLevels, width: htfW, tf: auxCandles?.srHtfMinutes || 0 },
        ];
        for (const st of streams) {
          if (!st.levels.length) continue;
          const { rPts, sPts } = buildRunningBand(st.levels, barTimes);
          if (!rPts.length && !sPts.length) continue;
          bands.push({
            rPts, sPts, fillColor: fillCol,
            rColor: withAlpha(rColor, actOp), sColor: withAlpha(sColor, actOp),
            lineWidth: st.width,
            rLabel: showLabels ? `HLDiff R${st.tf ? ' · ' + fmtTf(st.tf) : ''}` : '',
            sLabel: showLabels ? `HLDiff S${st.tf ? ' · ' + fmtTf(st.tf) : ''}` : '',
          });
        }
        if (bands.length) {
          const prim = new SrBandPrimitive(bands);
          (cs as any).attachPrimitive(prim);
          primitivesRef.current.push(prim);
        }
      } else {
        // Levels mode: cap each stream to the most-recent N (active kept).
        const cap = Math.max(1, Math.min(50, Math.floor(Number(m1?.maxLevelsPerTf) || 3)));
        const capLast = (a: SrLevel[]) => (a.length > cap ? a.slice(a.length - cap) : a);
        const srLevels = [...capLast(ctfLevels), ...capLast(htfLevels)];
        if (srLevels.length) {
          const hLevels: HLevel[] = srLevels.map(lv => ({
            price: lv.price,
            fromTime: lv.formedAt,
            color: withAlpha(lv.side === 'R' ? rColor : sColor, lv.active ? actOp : inactOp),
            width: lv.stream === 'HTF' ? htfW : ctfW,
            style: styleName,
            label: showLabels ? `${lv.methodLabel} ${lv.side}${lv.tfMinutes ? ' · ' + fmtTf(lv.tfMinutes) : ''}` : undefined,
          }));
          const prim = new HorizontalLevelsPrimitive(hLevels);
          (cs as any).attachPrimitive(prim);
          primitivesRef.current.push(prim);
        }
      }
    }

    // MACD (Round 94) — EMA(fast) − EMA(slow) on the CSH-smoothed close; signal
    // = SMA(macd, signalLength). Plotted as MACD + Signal lines on their own
    // price scale with a band whose color follows the MACD line's SLOPE: green
    // while MACD is rising, red while falling (NOT the MACD↔Signal crossover).
    // No histogram / dots / zero line.
    if (overlays.macd && cshClosesAligned.length > 5) {
      const mcfg = (settings as any)?.macd || {};
      const inPane = mcfg.pane !== false; const paneIdx = inPane ? PANE_INDEX : 0;
      const fast = Math.max(1, Math.floor(Number(mcfg.fastLength) || 12));
      const slow = Math.max(1, Math.floor(Number(mcfg.slowLength) || 26));
      const sigLen = Math.max(1, Math.floor(Number(mcfg.signalLength) || 9));
      const macdColor = mcfg.macdColor || '#2962ff';
      const signalColor = mcfg.signalColor || '#ff6d00';
      const mUp = mcfg.bandUpColor || '#26a69a';
      const mDown = mcfg.bandDownColor || '#ef5350';
      const mOp = Math.max(0, Math.min(1, Number(mcfg.bandOpacity ?? 0.20)));

      // R94c — MACD ALWAYS computes on the CSH-smoothed close (cshClosesAligned),
      // regardless of the chart's selected candle type, so the indicator is
      // stable across candle-type switches. Bars with no CSH value (the live
      // forming bar, which isn't in rawCandles) are skipped — matching the
      // RelStrength / ChgN% behaviour.
      const macdSrc: Array<number | null> = cshClosesAligned.map(v => (Number.isFinite(v) ? v : null));
      const fastE = emaNullable(macdSrc, fast);
      const slowE = emaNullable(macdSrc, slow);
      const macdArr: Array<number | null> = new Array(candles.length).fill(null);
      for (let i = 0; i < candles.length; i++) {
        if (macdSrc[i] == null) continue; // skip live/missing bar (no flat carry)
        const f = fastE[i], sl = slowE[i];
        if (f != null && sl != null && Number.isFinite(f) && Number.isFinite(sl)) macdArr[i] = f - sl;
      }
      const sigArr = smaNullable(macdArr, sigLen); // SMA of MACD, per the Pine source

      const macdData: LineData<Time>[] = [];
      const sigData: LineData<Time>[] = [];
      const bandPts: { time: number; upper: number; lower: number; color?: string }[] = [];
      // R94c — band/trend follow the MACD line's SLOPE (rising = green/up,
      // falling = red/down), not the MACD↔Signal crossover.
      let prevMacd: number | null = null;
      for (let i = 0; i < candles.length; i++) {
        const m = macdArr[i];
        if (m == null || !Number.isFinite(m)) continue;
        macdData.push({ time: candles[i].time as Time, value: Number(m.toFixed(6)) });
        const sg = sigArr[i];
        if (sg != null && Number.isFinite(sg)) {
          sigData.push({ time: candles[i].time as Time, value: Number(sg.toFixed(6)) });
          const rising = prevMacd != null ? m >= prevMacd : m >= sg;
          bandPts.push({
            time: candles[i].time as number,
            upper: Math.max(m, sg), lower: Math.min(m, sg),
            color: withAlpha(rising ? mUp : mDown, mOp),
          });
        }
        prevMacd = m;
      }
      if (macdData.length > 0) {
        const ms = chart.addSeries(LineSeries, {
          color: macdColor, lineWidth: 2, priceScaleId: 'macdRight',
          lastValueVisible: true, priceLineVisible: false,
          priceFormat: { type: 'price', precision: 4, minMove: 0.0001 },
          title: '',
        }, paneIdx);
        ms.setData(macdData);
        cfgScale(ms, inPane, true, { top: 0.75, bottom: 0.02 }, macdColor);
        overlaySeriesRefs.current.push(ms);
        if (sigData.length > 0) {
          const ss = chart.addSeries(LineSeries, {
            color: signalColor, lineWidth: 1, priceScaleId: 'macdRight',
            lastValueVisible: false, priceLineVisible: false,
            priceFormat: { type: 'price', precision: 4, minMove: 0.0001 },
            title: '',
          }, paneIdx);
          ss.setData(sigData);
          overlaySeriesRefs.current.push(ss);
          if (mOp > 0 && bandPts.length >= 2) {
            const bp = new BandPrimitive(bandPts, { fillColor: withAlpha(mUp, mOp) });
            (ms as any).attachPrimitive(bp);
            primitivesRef.current.push(bp);
          }
        }
      }
    }

    // Funding (Delta perpetuals) — funding rate (% per 8h) forward-filled onto
    // the candle grid AS-OF each bar's close (projectToBarClose) → flat between
    // the 8h settlements, and the forming bar shows the current rate on every TF.
    // Rendered as a sign-coloured baseline line (green below 0 = shorts pay longs;
    // red above 0 = longs pay shorts) in the lower pane with its own right scale +
    // a zero reference. Data is supplied pre-IST-shifted to match candle.time.
    if (overlays.funding && fundingSeries && fundingSeries.length) {
      const fcfg = (settings as any)?.funding || {};
      const inPane = fcfg.pane !== false; const paneIdx = inPane ? PANE_INDEX : 0;
      const posColor = fcfg.positiveColor || '#ef5350';
      const negColor = fcfg.negativeColor || '#26a69a';
      const lineWidth = Math.max(1, Math.min(4, Math.floor(Number(fcfg.lineWidth) || 2))) as 1 | 2 | 3 | 4;
      // capLastBar: the candles array can be a trimmed slice (replay), so samples
      // newer than the visible right edge must not leak onto it.
      const data: LineData<Time>[] = projectToBarClose(
        fundingSeries.filter(f => Number.isFinite(f.rate)).map(f => ({ time: f.time, value: f.rate })),
        candles.map(c => c.time as number),
        { capLastBar: true },
      ).map(d => ({ time: d.time as Time, value: Number(d.value.toFixed(4)) }));
      if (data.length) {
        const fSeries = chart.addSeries(BaselineSeries, {
          baseValue: { type: 'price', price: 0 },
          topLineColor: posColor, bottomLineColor: negColor,
          topFillColor1: withAlpha(posColor, 0.12), topFillColor2: withAlpha(posColor, 0.01),
          bottomFillColor1: withAlpha(negColor, 0.01), bottomFillColor2: withAlpha(negColor, 0.12),
          lineWidth, priceScaleId: 'fundingRight',
          lastValueVisible: true, priceLineVisible: false,
          priceFormat: { type: 'price', precision: 4, minMove: 0.0001 },
        }, paneIdx);
        fSeries.setData(data);
        cfgScale(fSeries, inPane, true, { top: 0.78, bottom: 0.02 }, posColor);
        try { fSeries.createPriceLine({ price: 0, color: C.dim, lineWidth: 1, lineStyle: 2, axisLabelVisible: false, title: '' } as any); } catch { /* ignore */ }
        overlaySeriesRefs.current.push(fSeries as any);

        // ── Funding Signals (contrarian crowding + reversion) ──────────────
        // Bottom-pane ONLY. Smooth funding (EMA), define adaptive extremes via
        // a rolling z-score (mean ± k·σ over `lookback`), and mark a signal when
        // the smoothed funding REVERTS back from an extreme (the squeeze
        // beginning). High +funding = crowded longs → fade SHORT; deep −funding
        // = crowded shorts → fade LONG. Exit (○) on a zero-cross while a bias is
        // live. Markers attach to the funding series so they render in this pane.
        if (fcfg.signals !== false && data.length > 12) {
          const vals = data.map(d => d.value as number);
          const ts = data.map(d => d.time as Time);
          const emaLen = Math.max(1, Math.min(100, Math.floor(Number(fcfg.emaLength) || 8)));
          const lookback = Math.max(10, Math.min(1000, Math.floor(Number(fcfg.lookback) || 100)));
          const k = Math.max(0.5, Math.min(5, Number(fcfg.k) || 2));
          const bandWidth = Math.max(1, Math.min(4, Math.floor(Number(fcfg.bandWidth) || 2))) as 1 | 2 | 3 | 4;
          const fEMA = emaSmooth(vals, emaLen);
          const mean = sma(vals, lookback);
          const std = stdDev(vals, mean, lookback);
          const upper = mean.map((m, i) => (m == null || std[i] == null) ? null : m + k * (std[i] as number));
          const lower = mean.map((m, i) => (m == null || std[i] == null) ? null : m - k * (std[i] as number));

          if (fcfg.showBands !== false) {
            const upData: LineData<Time>[] = []; const loData: LineData<Time>[] = [];
            for (let i = 0; i < data.length; i++) {
              if (upper[i] != null) upData.push({ time: ts[i], value: Number((upper[i] as number).toFixed(4)) });
              if (lower[i] != null) loData.push({ time: ts[i], value: Number((lower[i] as number).toFixed(4)) });
            }
            if (upData.length >= 2) {
              const u = chart.addSeries(LineSeries, { color: withAlpha(posColor, 0.55), lineWidth: bandWidth, lineStyle: 2, priceScaleId: 'fundingRight', lastValueVisible: false, priceLineVisible: false }, paneIdx);
              u.setData(upData); overlaySeriesRefs.current.push(u);
            }
            if (loData.length >= 2) {
              const lo = chart.addSeries(LineSeries, { color: withAlpha(negColor, 0.55), lineWidth: bandWidth, lineStyle: 2, priceScaleId: 'fundingRight', lastValueVisible: false, priceLineVisible: false }, paneIdx);
              lo.setData(loData); overlaySeriesRefs.current.push(lo);
            }
          }

          const fMarkers: Array<{ time: Time; position: 'aboveBar' | 'belowBar' | 'inBar'; color: string; shape: 'arrowUp' | 'arrowDown' | 'circle'; text: string; size: number }> = [];
          let fBias: 'LONG' | 'SHORT' | null = null;
          for (let i = 1; i < data.length; i++) {
            const cu = upper[i], cl = lower[i], pu = upper[i - 1], pl = lower[i - 1];
            if (cu == null || cl == null || pu == null || pl == null) continue;
            const pe = fEMA[i - 1], ce = fEMA[i];
            const zPrev = std[i - 1] && (std[i - 1] as number) > 0 ? Math.abs((pe - (mean[i - 1] as number)) / (std[i - 1] as number)) : 0;
            if (pe > (pu as number) && ce <= (cu as number)) {
              // crowded longs unwinding → contrarian SHORT
              fMarkers.push({ time: ts[i], position: 'aboveBar', color: posColor, shape: 'arrowDown', text: 'S', size: zPrev >= 3 ? 2 : 1 });
              fBias = 'SHORT';
            } else if (pe < (pl as number) && ce >= (cl as number)) {
              // crowded shorts unwinding → contrarian LONG
              fMarkers.push({ time: ts[i], position: 'belowBar', color: negColor, shape: 'arrowUp', text: 'L', size: zPrev >= 3 ? 2 : 1 });
              fBias = 'LONG';
            } else if (fBias && ((pe < 0) !== (ce < 0))) {
              // funding normalised (zero cross) → exit the funding bias
              fMarkers.push({ time: ts[i], position: 'inBar', color: C.dim, shape: 'circle', text: 'X', size: 1 });
              fBias = null;
            }
          }
          if (fMarkers.length) { try { createSeriesMarkers(fSeries as any, fMarkers as any); } catch { /* ignore */ } }
        }
      }
    }

    // JHM — JSR Hilega-Milega (Round 103). Exact Pine logic:
    //   rsi = RSI(src, rsiLen);  vwma = VWMA(rsi, vwmaLen);  ema = EMA(rsi, emaLen)
    //   bull = ema ≥ vwma. Plotted on its own 0–100 strip with a 50 midline,
    //   fixed bull/bear zone shading, a dynamic EMA/VWMA fill and crossover dots.
    // Computed on the RAW closed candles (stable across candle-type switches).
    if (overlays.jhm) {
      const jcfg = (settings as any)?.jhm || {};
      const inPane = jcfg.pane !== false; const paneIdx = inPane ? PANE_INDEX : 0;
      const rsiLen = Math.max(1, Math.min(200, Math.floor(Number(jcfg.rsiLength) || 9)));
      const vwmaLen = Math.max(1, Math.min(200, Math.floor(Number(jcfg.vwmaLength) || 21)));
      const emaLen = Math.max(1, Math.min(100, Math.floor(Number(jcfg.emaLength) || 3)));
      const useLinreg = jcfg.source === 'linregClose';
      const bullShade = withAlpha(jcfg.bullShadeColor || '#00C853', Math.max(0, Math.min(1, Number(jcfg.bullShadeOpacity ?? 0.12))));
      const bearShade = withAlpha(jcfg.bearShadeColor || '#FF1744', Math.max(0, Math.min(1, Number(jcfg.bearShadeOpacity ?? 0.12))));
      // Fixed, theme-safe colours for the non-configurable elements.
      const VWMA_COLOR = '#FF8C42', EMA_COLOR = '#00B8D4';
      const MID_COLOR = isDark ? 'rgba(176,176,176,0.7)' : 'rgba(110,110,110,0.7)';
      const FILL_BULL = withAlpha('#00B8D4', 0.16), FILL_BEAR = withAlpha('#FF8C42', 0.16);
      const DOT_UP = '#00E676', DOT_DOWN = '#FF1744';

      const srcCandles = (rawCandles && rawCandles.length) ? rawCandles : candles;
      const closesJ = srcCandles.map(c => Number(c.close));
      const srcArr = useLinreg ? linregOf(closesJ, 14).map(v => Number(v)) : closesJ;
      const rsiA = rsiWilder(srcArr, rsiLen);
      const volA = srcCandles.map(c => Number(c.volume) || 0);
      const vwmaA = vwmaSeries(rsiA, volA, vwmaLen);
      const emaA = emaSeries(rsiA, emaLen);

      const vwmaData: LineData<Time>[] = [], emaData: LineData<Time>[] = [], midData: LineData<Time>[] = [];
      const top: LineData<Time>[] = [], bot: LineData<Time>[] = [];
      const fillPts: BandPoint[] = [], bullPts: BandPoint[] = [], bearPts: BandPoint[] = [];
      const dots: Array<{ time: Time; position: 'inBar'; color: string; shape: 'circle'; size: number }> = [];
      let prevBull: boolean | null = null;
      for (let i = 0; i < srcCandles.length; i++) {
        const vw = vwmaA[i], em = emaA[i];
        if (!Number.isFinite(vw) || !Number.isFinite(em)) continue;
        const t = srcCandles[i].time as Time; const ts = srcCandles[i].time as number;
        vwmaData.push({ time: t, value: Math.round(vw * 100) / 100 });
        emaData.push({ time: t, value: Math.round(em * 100) / 100 });
        midData.push({ time: t, value: 50 });
        top.push({ time: t, value: 100 }); bot.push({ time: t, value: 0 });
        const bull = em >= vw;
        bullPts.push({ time: ts, upper: 100, lower: 50 });
        bearPts.push({ time: ts, upper: 50, lower: 0 });
        fillPts.push({ time: ts, upper: Math.max(em, vw), lower: Math.min(em, vw), color: bull ? FILL_BULL : FILL_BEAR });
        if (prevBull !== null && bull !== prevBull) dots.push({ time: t, position: 'inBar', color: bull ? DOT_UP : DOT_DOWN, shape: 'circle', size: 0.6 });
        prevBull = bull;
      }

      if (emaData.length > 1) {
        const opt = (color: string, lw: 1 | 2 | 3, title: string, lastVal = false) => ({
          color, lineWidth: lw, priceScaleId: 'jhm', lastValueVisible: lastVal, priceLineVisible: false,
          crosshairMarkerVisible: false, title, priceFormat: { type: 'price' as const, precision: 1, minMove: 0.1 },
        });
        // Invisible 0/100 anchors pin the strip to a fixed 0–100 scale.
        const aTop = chart.addSeries(LineSeries, { ...opt('rgba(0,0,0,0)', 1, 'JHM·top'), lastValueVisible: false }, paneIdx); aTop.setData(top);
        const aBot = chart.addSeries(LineSeries, { ...opt('rgba(0,0,0,0)', 1, 'JHM·bot'), lastValueVisible: false }, paneIdx); aBot.setData(bot);
        const mids = chart.addSeries(LineSeries, opt(MID_COLOR, 1, 'JHM 50'), paneIdx); mids.setData(midData);
        const vs = chart.addSeries(LineSeries, opt(VWMA_COLOR, 2, 'JHM VWMA', true), paneIdx); vs.setData(vwmaData);
        const es = chart.addSeries(LineSeries, opt(EMA_COLOR, 2, 'JHM EMA'), paneIdx); es.setData(emaData);
        // In a dedicated pane the strip spans the full pane; overlaying on price
        // it keeps the bottom-margin trick (top:0.72) so it sits under the candles.
        cfgScale(mids, inPane, true, { top: 0.72, bottom: 0.0 }, MID_COLOR);
        // Zone shades (fixed regions) + dynamic EMA/VWMA fill — attach to anchors/es.
        const bbull = new BandPrimitive(bullPts, { fillColor: bullShade }); (aTop as any).attachPrimitive(bbull); primitivesRef.current.push(bbull);
        const bbear = new BandPrimitive(bearPts, { fillColor: bearShade }); (aBot as any).attachPrimitive(bbear); primitivesRef.current.push(bbear);
        const bfill = new BandPrimitive(fillPts, { fillColor: FILL_BULL }); (es as any).attachPrimitive(bfill); primitivesRef.current.push(bfill);
        overlaySeriesRefs.current.push(aTop, aBot, mids, vs, es);
        if (dots.length) { try { createSeriesMarkers(es, dots as any); } catch { /* ignore */ } }
      }
    }

    // Vol — Weighted Volume (volume × close). Lower pane. Shows ONLY spike bars
    // (raw wVol > slow × 2) plus the Fast(3)/Slow(21) EMAs of the LinReg(7)-
    // smoothed wVol, with a bull/bear band filled between the EMAs (bull when
    // fast ≥ slow). EMA visibility + band colours/opacity are user-configurable;
    // the LinReg/EMA lengths and spike multiplier are hardcoded per spec.
    if (overlays.vol) {
      const vcfg = (settings as any)?.vol || {};
      const inPane = vcfg.pane !== false; const paneIdx = inPane ? PANE_INDEX : 0;
      const showEmas = vcfg.showEmas !== false;
      const op = Math.max(0, Math.min(1, Number(vcfg.bandOpacity ?? 0.35)));
      const bandBull = withAlpha(vcfg.bandBullColor || '#1565c0', op);
      const bandBear = withAlpha(vcfg.bandBearColor || '#f57f17', op);
      const bullSpike = vcfg.bullSpikeColor || '#ff6f00';
      const bearSpike = vcfg.bearSpikeColor || '#e040fb';
      const FAST_COLOR = vcfg.fastColor || '#29b6f6';
      const SLOW_COLOR = vcfg.slowColor || '#ffb74d';

      // R120.3 — compute on RAW candles (the candle-type-independent source the
      // OverView Vol VOTER uses), not the chart's display-transformed candles.
      // Otherwise on a non-Regular candle type a bar's bull/bear (close≥open)
      // classification — and even spike detection (wVol = volume × close) —
      // diverges between this pane and the voter, so the pane could show a green
      // spike while the voter (raw) latched bearish, painting the candle red.
      const volSrc = (rawCandles && rawCandles.length) ? rawCandles : candles;
      const wv = computeWVol(volSrc);
      const histData: HistogramData<Time>[] = [];
      const fastData: LineData<Time>[] = [], slowData: LineData<Time>[] = [];
      const fillPts: BandPoint[] = [];
      for (let i = 0; i < wv.length; i++) {
        const p = wv[i]; const t = p.time as Time; const ts = p.time as number;
        // Spikes only — non-spike bars are intentionally not drawn.
        if (p.isSpike) histData.push({ time: t, value: p.wVol, color: p.isBull ? bullSpike : bearSpike });
        if (Number.isFinite(p.fast) && Number.isFinite(p.slow)) {
          fastData.push({ time: t, value: p.fast });
          slowData.push({ time: t, value: p.slow });
          fillPts.push({ time: ts, upper: Math.max(p.fast, p.slow), lower: Math.min(p.fast, p.slow), color: p.fast >= p.slow ? bandBull : bandBear });
        }
      }

      if (histData.length || fastData.length) {
        const hs = chart.addSeries(HistogramSeries, {
          priceScaleId: 'vol', priceFormat: { type: 'volume' },
          lastValueVisible: false, priceLineVisible: false, base: 0,
        }, paneIdx);
        hs.setData(histData);
        overlaySeriesRefs.current.push(hs as any);
        if (showEmas && fastData.length > 1) {
          const lopt = (color: string, title: string) => ({
            color, lineWidth: 2 as const, priceScaleId: 'vol',
            lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false,
            title, priceFormat: { type: 'volume' as const },
          });
          const ss = chart.addSeries(LineSeries, lopt(SLOW_COLOR, 'Vol Slow EMA(21)'), paneIdx); ss.setData(slowData);
          const fs = chart.addSeries(LineSeries, lopt(FAST_COLOR, 'Vol Fast EMA(3)'), paneIdx); fs.setData(fastData);
          const bfill = new BandPrimitive(fillPts, { fillColor: bandBull }); (fs as any).attachPrimitive(bfill); primitivesRef.current.push(bfill);
          overlaySeriesRefs.current.push(ss, fs);
        }
        try {
          chart.priceScale('vol').applyOptions({
            visible: inPane,
            borderColor: isDark ? '#2b3a4a' : '#cfd8dc',
            scaleMargins: inPane ? { top: 0.12, bottom: 0.08 } : { top: 0.82, bottom: 0 },
          });
        } catch { /* ignore */ }
      }
    }

    // Volume — plain per-bar raw-volume histogram overlaid at the bottom of the price
    // pane (classic TradingView style), coloured green/red by the RAW candle's up/down
    // so it's identical across candle types. Cycles after HVol on the same chip.
    if (overlays.volume) {
      const volSrc: CandleRow[] = ((rawCandles && rawCandles.length) ? rawCandles : candles).slice();
      if (rawLatestCandle && Number.isFinite(rawLatestCandle.time as number) &&
          (!volSrc.length || (volSrc[volSrc.length - 1].time as number) < (rawLatestCandle.time as number))) volSrc.push(rawLatestCandle);
      const bull = withAlpha('#26a69a', 0.55), bear = withAlpha('#ef5350', 0.55);
      const data: HistogramData<Time>[] = [];
      for (const c of volSrc) {
        const v = Number(c.volume) || 0;
        if (v <= 0) continue;
        data.push({ time: c.time as Time, value: v, color: Number(c.close) >= Number(c.open) ? bull : bear });
      }
      if (data.length) {
        const hs = chart.addSeries(HistogramSeries, {
          priceScaleId: 'vol', priceFormat: { type: 'volume' },
          lastValueVisible: false, priceLineVisible: false, base: 0,
        }, 0); // overlay on the price pane (not a separate pane)
        hs.setData(data);
        overlaySeriesRefs.current.push(hs as any);
        try { chart.priceScale('vol').applyOptions({ visible: false, scaleMargins: { top: 0.8, bottom: 0 } }); } catch { /* ignore */ }
      }
    }

    // WR% — paired Williams %R fast/slow EMAs (Round 142, R116b zone logic).
    //   Pinned to a fixed -100..0 strip so the -50 mid line + fast/slow EMAs
    //   sit at stable Y positions across zoom / scroll.
    //   Zone classification (each bar):
    //     BUY    — slowEMA > -50 AND fastEMA rising  → band painted bull
    //     SHORT  — slowEMA < -50 AND fastEMA falling → band painted bear
    //     NEUTRAL→ band painted grey (no trade conviction)
    //   The -50 mid line is rendered thicker / brighter than OB/OS guides
    //   because it's the load-bearing reference for the zone decision; faint
    //   bull / bear zone shading (above / below -50) reinforces the boundary
    //   even when the band fill is grey.
    if (overlays.wrPct) {
      const wcfg = (settings as any)?.wrPct || {};
      const inPane = wcfg.pane !== false; const paneIdx = inPane ? PANE_INDEX : 0;
      const p = resolveWrPct(wcfg);
      const bullRaw = wcfg.bullBandColor || '#00C853';
      const bearRaw = wcfg.bearBandColor || '#FF1744';
      const bullOp = Math.max(0, Math.min(1, Number(wcfg.bullBandOpacity ?? 0.22)));
      const bearOp = Math.max(0, Math.min(1, Number(wcfg.bearBandOpacity ?? 0.22)));
      const bullCol = withAlpha(bullRaw, bullOp);
      const bearCol = withAlpha(bearRaw, bearOp);
      const neutralCol = withAlpha(isDark ? '#9e9e9e' : '#bdbdbd', Math.max(bullOp, bearOp) * 0.5);
      const FAST_COLOR = wcfg.fastLineColor || '#FFA000';     // yellow-orange (matches Pine's "yellow")
      const SLOW_COLOR = wcfg.slowLineColor || '#00B8D4';     // aqua (matches Pine's "aqua")
      // OB/OS guides are faint; the -50 mid line is the load-bearing reference
      // and gets a stronger / brighter colour.
      const GUIDE_COLOR = isDark ? 'rgba(176,176,176,0.45)' : 'rgba(110,110,110,0.45)';
      const MID_COLOR   = isDark ? 'rgba(255,213,79,0.95)'   : 'rgba(180,120,0,0.95)';  // amber, prominent
      const OB_OS_SHADE = withAlpha(isDark ? '#888' : '#aaa', 0.06);
      // Faint zone tint — bull above -50, bear below -50.
      const BULL_ZONE_SHADE = withAlpha(bullRaw, 0.06);
      const BEAR_ZONE_SHADE = withAlpha(bearRaw, 0.06);
      const DOT_UP = '#00E676', DOT_DOWN = '#FF1744';

      const srcCandles = (rawCandles && rawCandles.length) ? rawCandles : candles;
      const closesW = srcCandles.map(c => Number(c.close));
      const srcArrW = p.source === 'linregClose' ? linregOf(closesW, 14).map(v => Number(v)) : closesW;
      const highsW = srcCandles.map(c => Number(c.high));
      const lowsW  = srcCandles.map(c => Number(c.low));
      const { fast: fastA, slow: slowA } = wrPctPair(srcArrW, highsW, lowsW, p);

      const fastData: LineData<Time>[] = [], slowData: LineData<Time>[] = [], midData: LineData<Time>[] = [];
      const obData: LineData<Time>[] = [], osData: LineData<Time>[] = [];
      const top: LineData<Time>[] = [], bot: LineData<Time>[] = [];
      const fillPts: BandPoint[] = [], obShade: BandPoint[] = [], osShade: BandPoint[] = [];
      const bullZoneShade: BandPoint[] = [], bearZoneShade: BandPoint[] = [];
      const dots: Array<{ time: Time; position: 'inBar'; color: string; shape: 'circle'; size: number }> = [];
      let prevZone: 'BUY' | 'SHORT' | 'NEUTRAL' | null = null;
      for (let i = 0; i < srcCandles.length; i++) {
        const f = fastA[i], s = slowA[i];
        if (!Number.isFinite(f) || !Number.isFinite(s)) continue;
        const t = srcCandles[i].time as Time;
        const ts = srcCandles[i].time as number;
        fastData.push({ time: t, value: Math.round(f * 100) / 100 });
        slowData.push({ time: t, value: Math.round(s * 100) / 100 });
        midData.push({ time: t, value: WRPCT_MID });
        obData.push({ time: t, value: WRPCT_OVERBOUGHT });
        osData.push({ time: t, value: WRPCT_OVERSOLD });
        top.push({ time: t, value: 0 });        // pane top anchor
        bot.push({ time: t, value: -100 });     // pane bottom anchor
        const zone = wrPctZoneAt(fastA, slowA, i);
        const fillCol = zone === 'BUY' ? bullCol : zone === 'SHORT' ? bearCol : neutralCol;
        fillPts.push({ time: ts, upper: Math.max(f, s), lower: Math.min(f, s), color: fillCol });
        obShade.push({ time: ts, upper: 0, lower: WRPCT_OVERBOUGHT });
        osShade.push({ time: ts, upper: WRPCT_OVERSOLD, lower: -100 });
        bullZoneShade.push({ time: ts, upper: 0,         lower: WRPCT_MID });
        bearZoneShade.push({ time: ts, upper: WRPCT_MID, lower: -100 });
        // Mark BUY ↔ SHORT zone entries (skip NEUTRAL transitions — those are
        // ambiguous and would visually crowd the band).
        if (prevZone && zone !== prevZone) {
          if (zone === 'BUY')        dots.push({ time: t, position: 'inBar', color: DOT_UP,   shape: 'circle', size: 0.6 });
          else if (zone === 'SHORT') dots.push({ time: t, position: 'inBar', color: DOT_DOWN, shape: 'circle', size: 0.6 });
        }
        prevZone = zone;
      }

      if (fastData.length > 1) {
        const opt = (color: string, lw: 1 | 2 | 3, title: string, lastVal = false) => ({
          color, lineWidth: lw, priceScaleId: 'wrPct', lastValueVisible: lastVal, priceLineVisible: false,
          crosshairMarkerVisible: false, title, priceFormat: { type: 'price' as const, precision: 1, minMove: 0.1 },
        });
        // Invisible 0 and -100 anchors pin the strip to a fixed -100..0 scale so
        // crossovers stay at stable Y positions across zoom + scroll. The user
        // reads BUY/SHORT signals directly off the visual crossover — any
        // autoscaling would silently distort that.
        const aTop = chart.addSeries(LineSeries, { ...opt('rgba(0,0,0,0)', 1, 'WR%·top'), lastValueVisible: false }, paneIdx); aTop.setData(top);
        const aBot = chart.addSeries(LineSeries, { ...opt('rgba(0,0,0,0)', 1, 'WR%·bot'), lastValueVisible: false }, paneIdx); aBot.setData(bot);
        const obs  = chart.addSeries(LineSeries, opt(GUIDE_COLOR, 1, 'WR% −20 OB'), paneIdx); obs.setData(obData);
        const oss  = chart.addSeries(LineSeries, opt(GUIDE_COLOR, 1, 'WR% −80 OS'), paneIdx); oss.setData(osData);
        // -50 mid line is thicker (2px) + amber — load-bearing reference for
        // the BUY/SHORT zone decision.
        const mids = chart.addSeries(LineSeries, opt(MID_COLOR, 2, 'WR% −50 (zone mid)'), paneIdx); mids.setData(midData);
        const ss = chart.addSeries(LineSeries, opt(SLOW_COLOR, 2, 'WR% slow EMA', true), paneIdx); ss.setData(slowData);
        const fs = chart.addSeries(LineSeries, opt(FAST_COLOR, 2, 'WR% fast EMA'), paneIdx); fs.setData(fastData);
        cfgScale(mids, inPane, true, { top: 0.72, bottom: 0.0 }, MID_COLOR);
        // Faint zone shading (above / below -50) — reinforces the zone
        // boundary even when the band fill is grey (NEUTRAL).
        const bzBull = new BandPrimitive(bullZoneShade, { fillColor: BULL_ZONE_SHADE }); (aTop as any).attachPrimitive(bzBull); primitivesRef.current.push(bzBull);
        const bzBear = new BandPrimitive(bearZoneShade, { fillColor: BEAR_ZONE_SHADE }); (aBot as any).attachPrimitive(bzBear); primitivesRef.current.push(bzBear);
        // Faint OB / OS shading via the top/bot anchors so it spans the full
        // visible range regardless of zoom.
        const bob = new BandPrimitive(obShade, { fillColor: OB_OS_SHADE }); (aTop as any).attachPrimitive(bob); primitivesRef.current.push(bob);
        const bos = new BandPrimitive(osShade, { fillColor: OB_OS_SHADE }); (aBot as any).attachPrimitive(bos); primitivesRef.current.push(bos);
        // Zone-coloured band fill between fast and slow EMAs.
        const bfill = new BandPrimitive(fillPts, { fillColor: bullCol }); (fs as any).attachPrimitive(bfill); primitivesRef.current.push(bfill);
        overlaySeriesRefs.current.push(aTop, aBot, obs, oss, mids, ss, fs);
        if (dots.length) { try { createSeriesMarkers(fs, dots as any); } catch { /* ignore */ } }
      }
    }

    /* --- BBR + Wave OSCILLATORS (lower pane or price overlay) --------------
     *
     * These are the same numbers the BBR candle tint and the Wave arrows
     * already use - computeBbrSeries / computeWaveSeries are what
     * computeBbrReversals / computeWaveSignals call internally - so the curve
     * and the marks can never tell different stories.
     *
     * THE SCALE PROBLEM, and why this does NOT rescale anything.
     * Measured over 60 Delta symbols: BBR runs about -133..+148 (p1..p99) with
     * its bands at +/-100; Wave's wt1 runs about -69..+71 and reaches -100 on
     * 0.00% of bars. Their oversold lines are equivalent in RARITY (each tags
     * ~5.8% of bars) but nowhere near equivalent in VALUE. Drawn raw together,
     * BBR dwarfs Wave and the two sets of guides sit at different heights, so
     * "BBR is above Wave" reads as something it is not.
     *
     * The fix is to pin each series' OWN price scale to +/-1.5x its own
     * overbought level and give both the same scaleMargins. Both scales then map
     * their range onto the same pixel band, so BBR's -100 and Wave's -60 land on
     * the same row - visually comparable - while the crosshair still reports each
     * indicator's true value. Rescaling the data would have broken that.
     */
    const bbrOscOn = !!overlays.bbrOsc;
    const waveOscOn = !!overlays.waveOsc;
    if ((bbrOscOn || waveOscOn) && candles.length > 5) {
      const bcfg = (settings as any)?.bbr || {};
      const wcfgO = (settings as any)?.wave || {};
      const bwcfg = (settings as any)?.bbrWave || {};

      const bbObRaw = Number(bcfg.obLevel); const bbOb = Number.isFinite(bbObRaw) ? bbObRaw : 100;
      const bbOsRaw = Number(bcfg.osLevel); const bbOs = Number.isFinite(bbOsRaw) ? bbOsRaw : -100;
      const wvObRaw = Number(wcfgO.obLevel); const wvOb = Number.isFinite(wvObRaw) ? wvObRaw : 60;
      const wvOsRaw = Number(wcfgO.osLevel); const wvOs = Number.isFinite(wvOsRaw) ? wvOsRaw : -60;
      const lwOf = (v: unknown, d: number) => Math.max(1, Math.min(4, Math.floor(Number(v) || d))) as 1 | 2 | 3 | 4;
      const stepOf = (mode: unknown) => (mode === 'smooth' ? 0 : 1) as 0 | 1; // 1 = WithSteps
      const GUIDE = bwcfg.guideColor || (isDark ? 'rgba(176,176,176,0.45)' : 'rgba(110,110,110,0.45)');
      const showZones = bwcfg.showZones !== false;
      const showRelZones = bwcfg.showRelZones === true;

      /* ZONE FILLS. Own colours, not the BBR candle-tint ones they used to
       * borrow - restyling the tint silently restyled these, and two tint
       * colours set to the same hue made the two zones indistinguishable.
       *
       * Theme-aware the same way the exit marks are: a value still sitting at
       * its default is lifted to a hue that survives #0D1117 (10% of a red-800
       * over near-black is almost nothing), while a colour the user actually
       * picked is left exactly as picked. */
      const ZONE_DARK: Record<string, string> = { '#c62828': '#ef5350', '#2e7d32': '#66bb6a' };
      const zoneHue = (c: string) => (isDark && ZONE_DARK[String(c).toLowerCase()]) || c;
      const opOf = (v: unknown, fb: unknown) => Math.max(0, Math.min(1,
        Number(v ?? fb ?? 0.10)));
      const obZoneFill = withAlpha(
        zoneHue(bwcfg.obZoneColor || '#c62828'), opOf(bwcfg.obZoneOpacity, bwcfg.zoneOpacity));
      const osZoneFill = withAlpha(
        zoneHue(bwcfg.osZoneColor || '#2e7d32'), opOf(bwcfg.osZoneOpacity, bwcfg.zoneOpacity));

      /* RELATIVE-POSITION BACKGROUND. Tints the whole pane by which curve is on
       * top: BBR above Wave is bullish, at or below is bearish.
       *
       * COMPARED ON THE PINNED SCALES, NOT ON RAW VALUES. BBR runs about
       * -133..+148 with its threshold at 100; wt1 runs about -69..+71 with its
       * threshold at 60, and the pane pins each to +/-1.5x its OWN threshold so
       * the two rows line up. Raw `bbr > wt1` would therefore disagree with what
       * is on screen most of the time. Dividing each by its own overbought level
       * reproduces exactly the visual "which line is higher". */
      const relAboveFill = withAlpha(
        zoneHue(bwcfg.relAboveColor || '#2e7d32'), opOf(bwcfg.relZoneOpacity, 0.08));
      const relBelowFill = withAlpha(
        zoneHue(bwcfg.relBelowColor || '#c62828'), opOf(bwcfg.relZoneOpacity, 0.08));

      const bInPane = bcfg.pane !== false;
      const wInPane = wcfgO.pane !== false;

      /* SLOPE COLOURING, shared by both curves.
       *
       * Rising reads bullish, falling bearish. The direction is what is
       * actually being watched in this pane - the measured entry is "both
       * oversold AND BBR RISING", so the turn IS the signal, and one flat hue
       * hides it until the arrow prints.
       *
       * stepDirections carries a flat bar's previous direction and back-fills
       * the leading run, so a hold between two falls does not become a third
       * colour and the line never opens in a colour it has not earned. Per-point
       * colour survives WithSteps: v5 strokes each segment with the LEFT point's
       * colour, which is why this needs no splitting into runs. */
      const slopeOn = bwcfg.slopeColor !== false;
      const riseCol = bwcfg.riseColor || '#26a69a';
      const fallCol = bwcfg.fallColor || '#ef5350';
      /** Paint a line by its own slope; returns the colour of its LAST point so
       *  the price-axis badge can agree with the right-hand edge instead of
       *  showing a hue that is no longer on screen. */
      const paintSlope = (pts: LineData<Time>[], flat: string): string => {
        if (!slopeOn || pts.length < 2) return flat;
        const dirs = stepDirections(pts.map(d => Number(d.value)));
        for (let k = 0; k < pts.length; k++) {
          (pts[k] as any).color = dirs[k] === true ? riseCol : dirs[k] === false ? fallCol : flat;
        }
        const lastDir = dirs[dirs.length - 1];
        return lastDir === true ? riseCol : lastDir === false ? fallCol : flat;
      };
      /* The zero line. It is drawn ONCE for the pane, by whichever block runs
       * first, exactly like the OB/OS guides: both scales are pinned to
       * +/-1.5x their own overbought level, so zero lands on the same row for
       * both curves and a second line would sit on top of the first. */
      const showMid = bwcfg.showMidline !== false;
      const midCol = bwcfg.midlineColor || 'rgba(120,130,140,0.38)';

      /* Computed on bwSource, NOT on `candles` - see the bwSource memo. It is
       * built by mapping over `candles` itself, so it is index-aligned with the
       * plot by construction: the levels come from the chosen source while the
       * marks below still price on real bars. */
      const bbrArr = computeBbrSeries(bwSource, Number(bcfg.bbLen) || 20, Number(bcfg.bbMult) || 2);
      const waveArr = computeWaveSeries(bwSource, Number(wcfgO.channelLength) || 10, Number(wcfgO.averageLength) || 21);

      const mkOpt = (scaleId: string) => (
        color: string, width: 1 | 2 | 3 | 4, title: string, lineType: 0 | 1, lastVal = false,
      ) => ({
        color, lineWidth: width, priceScaleId: scaleId, lastValueVisible: lastVal, priceLineVisible: false,
        crosshairMarkerVisible: false, title, lineType,
        priceFormat: { type: 'price' as const, precision: 1, minMove: 0.1 },
      });

      /* --- Higher-timeframe curves (utils/oscHtf.ts) ------------------------
       * BBR / Wave of the Strategies-mapped higher timeframe, stamped onto this
       * chart's bars so each HTF value lies flat across its own window. Drawn
       * FIRST so the chart's own curves paint on top of them, on the SAME pinned
       * scale id, so the HTF -100 / -60 rows are the chart's rows.
       *
       * Aligned, not causal, by the owner's choice: this is for reading. No
       * signal below reads it. The forming HTF bar is rebuilt from this chart's
       * own bars, so it moves with every tick, and it is dashed because its value
       * is not final. In bar replay only HTF bars closed at the playhead count. */
      const htfMin = Number(auxCandles?.srHtfMinutes) || 0;
      const htfRawOsc = (auxCandles?.srHtf || []) as CandleRow[];
      const wantBbrHtf = bbrOscOn && bcfg.showHtf === true;
      const wantWaveHtf = waveOscOn && wcfgO.showHtf === true;
      let bbrHtfStamps: OscHtfStamp[] = [];
      let waveHtfStamps: OscHtfStamp[] = [];
      if ((wantBbrHtf || wantWaveHtf) && htfMin > (intervalMinutes || 0) && htfRawOsc.length > 1) {
        const hb = buildHtfBarsForOsc(htfRawOsc, htfMin, bwRealBars as CandleRow[], {
          ltfMin: intervalMinutes || 0, replayCut,
        });
        // The same source candle the chart's own curves compute on.
        const hSrc = bwSrcType === 'Regular' ? hb.bars : indicatorSourceCandles(hb.bars, bwSrcType, bwClnLen);
        const ltfTimes = candles.map(c => Number(c.time));
        if (wantBbrHtf) {
          const v = computeBbrSeries(hSrc, Number(bcfg.bbLen) || 20, Number(bcfg.bbMult) || 2);
          bbrHtfStamps = stampOscHtf(ltfTimes, hb.bars, v, htfMin, hb.closedLen);
        }
        if (wantWaveHtf) {
          const w = computeWaveSeries(hSrc, Number(wcfgO.channelLength) || 10, Number(wcfgO.averageLength) || 21);
          const wv = w.wt1.map((x, i) => (i >= w.warmup ? x : null));
          waveHtfStamps = stampOscHtf(ltfTimes, hb.bars, wv, htfMin, hb.closedLen);
        }
      }
      const htfTag = htfMin ? catalogIntervalLabel(htfMin) : '';
      const showForming = bwcfg.htfShowForming !== false;
      // Default colours are lifted on dark only while still at default — a
      // colour the user picked is left as picked.
      const HTF_DARK: Record<string, string> = { '#8e24aa': '#ce93d8', '#f9a825': '#ffd54f' };
      const htfHue = (c: string) => (isDark && HTF_DARK[String(c).toLowerCase()]) || c;
      const drawHtf = (
        stamps: OscHtfStamp[], scaleId: string, inPane: boolean, color: string, width: unknown, name: string,
      ): ISeriesApi<'Line'> | null => {
        if (stamps.length < 2) return null;
        const paneIdx = inPane ? PANE_INDEX : 0;
        const hue = htfHue(color);
        const w = Math.max(1, Math.min(4, Math.floor(Number(width) || 3))) as 1 | 2 | 3 | 4;
        /* SLOPE COLOURING, the same rule as the chart-timeframe curves: rising
         * paints `riseColor`, falling `fallColor`, from the same bbrWave switch.
         * The stamps hold flat across each HTF window and step at its boundary,
         * and stepDirections carries a flat step's previous direction, so the
         * direction is decided HTF bar against HTF bar, not per LTF bar. With
         * slope colouring off the HTF curve keeps its own colour. The HTF curve
         * is told apart from the chart's by its width, its lighter alpha, its
         * staircase and its "1D" badge. */
        const A_CLOSED = 0.85, A_FORMING = 0.6;
        const dirs = slopeOn ? stepDirections(stamps.map(s => s.value)) : [];
        const colourAt = (k: number, alpha: number): string => {
          const d = dirs[k];
          return withAlpha(d === true ? riseCol : d === false ? fallCol : hue, alpha);
        };
        const closedPts: LineData<Time>[] = [];
        const formPts: LineData<Time>[] = [];
        stamps.forEach((s, k) => {
          const p: LineData<Time> = { time: s.time as Time, value: Math.round(s.value * 10) / 10 };
          if (slopeOn) (p as any).color = colourAt(k, s.forming ? A_FORMING : A_CLOSED);
          (s.forming ? formPts : closedPts).push(p);
        });
        const lastIdx = stamps.length - 1;
        const base = {
          priceScaleId: scaleId, priceLineVisible: false, crosshairMarkerVisible: false,
          lineType: 1 as const, priceFormat: { type: 'price' as const, precision: 1, minMove: 0.1 },
        };
        const drawForming = showForming && formPts.length > 0;
        // The axis badge takes the colour of the last point DRAWN, so it agrees
        // with the right-hand edge.
        const lastClosedK = lastIdx - formPts.length;
        let first: ISeriesApi<'Line'> | null = null;
        if (closedPts.length > 1) {
          const s = chart.addSeries(LineSeries, {
            ...base,
            color: slopeOn ? colourAt(lastClosedK, A_CLOSED) : withAlpha(hue, A_CLOSED), lineWidth: w,
            title: drawForming ? '' : name, lastValueVisible: !drawForming,
          }, paneIdx);
          s.setData(closedPts); overlaySeriesRefs.current.push(s); first = s;
        }
        if (drawForming) {
          // Joined to the last closed point so the step reads as one line. v5
          // strokes a step segment in its LEFT point's colour, so the joining
          // point carries the FORMING direction — the riser into the open period
          // then shows which way it is moving.
          const joined = closedPts.length
            ? [{ ...closedPts[closedPts.length - 1], ...(slopeOn ? { color: colourAt(lastClosedK + 1, A_FORMING) } : {}) },
              ...formPts]
            : formPts;
          const s = chart.addSeries(LineSeries, {
            ...base,
            color: slopeOn ? colourAt(lastIdx, A_FORMING) : withAlpha(hue, A_FORMING), lineWidth: w, lineStyle: 2,
            title: name, lastValueVisible: true,
          }, paneIdx);
          s.setData(joined); overlaySeriesRefs.current.push(s); first = first || s;
        }
        return first;
      };
      const bbrHtfSeries = wantBbrHtf
        ? drawHtf(bbrHtfStamps, 'bbrOsc', bInPane, bcfg.htfColor || '#8e24aa', bcfg.htfLineWidth, 'BBR ' + htfTag)
        : null;
      const waveHtfSeries = wantWaveHtf
        ? drawHtf(waveHtfStamps, 'waveOsc', wInPane, wcfgO.htfColor || '#f9a825', wcfgO.htfLineWidth, 'Wave ' + htfTag)
        : null;

      /* HTF CONFLUENCE SHADE (default off). Tints the bars where EVERY HTF curve
       * that is on sits beyond its own level on the same side — the higher
       * timeframe agreeing that this is an extreme. A reading aid, not a signal:
       * nothing was measured on it and no mark depends on it. */
      if (bwcfg.htfConfluence === true && (bbrHtfSeries || waveHtfSeries)) {
        // Zone HUES, but its OWN low opacity: this covers the full pane height.
        // At the OB/OS band opacity, and then at the owner's strong BBR-vs-Wave
        // tint opacity, it swamped both curves (both seen in live screenshots).
        const cfOp = opOf(bwcfg.htfConfluenceOpacity, 0.08);
        const cfOs = withAlpha(zoneHue(bwcfg.osZoneColor || '#2e7d32'), cfOp);
        const cfOb = withAlpha(zoneHue(bwcfg.obZoneColor || '#c62828'), cfOp);
        const bMap = new Map(bbrHtfStamps.map(s => [s.time, s.value]));
        const wMap = new Map(waveHtfStamps.map(s => [s.time, s.value]));
        const zpts: VerticalZonePoint[] = [];
        for (const c of candles) {
          const t = Number(c.time);
          const b = bMap.get(t); const w = wMap.get(t);
          if ((bbrHtfSeries && b == null) || (waveHtfSeries && w == null)) continue;
          const os = (!bbrHtfSeries || (b as number) <= bbOs) && (!waveHtfSeries || (w as number) <= wvOs);
          const ob = (!bbrHtfSeries || (b as number) >= bbOb) && (!waveHtfSeries || (w as number) >= wvOb);
          zpts.push({ time: t, color: os ? cfOs : ob ? cfOb : 'rgba(0,0,0,0)' });
        }
        if (zpts.length > 1) {
          const zp = new VerticalZonesPrimitive(zpts);
          ((bbrHtfSeries || waveHtfSeries) as any).attachPrimitive(zp);
          primitivesRef.current.push(zp);
        }
      }

      // --- BBR ---------------------------------------------------------------
      if (bbrOscOn) {
        const paneIdx = bInPane ? PANE_INDEX : 0;
        const opt = mkOpt('bbrOsc');
        const line: LineData<Time>[] = [], gOb: LineData<Time>[] = [], gOs: LineData<Time>[] = [];
        const gMid: LineData<Time>[] = [];
        const top: LineData<Time>[] = [], bot: LineData<Time>[] = [];
        const obShade: BandPoint[] = [], osShade: BandPoint[] = [];
        const span = Math.max(Math.abs(bbOb), Math.abs(bbOs)) * 1.5;
        for (let i = 0; i < candles.length; i++) {
          const v = bbrArr[i];
          if (v == null || !Number.isFinite(v)) continue;
          const t = candles[i].time as Time; const ts = candles[i].time as number;
          line.push({ time: t, value: Math.round(v * 10) / 10 });
          gOb.push({ time: t, value: bbOb }); gOs.push({ time: t, value: bbOs });
          gMid.push({ time: t, value: 0 });
          top.push({ time: t, value: span }); bot.push({ time: t, value: -span });
          obShade.push({ time: ts, upper: span, lower: bbOb });
          osShade.push({ time: ts, upper: bbOs, lower: -span });
        }
        if (line.length > 1) {
          // Title deliberately blank: these exist only to pin the scale, and a
          // title puts a badge on the price axis for a line nobody can see.
          const aTop = chart.addSeries(LineSeries, opt('rgba(0,0,0,0)', 1, '', 0), paneIdx); aTop.setData(top);
          const aBot = chart.addSeries(LineSeries, opt('rgba(0,0,0,0)', 1, '', 0), paneIdx); aBot.setData(bot);
          // With Wave on, this row is BOTH indicators' threshold - that is the whole
          // point of the pinning - so name both rather than implying it is BBR's alone.
          const obLabel = waveOscOn ? 'OB  BBR ' + bbOb + ' / Wave ' + wvOb : 'BBR ' + bbOb + ' OB';
          const osLabel = waveOscOn ? 'OS  BBR ' + bbOs + ' / Wave ' + wvOs : 'BBR ' + bbOs + ' OS';
          const gO = chart.addSeries(LineSeries, opt(GUIDE, 1, obLabel, 0), paneIdx); gO.setData(gOb);
          const gS = chart.addSeries(LineSeries, opt(GUIDE, 1, osLabel, 0), paneIdx); gS.setData(gOs);
          if (showMid) {
            const gM = chart.addSeries(LineSeries, opt(midCol, 1, '', 0), paneIdx);
            gM.setData(gMid); overlaySeriesRefs.current.push(gM);
          }
          const bbrColorLine = bcfg.lineColor || '#5e35b1';
          // `showLtf: false` hides only the chart-timeframe CURVE, for reading
          // the higher timeframe alone. Everything else stays: the anchors pin
          // the shared scale the HTF curve sits on, the guides and zones mark
          // its rows, and the BW / BD / X marks on price are still computed
          // from this curve - hiding a line is not a reason to change a signal.
          // The scale is configured through the anchor when the line is absent.
          let ls: ISeriesApi<'Line'> | null = null;
          if (bcfg.showLtf !== false) {
            // The badge colour follows the last point, so the axis agrees with the
            // right-hand edge rather than showing a hue that has scrolled away.
            const bbrBadge = paintSlope(line, bbrColorLine);
            ls = chart.addSeries(
              LineSeries, opt(bbrBadge, lwOf(bcfg.lineWidth, 2), 'BBR', stepOf(bcfg.lineMode), true), paneIdx,
            );
            ls.setData(line);
          }
          cfgScale(ls || aTop, bInPane, true, { top: 0.72, bottom: 0.0 }, bbrColorLine);
          if (showZones) {
            const so = new BandPrimitive(obShade, { fillColor: obZoneFill });
            (aTop as any).attachPrimitive(so); primitivesRef.current.push(so);
            const su = new BandPrimitive(osShade, { fillColor: osZoneFill });
            (aBot as any).attachPrimitive(su); primitivesRef.current.push(su);
          }
          overlaySeriesRefs.current.push(aTop, aBot, gO, gS);
          if (ls) overlaySeriesRefs.current.push(ls);
          // Null when the curve is hidden: the in-pane signal dots and the
          // BBR-vs-Wave tint both describe THIS curve, so they go with it.
          bbrOscSeriesRef = ls;
        }
      }

      // --- Wave --------------------------------------------------------------
      if (waveOscOn && waveArr.wt1.length) {
        const paneIdx = wInPane ? PANE_INDEX : 0;
        const opt = mkOpt('waveOsc');
        const l1: LineData<Time>[] = [], l2: LineData<Time>[] = [];
        const gOb: LineData<Time>[] = [], gOs: LineData<Time>[] = [];
        const top: LineData<Time>[] = [], bot: LineData<Time>[] = [];
        // Same 1.5x multiple as BBR - this is what makes -60 land on -100's row.
        const span = Math.max(Math.abs(wvOb), Math.abs(wvOs)) * 1.5;
        for (let i = waveArr.warmup; i < candles.length; i++) {
          const v = waveArr.wt1[i];
          if (!Number.isFinite(v)) continue;
          const t = candles[i].time as Time;
          l1.push({ time: t, value: Math.round(v * 10) / 10 });
          if (Number.isFinite(waveArr.wt2[i])) l2.push({ time: t, value: Math.round(waveArr.wt2[i] * 10) / 10 });
          gOb.push({ time: t, value: wvOb }); gOs.push({ time: t, value: wvOs });
          top.push({ time: t, value: span }); bot.push({ time: t, value: -span });
        }
        if (l1.length > 1) {
          const aTop = chart.addSeries(LineSeries, opt('rgba(0,0,0,0)', 1, '', 0), paneIdx); aTop.setData(top);
          const aBot = chart.addSeries(LineSeries, opt('rgba(0,0,0,0)', 1, '', 0), paneIdx); aBot.setData(bot);
          overlaySeriesRefs.current.push(aTop, aBot);
          // Guides only when BBR is absent - otherwise BBR's own lines already
          // mark the same row and a second pair is visual noise.
          if (!bbrOscOn) {
            const gO = chart.addSeries(LineSeries, opt(GUIDE, 1, 'Wave ' + wvOb + ' OB', 0), paneIdx); gO.setData(gOb);
            const gS = chart.addSeries(LineSeries, opt(GUIDE, 1, 'Wave ' + wvOs + ' OS', 0), paneIdx); gS.setData(gOs);
            overlaySeriesRefs.current.push(gO, gS);
            // Zero lands on the same pinned row for both curves, so BBR draws it
            // when BBR is on and this is the only other chance to.
            if (showMid) {
              const gM = chart.addSeries(LineSeries, opt(midCol, 1, '', 0), paneIdx);
              gM.setData(l1.map(d => ({ time: d.time, value: 0 })));
              overlaySeriesRefs.current.push(gM);
            }
          }
          // Shade Wave's own zones ONLY when BBR is absent. With both on, BBR's
          // bands already cover the same rows (that is the whole point of the
          // pinning) and a second shade just doubles the tint.
          if (!bbrOscOn && showZones) {
            const obShade: BandPoint[] = [], osShade: BandPoint[] = [];
            for (let i = waveArr.warmup; i < candles.length; i++) {
              if (!Number.isFinite(waveArr.wt1[i])) continue;
              const ts = candles[i].time as number;
              obShade.push({ time: ts, upper: span, lower: wvOb });
              osShade.push({ time: ts, upper: wvOs, lower: -span });
            }
            const so = new BandPrimitive(obShade, { fillColor: obZoneFill });
            (aTop as any).attachPrimitive(so); primitivesRef.current.push(so);
            const su = new BandPrimitive(osShade, { fillColor: osZoneFill });
            (aBot as any).attachPrimitive(su); primitivesRef.current.push(su);
          }
          // `showLtf: false` hides the chart-timeframe curve (and its wt2) only —
          // see the matching note in the BBR block.
          const waveLtfOn = wcfgO.showLtf !== false;
          if (waveLtfOn && wcfgO.showWt2 === true && l2.length > 1) {
            const s2 = chart.addSeries(
              LineSeries, opt(wcfgO.wt2Color || '#80cbc4', 1, 'Wave wt2', stepOf(wcfgO.lineMode)), paneIdx,
            );
            s2.setData(l2); overlaySeriesRefs.current.push(s2);
          }
          const waveColorLine = wcfgO.lineColor || '#00897b';
          let ws: ISeriesApi<'Line'> | null = null;
          if (waveLtfOn) {
            const waveBadge = paintSlope(l1, waveColorLine);
            ws = chart.addSeries(
              LineSeries, opt(waveBadge, lwOf(wcfgO.lineWidth, 2), 'Wave', stepOf(wcfgO.lineMode), true), paneIdx,
            );
            ws.setData(l1);
            overlaySeriesRefs.current.push(ws);
          }
          cfgScale(ws || aTop, wInPane, true, { top: 0.72, bottom: 0.0 }, waveColorLine);

          /* Background tint by which curve is on top. Attached HERE, to the
           * Wave series, because it needs BOTH curves to mean anything and the
           * Wave block is the one that runs second - so reaching this line is
           * itself the proof that both exist. It draws at zOrder 'bottom', so
           * the OB/OS shading and both lines stay on top of it. */
          if (showRelZones && ws && bbrOscSeriesRef && bbrArr.length && waveArr.wt1.length) {
            const zpts: VerticalZonePoint[] = [];
            const n = Math.min(bwSource.length, bbrArr.length, waveArr.wt1.length);
            for (let i = 0; i < n; i++) {
              const b = bbrArr[i];
              const w = waveArr.wt1[i];
              if (b == null || !isFinite(Number(w))) continue;
              // Normalised by each series' OWN overbought level - this is the
              // comparison the pinned pane actually shows.
              const bn = Number(b) / (bbOb || 100);
              const wn = Number(w) / (wvOb || 60);
              zpts.push({ time: Number(bwSource[i].time), color: bn > wn ? relAboveFill : relBelowFill });
            }
            if (zpts.length > 1) {
              const zp = new VerticalZonesPrimitive(zpts);
              (ws as any).attachPrimitive(zp);
              primitivesRef.current.push(zp);
            }
          }
        }
      }

      /* --- The conjunction, measured ---------------------------------------
       * Both oscillators at or below their own oversold level AND BBR rising
       * against the previous bar. Over 171 Delta symbols / ~102k daily bars that
       * returned +2.50% over 5 days against a -0.34% baseline (58.2% win vs
       * 44.0%) on 156 of 171 symbols, and held out of sample.
       *
       * It is NOT "BBR crosses Wave", which is what it looks like on a chart:
       * that measured +1.21% scale-matched, and a cross anywhere below zero
       * measured -1.17%. BBR oversold alone is worth +0.01%. The cross rides
       * along with the turn-up during the recovery, which is why it looks causal.
       */
      if (bwcfg.showSignals !== false && waveArr.wt1.length) {
        const needTurn = bwcfg.requireTurn !== false;
        // Closed bars only - the forming bar's BBR still moves, and a mark that
        // appears and vanishes intraday is worse than no mark.
        const formingLast = !!(latestCandle && candles.length
          && candles[candles.length - 1].time === latestCandle.time);
        const lastIdx = candles.length - (formingLast ? 2 : 1);

        /* PRICES STAY REAL even when the indicator source does not.
         *
         * Only BBR and Wave move with settings.bbrWave.srcCandle. The bars the
         * trade is priced on, the ATR the stop is sized from and the EMA50 the
         * short filters on are always the real market series - a stop measured
         * in Heikin-Ashi points is not a stop anyone can place, and a return
         * between two regression values is not a return anyone collects.
         * verifyBwSource.ts measures the sources exactly this way. */
        const realBars = bwRealBars;
        const atrArr = atrSeries(realBars, 14);
        const closes = realBars.map(c => Number(c.close));
        const ema50 = emaNullable(closes, 50);

        /* The walk itself lives in utils/bbrWaveTrades.ts, which is a
         * byte-identical twin of the backend copy (check-engine-parity.mjs).
         * The chart and the script that verifies the numbers call the SAME
         * function, so "the marks agree with the research" is checkable rather
         * than asserted. It returns trades; drawing them is this file's job.
         *
         * Nothing below places an order, arms an alert, or reaches a scan. */
        const trades = computeBwTrades(
          realBars.map(c => ({
            time: c.time as number, high: Number(c.high), low: Number(c.low), close: Number(c.close),
          })),
          bbrArr, waveArr.wt1, atrArr, ema50, waveArr.warmup, lastIdx,
          {
            bbOs, bbOb, wvOs, wvOb,
            requireTurn: needTurn,
            exitLevel: Number.isFinite(Number(bwcfg.exitLevel)) ? Number(bwcfg.exitLevel) : 0,
            stopAtr: Math.max(0, Number(bwcfg.stopAtr ?? 1.5)),
            maxHold: Math.max(1, Math.floor(Number(bwcfg.maxHold) || 20)),
            shortMode: bwcfg.shortMode === 'breakdown' ? 'breakdown' : 'off',
            shortTargetAtr: Math.max(0.1, Number(bwcfg.shortTargetAtr ?? 1.5)),
            shortStopAtr: Math.max(0.1, Number(bwcfg.shortStopAtr ?? 1.5)),
            shortMaxBars: Math.max(1, Math.floor(Number(bwcfg.shortMaxBars) || 5)),
          },
        );

        const buys: number[] = [], shorts: number[] = [];
        const exits: Array<{ time: number; side: 'BUY' | 'SHORT'; why: 'level' | 'stop' | 'time' }> = [];
        const wantExits = bwcfg.showExits !== false;
        for (const t of trades) {
          (t.side === 'BUY' ? buys : shorts).push(t.entryTime);
          if (wantExits && t.exitTime != null && t.why) {
            exits.push({ time: t.exitTime, side: t.side, why: t.why });
          }
        }
        bbrWaveSignals = { buys, shorts, exits };

        // Dots on the oscillator itself so the pane and the candles agree.
        if (bbrOscSeriesRef && (buys.length || shorts.length || exits.length)) {
          const dots = [
            ...buys.map(t => ({
              time: t as Time, position: 'inBar' as const,
              color: bwcfg.buyColor || '#00c853', shape: 'circle' as const, size: 0.8,
            })),
            ...shorts.map(t => ({
              time: t as Time, position: 'inBar' as const,
              color: bwcfg.shortColor || '#d50000', shape: 'circle' as const, size: 0.8,
            })),
            ...exits.map(e => ({
              time: e.time as Time, position: 'inBar' as const,
              color: e.why === 'stop' ? (bwcfg.stopColor || '#ef6c00') : (bwcfg.exitColor || '#1565c0'),
              shape: 'square' as const, size: 0.7,
            })),
          ].sort((a, b) => (a.time as number) - (b.time as number));
          try { createSeriesMarkers(bbrOscSeriesRef, dots as any); } catch { /* ignore */ }
        }
      }
    }

    // Flow — Order Flow ribbon. Round 67h: redesigned per user feedback.
    //   • 7-state colour palette computed from buyPct + trend (Bulls in
    //     control / holding / fading / Neutral / Bears fading / holding /
    //     in control). One bar per flow candle so widths follow flow data
    //     density and align with candles naturally (both feed off the
    //     shared time scale at the same interval boundaries).
    //   • Strip is half the previous height (scaleMargins.top 0.96 → ~4 %
    //     of chart height) so it reads as a ribbon under the candles.
    //   • Per-bar bias is communicated by colour only — no triangles, no
    //     stars on the candles. The hover tooltip is wired up via the
    //     crosshair subscription effect further down.
    if (overlays.flow && flowCandles && flowCandles.length > 0) {
      const fcfg = (settings as any)?.flow || {};
      if (fcfg.showStrip !== false) {
        const sorted = flowCandles
          .filter(f => Number.isFinite(f.time))
          .slice()
          .sort((a, b) => a.time - b.time);

        // R69a — bar HEIGHT proportional to total taker volume so a glance
        // shows where the action was (busy bars stand tall, quiet bars sit
        // flush). Outliers are clipped to the 95th percentile so a single
        // volume spike doesn't squash the rest of the ribbon. When buyVol /
        // sellVol aren't available (back-compat with old payloads) we fall
        // back to uniform height so colour is still informative.
        const volsForP95: number[] = [];
        for (const f of sorted) {
          const v = (Number(f.buyVol) || 0) + (Number(f.sellVol) || 0);
          if (v > 0) volsForP95.push(v);
        }
        let p95 = 0;
        if (volsForP95.length > 0) {
          volsForP95.sort((a, b) => a - b);
          p95 = volsForP95[Math.min(volsForP95.length - 1, Math.floor(volsForP95.length * 0.95))];
        }
        // If we have NO volume info at all, fall back to constant value=1.
        const hasVolume = p95 > 0;

        const data: HistogramData<Time>[] = [];
        let prevBuyPct: number | null = null;
        const seenTimes = new Set<number>();
        for (const f of sorted) {
          if (seenTimes.has(f.time)) continue;
          seenTimes.add(f.time);
          const state = classifyFlowState(f.buyPct, prevBuyPct);
          // Bar value: clipped volume so p95 = full strip height. The price
          // scale auto-scales to [0, max-in-data], and scaleMargins.top:0.96
          // caps the strip's pixel height at ~4 % of the chart. Bars
          // therefore physically cannot intrude into the candle area.
          let value: number;
          if (hasVolume) {
            const total = (Number(f.buyVol) || 0) + (Number(f.sellVol) || 0);
            // Floor at a small fraction so even zero-volume slots stay
            // visible (otherwise gaps look like missing data).
            value = Math.max(p95 * 0.05, Math.min(total, p95));
          } else {
            value = 1;
          }
          data.push({ time: f.time as Time, value, color: FLOW_STATE_COLORS[state] });
          prevBuyPct = f.buyPct;
        }
        if (data.length > 0) {
          const fs = chart.addSeries(HistogramSeries, {
            priceScaleId: 'flowStrip',
            priceFormat: { type: 'volume' },
            lastValueVisible: false,
            priceLineVisible: false,
            base: 0,
          });
          fs.setData(data);
          try {
            chart.priceScale('flowStrip').applyOptions({
              visible: false,
              // ~4% strip — a true ribbon under the candles.
              scaleMargins: { top: 0.96, bottom: 0 },
            });
          } catch { /* ignore */ }
          overlaySeriesRefs.current.push(fs as any);
        }
      }
    }

    // Bkground (R110 / R119) — per-candle vertical background zones on the PRICE
    // pane. Two computation methods, selected via settings.bkground.method:
    //
    //   • 'ctf' (Overview_CTF, default/legacy): colour by the SIGN of the
    //     combined chart-TF + HTF trend score (> 0 → bullish, ≤ 0 → bearish),
    //     using the SAME smoothed combined as the Trend indicator (settings.trend
    //     smoothing) so the zones line up with the Trend "Combined" line's zero
    //     crossings. Colours from settings.bkground.bullColor / bearColor.
    //
    //   • 'htf' (Overview_HTF, R119): mirror the HTF trend. Paint each chart-TF
    //     candle with the RockBand zone colour of the HTF bar that CONTAINS the
    //     candle's start time — i.e. the same 5-zone OverView colour the HTF
    //     candle would carry under RockBand. Independent of the current chart's
    //     strategy / candle type / other indicators because it reads the HTF's
    //     own OverView score series (overviewHtf). Uses the raw (un-smoothed)
    //     score → zoneForScore, matching RockBand's candle colouring exactly.
    //
    //   • 'clhMid' (2026-09-20): the SLOPE of the CLH Band's higher-timeframe
    //     midline. Bullish where that line is falling, bearish where it is
    //     rising - the same reading as the midline's own colours, so the
    //     background and the line agree by construction. Two things separate it
    //     from 'htf': it is strictly CAUSAL (each candle sees the last HTF bar
    //     that CLOSED before it, never the HTF bar containing it, which closes
    //     in that candle's future), and it does not require the CLH Band
    //     overlay to be switched on.
    //
    // Drawn behind the candles via VerticalZonesPrimitive (zOrder 'bottom').
    if (showBkground) {
      const bcfg = (settings as any)?.bkground || {};
      const bgMethod = bcfg.method === 'htf' ? 'htf'
        : bcfg.method === 'clhMid' ? 'clhMid'
          : bcfg.method === 'stack' ? 'stack' : 'ctf';
      /* Theme parity. The same alpha does NOT read the same on both grounds: a
         saturated hue at 10% over white washes out toward white, while over the
         near-black chart background nothing dilutes it and the bands dominate
         the candles. The user's setting is honoured proportionally, scaled so the
         two themes carry comparable weight. */
      const bgOpRaw = Math.max(0, Math.min(1, Number(bcfg.opacity ?? 0.10)));
      const bgOp = isDark ? bgOpRaw * 0.7 : bgOpRaw;
      if (bgOp > 0 && bgMethod === 'stack') {
        /* ── Candle / LR / HTF stack. The rule lives in utils/regimeStack.ts;
         * this block only decides which bars to hand it and what to paint.
         *
         * THE BARS ARE `bwRealBars`, NOT `candles`. On a Heikin-Ashi or CLH
         * chart the displayed series is ALREADY smoothed, so `candles[i].close`
         * is not the real close and the first rung would be comparing a
         * smoothed number against a smoothed body — a rule that looks plausible
         * on screen and means nothing. bwRealBars is the same on-screen window
         * joined to the raw history BY TIME, which is the join that exists
         * because the naive length check silently disabled the BBR/Wave source
         * setting for a whole release. */
        const htfMin = Number(auxCandles?.srHtfMinutes) || 0;
        const htfRaw = (auxCandles?.srHtf || []) as CandleRow[];
        // htfMin <= the chart's own interval means there is no higher timeframe
        // to map to (a 1W chart tops out the ladder).
        if (htfMin > (intervalMinutes || 0) && htfRaw.length > 1 && bwRealBars.length > 1) {
          const srcRaw = String(bcfg.srcCandle || 'CLN');
          const stackSrc = (srcRaw === 'Regular' || srcRaw === 'HA' || srcRaw === 'CLH' || srcRaw === 'CLN')
            ? srcRaw as StackCandleType : 'CLN';
          const bgBull = bcfg.bullColor || '#26a69a';
          const bgBear = bcfg.bearColor || '#ef5350';
          const bgNeut = bcfg.neutralColor || '#90a4ae';
          const stamps = computeRegimeStack(
            bwRealBars,
            htfRaw,
            htfMin,
            dropFormingHtfBars(htfRaw, htfMin).length,
            stackSrc,
            Number(settings.candle?.clnLength) || CLN_DEFAULT_LENGTH,
          );
          const zonePts: { time: number; color: string }[] = [];
          for (const st of stamps) {
            /* All three states are drawn at the SAME weight.
             *
             * The first version dimmed neutral to 0.55x on the argument that
             * the absence of a reading should not look like a decision. On
             * screen that was wrong: at the shipped 0.10 opacity it came out at
             * 5.5% over white and 3.9% over the dark ground, which is not
             * "quieter", it is invisible - indistinguishable from the indicator
             * being switched off. Neutral is one of the three answers this
             * method gives, so it has to be legible as one. The grey already
             * says it has no opinion. */
            const hue = st.regime === 'bull' ? bgBull : st.regime === 'bear' ? bgBear : bgNeut;
            zonePts.push({ time: st.time, color: withAlpha(hue, bgOp) });
          }
          if (zonePts.length >= 2) {
            const zp = new VerticalZonesPrimitive(zonePts);
            (cs as any).attachPrimitive(zp);
            primitivesRef.current.push(zp);
          }
        }
      } else if (bgOp > 0 && bgMethod === 'clhMid') {
        // ── CLH HTF midline slope. Computed here rather than read off the CLH
        // Band block because this is its own indicator: it has to work with the
        // band switched off, and the band may be drawing only its edges.
        const htfMin = Number(auxCandles?.srHtfMinutes) || 0;
        const htfRaw = (auxCandles?.srHtf || []) as CandleRow[];
        const htfClosed = htfMin > 0 ? dropFormingHtfBars(htfRaw, htfMin) : [];
        if (htfMin > (intervalMinutes || 0) && htfClosed.length > 40) {
          const bgBull = bcfg.bullColor || '#26a69a';
          const bgBear = bcfg.bearColor || '#ef5350';
          const times = candles.map(c => Number(c.time));
          const stamped = stampHtfLevels(times, computeClhBandLevels(htfClosed), htfMin);
          const dirs = stepDirections(stamped.map(x => (x ? x.flip : null)));
          const zonePts: { time: number; color: string }[] = [];
          for (let i = 0; i < times.length; i++) {
            if (!stamped[i] || dirs[i] == null) continue;   // no closed HTF bar yet
            zonePts.push({ time: times[i], color: withAlpha(dirs[i] ? bgBear : bgBull, bgOp) });
          }
          if (zonePts.length >= 2) {
            const zp = new VerticalZonesPrimitive(zonePts);
            (cs as any).attachPrimitive(zp);
            primitivesRef.current.push(zp);
          }
        }
      } else if (bgOp > 0 && bgMethod === 'htf') {
        // ── Overview_HTF: HTF bar's RockBand zone colour, mapped by start time.
        const htfS = (overviewHtf || []).slice().sort((a, b) => a.time - b.time);
        if (htfS.length) {
          const ovCfg = (settings as any)?.overview || {};
          const strong = Number(ovCfg.strongThreshold ?? 60);
          const probable = Number(ovCfg.probableThreshold ?? 20);
          // Honour the user's OverView zone palette (matches the on-chart HTF
          // gauge); falls back to the global RockBand zone colours.
          const zoneColors = { ...OVERVIEW_ZONE_COLORS, ...(ovCfg.zoneColors || {}) } as Record<OverviewZone, string>;
          const zonePts: { time: number; color: string }[] = [];
          let cur = -1;
          for (const c of candles) {
            const t = c.time as number;
            // Forward-fill: the HTF bar whose start time is the latest ≤ this
            // chart-TF candle's START time is the bar this candle belongs to.
            while (cur + 1 < htfS.length && htfS[cur + 1].time <= t) cur++;
            if (cur < 0) continue;
            const sc = htfS[cur].score;
            if (!Number.isFinite(sc)) continue;
            const zone = zoneForScore(sc, strong, probable);
            zonePts.push({ time: t, color: withAlpha(zoneColors[zone], bgOp) });
          }
          if (zonePts.length >= 2) {
            const zp = new VerticalZonesPrimitive(zonePts);
            (cs as any).attachPrimitive(zp);
            primitivesRef.current.push(zp);
          }
        }
      } else if (bgOp > 0 && overviewTf && overviewTf.length) {
        // ── Overview_CTF (legacy): sign of the smoothed combined TF+HTF score.
        const bgBull = bcfg.bullColor || '#26a69a';
        const bgBear = bcfg.bearColor || '#ef5350';
        const tcfg = (settings as any)?.trend || {};
        const smMethod = (tcfg.smoothing as string) || 'linreg';
        const smPeriod = Math.max(1, Math.min(200, Math.floor(Number(tcfg.smoothingPeriod) || 14)));
        // Smoothed chart-TF score, keyed by candle time.
        const tfT: number[] = []; const tfR: number[] = [];
        for (const p of overviewTf) { if (Number.isFinite(p.score)) { tfT.push(p.time as number); tfR.push(p.score); } }
        const tfSmB = smoothScores(tfR, smMethod, smPeriod);
        const tfMap = new Map<number, number>(); tfT.forEach((t, i) => tfMap.set(t, tfSmB[i]));
        // Forward-fill HTF onto the candle grid, then smooth.
        const htfS = (overviewHtf || []).slice().sort((a, b) => a.time - b.time);
        const htfT: number[] = []; const htfR: number[] = [];
        if (htfS.length) {
          let cur = -1;
          for (const c of candles) {
            const t = c.time as number;
            while (cur + 1 < htfS.length && htfS[cur + 1].time <= t) cur++;
            if (cur >= 0 && Number.isFinite(htfS[cur].score)) { htfT.push(t); htfR.push(htfS[cur].score); }
          }
        }
        const htfSmB = smoothScores(htfR, smMethod, smPeriod);
        const htfMap = new Map<number, number>(); htfT.forEach((t, i) => htfMap.set(t, htfSmB[i]));
        const zonePts: { time: number; color: string }[] = [];
        for (const c of candles) {
          const t = c.time as number;
          const a = tfMap.get(t), b = htfMap.get(t);
          if (a == null || b == null || !Number.isFinite(a) || !Number.isFinite(b)) continue;
          zonePts.push({ time: t, color: withAlpha((a + b) > 0 ? bgBull : bgBear, bgOp) });
        }
        if (zonePts.length >= 2) {
          const zp = new VerticalZonesPrimitive(zonePts);
          (cs as any).attachPrimitive(zp);
          primitivesRef.current.push(zp);
        }
      }
    }

    // DbCrZone (Debit/Credit Zone) — vertical background zones marking the option-market REGIME
    // on the underlying, forward-filled from the persisted change-points (dbcrZones, epoch MS).
    // DEBIT = trending / premium-expansion; CREDIT = range / premium-decay; NEUTRAL = no fill.
    // Colours + opacity from settings.dbcr. Independent of the chart TF (regime is time-based).
    if (dbcrZones && dbcrZones.length && candles.length) {
      const dcfg = (settings as any)?.dbcr || {};
      const op = Math.max(0, Math.min(1, Number(dcfg.opacity ?? 0.12)));
      const debitCol = dcfg.debitColor || '#ef5350';   // trending → warm
      const creditCol = dcfg.creditColor || '#26a69a'; // range → cool
      if (op > 0) {
        // Change-points sorted ascending; each candle gets the zone active AS-OF its
        // close (projectToBarClose, capLastBar for replay slices) so the right-edge
        // regime is current on every TF. Candle times are IST-shifted (+19800s);
        // dbcr ts is real epoch — shift it onto the same grid.
        const IST_SHIFT_SEC = 19800;
        const zs = dbcrZones.slice().sort((a, b) => a.ts - b.ts).map(z => ({ sec: Math.floor(z.ts / 1000) + IST_SHIFT_SEC, zone: z.zone }));
        const zonePts: { time: number; color: string }[] = [];
        for (const d of projectToBarClose(zs.map((z, i) => ({ time: z.sec, value: i })), candles.map(c => c.time as number), { capLastBar: true })) {
          const zone = zs[d.value]?.zone;
          if (!zone || zone === 'NEUTRAL') continue; // no fill for neutral
          zonePts.push({ time: d.time, color: withAlpha(zone === 'DEBIT' ? debitCol : creditCol, op) });
        }
        if (zonePts.length >= 2) {
          const zp = new VerticalZonesPrimitive(zonePts);
          (cs as any).attachPrimitive(zp);
          primitivesRef.current.push(zp);
        }
      }
    }

    // Trend overlay (Round 98 → expanded Round 104) — overlay OverView trend
    // scores on the price pane via a hidden overlay price scale ('trendBand').
    // The HTF (sparse) scores are forward-filled onto each chart-TF bar so every
    // line shares the candle x-positions exactly (no extra time slots polluting
    // the shared time scale). Three independently-toggleable line groups:
    //   • Individual: chart-TF (thin) + HTF (thick) lines, each ±100.
    //   • Combined:   TF score + HTF score (±200), summing the two above.
    //   • HMA:        Hull MA of the combined line (thicker, very smooth).
    // A directional band fills the zone between Combined and HMA — positive
    // colour when Combined ≥ HMA, negative below. Colours / opacity / widths /
    // HMA period all come from settings.trend.
    if (showTrendBand && overviewTf && overviewTf.length) {
      const tcfg = (settings as any)?.trend || {};
      const inPane = tcfg.pane !== false; const paneIdx = inPane ? PANE_INDEX : 0;
      const opacity = Math.max(0, Math.min(1, Number(tcfg.opacity ?? 0.9)));
      const lineColor = hexToRgba(tcfg.color || '#42a5f5', opacity);
      const tfW = Math.max(1, Math.min(6, Math.floor(Number(tcfg.tfLineWidth) || 1))) as 1 | 2 | 3 | 4;
      const htfW = Math.max(1, Math.min(6, Math.floor(Number(tcfg.htfLineWidth) || 3))) as 1 | 2 | 3 | 4;
      // Smoothing — turn the stepped score into a smooth curve (configurable).
      const smMethod = (tcfg.smoothing as string) || 'linreg';
      const smPeriod = Math.max(1, Math.min(200, Math.floor(Number(tcfg.smoothingPeriod) || 14)));
      // R104 — line-group toggles (default all on), combined/HMA styling + band.
      const showIndividual = tcfg.showIndividual !== false;
      const showCombined = tcfg.showCombined !== false;
      const showHma = tcfg.showHma !== false;
      const combinedW = Math.max(1, Math.min(6, Math.floor(Number(tcfg.combinedLineWidth) || 2))) as 1 | 2 | 3 | 4;
      const hmaW = Math.max(1, Math.min(6, Math.floor(Number(tcfg.hmaLineWidth) || 4))) as 1 | 2 | 3 | 4;
      const hmaPeriod = Math.max(2, Math.min(200, Math.floor(Number(tcfg.hmaPeriod) || 20)));
      // R106 — one shared Bull / Bear / Neutral palette across the indicator
      // (lines, fills, zones, divergence), plus divergence + zero-area + zone.
      const bull = tcfg.bullColor || '#26a69a';
      const bear = tcfg.bearColor || '#ef5350';
      const neutral = tcfg.neutralColor || '#b0bec5';
      const showDivergence = tcfg.showDivergence !== false;
      const bandOp = Math.max(0, Math.min(1, Number(tcfg.bandOpacity ?? 0.18)));
      const zeroAreaOp = Math.max(0, Math.min(1, Number(tcfg.zeroAreaOpacity ?? 0.15)));
      const zoneOp = Math.max(0, Math.min(1, Number(tcfg.zoneOpacity ?? 0.06)));
      const axisBorder = isDark ? '#3a4a5a' : '#cfd8dc';

      const tfTimes: Time[] = []; const tfRaw: number[] = [];
      for (const p of overviewTf) {
        if (Number.isFinite(p.score)) { tfTimes.push(p.time as Time); tfRaw.push(p.score); }
      }
      const tfSm = smoothScores(tfRaw, smMethod, smPeriod);
      const tfData: LineData<Time>[] = tfTimes.map((t, i) => ({ time: t, value: Math.round(tfSm[i] * 100) / 100 }));
      const tfByTime = new Map<number, number>();
      tfTimes.forEach((t, i) => tfByTime.set(t as number, tfSm[i]));

      // Forward-fill HTF scores onto the chart-TF candle grid, then smooth.
      const htfSorted = (overviewHtf || []).slice().sort((a, b) => a.time - b.time);
      const htfTimes: Time[] = []; const htfRaw: number[] = [];
      if (htfSorted.length) {
        let cur = -1;
        for (const c of candles) {
          const t = c.time as number;
          while (cur + 1 < htfSorted.length && htfSorted[cur + 1].time <= t) cur++;
          if (cur >= 0 && Number.isFinite(htfSorted[cur].score)) { htfTimes.push(c.time as Time); htfRaw.push(htfSorted[cur].score); }
        }
      }
      const htfSm = smoothScores(htfRaw, smMethod, smPeriod);
      const htfData: LineData<Time>[] = htfTimes.map((t, i) => ({ time: t, value: Math.round(htfSm[i] * 100) / 100 }));
      const htfByTime = new Map<number, number>();
      htfTimes.forEach((t, i) => htfByTime.set(t as number, htfSm[i]));

      // Real price highs/lows by time (raw OHLC preferred so divergence reads
      // true price, not a smoothed / Heikin-Ashi candle).
      const priceSrc = (rawCandles && rawCandles.length) ? rawCandles : candles;
      const rawByTime = new Map<number, { high: number; low: number }>();
      for (const c of priceSrc) rawByTime.set(c.time as number, { high: Number(c.high), low: Number(c.low) });

      // Combined = smoothed TF + smoothed HTF, per candle where both exist.
      // Then the Hull MA of that combined series. Price H/L kept aligned for
      // divergence detection.
      const combTimes: number[] = []; const combVals: number[] = [];
      const priceHigh: number[] = []; const priceLow: number[] = [];
      for (const c of candles) {
        const t = c.time as number;
        const a = tfByTime.get(t); const b = htfByTime.get(t);
        if (a == null || b == null || !Number.isFinite(a) || !Number.isFinite(b)) continue;
        combTimes.push(t); combVals.push(a + b);
        const pr = rawByTime.get(t);
        priceHigh.push(pr ? pr.high : Number(c.high));
        priceLow.push(pr ? pr.low : Number(c.low));
      }
      const hmaVals = hullSmooth(combVals, hmaPeriod);
      // Per-point colours: Combined = bull when ≥ HMA else bear (#5); HMA =
      // bull rising / bear falling / neutral flat (#6). baselineData is the
      // plain Combined value that feeds the above/below-zero area fill (#2).
      const combinedData: LineData<Time>[] = combTimes.map((t, i) => ({
        time: t as Time, value: Math.round(combVals[i] * 100) / 100,
        color: combVals[i] >= hmaVals[i] ? bull : bear,
      }));
      const hmaData: LineData<Time>[] = combTimes.map((t, i) => {
        const col = i === 0 ? neutral : (hmaVals[i] > hmaVals[i - 1] ? bull : (hmaVals[i] < hmaVals[i - 1] ? bear : neutral));
        return { time: t as Time, value: Math.round(hmaVals[i] * 100) / 100, color: col };
      });
      const baselineData: LineData<Time>[] = combTimes.map((t, i) => ({ time: t as Time, value: Math.round(combVals[i] * 100) / 100 }));

      // In a dedicated pane the trend scale shows its own ±-score axis; when
      // overlaying on price it stays an invisible scale floating over the
      // candles (the classic behaviour).
      const applyTrendScale = (s: ISeriesApi<any>) =>
        cfgScale(s, inPane, false, { top: 0.08, bottom: 0.08 }, axisBorder);

      // Track the first trend series so we can hang a Zero reference line off it.
      let trendFirst: ISeriesApi<any> | null = null;

      // Individual lines — HTF first (thick) so the thin TF line draws on top.
      if (showIndividual && htfData.length > 0) {
        const hs = chart.addSeries(LineSeries, {
          color: lineColor, lineWidth: htfW, priceScaleId: 'trendBand',
          lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false,
          title: '',
        }, paneIdx);
        hs.setData(htfData);
        applyTrendScale(hs);
        overlaySeriesRefs.current.push(hs as any);
        if (!trendFirst) trendFirst = hs;
      }
      if (showIndividual && tfData.length > 0) {
        const ts = chart.addSeries(LineSeries, {
          color: lineColor, lineWidth: tfW, priceScaleId: 'trendBand',
          lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false,
          title: '',
        }, paneIdx);
        ts.setData(tfData);
        applyTrendScale(ts);
        overlaySeriesRefs.current.push(ts as any);
        if (!trendFirst) trendFirst = ts;
      }

      // Above/below-zero area shade (#2) — BaselineSeries fed the Combined
      // value, bull fill above zero, bear below. Drawn before the lines so they
      // sit on top; its own line is hidden (the Combined line is drawn
      // separately with dynamic colour). Tied to the Combined being shown.
      if (showCombined && zeroAreaOp > 0 && baselineData.length > 0) {
        const bs = chart.addSeries(BaselineSeries, {
          baseValue: { type: 'price', price: 0 },
          priceScaleId: 'trendBand',
          lineVisible: false, lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false,
          topFillColor1: withAlpha(bull, zeroAreaOp), topFillColor2: withAlpha(bull, zeroAreaOp * 0.18),
          bottomFillColor1: withAlpha(bear, zeroAreaOp * 0.18), bottomFillColor2: withAlpha(bear, zeroAreaOp),
          topLineColor: 'rgba(0,0,0,0)', bottomLineColor: 'rgba(0,0,0,0)',
        }, paneIdx);
        bs.setData(baselineData);
        applyTrendScale(bs);
        overlaySeriesRefs.current.push(bs as any);
        if (!trendFirst) trendFirst = bs;
      }

      // Combined line (#5) — per-point bull/bear by position vs HMA. HMA line
      // (#6) — per-point bull/bear/neutral by its own slope. Combined first so
      // the thicker HMA draws over it.
      let bandHost: ISeriesApi<'Line'> | null = null;
      let cSeries: ISeriesApi<'Line'> | null = null;
      let mSeries: ISeriesApi<'Line'> | null = null;
      if (showCombined && combinedData.length > 0) {
        const cs = chart.addSeries(LineSeries, {
          color: bull, lineWidth: combinedW, priceScaleId: 'trendBand',
          lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false,
          title: '',
        }, paneIdx);
        cs.setData(combinedData);
        applyTrendScale(cs);
        overlaySeriesRefs.current.push(cs as any);
        bandHost = cs; cSeries = cs;
        if (!trendFirst) trendFirst = cs;
      }
      if (showHma && hmaData.length > 0) {
        const ms = chart.addSeries(LineSeries, {
          color: neutral, lineWidth: hmaW, priceScaleId: 'trendBand',
          lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false,
          title: '',
        }, paneIdx);
        ms.setData(hmaData);
        applyTrendScale(ms);
        overlaySeriesRefs.current.push(ms as any);
        mSeries = ms;
        if (!bandHost) bandHost = ms;
        if (!trendFirst) trendFirst = ms;
      }

      // Band (#4) — fill between Combined and HMA, ONLY when BOTH are plotted.
      if (bandOp > 0 && showCombined && showHma && bandHost && combTimes.length >= 2) {
        const bandPts = combTimes.map((t, i) => ({
          time: t,
          upper: Math.max(combVals[i], hmaVals[i]),
          lower: Math.min(combVals[i], hmaVals[i]),
          color: withAlpha(combVals[i] >= hmaVals[i] ? bull : bear, bandOp),
        }));
        const bp = new BandPrimitive(bandPts, { fillColor: withAlpha(bull, bandOp) });
        (bandHost as any).attachPrimitive(bp);
        primitivesRef.current.push(bp);
      }

      // Vertical regime zones (#3) — full-pane background tint by sign of the
      // Combined Trend (bull above zero / bear below). Drawn behind everything.
      if (zoneOp > 0 && showCombined && bandHost && combTimes.length >= 2) {
        const zonePts = combTimes.map((t, i) => ({
          time: t, color: withAlpha(combVals[i] >= 0 ? bull : bear, zoneOp),
        }));
        const zp = new VerticalZonesPrimitive(zonePts);
        (bandHost as any).attachPrimitive(zp);
        primitivesRef.current.push(zp);
      }

      // Zero line — dashed neutral reference at score 0 (axis label shown only
      // in the dedicated pane, where the trend scale is visible).
      if (trendFirst) {
        try {
          trendFirst.createPriceLine({
            price: 0,
            color: isDark ? 'rgba(176,176,176,0.55)' : 'rgba(110,110,110,0.55)',
            lineWidth: 1, lineStyle: 2, axisLabelVisible: inPane, title: '',
          });
        } catch { /* ignore */ }
      }

      // Divergence (#1) — detected on the HMA (smooth → clean pivots) vs real
      // price; markers placed on the visible host (HMA preferred, else Combined).
      const divHost = showHma ? mSeries : cSeries;
      if (showDivergence && divHost && combTimes.length > 12) {
        const markers = detectTrendDivergences(combTimes, hmaVals, priceHigh, priceLow, {
          pivotL: 5, pivotR: 5, minBars: 5, maxBars: 60, bull, bear, includeHidden: true,
        });
        if (markers.length) { try { createSeriesMarkers(divHost, markers as any); } catch { /* ignore */ } }
      }
    }

    // RelStrength — ratio of LinReg(close,14) of chart symbol vs the same
    // indicator on the benchmark, normalised so the first valid pair = 100.
    // Plotted on its own price scale ('rsLeft') with the benchmark label in
    // the series title so the user knows what's being compared against.
    // ── Synthiya (synthetic-symbols plan §5) — staircase PCR / ATM-IV / RelStrength
    // of the UNDERLYING in the oscillator pane. Series arrive pre-aligned from the
    // page; each gets its own hidden price scale + per-STEP up/down colouring
    // (colours/widths in Chart Settings → Synthiya).
    if (overlays.synthiya && synthiya) {
      const sCfg = (settings as any)?.synthiya || {};
      const mkStair = (
        pts: Array<{ time: number; value: number }>, scaleId: string,
        up: string, dn: string, width: 1 | 2 | 3 | 4, dashed: boolean, axisVisible: boolean,
      ) => {
        if (!pts || pts.length < 2) return;
        const data = pts.map((d, i) => ({
          time: d.time as Time, value: d.value,
          color: i > 0 && d.value < pts[i - 1].value ? dn : up,
        }));
        const s = chart.addSeries(LineSeries, {
          color: up, lineWidth: width, lineStyle: dashed ? 2 : 0, lineType: 1, // 1 = WithSteps (staircase)
          priceScaleId: scaleId, priceLineVisible: false, lastValueVisible: axisVisible,
          crosshairMarkerVisible: false,
        } as any, PANE_INDEX);
        s.setData(data as any);
        try { s.priceScale().applyOptions({ visible: axisVisible, scaleMargins: { top: 0.12, bottom: 0.12 } }); } catch { /* scale not ready */ }
        overlaySeriesRefs.current.push(s);
      };
      const w = (v: any, d: number) => Math.max(1, Math.min(4, Number(v) || d)) as 1 | 2 | 3 | 4;
      mkStair(synthiya.pcr, 'synthiya-pcr', sCfg.pcrUpColor || '#26a69a', sCfg.pcrDownColor || '#ef5350', w(sCfg.pcrWidth, 2), false, true);
      mkStair(synthiya.iv, 'synthiya-iv', sCfg.ivUpColor || '#42a5f5', sCfg.ivDownColor || '#ef6c00', w(sCfg.ivWidth, 1), false, false);
      mkStair(synthiya.rs, 'synthiya-rs', sCfg.rsUpColor || '#9ccc65', sCfg.rsDownColor || '#ab47bc', w(sCfg.rsWidth, 1), true, false);
    }

    if (overlays.relStrength && benchmarkCandles && benchmarkCandles.length > 14 && cshClosesAligned.length > 14) {
      // R81 — RelStrength now ALWAYS reads CSH-transformed close for BOTH
      // the chart symbol AND the benchmark, regardless of the user's chart
      // candle-type selection. The benchmark is CSH-transformed inline so
      // the ratio compares apples to apples.
      const rsCfg = (settings as any)?.relStrength || {};
      const inPane = rsCfg.pane !== false; const paneIdx = inPane ? PANE_INDEX : 0;
      const rsColor = rsCfg.color || '#7e57c2';
      const rsWidth = Math.max(1, Math.min(4, Number(rsCfg.lineWidth) || 2)) as 1 | 2 | 3 | 4;
      const emaPeriod = Math.max(1, Math.floor(Number(rsCfg.emaPeriod) || 7));
      const bandFill = rsCfg.bandFillColor || rsColor;
      const bandOp = Math.max(0, Math.min(1, Number(rsCfg.bandOpacity ?? 0.20)));
      // R94 — directional band: green when RS ≥ its EMA, red below.
      const bandUp = rsCfg.bandUpColor || '#26a69a';
      const bandDown = rsCfg.bandDownColor || '#ef5350';

      const symLR = linregOf(cshClosesAligned, 14);
      const cshBenchmark = transformCandles(benchmarkCandles, 'CSH');
      const benchCloses: number[] = cshBenchmark.map(c => c.close);
      const benchLR = linregOf(benchCloses, 14);
      const benchLRByTime = new Map<number, number>();
      for (let i = 0; i < benchmarkCandles.length; i++) {
        const v = benchLR[i];
        if (v != null && Number.isFinite(v) && v > 0) benchLRByTime.set(benchmarkCandles[i].time as number, v);
      }

      const data: LineData<Time>[] = [];
      const rsArr: Array<number | null> = new Array(candles.length).fill(null);
      let firstRatio: number | null = null;
      for (let i = 0; i < candles.length; i++) {
        const t = candles[i].time as number;
        const symVal = symLR[i];
        const benchVal = benchLRByTime.get(t);
        if (symVal == null || benchVal == null || !Number.isFinite(symVal) || !Number.isFinite(benchVal) || benchVal === 0) continue;
        const ratio = symVal / benchVal;
        if (firstRatio === null) firstRatio = ratio;
        const normalised = (ratio / firstRatio) * 100;
        data.push({ time: t as Time, value: Number(normalised.toFixed(3)) });
        rsArr[i] = normalised;
      }

      if (data.length > 0) {
        const s = chart.addSeries(LineSeries, {
          color: rsColor,
          lineWidth: rsWidth,
          priceScaleId: 'rsLeft',
          lastValueVisible: true,
          priceLineVisible: false,
          priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
          title: benchmarkLabel ? `RS vs ${benchmarkLabel}` : 'RelStrength',
        }, paneIdx);
        s.setData(data);
        cfgScale(s, inPane, true, { top: 0.05, bottom: 0.05 }, rsColor);
        try {
          // 100 reference line so the user can see relative outperformance at a glance
          s.createPriceLine({
            price: 100, color: rsColor, lineWidth: 1, lineStyle: 2,
            axisLabelVisible: false, title: '',
          });
        } catch { /* ignore */ }
        overlaySeriesRefs.current.push(s);

        // R73 — EMA(period) of the RS line + translucent band between them.
        // R73-fix — use the null-aware ema() helper (see ChgN% block).
        const emaArr = emaNullable(rsArr, emaPeriod);
        const emaData: LineData<Time>[] = [];
        const bandPts: { time: number; upper: number; lower: number; color?: string }[] = [];
        for (let i = 0; i < candles.length; i++) {
          const e = emaArr[i];
          const v = rsArr[i];
          if (e == null || !Number.isFinite(e) || v == null || !Number.isFinite(v)) continue;
          emaData.push({ time: candles[i].time as Time, value: Number(e.toFixed(3)) });
          bandPts.push({
            time: candles[i].time as number,
            upper: Math.max(v, e),
            lower: Math.min(v, e),
            color: withAlpha(v >= e ? bandUp : bandDown, bandOp),
          });
        }
        if (emaData.length > 0) {
          const emaS = chart.addSeries(LineSeries, {
            color: rsColor,
            lineWidth: 1,
            lineStyle: 2, // dashed
            priceScaleId: 'rsLeft',
            lastValueVisible: false,
            priceLineVisible: false,
            priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
            title: '',
          }, paneIdx);
          emaS.setData(emaData);
          overlaySeriesRefs.current.push(emaS);
          if (bandOp > 0 && bandPts.length >= 2) {
            const bp = new BandPrimitive(bandPts, { fillColor: withAlpha(bandFill, bandOp) });
            (s as any).attachPrimitive(bp);
            primitivesRef.current.push(bp);
          }
        }
      }
    }

    /* ── Advanced overlays (DailyBlast-derived) ── */
    // Round 42 — the standalone Combined Midline (CMid) overlay still uses the
    // combined envelope, but AvgCloud no longer does — it now picks its own
    // boundary (C-POC / EMA200 / EMA128) per Chart Settings. So we only
    // compute the combined envelope when the CMid overlay itself is on.
    const ctf = auxCandles?.ctf || [];
    const htf = auxCandles?.htf || [];
    // R70/R72 — Combined Midline (CMid). Computed once when EITHER the CMid
    // line overlay OR AvgCloud is enabled, because R72 wires AvgCloud to use
    // CMid as its reference boundary (replacing the old cpoc/ema200/ema128
    // dropdown that was only there because CMid hadn't been computing
    // correctly). Pass CTF + HTF so computeCombinedEnvelope can build the
    // full 3-timeframe Pine blend; ChartsPage pre-fetches ≥1000 CTF/HTF
    // bars when either overlay is on, so EMA(200) at CTF/HTF cadence has
    // runway to converge.
    let combinedCmid: ReturnType<typeof computeCombinedEnvelope> | null = null;
    const cmidByTime = new Map<number, number>();
    // The Combined Midline blends LTF + CTF + HTF envelopes (EMA-200 based), so
    // it is only correct once BOTH the CTF and HTF aux candles for the CURRENT
    // symbol have loaded. Until then, skip computing it entirely — otherwise the
    // CMid (and the AvgCloud bounded by it) would first draw against missing /
    // stale aux and then visibly jump when the 1000-bar aux finishes loading.
    const auxReadyForCmid = ctf.length > 0 && htf.length > 0;
    if ((overlays.combinedMidline || overlays.avgCloud) && candles.length > 0 && auxReadyForCmid) {
      // Pine computes the LTF/CTF/HTF envelopes from RAW OHLC regardless of the
      // chart's display candle type — `candle_type` only changes the plotted
      // candles, never the envelope maths. Feeding the DISPLAY candles here
      // (e.g. Heikin-Ashi) shifted the Combined Midline so the AvgCloud's
      // raw-based Avg band was compared against an HA-based midline → wrong
      // cloud colour vs TradingView (cloud stuck red while price rose). Use the
      // RAW candles; CTF/HTF aux candles are already raw, so all three
      // timeframes stay consistent. Keyed by time so the (possibly transformed)
      // display grid maps back to the right midline value per bar.
      const envInput = (rawCandles && rawCandles.length) ? rawCandles : candles;
      const ltfEnv = computeEnvelope(envInput);
      combinedCmid = computeCombinedEnvelope(envInput, ltfEnv, ctf, htf);
      combinedCmid.times.forEach((t, i) => {
        const v = combinedCmid!.mid[i];
        if (v != null && Number.isFinite(v)) cmidByTime.set(t, v);
      });
    }
    if (overlays.combinedMidline && combinedCmid) {
      const midColor = settings.cMid.color;
      const midWidth = Math.max(1, Math.min(4, Math.floor(settings.cMid.lineWidth || 2))) as 1 | 2 | 3 | 4;
      const midD: LineData<Time>[] = [];
      for (let i = 0; i < candles.length; i++) {
        const v = cmidByTime.get(candles[i].time as number);
        if (v != null) midD.push({ time: candles[i].time as Time, value: v });
      }
      const midS = chart.addSeries(LineSeries, {
        color: midColor, lineWidth: midWidth, lineStyle: 2, // dashed
        lastValueVisible: false, priceLineVisible: false,
      });
      midS.setData(midD);
      overlaySeriesRefs.current.push(midS);
    }

    // AV_Band — Anchored Volume Band (price pane). Hold a High/Low zone from
    // WVol spike bars (carried forward until the next spike); pick an Effective
    // Line per bar from this candle's chart-TF OverView score (>20 → zone high,
    // <-20 → zone low, else mid); plot fast/slow HMAs of that line with a
    // bull/bear band between them (bull when fast HMA ≥ slow HMA).
    if (overlays.avBand) {
      const acfg = (settings as any)?.avBand || {};
      const fastP = Math.max(2, Math.min(400, Math.floor(Number(acfg.fastPeriod) || 14)));
      const slowP = Math.max(2, Math.min(400, Math.floor(Number(acfg.slowPeriod) || 100)));
      const lw = Math.max(1, Math.min(4, Math.floor(Number(acfg.lineWidth) || 2))) as 1 | 2 | 3 | 4;
      const effW = Math.max(1, Math.min(4, Math.floor(Number(acfg.effectiveWidth) || 1))) as 1 | 2 | 3 | 4;
      const showFast = acfg.showFast !== false;
      const showSlow = acfg.showSlow !== false;
      const showBand = acfg.showBand !== false;
      const showEff = !!acfg.showEffective;
      // Effective Line boundary rule. logic1 (default): strong-up → zone HIGH,
      // strong-down → zone LOW. logic2: inverted (strong-up → LOW, down → HIGH).
      const effLogic2 = acfg.effectiveMode === 'logic2';
      const op = Math.max(0, Math.min(1, Number(acfg.bandOpacity ?? 0.25)));
      const bandBull = withAlpha(acfg.bandBullColor || '#26a69a', op);
      const bandBear = withAlpha(acfg.bandBearColor || '#ef5350', op);
      const fastColor = acfg.fastColor || '#26a69a';
      // No slowColor: the slow HMA's colour is DERIVED per bar (see slowHueAt below).
      const effColor = acfg.effectiveColor || (isDark ? '#b0bec5' : '#607d8b');

      // R120.3 — compute on RAW candles (same source the avBand VOTER uses), so
      // the drawn band/Effective Line matches the voter exactly regardless of the
      // chart's display candle type (avoids the spike/zone divergence the Vol
      // pane had). Times align with the display grid for closed bars.
      const avSrc = (rawCandles && rawCandles.length) ? rawCandles : candles;
      // R120 — use the baseScore (OverView EXCLUDING avBand) so the drawn
      // Effective Line is identical to the avBand voter's line (no circular
      // feedback now that avBand contributes to the score).
      const scoreByTime = new Map<number, number>();
      for (const p of (overviewTf || [])) {
        const s = Number.isFinite((p as any).scoreBase) ? (p as any).scoreBase : p.score;
        if (Number.isFinite(s)) scoreByTime.set(p.time as number, s);
      }

      // The maths now lives in utils/avBand.ts, which avTrendSignal.ts on the
      // backend also calls — so the band drawn here and anything acting on it
      // cannot be two different calculations. This block keeps only what is
      // genuinely about drawing: which series are shown, and the colours.
      const avb = computeAvBandSeries(avSrc, scoreByTime, {
        fastPeriod: fastP,
        slowPeriod: slowP,
        effLogic2,
        clnLength: Number(settings.candle?.clnLength) || CLN_DEFAULT_LENGTH,
      });
      const { eff, fast: fastH, slow: slowH } = avb;
      /** The slow line's hue. Both the regression close and the slow line's own
       *  slope must agree for a direction; anything else is neutral rather than
       *  guessed. Independent of the DISPLAY candle type by design — the rule is
       *  defined on the regression of the raw closes, so it means the same thing
       *  on Regular / HA / CSH / CLN. */
      const slowHueAt = (i: number): string => (
        avb.slowHue[i] === 1 ? AV_SLOW_UP : avb.slowHue[i] === -1 ? AV_SLOW_DOWN : AV_SLOW_NEUTRAL
      );

      const effData: LineData<Time>[] = [], fastData: LineData<Time>[] = [], slowData: LineData<Time>[] = [];
      const fillPts: BandPoint[] = [];
      for (let i = 0; i < avSrc.length; i++) {
        const t = avSrc[i].time as Time; const ts = avSrc[i].time as number;
        if (showEff && Number.isFinite(eff[i])) effData.push({ time: t, value: eff[i] });
        if (avb.upper[i] != null && avb.lower[i] != null) {
          fastData.push({ time: t, value: fastH[i] });
          // Per-point colour: lightweight-charts v5 strokes each segment with the
          // LEFT point's colour (walkLine, both Simple and WithSteps), the same
          // convention BandPrimitive's directional mode uses. One series, no splitting.
          slowData.push({ time: t, value: slowH[i], color: slowHueAt(i) });
          fillPts.push({ time: ts, upper: avb.upper[i] as number, lower: avb.lower[i] as number, color: avb.bandUp[i] ? bandBull : bandBear });
        }
      }

      if (fastData.length > 1) {
        if (showEff && effData.length) {
          const es = chart.addSeries(LineSeries, { color: effColor, lineWidth: effW, lineStyle: 2, lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false });
          es.setData(effData); overlaySeriesRefs.current.push(es);
        }
        // Each of the two HMA lines + the band fill is independently toggleable.
        // Line display (Chart Settings): 'staircase' (stepped, default) or 'line'.
        const avLineType = (acfg.lineMode === 'line') ? 0 : 1; // 0=Simple, 1=WithSteps
        let bandAnchor: ISeriesApi<any> | null = null;
        if (showSlow) {
          // Series-level colour is only the fallback for a point that carries none.
          const ss = chart.addSeries(LineSeries, { color: AV_SLOW_NEUTRAL, lineWidth: lw, lineType: avLineType, lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false });
          ss.setData(slowData); overlaySeriesRefs.current.push(ss); bandAnchor = ss;
        }
        if (showFast) {
          const fs = chart.addSeries(LineSeries, { color: fastColor, lineWidth: lw, lineType: avLineType, lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false });
          fs.setData(fastData); overlaySeriesRefs.current.push(fs); bandAnchor = fs;
        }
        if (showBand) {
          // The band needs a host series to attach to; if both HMA lines are
          // hidden, attach to a transparent anchor carrying the fast values.
          if (!bandAnchor) {
            const anchor = chart.addSeries(LineSeries, { color: 'rgba(0,0,0,0)', lineWidth: 1, lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false });
            anchor.setData(fastData); overlaySeriesRefs.current.push(anchor); bandAnchor = anchor;
          }
          const bfill = new BandPrimitive(fillPts, { fillColor: bandBull }); (bandAnchor as any).attachPrimitive(bfill); primitivesRef.current.push(bfill);
        }
      }

      // ── AV_Trend Band — stepped support/resistance channel off the SLOW HMA ──
      // A fresh REGULAR close ABOVE the slow HMA → AV_Trend Long: set the LOWER edge
      // to the lowest low since the last slow-HMA touch (support). A fresh close BELOW
      // → Short: set the UPPER edge to the highest high since the last touch (resistance).
      // Each edge holds (steps) until its OWN next cross, so the band is the channel
      // between the two most-recent opposite-type levels; fill green while Long / red
      // while Short. Computed on the RAW (Regular) candles (avSrc).
      if (acfg.showTrendBand) {
        const trOp = Math.max(0, Math.min(1, Number(acfg.trendOpacity ?? 0.15)));
        const trBull = withAlpha(acfg.trendBullColor || '#26a69a', trOp);
        const trBear = withAlpha(acfg.trendBearColor || '#ef5350', trOp);
        const trBullLine = acfg.trendBullColor || '#26a69a';
        const trBearLine = acfg.trendBearColor || '#ef5350';
        let lowerLevel: number | null = null;   // latest Long-cross lowest-low (support, bottom)
        let upperLevel: number | null = null;   // latest Short-cross highest-high (resistance, top)
        let trend: 'long' | 'short' | null = null;
        let lowAcc = Infinity, highAcc = -Infinity;
        let prevClose = NaN, prevSlow = NaN;
        const trFill: BandPoint[] = [];
        const upEdge: LineData<Time>[] = [], loEdge: LineData<Time>[] = [];
        for (let i = 0; i < avSrc.length; i++) {
          const sh = slowH[i];
          if (!Number.isFinite(sh)) continue;
          const hi = Number(avSrc[i].high), lo = Number(avSrc[i].low), cl = Number(avSrc[i].close);
          const t = avSrc[i].time as Time; const ts = avSrc[i].time as number;
          lowAcc = Math.min(lowAcc, lo); highAcc = Math.max(highAcc, hi);
          if (Number.isFinite(prevClose) && Number.isFinite(prevSlow)) {
            if (prevClose <= prevSlow && cl > sh) { lowerLevel = Number.isFinite(lowAcc) ? lowAcc : lo; trend = 'long'; }
            else if (prevClose >= prevSlow && cl < sh) { upperLevel = Number.isFinite(highAcc) ? highAcc : hi; trend = 'short'; }
          }
          // A candle "touches" when its range straddles the slow HMA — restart the
          // extreme window there so the next cross measures from the pullback.
          if (lo <= sh && hi >= sh) { lowAcc = lo; highAcc = hi; }
          if (lowerLevel != null && upperLevel != null && trend != null) {
            trFill.push({ time: ts, upper: upperLevel, lower: lowerLevel, color: trend === 'long' ? trBull : trBear });
            upEdge.push({ time: t, value: upperLevel });
            loEdge.push({ time: t, value: lowerLevel });
          }
          prevClose = cl; prevSlow = sh;
        }
        if (trFill.length > 1) {
          const anchor = chart.addSeries(LineSeries, { color: 'rgba(0,0,0,0)', lineWidth: 1, lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false });
          anchor.setData(loEdge); overlaySeriesRefs.current.push(anchor);
          const bp = new BandPrimitive(trFill, { fillColor: trBull }); (anchor as any).attachPrimitive(bp); primitivesRef.current.push(bp);
          // Thin stepped edges: support (green, bottom) + resistance (red, top).
          const upS = chart.addSeries(LineSeries, { color: trBearLine, lineWidth: 1, lineType: 1, lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false });
          upS.setData(upEdge); overlaySeriesRefs.current.push(upS);
          const loS = chart.addSeries(LineSeries, { color: trBullLine, lineWidth: 1, lineType: 1, lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false });
          loS.setData(loEdge); overlaySeriesRefs.current.push(loS);
        }
      }
    }

    /* ── CLN HTF context band ───────────────────────────────────────────────
     * Runs the SAME regression (computeClnBars) on the Strategies-mapped HIGHER
     * timeframe and draws a ribbon connecting those bodies behind the candles.
     * It is context, not a signal: no markers, no state, no geometry change.
     *
     * Four decisions worth not re-litigating:
     *
     * 1. NOT gated on the CLN candle type. The band is defined on the
     *    regression of the raw closes, so it means the same thing on Regular /
     *    HA / CSH / CSL / CLH — the same reasoning that makes the AV_Band slow
     *    HMA colour display-type independent further up this file.
     * 2. The still-forming HTF bar is DROPPED. Painting it would repaint the
     *    band on every tick, and an HTF body that moves is not context.
     *    Consequence, deliberate: the current (open) HTF window stays unpainted,
     *    so the ribbon stops up to one HTF bar short of the right edge. That gap
     *    IS the honest reading — "this HTF bar has not closed yet".
     * 3. Stamped per LTF bar rather than drawn as one quad per HTF bar, so the
     *    level holds flat across the HTF window and rises over a single LTF bar
     *    at the boundary — the "staircase of variable height", unbroken.
     * 4. Split into consecutive same-direction runs, one BandPrimitive each.
     *    BandPrimitive's per-point `color` and its `step` mode do NOT compose
     *    (the directional branch draws raw quads and never calls stepped()), so
     *    this uses the run-splitting pattern the CPR / C-POC bands below use.
     */
    /* CLH Band ---------------------------------------------------------------
     * The REAL price at which the NEXT CLH body breaks out of the current one.
     * Solved in closed form from the CLH transform itself - see utils/clhBand.ts
     * for why that is exact rather than fitted.
     *
     * Three things about this block are deliberate:
     *
     * 1. RAW CANDLES, always. The band describes the CLH candle, so feeding it
     *    the displayed series would make it describe something else entirely
     *    whenever the chart is on HA/CSH/CLN. Same rule as AV_Band.
     * 2. THE LEVEL AT BAR i IS DRAWN STARTING AT BAR i+1. It becomes known at
     *    bar i's CLOSE and governs the next bar; drawing it on bar i would
     *    imply it was available while bar i was still forming.
     * 3. NO AUTOSCALE. BandPrimitive does not implement autoscaleInfo, so a
     *    level 30% away clips off-screen instead of squashing the candles into
     *    a line - which matters here, because a quarter of bars put the level
     *    beyond ~10 ATR. The right-edge labels keep it readable anyway.
     */
    if (overlays.clhBand) {
      const ccfg = (settings as any)?.clhBand || {};
      const src = (rawCandles && rawCandles.length) ? rawCandles : candles;
      const clampW = (v: any, d: number) =>
        Math.max(1, Math.min(4, Math.floor(Number(v) || d))) as 1 | 2 | 3 | 4;
      const lwTf = clampW(ccfg.lineWidth, 2);
      const lwHtf = clampW(ccfg.lineWidthHtf, 2);
      const lsty = Math.max(0, Math.min(4, Math.floor(Number(ccfg.lineStyle) ?? 0)));
      const lstyRaw = Number(ccfg.lineStyleHtf);
      const lstyHtf = Number.isFinite(lstyRaw) ? Math.max(0, Math.min(4, Math.floor(lstyRaw))) : 2;
      const fillOpRaw = Number(ccfg.fillOpacity);
      const fillOp = Number.isFinite(fillOpRaw) ? Math.max(0, Math.min(0.6, fillOpRaw)) : 0.10;
      const upC = ccfg.upperColor || '#26a69a';
      const dnC = ccfg.lowerColor || '#ef5350';
      // The middle line takes its colour from ITS OWN SLOPE, not from price: a
      // FALLING midline is bullish (the level the next body has to beat to turn
      // green is coming down towards price), a RISING one is bearish. Per-point
      // colour on a LineSeries is honoured even in WithSteps mode - v5 strokes
      // each segment with the LEFT point's colour - which is why this does not
      // need splitting into runs the way the BandPrimitive fill does.
      // flipAbove/BelowColor are the pre-rename keys, read as the fallback so a
      // template customised before the rename keeps its colours.
      const flFall = ccfg.flipFallColor || ccfg.flipAboveColor || '#26a69a';
      const flRise = ccfg.flipRiseColor || ccfg.flipBelowColor || '#ef5350';
      const fBull = ccfg.fillBullColor || '#26a69a';
      const fBear = ccfg.fillBearColor || '#ef5350';
      const htfScaleRaw = Number(ccfg.htfOpacityScale);
      const htfScale = Number.isFinite(htfScaleRaw) ? Math.max(0.1, Math.min(1, htfScaleRaw)) : 0.55;
      const showFill = ccfg.showFill !== false;
      // The band edges and the middle line are independent, so the middle line
      // can be shown on its own - which is the least cluttered way to read this
      // indicator, since the two edges sit ~36x the CLH body apart.
      const showBandLines = ccfg.showBandLines !== false;
      const showFlip = ccfg.showFlip === true;
      const showLabels = ccfg.showLabels !== false;

      /** Draw one set of levels. `shift` puts point i on bar i+1 (see note 2). */
      const drawClh = (
        pts: Array<ClhLevelPoint | null>,
        times: number[],
        tag: string,
        dim: number,
        dashed: boolean,
        width: 1 | 2 | 3 | 4,
      ) => {
        const up: LineData<Time>[] = [], dn: LineData<Time>[] = [], fl: LineData<Time>[] = [];
        type Seg = { points: BandPoint[]; up: boolean };
        const segs: Seg[] = [];
        for (let i = 0; i < times.length - 1; i++) {
          const p = pts[i];
          if (!p) continue;
          const t = times[i + 1] as Time;          // the bar this level GOVERNS
          if (showBandLines && p.upper != null) up.push({ time: t, value: p.upper });
          if (showBandLines && p.lower != null) dn.push({ time: t, value: p.lower });
          if (showFlip && p.flip != null) fl.push({ time: t, value: p.flip });
          if (showFill && p.upper != null && p.lower != null) {
            const hi = Math.max(p.upper, p.lower), lo = Math.min(p.upper, p.lower);
            const open = segs.length ? segs[segs.length - 1] : null;
            // Same-direction runs, one BandPrimitive each: per-point colour and
            // `step` do NOT compose in BandPrimitive (documented at its line 49),
            // so a staircase that also changes colour has to be split.
            if (open && open.up === p.bodyUp) open.points.push({ time: times[i + 1], upper: hi, lower: lo });
            else {
              const seg: Seg = { points: [], up: p.bodyUp };
              const prev = open && open.points.length ? open.points[open.points.length - 1] : null;
              if (prev) seg.points.push(prev);     // bridge so the colour change lands on the riser
              seg.points.push({ time: times[i + 1], upper: hi, lower: lo });
              segs.push(seg);
            }
          }
        }
        /* Colour the midline by its own slope. Done in a pass of its own rather
           than inline above because the direction of a point needs its
           predecessor, and the leading run - which has none - is back-filled
           with the first direction the line actually takes, so it never opens
           in a colour it has not earned. A flat step carries the previous
           direction; see stepDirections in utils/clhBand.ts. */
        const flDirs = stepDirections(fl.map(d => Number(d.value)));
        for (let k = 0; k < fl.length; k++) {
          (fl[k] as any).color = withAlpha(flDirs[k] === true ? flRise : flFall, dim);
        }

        for (const seg of segs) {
          if (seg.points.length < 2) continue;
          const bp = new BandPrimitive(seg.points, {
            fillColor: withAlpha(seg.up ? fBull : fBear, fillOp * dim),
            step: true,
            zOrder: 'bottom',
            minHeightPx: 2,
          });
          cs.attachPrimitive(bp as any);
          primitivesRef.current.push(bp);
        }
        const line = (data: LineData<Time>[], color: string, title: string) => {
          if (data.length < 2) return;
          const ser = chart.addSeries(LineSeries, {
            color: withAlpha(color, dim),
            lineWidth: width,
            lineType: 1,                                   // WithSteps - the level holds for a bar
            lineStyle: dashed ? lstyHtf : lsty,
            lastValueVisible: showLabels,
            priceLineVisible: false,
            crosshairMarkerVisible: false,
            title: showLabels ? title : '',
            // KEEP THIS OUT OF THE PRICE SCALE. A quarter of bars put the level
            // beyond ~10 ATR and it can run to several times spot, so letting it
            // drive autoscale squashes the candles into a ribbon - which is
            // exactly what the first deployed build did (axis -10,000..115,000
            // on a chart whose price was 81,267). The fill was already safe
            // because BandPrimitive implements no autoscaleInfo; the LINES were
            // not. Returning null contributes nothing to the range while leaving
            // the price-scale label, which is how a far level stays readable.
            autoscaleInfoProvider: () => null,
          });
          ser.setData(data);
          overlaySeriesRefs.current.push(ser);
        };
        line(up, upC, tag + '↑');
        line(dn, dnC, tag + '↓');
        // Series-level colour is only the fallback for a point carrying none;
        // every middle-line point sets its own, so this value is never used.
        if (showFlip) line(fl, flFall, tag + '⇅');
      };

      const times = src.map(c => Number(c.time));
      if (ccfg.showTf !== false && src.length > 40) {
        drawClh(computeClhBandLevels(src as CandleRow[]), times, 'CLH', 1, false, lwTf);
      }

      // Higher timeframe. Levels come from CLOSED HTF bars only and are carried
      // forward by stampHtfLevels, which walks to the last HTF bar that closed
      // STRICTLY BEFORE each bar. The HTF bar containing a bar closes in its
      // future - using that is the look-ahead this indicator exists to avoid.
      if (ccfg.showHtf === true) {
        const htfMin = Number(auxCandles?.srHtfMinutes) || 0;
        const htfRaw = (auxCandles?.srHtf || []) as CandleRow[];
        const htfClosed = htfMin > 0 ? dropFormingHtfBars(htfRaw, htfMin) : [];
        if (htfMin > (intervalMinutes || 0) && htfClosed.length > 40) {
          const htfLv = computeClhBandLevels(htfClosed);
          const stamped = stampHtfLevels(times, htfLv, htfMin);
          // stampHtfLevels has ALREADY carried each level onto the bar it is in
          // force for, so stamped[j] belongs AT bar j. drawClh draws pts[i] at
          // times[i+1] (right for a TF level, which is computed at bar i and
          // governs bar i+1), so the stamps are handed in one index EARLIER to
          // cancel that. Prepending instead of dropping - which is what this did
          // until 2026-09-20 - shifted the whole HTF half two bars late.
          const shifted = stamped.slice(1);
          drawClh(shifted, times, 'HTF', htfScale, true, lwHtf);
        }
      }
    }

    /* CLN_Break levels.
     *
     * The level in force DURING bar i is built from the LR bodies of bars
     * i-3..i-1, so `steps[i].buyLevel` already belongs AT bar i — there is no
     * index shift here, unlike the CLH band above whose level is computed at bar
     * i and governs bar i+1.
     *
     * Computed on rawCandles, never on the display series: under Heikin-Ashi the
     * display close is already smoothed and the regression would be fitted to
     * synthetic prices. `candles` also carries the live bar and `rawCandles` does
     * not, and their lengths NEVER match — the trap that shipped the BBR/Wave
     * source setting completely inert — so the two are joined by TIME and the
     * forming bar's level is appended explicitly rather than assumed to line up.
     *
     * Appending it is the point, not a nicety: the level for the bar you are
     * standing in is the only one you can still place an order against, and it
     * is fully determined by three CLOSED bodies.
     */
    if (overlays.clnBreakLevels) {
      const bcfg = (settings as any)?.clnBreak || {};
      const bsrc = (rawCandles && rawCandles.length) ? rawCandles : candles;
      if (bcfg.showLevels !== false && bsrc.length > 20) {
        const bw = Math.max(1, Math.min(4, Math.floor(Number(bcfg.lineWidth) || 2))) as 1 | 2 | 3 | 4;
        const bsty = Math.max(0, Math.min(4, Math.floor(Number(bcfg.lineStyle) ?? 0)));
        // The chart's OWN CLN length — the bodies the owner reads the levels
        // against. See resolveClnBreakOptions for why it is not a separate dial.
        const cfg = resolveClnBreakOptions({
          chartClnLength: Number(settings.candle?.clnLength),
          clnBreakLookback: Number(bcfg.lookback),
          clnBreakBufferAtr: Number(bcfg.bufferAtr),
          clnBreakBreach: bcfg.breach === 'close' ? 'close' : 'touch',
          clnBreakRequireLrAgree: bcfg.requireLrAgree === true,
          clnBreakStopAtr: Number(bcfg.stopAtr),
        });
        const { steps: bSteps } = computeClnBreakSeries(bsrc as CandleRow[], cfg);
        const buyPts: LineData<Time>[] = [];
        const shortPts: LineData<Time>[] = [];
        for (let i = 0; i < bSteps.length; i++) {
          const st = bSteps[i];
          if (st.buyLevel == null || st.shortLevel == null) continue;
          buyPts.push({ time: bSteps[i].time as Time, value: st.buyLevel });
          shortPts.push({ time: bSteps[i].time as Time, value: st.shortLevel });
        }
        // The forming bar, if the display series has one beyond the closed
        // history. Its level needs only closed bodies, so carrying the last
        // closed level forward one bar is exact, not an extrapolation.
        const lastClosed = bSteps.length ? Number(bSteps[bSteps.length - 1].time) : 0;
        const lastShown = candles.length ? Number(candles[candles.length - 1].time) : 0;
        if (buyPts.length && lastShown > lastClosed) {
          buyPts.push({ time: lastShown as Time, value: Number(buyPts[buyPts.length - 1].value) });
          shortPts.push({ time: lastShown as Time, value: Number(shortPts[shortPts.length - 1].value) });
        }
        // The series carries the axis label and the autoscale; the LINE is drawn
        // by BarLevelPrimitive, which spans each level across the bar it governs.
        // A `WithSteps` line draws point i from bar i's CENTRE to bar i+1's, so
        // the left half of every candle sat under the PREVIOUS bar's level — a
        // wick could be read against a level that was not the one it was tested
        // against. Measured and fixed 2026-09-24.
        const bLine = (data: LineData<Time>[], color: string, title: string) => {
          if (data.length < 2) return;
          const ser = chart.addSeries(LineSeries, {
            color,
            lineWidth: bw,
            lineVisible: false,
            lastValueVisible: true,
            priceLineVisible: false,
            crosshairMarkerVisible: false,
            title,
          });
          ser.setData(data);
          (ser as any).attachPrimitive(new BarLevelPrimitive(
            data.map(p => ({ time: Number(p.time), value: Number(p.value) })),
            { color, width: bw, style: bsty },
          ));
          overlaySeriesRefs.current.push(ser);
        };
        // These sit ~0.36 ATR either side of the recent regression bodies, i.e.
        // always next to price, so unlike the CLH band they are safe in the price
        // scale and keep their axis labels.
        bLine(buyPts, bcfg.buyColor || CLN_BREAK_BUY_LINE, 'Brk↑');
        bLine(shortPts, bcfg.shortColor || CLN_BREAK_SHORT_LINE, 'Brk↓');
      }
    }

    if ((overlays.clnHtfBand ?? settings.candle?.clnHtfBand === true) && candles.length > 1) {
      const htfMin = Number(auxCandles?.srHtfMinutes) || 0;
      const htfRaw = auxCandles?.srHtf || [];
      // htfMin <= the chart's own interval means there is no higher timeframe to
      // map to (a 1W chart tops out the ladder) — draw nothing rather than a
      // band of the chart against itself.
      if (htfMin > (intervalMinutes || 0) && htfRaw.length > 1) {
        // Compute the regression over the FULL series (forming bar included) and
        // remember where the closed part ends. computeClnBars/linreg are causal —
        // a trailing window, so every closed bar's body is bit-identical to what
        // it was when the forming bar was dropped outright. That lets one pass
        // serve both halves instead of computing the series twice.
        const closedLen = dropFormingHtfBars(htfRaw as CandleRow[], htfMin).length;
        const drawForming = (settings as any).htfBand?.showForming ?? (settings.candle?.clnHtfBandForming !== false);
        // The stamping walk lives in utils/clnHtfBand.ts so the band is readable
        // off the chart. Only the run-splitting below is rendering.
        const stamps = computeClnHtfStamps(
          candles as CandleRow[],
          (drawForming ? htfRaw : htfRaw.slice(0, closedLen)) as CandleRow[],
          htfMin,
          closedLen,
          Number(settings.candle?.clnLength) || CLN_DEFAULT_LENGTH,
        );
        if (stamps.length > 1) {
          const bandUp = settings.candle.risingColor || C.chartBull;
          const bandDown = settings.candle.fallingColor || C.chartBear;
          // `?? 0.12` on a Number() cast would never fire — Number(undefined) is
          // NaN, not nullish, and a NaN alpha silently produces a broken rgba().
          const opRaw = Number((settings as any).htfBand?.opacity ?? settings.candle?.clnHtfBandOpacity);
          const bandOp = Number.isFinite(opRaw) ? Math.max(0.02, Math.min(0.40, opRaw)) : 0.12;

          // Consecutive same-direction runs. The open segment is read back off
          // `segs` rather than held in a reassigned local: a mutable accumulator
          // here makes `segs -> cur -> bridge -> cur` a self-referential
          // initializer that TS cannot type (TS7022, then `never`).
          // Runs also break at the closed→forming boundary, because the two
          // halves are drawn differently and a BandPrimitive has one style.
          type ClnSeg = { points: BandPoint[]; up: boolean; forming: boolean };
          const segs: ClnSeg[] = [];
          // Colour: BOTH lines stepping up vs the previous HTF bar -> rising, both
          // down -> falling, otherwise the bar's regression direction (utils/bandStepColour).
          const stampUp = colourHtfStamps(stamps);
          for (let si = 0; si < stamps.length; si++) {
            const s = stamps[si];
            const sUp = stampUp[si];
            const open = segs.length ? segs[segs.length - 1] : null;
            if (open && open.up === sUp && open.forming === s.forming) {
              open.points.push({ time: s.time, upper: s.upper, lower: s.lower });
              continue;
            }
            // Bridge at the PREVIOUS level so the new run connects to the old one
            // and the colour change lands on the riser, not on a gap.
            const seg: ClnSeg = { points: [], up: sUp, forming: s.forming };
            const prev = open && open.points.length ? open.points[open.points.length - 1] : null;
            if (prev) seg.points.push({ time: prev.time, upper: prev.upper, lower: prev.lower });
            seg.points.push({ time: s.time, upper: s.upper, lower: s.lower });
            segs.push(seg);
          }
          for (const seg of segs) {
            if (seg.points.length < 2) continue;
            const hue = seg.up ? bandUp : bandDown;
            // The forming segment REPAINTS — its regression body moves as the HTF
            // bar develops — so it must not look like settled context. Same hue
            // and same level (it is the same measurement), but a lighter wash and
            // a dashed edge, which is how the rest of the app already marks
            // "provisional" without inventing a third colour.
            const bp = new BandPrimitive(seg.points, {
              fillColor: withAlpha(hue, seg.forming ? bandOp * 0.55 : bandOp),
              // The fill alone cannot carry this band. An HTF regression body is
              // often a sliver — a 1D body measured ~5px tall across a whole 60m
              // pane — and 5px of 12%-alpha wash is invisible, which is exactly
              // how the first version screenshotted. Stroking the two edges is
              // what CPR already does for the same reason: the EDGE says where
              // the level is at any thickness, and the fill carries how thick.
              lineColor: withAlpha(hue, Math.min(0.9, bandOp * 4 + 0.25)),
              lineWidth: 1,
              ...(seg.forming ? { lineDash: [4, 3] } : {}),
              zOrder: 'bottom',
              // Floor the drawn thickness so a near-flat HTF bar still reads as
              // present rather than absent. Same floor, same reason, as
              // LrBodyPrimitive's MIN_BODY_PX; above it the true height shows.
              minHeightPx: 3,
            });
            cs.attachPrimitive(bp as any);
            primitivesRef.current.push(bp);
          }
        }
      }
    }

    /* P-Zone — Zone 1 (solid) and Zone 2 (dashed) as per-bar levels with a light
     * fill between them, one colour (amber by default) so it never reads as a trend
     * colour. A run breaks wherever the zone is absent (HTF Band mixed / flat) or
     * flips side. Levels are flat across each HTF period by construction. */
    if (overlays.profitZone && profitZones) {
      const pzc = (settings as any).profitZone || {};
      const zc: string = pzc.color || '#f5a623';
      const foRaw = Number(pzc.fillOpacity);
      const fo = Number.isFinite(foRaw) ? Math.max(0, Math.min(0.4, foRaw)) : 0.1;
      const lw = Math.max(1, Math.min(4, Math.round(Number(pzc.lineWidth) || 1)));
      const fit = pzc.fitScale !== false;
      const shown = new Set(candles.map(c => c.time as number));
      const z1: BarLevelPoint[] = [], z2: BarLevelPoint[] = [];
      const fill: Array<{ time: number; upper: number; lower: number; color: string }> = [];
      let prevDir = 0;
      for (const p of profitZones.points) {
        if (typeof p === 'string' || !shown.has(p.time)) { prevDir = 0; continue; }
        const seg = p.dir !== prevDir;
        z1.push({ time: p.time, value: p.z1, ...(seg ? { newSeg: true } : {}) });
        z2.push({ time: p.time, value: p.z2, ...(seg ? { newSeg: true } : {}) });
        if (fo > 0) fill.push({ time: p.time, upper: Math.max(p.z1, p.z2), lower: Math.min(p.z1, p.z2), color: withAlpha(zc, fo) });
        prevDir = p.dir;
      }
      const pzHost = (pts: BarLevelPoint[], style: number) => {
        if (!pts.length) return;
        const ser = chart.addSeries(LineSeries, {
          color: zc, lineWidth: 1, lineVisible: false, lastValueVisible: false, priceLineVisible: false,
          crosshairMarkerVisible: false, ...(fit ? {} : { autoscaleInfoProvider: () => null }),
        });
        ser.setData(pts.map(q => ({ time: q.time as Time, value: q.value })));
        (ser as any).attachPrimitive(new BarLevelPrimitive(pts, { color: zc, width: lw, style }));
        overlaySeriesRefs.current.push(ser);
      };
      if (fill.length) {
        const fser = chart.addSeries(LineSeries, {
          color: zc, lineWidth: 1, lineVisible: false, lastValueVisible: false, priceLineVisible: false,
          crosshairMarkerVisible: false, autoscaleInfoProvider: () => null,
        });
        fser.setData(fill.map(q => ({ time: q.time as Time, value: q.upper })));
        (fser as any).attachPrimitive(new BarBandPrimitive(fill));
        overlaySeriesRefs.current.push(fser);
      }
      pzHost(z1, 0);
      pzHost(z2, 2);
      // Axis labels for the zones in force on the last bar.
      const last = profitZones.points[profitZones.points.length - 1];
      const lastT = candles.length ? (candles[candles.length - 1].time as number) : 0;
      if (last && typeof last !== 'string' && lastT) {
        for (const [v, title] of [[last.z1, 'PZ1'], [last.z2, 'PZ2']] as Array<[number, string]>) {
          const ser = chart.addSeries(LineSeries, {
            color: zc, lineWidth: 1, lineVisible: false, lastValueVisible: true, priceLineVisible: false,
            crosshairMarkerVisible: false, title, autoscaleInfoProvider: () => null,
          });
          ser.setData([{ time: lastT as Time, value: v }]);
          overlaySeriesRefs.current.push(ser);
        }
      }
    }

    // Round 34 — track the right-most projection time across CPR/POC so we
    // can extend the chart's rightOffset to fit the projection visualisations.
    let maxProjTime: number | null = null;
    const noteProj = (t: number) => {
      if (!Number.isFinite(t)) return;
      if (maxProjTime == null || t > maxProjTime) maxProjTime = t;
    };

    /* CPR — segmented per-period band. Line colour = fill colour (Round 34).
     *
     * Each direction-run renders as its own BandPrimitive with fillColor at the
     * configured opacity and lineColor at full opacity, so the top/bottom edges
     * of the band match the fill exactly — no more hardcoded teal/red TC/BC
     * line series.  The Pivot midline (optional) keeps its yellow theme colour
     * since it sits inside the band, not on its edge. */
    if (overlays.cpr && ctf.length > 0) {
      const period: CprPeriod = auxCandles?.cprPeriod || 'D';
      const cprPts = computeCpr(ctf, period, auxCandles?.exchange);
      type CprStamp = { time: number; tc: number; bc: number; pivot: number; bandDir: 'rising'|'falling'|'neutral'; periodKey: string };
      const stamps: CprStamp[] = [];
      let j = 0;
      for (let i = 0; i < candles.length; i++) {
        const t = candles[i].time;
        while (j + 1 < cprPts.length && cprPts[j + 1].time <= t) j++;
        const p = cprPts[j];
        if (!p || p.time > t) continue;
        stamps.push({
          time: t, tc: p.tc, bc: p.bc, pivot: p.pivot,
          bandDir: p.bandDir || 'neutral',
          periodKey: p.periodKey || '',
        });
      }
      const pvD: LineData<Time>[] = stamps.map(s => ({ time: s.time as Time, value: s.pivot }));
      const pvColor = isDark ? '#fff176' : '#f9a825';
      const dirColor = (dir: 'rising'|'falling'|'neutral') =>
        dir === 'rising' ? settings.cpr.risingColor
        : dir === 'falling' ? settings.cpr.fallingColor
        : settings.cpr.neutralColor;
      const dirFill = (dir: 'rising'|'falling'|'neutral') => withAlpha(dirColor(dir), settings.cpr.opacity);

      // Segment stamps into consecutive runs sharing the same direction so we
      // can stamp matching line + fill colour per primitive.
      type Seg = { points: BandPoint[]; dir: 'rising'|'falling'|'neutral' };
      const segs: Seg[] = [];
      let cur: Seg | null = null;
      for (const s of stamps) {
        const upper = Math.max(s.tc, s.bc);
        const lower = Math.min(s.tc, s.bc);
        if (!cur || cur.dir !== s.bandDir) {
          if (cur && cur.points.length) {
            // Bridge at the PREVIOUS level so the new segment connects + steps to
            // the new level (draws the connector at the boundary instead of a gap).
            const last: BandPoint = cur.points[cur.points.length - 1];
            cur = { points: [{ time: last.time, upper: last.upper, lower: last.lower }], dir: s.bandDir };
          } else {
            cur = { points: [], dir: s.bandDir };
          }
          segs.push(cur);
        }
        cur.points.push({ time: s.time, upper, lower });
      }
      for (const seg of segs) {
        if (seg.points.length >= 2) {
          const bp = new BandPrimitive(seg.points, {
            fillColor: dirFill(seg.dir),
            lineColor: dirColor(seg.dir),
            lineWidth: 1,
          });
          cs.attachPrimitive(bp as any);
          primitivesRef.current.push(bp);
        }
      }

      if (settings.cpr.showProjection && stamps.length > 0) {
        // Project from the SAME source + period as the plotted rectangles (ctf),
        // not the display candles — otherwise the projected level/direction and its
        // period-boundary timing diverge from the last rectangle (worst on HA / higher
        // timeframes, where ctf ≠ the chart candles). Keeps the projection aligned on
        // every timeframe + candle type.
        const proj = computeProjectedCpr(ctf, period, auxCandles?.exchange);
        // Only draw a real, finite projected band. (The old slope from the current
        // width to the projected width collapsed into a converging TRIANGLE whenever
        // the projected CPR was narrow — the "not working" look.)
        if (proj && Number.isFinite(proj.tcTop) && Number.isFinite(proj.bcBot)) {
          const lastStamp = stamps[stamps.length - 1];
          const lastUpper = Math.max(lastStamp.tc, lastStamp.bc);
          const lastLower = Math.min(lastStamp.tc, lastStamp.bc);
          const periodSec = period === 'D' ? 86400 : period === 'W' ? 604800 : 2592000;
          // The CURRENT band holds until its period ENDS (predefined duration); the
          // PROJECTED band starts AT that period boundary and holds flat one period
          // forward (a clean forward box, not a converging triangle).
          const periodEnd = proj.periodEndTime > (lastStamp.time as number) ? proj.periodEndTime : (lastStamp.time as number) + periodSec;
          const projEnd = periodEnd + periodSec;
          // 1 — extend the current CPR flat from now to the end of its period.
          const extend: BandPoint[] = [
            { time: lastStamp.time, upper: lastUpper, lower: lastLower },
            { time: periodEnd,      upper: lastUpper, lower: lastLower },
          ];
          const bpExt = new BandPrimitive(extend, {
            fillColor: dirFill(lastStamp.bandDir), lineColor: dirColor(lastStamp.bandDir), lineWidth: 1, lastBarTime, barSpacingSec,
          });
          cs.attachPrimitive(bpExt as any); primitivesRef.current.push(bpExt);
          // 2 — projected CPR: flat at the projected level, from the period boundary
          // forward (steps up/down from the current level at the boundary).
          const projPts: BandPoint[] = [
            { time: periodEnd, upper: proj.tcTop, lower: proj.bcBot },
            { time: projEnd,   upper: proj.tcTop, lower: proj.bcBot },
          ];
          const projColor = dirColor(proj.bandDir);
          const projAlpha = Math.max(0.04, settings.cpr.opacity * 0.6);
          const bpProj = new BandPrimitive(projPts, {
            fillColor: withAlpha(projColor, projAlpha), lineColor: projColor, lineWidth: 1, lastBarTime, barSpacingSec,
          });
          cs.attachPrimitive(bpProj as any);
          primitivesRef.current.push(bpProj);
          noteProj(projEnd);
        }
      }

      // Optional Pivot midline.
      if (settings.cpr.showMidline) {
        const pv = chart.addSeries(LineSeries, { color: pvColor, lineWidth: 1, lineStyle: 2, lastValueVisible: false, priceLineVisible: false });
        pv.setData(pvD);
        overlaySeriesRefs.current.push(pv);
      }
    }

    // Round 33 — shared renderer for C-POC and H-POC. Splits the POC band
    // into Rising / Falling / Neutral segments, draws each as its own
    // BandPrimitive, then (if enabled) extends the last period to its
    // boundary and renders a separate projected band from the developing
    // period's volume profile.
    // Round 34 — line colour = fill colour per segment (no hardcoded line).
    const renderPocBand = (
      poc: ReturnType<typeof computePocPerPeriod>,
      pocPeriod: CprPeriod,
    ) => {
      if (!poc.length) return;
      const dirColor = (dir: 'rising'|'falling'|'neutral') =>
        dir === 'rising'  ? settings.poc.risingColor
        : dir === 'falling' ? settings.poc.fallingColor
        : settings.poc.neutralColor;
      const dirFill = (dir: 'rising'|'falling'|'neutral') => withAlpha(
        dirColor(dir),
        Math.max(settings.poc.opacity, 0.20),
      );
      type Seg = { points: BandPoint[]; dir: 'rising'|'falling'|'neutral' };
      const segs: Seg[] = [];
      let cur: Seg | null = null;
      for (const p of poc) {
        const dir = p.bandDir || 'neutral';
        const upper = p.top;
        const lower = p.bottom;
        if (!cur || cur.dir !== dir) {
          if (cur && cur.points.length) {
            // Bridge to the new segment at the PREVIOUS level (not the new one) so
            // the new segment connects to where the old one ended and then slopes
            // up/down to the new level — drawing the step connector at the period
            // boundary. (Bridging at the new level left a visible gap on a
            // direction change; within-direction changes already connected.)
            const last: BandPoint = cur.points[cur.points.length - 1];
            cur = { points: [{ time: last.time, upper: last.upper, lower: last.lower }], dir };
          } else {
            cur = { points: [], dir };
          }
          segs.push(cur);
        }
        cur.points.push({ time: p.time, upper, lower });
      }
      for (const seg of segs) {
        if (seg.points.length >= 2) {
          const bp = new BandPrimitive(seg.points, {
            fillColor: dirFill(seg.dir),
            lineColor: dirColor(seg.dir),
            lineWidth: 1,
          });
          cs.attachPrimitive(bp as any);
          primitivesRef.current.push(bp);
        }
      }
      if (settings.poc.showProjection) {
        // Project from the SAME raw source as the plotted POC band (pocSrc), not the
        // display candles, so the projected level + boundary stay consistent across
        // candle types / timeframes (mirrors the CPR projection fix).
        const proj = computeProjectedPoc(pocSrc, pocPeriod, auxCandles?.exchange);
        // Only draw a finite projected band; hold it FLAT one period forward (a clean
        // forward box) instead of a slope that collapses to a converging triangle.
        if (proj && Number.isFinite(proj.top) && Number.isFinite(proj.bottom)) {
          const last = poc[poc.length - 1];
          const periodSec = pocPeriod === 'D' ? 86400 : pocPeriod === 'W' ? 604800 : 2592000;
          // Current POC holds to its period END; projected POC starts at that boundary.
          const periodEnd = proj.periodEndTime > (last.time as number) ? proj.periodEndTime : (last.time as number) + periodSec;
          const projEnd = periodEnd + periodSec;
          // 1 — extend the current POC flat to the end of its period.
          const extend: BandPoint[] = [
            { time: last.time,  upper: last.top, lower: last.bottom },
            { time: periodEnd,  upper: last.top, lower: last.bottom },
          ];
          const bpExt = new BandPrimitive(extend, {
            fillColor: dirFill(last.bandDir || 'neutral'), lineColor: dirColor(last.bandDir || 'neutral'), lineWidth: 1, lastBarTime, barSpacingSec,
          });
          cs.attachPrimitive(bpExt as any); primitivesRef.current.push(bpExt);
          // 2 — projected POC: flat at the projected level, from the period boundary forward.
          const projPts: BandPoint[] = [
            { time: periodEnd, upper: proj.top, lower: proj.bottom },
            { time: projEnd,   upper: proj.top, lower: proj.bottom },
          ];
          const projColor = dirColor(proj.bandDir);
          const projAlpha = Math.max(0.05, settings.poc.opacity * 0.6);
          const bpProj = new BandPrimitive(projPts, {
            fillColor: withAlpha(projColor, projAlpha),
            lineColor: projColor, lineWidth: 1,
            lastBarTime, barSpacingSec,
          });
          cs.attachPrimitive(bpProj as any);
          primitivesRef.current.push(bpProj);
          noteProj(projEnd);
        }
      }
    };

    /* CTF POC — volume profile aligned to CPR period boundaries (round 32).
       R120.5 — compute on RAW candles (the candle-type-independent source the
       C-POC voter uses) so the drawn band matches the bar colour the voter
       produces on any candle type. */
    const pocSrc = (rawCandles && rawCandles.length) ? rawCandles : candles;
    if (overlays.ctfPoc && pocSrc.length > 5) {
      const period: CprPeriod = auxCandles?.cprPeriod || 'D';
      const poc = computePocPerPeriod(pocSrc, period, auxCandles?.exchange);
      renderPocBand(poc, period);
    }

    /* HTF POC — same per-period approach as C-POC, but stepped one period higher. */
    if (overlays.htfPoc && pocSrc.length > 5) {
      const period: CprPeriod = auxCandles?.cprPeriod === 'D' ? 'W' : 'M';
      const poc = computePocPerPeriod(pocSrc, period, auxCandles?.exchange);
      renderPocBand(poc, period);
    }

    /* MM (Market Maker) — mark the body (Open/Close) of each SOURCE candle that is a
       BBR reversal OR a Vol spike, and join consecutive marks into a condition-driven
       staircase band. The SOURCE candle type (Regular / HA / CLH) is fixed in Chart
       Settings → MM, independent of the chart's display candle type. Each stair holds
       until the next qualifying candle; it's filled BULL when it stepped up vs the
       previous mark, BEAR when down (user colours + opacity). */
    if (overlays.mm) {
      const mcfg = (settings as any)?.mm || {};
      const ctype = String(mcfg.candleType || 'Regular');
      const bull = mcfg.bullColor || '#26a69a';
      const bear = mcfg.bearColor || '#ef5350';
      const clampOp = (v: unknown, d: number): number => { const n = Number(v); return Number.isFinite(n) ? Math.max(0.02, Math.min(1, n)) : d; };
      type MmMark = { time: number; upper: number; lower: number; mid: number };
      const mmMarks = (src: CandleRow[]): MmMark[] => {
        if (src.length <= 5) return [];
        const bbr = computeBbrReversals(src);
        const spikes = computeVolumeSpikes(src);
        const out: MmMark[] = [];
        for (const c of src) {
          const t = c.time as number;
          if (bbr.overboughtAt.has(t) || bbr.oversoldAt.has(t) || spikes.has(t)) {
            const upper = Math.max(c.open, c.close), lower = Math.min(c.open, c.close);
            out.push({ time: t, upper, lower, mid: (upper + lower) / 2 });
          }
        }
        return out;
      };
      /** One stair per mark from `start` to the next mark's `start`; filled BULL when
       *  it stepped up vs the previous mark, BEAR when down. `start` must be a chart
       *  bar time (the stepped edge series need it on the chart's own grid). */
      const drawStairs = (marks: Array<MmMark & { start: number }>, op: number, dashed: boolean): void => {
        if (marks.length < 2) return;
        // Stepped edge lines (upper + lower) so consecutive stairs are joined by
        // vertical risers — the staircase reads as one continuous band instead of
        // detached boxes. Per-point colour matches the step's bull/bear direction.
        const upperStep: LineData<Time>[] = [];
        const lowerStep: LineData<Time>[] = [];
        for (let i = 0; i < marks.length; i++) {
          const m = marks[i];
          const dir = i > 0 ? (m.mid >= marks[i - 1].mid ? 1 : -1)
            : (marks[1] && marks[1].mid < m.mid ? -1 : 1);
          const nextT = i + 1 < marks.length ? marks[i + 1].start
            : (lastBarTime ? lastBarTime + barSpacingSec : m.start + barSpacingSec);
          if (nextT <= m.start) continue;
          const color = dir > 0 ? bull : bear;
          const bp = new BandPrimitive(
            [{ time: m.start, upper: m.upper, lower: m.lower }, { time: nextT, upper: m.upper, lower: m.lower }],
            { fillColor: withAlpha(color, op), lineColor: color, lineWidth: 1, lastBarTime, barSpacingSec, ...(dashed ? { lineDash: [4, 3] } : {}) },
          );
          cs.attachPrimitive(bp as any);
          primitivesRef.current.push(bp);
          upperStep.push({ time: m.start as Time, value: m.upper, color });
          lowerStep.push({ time: m.start as Time, value: m.lower, color });
          if (i === marks.length - 1) {
            upperStep.push({ time: nextT as Time, value: m.upper, color });
            lowerStep.push({ time: nextT as Time, value: m.lower, color });
          }
        }
        for (const data of [upperStep, lowerStep]) {
          if (data.length < 2) continue;
          const s = chart.addSeries(LineSeries, {
            color: bull, lineWidth: dashed ? 2 : 1, lineType: 1 /* WithSteps → vertical risers */,
            lineStyle: dashed ? 2 : 0,
            lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false,
          });
          s.setData(data); overlaySeriesRefs.current.push(s);
        }
      };

      // Chart timeframe — the original MM.
      if (mcfg.showLtf !== false) {
        const baseSrc: CandleRow[] = (rawCandles && rawCandles.length) ? rawCandles : candles;
        const src = ctype === 'Regular' ? baseSrc : transformCandles(baseSrc, ctype as any);
        drawStairs(mmMarks(src).map(m => ({ ...m, start: m.time })), clampOp(mcfg.opacity, 0.22), false);
      }

      // Higher timeframe — the Strategies-mapped HTF, the same series the HTF Band
      // reads. CAUSAL by the owner's choice: a mark made by HTF bar k exists only
      // once k has CLOSED, so its stair starts at the first chart bar at or after
      // that close, never across bar k itself. Only CLOSED HTF bars are used; in
      // Bar Replay the fetched HTF is not cut, so "closed" is judged at the end of
      // the playhead bar rather than the clock. Dashed edges tell it apart.
      const htfMin = Number(auxCandles?.srHtfMinutes) || 0;
      const htfRaw = (auxCandles?.srHtf || []) as CandleRow[];
      if (mcfg.showHtf === true && htfMin > (intervalMinutes || 0) && htfRaw.length > 5 && candles.length) {
        const ltfSec = Math.max(1, intervalMinutes || 1) * 60;
        const asOf = replayCut != null ? Number(replayCut) + ltfSec : Math.floor(Date.now() / 1000) + 19800;
        const closed = htfRaw.filter(bar => htfBarCloseTime(bar.time as number, htfMin) <= asOf);
        const hsrc = ctype === 'Regular' ? closed : transformCandles(closed, ctype as any);
        const times = candles.map(c => c.time as number);
        const staged: Array<MmMark & { start: number }> = [];
        let j = 0;
        for (const m of mmMarks(hsrc)) {
          const closeAt = htfBarCloseTime(m.time, htfMin);
          while (j < times.length && times[j] < closeAt) j++;
          if (j >= times.length) break;
          const st = { ...m, start: times[j] };
          // Two HTF marks landing on one chart bar: the later one is what holds.
          if (staged.length && staged[staged.length - 1].start === st.start) staged[staged.length - 1] = st;
          else staged.push(st);
        }
        drawStairs(staged, clampOp(mcfg.htfOpacity, 0.12), true);
      }
    }

    /* LR_HAMA_Band — Avg of SLR (CSL) and HAMA open/close clouds.
     *
     * Round 34: replaces the old LRBnd (which was only the CSL open/close
     * band) with the Pine "Avg SLR+HAMA O/C Cloud" — the average of the
     * SLR top/bot band and the HAMA top/bot band, per bar. Single colour +
     * opacity (matches Pine default). Computed from the *raw* candles
     * regardless of the chart's display candle type so the band stays
     * identical across HA / CSL / CLH etc.
     *
     * AvgCloud (below) depends on this Avg band, so we compute it here
     * and cache for reuse when AvgCloud is enabled (avoids double work). */
    const sigInput: CandleRow[] = (rawCandles && rawCandles.length) ? rawCandles : candles;
    let lrHamaBand: ReturnType<typeof computeLRHamaBand> | null = null;
    if ((overlays.lrHama || overlays.avgCloud) && sigInput.length >= 5) {
      try {
        lrHamaBand = computeLRHamaBand(sigInput);
      } catch { lrHamaBand = null; }
    }
    if (overlays.lrHama && lrHamaBand && lrHamaBand.length >= 2) {
      const lrCol = settings.lrBnd?.color || '#FF9800';
      const lrOp = typeof settings.lrBnd?.opacity === 'number' ? settings.lrBnd.opacity : 0.18;
      // Round 38 — match Pine exactly. The pinescript renders the LR_HAMA
      // Avg cloud as a FILL between two `display.none` plots — no top/bottom
      // edge lines. We omit lineColor so the BandPrimitive draws only the
      // translucent fill, just like Pine.
      const bp = new BandPrimitive(lrHamaBand, {
        fillColor: withAlpha(lrCol, Math.max(0.05, Math.min(1, lrOp))),
      });
      cs.attachPrimitive(bp as any);
      primitivesRef.current.push(bp);
    }

    /* AvgCloud — dynamic cloud between the LR_HAMA_Band edge and a
     * user-chosen REFERENCE line (Round 42).
     *
     * The original Pine boundary (Combined Midline) proved unreliable in
     * PrashantWeb because the combined envelope's CTF EMA can't converge
     * on the limited CTF history we fetch. The user asked to instead pick
     * the second cloud boundary from one of:
     *   • 'cpoc'   — the C-POC band centre line
     *   • 'ema200' — EMA(200) of LinReg(close,14)  (same maths as EMAs)
     *   • 'ema128' — EMA(128) of LinReg(close,14)
     * chosen via Chart Settings → Bands.
     *
     * Cloud logic is unchanged otherwise:
     *   center   = (avg_top + avg_bot) / 2
     *   dir      = center > ref ? bull : center < ref ? bear : neutral
     *   edge     = center > ref ? avg_bot : avg_top   (band edge nearest ref)
     *   fill(edge, ref) with the directional colour.
     * Per-bar direction is segmented so each colour run is one primitive. */
    if (overlays.avgCloud && lrHamaBand && lrHamaBand.length >= 2) {
      // R72 — reference line is the Combined Midline (CMid). Old dropdown
      // (cpoc / ema200 / ema128) existed only because CMid wasn't computing
      // correctly; R70+R71 fixed that, so we revert to the originally-planned
      // design: cloud between LR_HAMA_Band edge and CMid. computedCombinedEnvelope
      // was already computed above (combinedCmid) — reuse those values.
      const refArr: Array<number | null> = new Array(candles.length).fill(null);
      for (let i = 0; i < candles.length; i++) {
        const v = cmidByTime.get(candles[i].time as number);
        if (v != null) refArr[i] = v;
      }

      const bandByTime = new Map<number, BandPoint>();
      for (const b of lrHamaBand) bandByTime.set(b.time, b);

      const acFill = (dir: 'bull'|'bear'|'neutral') => {
        const c = dir === 'bull' ? settings.avgCloud.bullishColor
                : dir === 'bear' ? settings.avgCloud.bearishColor
                :                  settings.avgCloud.neutralColor;
        return withAlpha(c, Math.max(0.05, Math.min(1, settings.avgCloud.opacity)));
      };
      type Seg = { points: BandPoint[]; dir: 'bull'|'bear'|'neutral' };
      const segs: Seg[] = [];
      let cur: Seg | null = null;
      for (let i = 0; i < candles.length; i++) {
        const t = candles[i].time;
        const ab = bandByTime.get(t);
        const ref = refArr[i];
        if (!ab || ref == null) continue;
        const center = (ab.upper + ab.lower) / 2;
        let dir: 'bull'|'bear'|'neutral';
        if (center > ref) dir = 'bull';
        else if (center < ref) dir = 'bear';
        else dir = 'neutral';
        // Cloud edge = the band edge on the SAME side as the reference (lower
        // edge when band is above ref, upper edge when below). Fill spans
        // edge ↔ ref.
        const edge = center > ref ? ab.lower : ab.upper;
        const upper = Math.max(edge, ref);
        const lower = Math.min(edge, ref);
        if (!cur || cur.dir !== dir) {
          if (cur && cur.points.length) {
            const last: BandPoint = cur.points[cur.points.length - 1];
            cur = { points: [{ time: last.time, upper, lower }], dir };
          } else {
            cur = { points: [], dir };
          }
          segs.push(cur);
        }
        cur.points.push({ time: t, upper, lower });
      }
      for (const seg of segs) {
        if (seg.points.length >= 2) {
          // Fill only, no edges — Pine fills between two `display.none` plots.
          const bp = new BandPrimitive(seg.points, {
            fillColor: acFill(seg.dir),
          });
          cs.attachPrimitive(bp as any);
          primitivesRef.current.push(bp);
        }
      }
    }

    /* BOLev_D / BOLev_W (Round 112) — breakout-level projector from Daily /
       Weekly CLH candles, drawn on the price scale. Levels (max/min of the
       previous N completed CLH candles) are non-repainting step lines stamped
       per period; an active breakout (ZONE) gets a shaded band + an arrow.
       Works on ANY chart timeframe and candle type. */
    if ((overlays.bolevD && auxCandles?.daily && auxCandles.daily.length) ||
        (overlays.bolevW && auxCandles?.weekly && auxCandles.weekly.length) ||
        (overlays.bolev4h && auxCandles?.h4 && auxCandles.h4.length)) {
      const bcfg = (settings as any)?.bolev || {};
      const buyCol = bcfg.buyColor || '#26a69a';
      const shortCol = bcfg.shortColor || '#ef5350';
      const bandOp = Math.max(0, Math.min(1, Number(bcfg.bandOpacity ?? 0.12)));
      // Item 4b — translucent CHANNEL fill spanning BUY↔SHORT across every
      // period (independent of the compression-only "narrow" zone). User color+opacity.
      const channelOn = bcfg.channelFill === true;
      const channelCol = bcfg.channelColor || '#42a5f5';
      const channelOp = Math.max(0, Math.min(1, Number(bcfg.channelOpacity ?? 0.08)));
      const lw = Math.max(1, Math.min(4, Math.floor(Number(bcfg.lineWidth) || 2))) as 1 | 2 | 3 | 4;
      const lookback = Math.max(1, Math.min(10, Math.floor(Number(bcfg.lookback) || 2)));
      const stateAlpha = (s: BolevPeriod['buy']) => s === 'zone' ? 1.0 : s === 'lookout' ? 0.65 : 0.22;
      // R120 narrow / pre-blast zone — shaded when BUY↔SHORT gap < mult × ATR(14).
      const narrowMult = Math.max(0.1, Math.min(10, Number(bcfg.narrowAtrMult ?? 1)));
      const narrowCol = bcfg.narrowColor || '#ffb74d';
      const narrowOp = Math.max(0, Math.min(1, Number(bcfg.narrowOpacity ?? 0.18)));

      // ATR(14) on the raw period candles, keyed by candle time (= period time).
      const atrByTime = (periodCandles: CandleRow[]): Map<number, number> => {
        const m = new Map<number, number>();
        const P = 14; const tr: number[] = [];
        for (let i = 0; i < periodCandles.length; i++) {
          const c = periodCandles[i];
          const prevC = i > 0 ? periodCandles[i - 1].close : c.close;
          tr.push(Math.max(c.high - c.low, Math.abs(c.high - prevC), Math.abs(c.low - prevC)));
        }
        let sum = 0;
        for (let i = 0; i < periodCandles.length; i++) {
          sum += tr[i];
          if (i >= P) sum -= tr[i - P];
          if (i >= P - 1) m.set(periodCandles[i].time, sum / P);
        }
        return m;
      };

      const renderBolev = (periodCandles: CandleRow[] | undefined, tag: string, dashed: boolean, prefix: string) => {
        if (!periodCandles || periodCandles.length < lookback + 1) return;
        const showLines = bcfg[`${prefix}ShowLines`] !== false;
        const showArrows = bcfg[`${prefix}ShowArrows`] !== false;
        const showZone = bcfg[`${prefix}ShowZone`] !== false;
        const periods = computeBolev(periodCandles, lookback).periods.slice().sort((a, b) => a.time - b.time);
        if (!periods.length) return;
        const atrMap = atrByTime(periodCandles);
        const buyData: LineData<Time>[] = []; const shortData: LineData<Time>[] = [];
        const buyBand: BandPoint[] = []; const shortBand: BandPoint[] = [];
        // Narrow / pre-blast zones are kept as SEPARATE segments — one per
        // contiguous narrow period — so distinct squeezes never merge into one
        // blob (a single BandPrimitive would bridge the fill across the gaps).
        const narrowSegs: BandPoint[][] = []; let narrowSeg: BandPoint[] | null = null; let narrowSegPeriod = -1;
        // Channel fill (BUY↔SHORT) — gap-segmented + STEPPED, so the fill sits exactly
        // between the two step-lines (no diagonal spill-over at each period step) and
        // never bridges across candle gaps (which produced the stray wedges/patches).
        const channelSegs: BandPoint[][] = [];
        let chSeg: BandPoint[] | null = null; let chPrevT = -1; let chPrevU = NaN; let chPrevL = NaN;
        const chBarSec = (intervalMinutes || 1) * 60;
        const buyMarks: any[] = []; const shortMarks: any[] = [];
        let cur = -1; let lastPt = -1; let buyArrow = false; let shortArrow = false;
        // R115 — emit a Cond 3 marker on the FIRST chart-bar within each daily
        // (or weekly) period that fired strict no-wick + level-break Cond 3.
        // Marker is independent of the lookout/zone state machine and visible
        // whether or not RockBand is the active strategy. Tracked per period
        // via cond3Fired so only one arrow per period appears even on intraday
        // chart-TFs (many chart bars share one daily period).
        let cond3Fired = false;
        for (const c of candles) {
          const t = c.time as number;
          while (cur + 1 < periods.length && periods[cur + 1].time <= t) cur++;
          if (cur < 0) continue;
          const p = periods[cur];
          if (p.time !== lastPt) { lastPt = p.time; buyArrow = false; shortArrow = false; cond3Fired = false; }
          if (p.buyLevel != null) {
            buyData.push({ time: c.time as Time, value: p.buyLevel, color: withAlpha(buyCol, stateAlpha(p.buy)) });
            if (p.buy === 'zone') {
              buyBand.push({ time: t, upper: Math.max(c.high, p.buyLevel), lower: p.buyLevel, color: withAlpha(buyCol, bandOp) });
              if (showArrows && p.buyBreak && !buyArrow && c.high > p.buyLevel) { buyMarks.push({ time: c.time, position: 'belowBar', color: buyCol, shape: 'arrowUp', size: 1, text: 'B' + tag }); buyArrow = true; }
            }
          }
          if (p.shortLevel != null) {
            shortData.push({ time: c.time as Time, value: p.shortLevel, color: withAlpha(shortCol, stateAlpha(p.short)) });
            if (p.short === 'zone') {
              shortBand.push({ time: t, upper: p.shortLevel, lower: Math.min(c.low, p.shortLevel), color: withAlpha(shortCol, bandOp) });
              if (showArrows && p.shortBreak && !shortArrow && c.low < p.shortLevel) { shortMarks.push({ time: c.time, position: 'aboveBar', color: shortCol, shape: 'arrowDown', size: 1, text: 'S' + tag }); shortArrow = true; }
            }
          }
          // Item 4b — full channel fill between the BUY & SHORT levels. Built as
          // stepped, gap-broken segments so it hugs the two step-lines cleanly.
          if (channelOn && p.buyLevel != null && p.shortLevel != null && p.buyLevel > p.shortLevel) {
            const gap = chPrevT >= 0 && (t - chPrevT) > chBarSec * 1.5;
            if (!chSeg || gap) {
              if (chSeg && chSeg.length >= 2) channelSegs.push(chSeg);
              chSeg = []; chPrevU = NaN; chPrevL = NaN;
            }
            // Vertical riser at a period step (match the WithSteps level lines):
            // hold the previous levels up to this time, then jump.
            if (chSeg.length && (p.buyLevel !== chPrevU || p.shortLevel !== chPrevL)) {
              chSeg.push({ time: t, upper: chPrevU, lower: chPrevL });
            }
            chSeg.push({ time: t, upper: p.buyLevel, lower: p.shortLevel });
            chPrevT = t; chPrevU = p.buyLevel; chPrevL = p.shortLevel;
          } else if (chSeg) {
            if (chSeg.length >= 2) channelSegs.push(chSeg);
            chSeg = null; chPrevT = -1; chPrevU = NaN; chPrevL = NaN;
          }
          // Narrow / pre-blast zone: BUY↔SHORT gap has compressed below the ATR
          // threshold → a big move is brewing. Shade between the two levels.
          // Each period's narrow zone is its OWN segment (start a fresh one when
          // the period changes or after any non-narrow gap) so they stay visually
          // distinct rather than connecting into one continuous band.
          let isNarrow = false;
          if (showZone && p.buyLevel != null && p.shortLevel != null) {
            const gap = p.buyLevel - p.shortLevel;
            const atr = atrMap.get(p.time);
            if (gap > 0 && atr != null && Number.isFinite(atr) && gap < narrowMult * atr) isNarrow = true;
          }
          if (isNarrow) {
            if (!narrowSeg || cur !== narrowSegPeriod) {
              if (narrowSeg && narrowSeg.length >= 2) narrowSegs.push(narrowSeg);
              narrowSeg = []; narrowSegPeriod = cur;
            }
            narrowSeg.push({ time: t, upper: p.buyLevel as number, lower: p.shortLevel as number, color: withAlpha(narrowCol, narrowOp) });
          } else if (narrowSeg) {
            if (narrowSeg.length >= 2) narrowSegs.push(narrowSeg);
            narrowSeg = null; narrowSegPeriod = -1;
          }
          if (showArrows && !cond3Fired) {
            if (p.cond3Buy)        { buyMarks.push({ time: c.time, position: 'belowBar', color: buyCol,   shape: 'arrowUp',   size: 2, text: 'C3' + tag }); cond3Fired = true; }
            else if (p.cond3Short) { shortMarks.push({ time: c.time, position: 'aboveBar', color: shortCol, shape: 'arrowDown', size: 2, text: 'C3' + tag }); cond3Fired = true; }
          }
        }
        const mkLine = (data: LineData<Time>[], baseCol: string, _title: string) => {
          if (data.length < 2) return null;
          // No price-axis label/title — indicators must not plot levels on the Y axis.
          const s = chart.addSeries(LineSeries, {
            color: baseCol, lineWidth: lw, lineStyle: dashed ? 2 : 0, lineType: 1,
            lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false,
          });
          s.setData(data); overlaySeriesRefs.current.push(s); return s;
        };
        // Channel fill sits UNDER the level lines / zone shades — one primitive per
        // contiguous stepped segment (never bridging gaps).
        if (chSeg && chSeg.length >= 2) channelSegs.push(chSeg); // flush the last open segment
        if (channelOn && channelOp > 0) { for (const seg of channelSegs) { if (seg.length >= 2) { const bp = new BandPrimitive(seg, { fillColor: withAlpha(channelCol, channelOp) }); cs.attachPrimitive(bp as any); primitivesRef.current.push(bp); } } }
        const buyS = showLines ? mkLine(buyData, buyCol, 'BUY_' + tag) : null;
        const shortS = showLines ? mkLine(shortData, shortCol, 'SHORT_' + tag) : null;
        // Split a per-candle band into contiguous runs so the fill never bridges a
        // candle gap (which drew stray wedges across empty regions).
        const splitGaps = (band: BandPoint[]): BandPoint[][] => {
          const segs: BandPoint[][] = []; let seg: BandPoint[] = [];
          for (let i = 0; i < band.length; i++) {
            if (seg.length && (band[i].time - band[i - 1].time) > chBarSec * 1.5) { if (seg.length >= 2) segs.push(seg); seg = []; }
            seg.push(band[i]);
          }
          if (seg.length >= 2) segs.push(seg);
          return segs;
        };
        if (bandOp > 0) for (const seg of splitGaps(buyBand)) { const bp = new BandPrimitive(seg, { fillColor: withAlpha(buyCol, bandOp) }); cs.attachPrimitive(bp as any); primitivesRef.current.push(bp); }
        if (bandOp > 0) for (const seg of splitGaps(shortBand)) { const bp = new BandPrimitive(seg, { fillColor: withAlpha(shortCol, bandOp) }); cs.attachPrimitive(bp as any); primitivesRef.current.push(bp); }
        if (narrowSeg && narrowSeg.length >= 2) narrowSegs.push(narrowSeg); // flush the last open segment
        if (showZone && narrowOp > 0) { for (const seg of narrowSegs) { if (seg.length >= 2) { const bp = new BandPrimitive(seg, { fillColor: withAlpha(narrowCol, narrowOp) }); cs.attachPrimitive(bp as any); primitivesRef.current.push(bp); } } }
        // Markers need a host series; if lines are hidden but arrows wanted,
        // attach to a transparent anchor carrying the level values.
        if (showArrows && (buyMarks.length || shortMarks.length)) {
          let hostBuy = buyS, hostShort = shortS;
          if (!hostBuy && buyMarks.length && buyData.length >= 2) { hostBuy = chart.addSeries(LineSeries, { color: 'rgba(0,0,0,0)', lineWidth: 1, lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false }); hostBuy.setData(buyData); overlaySeriesRefs.current.push(hostBuy); }
          if (!hostShort && shortMarks.length && shortData.length >= 2) { hostShort = chart.addSeries(LineSeries, { color: 'rgba(0,0,0,0)', lineWidth: 1, lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false }); hostShort.setData(shortData); overlaySeriesRefs.current.push(hostShort); }
          if (hostBuy && buyMarks.length) { try { createSeriesMarkers(hostBuy, buyMarks.sort((a, b) => a.time - b.time) as any); } catch { /* ignore */ } }
          if (hostShort && shortMarks.length) { try { createSeriesMarkers(hostShort, shortMarks.sort((a, b) => a.time - b.time) as any); } catch { /* ignore */ } }
        }
      };

      if (overlays.bolevD) renderBolev(auxCandles?.daily, 'D', false, 'd');
      if (overlays.bolevW) renderBolev(auxCandles?.weekly, 'W', true, 'w');
      if (overlays.bolev4h) renderBolev(auxCandles?.h4, '4H', false, 'h4');
    }

    /* Blast (Inside/Engulfing) bands — Daily & Weekly. Each detected
       inside (HA) / engulfing (Regular) period candle draws a flat band at its
       raw High/Low, projected forward 2 periods. Daily renders on chart
       intervals ≤ 1D, Weekly on ≤ 1W. The breakout BUY/SHORT (close-cross of
       C-POC while a band is active) is emitted with the signal indicators
       below. Independent of the chart's interval / candle type. */
    if (overlays.blast && (auxCandles?.daily?.length || auxCandles?.weekly?.length)) {
      const cfg = (settings as any)?.blast || {};
      const ltfMin = intervalMinutes || 1;
      const barSpacingSec = ltfMin * 60;
      const lastBarTime = candles.length ? (candles[candles.length - 1].time as number) : undefined;
      const bull = cfg.bullColor || '#26a69a';
      const bear = cfg.bearColor || '#ef5350';
      // STAIRCASE: each detected inside/engulfing period holds its raw High/Low level until
      // the NEXT detected event, then steps — filled BULL when the level stepped up vs the
      // previous event, BEAR when down (condition-driven, replacing the fixed N-bar projection).
      const renderBlastBands = (periodCandles: CandleRow[] | undefined, periodSec: number, op: number) => {
        if (!periodCandles || periodCandles.length < 2 || op <= 0) return;
        // Detect on CLOSED period candles only (repaint / look-ahead safe).
        const events = detectBlastEvents(closedPeriodCandles(periodCandles, periodSec)).slice().sort((a, b) => a.time - b.time);
        if (!events.length) return;
        // Stepped edge lines join consecutive events with vertical risers so the
        // staircase is continuous (rather than detached High/Low boxes).
        const upperStep: LineData<Time>[] = [];
        const lowerStep: LineData<Time>[] = [];
        for (let i = 0; i < events.length; i++) {
          const e = events[i];
          const mid = (e.high + e.low) / 2;
          const dir = i > 0 ? (mid >= (events[i - 1].high + events[i - 1].low) / 2 ? 1 : -1) : 1;
          const startT = e.time;
          const endT = i + 1 < events.length ? events[i + 1].time
            : (lastBarTime != null ? lastBarTime + barSpacingSec : startT + periodSec);
          if (endT <= startT) continue;
          const pts: BandPoint[] = [];
          for (const c of candles) { const t = c.time as number; if (t >= startT && t < endT) pts.push({ time: t, upper: e.high, lower: e.low }); }
          if (lastBarTime != null && endT > lastBarTime) pts.push({ time: endT, upper: e.high, lower: e.low });
          if (pts.length < 2) { pts.length = 0; pts.push({ time: startT, upper: e.high, lower: e.low }, { time: endT, upper: e.high, lower: e.low }); }
          const color = dir > 0 ? bull : bear;
          const bp = new BandPrimitive(pts, { fillColor: withAlpha(color, op), lineColor: color, lineWidth: 1, lastBarTime, barSpacingSec });
          cs.attachPrimitive(bp as any);
          primitivesRef.current.push(bp);
          upperStep.push({ time: startT as Time, value: e.high, color });
          lowerStep.push({ time: startT as Time, value: e.low, color });
          if (i === events.length - 1) {
            upperStep.push({ time: endT as Time, value: e.high, color });
            lowerStep.push({ time: endT as Time, value: e.low, color });
          }
        }
        for (const data of [upperStep, lowerStep]) {
          if (data.length < 2) continue;
          const s = chart.addSeries(LineSeries, {
            color: bull, lineWidth: 1, lineType: 1 /* WithSteps → vertical risers */,
            lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false,
          });
          s.setData(data); overlaySeriesRefs.current.push(s);
        }
      };
      if (ltfMin <= 1440 && cfg.showDaily !== false) {
        renderBlastBands(auxCandles?.daily, 86400, Math.max(0, Math.min(1, Number(cfg.dailyOpacity ?? 0.16))));
      }
      if (ltfMin <= 10080 && cfg.showWeekly !== false) {
        renderBlastBands(auxCandles?.weekly, 604800, Math.max(0, Math.min(1, Number(cfg.weeklyOpacity ?? 0.12))));
      }
    }

    /* VOB — Volume-Trend Order Block Engine: supertrend cloud + trend-stop line +
       volume-split order-block boxes. Retest cross markers are pushed into the
       shared signal-markers array further below (vobRetests). Computed on CLOSED
       RAW candles so it's repaint-safe and candle-type independent. */
    let vobRetests: { buys: number[]; shorts: number[] } | null = null;
    if (overlays.vob) {
      const v = (settings as any)?.vob || {};
      const vSrc: CandleRow[] = (rawCandles && rawCandles.length) ? rawCandles : candles;
      const vClosed = (latestCandle && vSrc.length && vSrc[vSrc.length - 1].time === latestCandle.time) ? vSrc.slice(0, -1) : vSrc.slice();
      const vob = computeVob(vClosed, {
        stLen: Math.max(1, Math.floor(Number(v.stLen) || 50)),
        stMult: Number(v.stMult) || 3.5,
        pivotLen: Math.max(1, Math.floor(Number(v.pivotLen) || 7)),
        deleteOnBreak: v.deleteOnBreak !== false,
        showBullRetest: v.showBullRetest !== false,
        showBearRetest: v.showBearRetest !== false,
        bullVolPct: Number(v.bullVolPct ?? 50),
        bearVolPct: Number(v.bearVolPct ?? 50),
      });
      vobRetests = vob.retests;

      const cBull = v.cloudBullColor || '#00ffcc';
      const cBear = v.cloudBearColor || '#ff007f';
      const obBull = v.bullColor || '#00ffcc';
      const obBear = v.bearColor || '#ff007f';
      const txtCol = v.txtColor || '#e0e0e0';

      // Directional cloud fill between close and the trend-stop line.
      if (v.showFill !== false && vob.cloud.length >= 2) {
        const pts: BandPoint[] = vob.cloud.map(p => ({ time: p.time, upper: p.upper, lower: p.lower, color: withAlpha(p.trend === 1 ? cBull : cBear, 0.16) }));
        const bp = new BandPrimitive(pts, { fillColor: withAlpha(cBull, 0.16) });
        cs.attachPrimitive(bp as any); primitivesRef.current.push(bp);
      }

      // Single continuous trend-stop line — ONE series, coloured per point by
      // trend direction (teal bullish / pink bearish) via LineData per-point colour.
      if (vob.trend.length >= 2) {
        const s = chart.addSeries(LineSeries, { color: cBull, lineWidth: 2, lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false });
        s.setData(vob.trend.map(p => ({ time: p.time as Time, value: p.value, color: p.dir === 1 ? cBull : cBear })));
        overlaySeriesRefs.current.push(s);
      }

      // Order-block boxes — stronger shade on the dominant half (Buy half in a
      // bull OB, Sell half in a bear OB), matching the source script.
      if (vob.orderBlocks.length) {
        const boxes: ObBox[] = vob.orderBlocks.map(o => {
          const base = o.trend === 1 ? obBull : obBear;
          const topOp = o.trend === 1 ? 0.12 : 0.22;
          const botOp = o.trend === 1 ? 0.22 : 0.12;
          return {
            leftTime: o.leftTime, rightTime: o.rightTime, top: o.top, bottom: o.bottom, splitPrice: o.splitPrice,
            topFill: withAlpha(base, topOp), botFill: withAlpha(base, botOp), border: withAlpha(base, 0.55),
            topText: `Sell: ${o.sellPct}%`, botText: `Buy: ${o.buyPct}%`, textColor: txtCol,
          };
        });
        // Dashed connector joining consecutive order blocks so they read as one
        // continuous sequence rather than detached rectangles.
        const obp = new OrderBlockPrimitive(boxes, { connectorColor: withAlpha(txtCol, 0.5) });
        cs.attachPrimitive(obp as any); primitivesRef.current.push(obp);
      }
    }

    /* Session start vertical lines — respects settings.sessions.*. The LTF
       (intervalMinutes) decides grouping resolution: ≤15m → every session,
       ≤4h → week-start (Monday), >4h → month-start (1st of month). */
    if (settings.sessions.enabled) {
      const starts = sessionStarts(candles, auxCandles?.exchange, intervalMinutes, {
        mode: settings.sessions.mode,
        nseExpiryDow: settings.sessions.nseExpiryDow,
        nseExpiryTime: settings.sessions.nseExpiryTime,
        deltaExpiryTime: settings.sessions.deltaExpiryTime,
      });
      if (starts.length) {
        const vp = new VerticalLinePrimitive(starts, {
          color: withAlpha(settings.sessions.color, settings.sessions.opacity),
          style: settings.sessions.style,
          width: 1,
        });
        cs.attachPrimitive(vp as any);
        primitivesRef.current.push(vp);
      }
    }

    // Round 42 — keep rightOffset SMALL and FIXED at the user's setting.
    //
    // Earlier rounds widened rightOffset to fit the whole projection
    // (up to 500 bars). That pushed the latest real candle far to the
    // left — the user's "chart points at the very first candle on fresh
    // open" complaint. Per the user: showing the latest candle matters
    // more than seeing the entire projection, and the right-side empty
    // space is sacrificable.
    //
    // So: rightOffset stays at the small, view-relative gap (see plannedFit).
    // The projection bands still render — BandPrimitive computes their
    // coordinates via bar-spacing arithmetic, so the slice that falls in
    // that whitespace is visible; the rest extends off-screen and the user
    // can pan/zoom right to see it.
    const baseOffset = plannedFit(candles.length, defaultVisibleBars).gap;
    // Only force the configured right offset on a fresh fit. On a continuation
    // (auto-refresh) we restore the user's exact logical range below — which
    // includes whatever right-side whitespace they panned in — so re-applying
    // rightOffset here would fight that and snap the chart back to the right.
    if (!savedLogical) {
      try {
        chart.timeScale().applyOptions({ rightOffset: baseOffset });
      } catch { /* ignore */ }
    }

    // Remove old price lines before adding new ones
    for (const oldPl of priceLinesRef.current) {
      try { cs.removePriceLine(oldPl); } catch {}
    }
    priceLinesRef.current = [];

    // Price levels (NBUY/NSHORT/BBUY/BSHORT, MaxPain, etc.)
    if (priceLevels?.length) {
      for (const pl of priceLevels) {
        const line = cs.createPriceLine({
          price: pl.price,
          color: pl.color,
          lineWidth: 2,
          lineStyle: pl.lineStyle ?? 0,
          // FNO walls draw line-only (colour/style = meaning) — no axis tag / text.
          axisLabelVisible: !pl.noAxisLabel,
          title: pl.noAxisLabel ? '' : (pl.axisLabel ?? pl.type),
        });
        priceLinesRef.current.push(line);
      }
    }

    // FNO running level lines — per-tier stepped LineSeries (option-derived).
    if (fnoLevels?.length) {
      for (const fl of fnoLevels) {
        if (!fl.data || fl.data.length < 2) continue;
        const s = chart.addSeries(LineSeries, {
          color: fl.color, lineWidth: (fl.lineWidth || 1) as any, lineStyle: fl.lineStyle ?? 0, lineType: 1,
          // NO text at all — those D·R1 / D·GEXflip / D·MaxPain tags obstructed the
          // chart. The line colour/style carry the meaning; crosshair shows the value.
          lastValueVisible: false, title: '', priceLineVisible: false, crosshairMarkerVisible: false,
          // Never rescale the price axis: a far wall on a thin near expiry (or a jumpy γ flip)
          // squashed the candles into a strip. Off-screen levels are read from the legend.
          autoscaleInfoProvider: () => null,
        } as any);
        s.setData(gapLine(fl.data, fl.color));
        overlaySeriesRefs.current.push(s);
      }
    }

    // ── FNO view (2026-11-03) ──────────────────────────────────────────────────────
    // One meaning per pane: PCR, ATM IV and OI change each get their OWN lower pane with a
    // label + the hovered value, placed after the shared indicator pane (if any). Every
    // series breaks at a collector gap (gapLine / whitespace), gaps are shaded, expiry
    // rolls are marked.
    if (fnoView && fnoView.bars.length) {
      const fv = fnoView;
      const bars = fv.bars;
      const col = fv.colors;
      const hexA = (hex: string, a: number) => {
        const h = String(hex || '').replace('#', '');
        if (h.length !== 6) return hex;
        const n = parseInt(h, 16);
        return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
      };
      const gapSet = new Set(fv.gapTimes || []);
      const gapColor = isDark ? 'rgba(148,163,184,0.10)' : 'rgba(100,116,139,0.10)';
      const gapZones = (): VerticalZonePoint[] => bars.map(b => ({ time: b.time, color: gapSet.has(b.time) ? gapColor : 'rgba(0,0,0,0)' }));
      const shadeGaps = (host: ISeriesApi<any>) => {
        if (!gapSet.size) return;
        const z = new VerticalZonesPrimitive(gapZones());
        try { (host as any).attachPrimitive(z); primitivesRef.current.push(z); } catch { /* ignore */ }
      };
      // The shared indicator pane exists only if a non-FNO indicator put a series in it.
      let paneIdx = 1;
      try { const ps = chart.panes(); if (ps[1] && ps[1].getSeries().length > 0) paneIdx = 2; } catch { /* ignore */ }
      const legends: Array<(i: number) => void> = [];
      const idxByTime = new Map<number, number>();
      bars.forEach((b, i) => idxByTime.set(b.time, i));
      const fmtK = (v: number) => { const a = Math.abs(v); return a >= 1e6 ? `${(v / 1e6).toFixed(2)}M` : a >= 1e4 ? `${(v / 1e3).toFixed(1)}k` : Math.round(v).toLocaleString('en-IN'); };
      const signed = (v: number) => `${v > 0 ? '+' : v < 0 ? '−' : '±'}${fmtK(Math.abs(v))}`;
      const noData = { text: 'no data', color: undefined } as PaneLegendItem;

      // PCR (OI) — baseline at 1.0: green above (put-heavy = writers defend below price),
      // red below (call-heavy = writers cap above price).
      if (fv.pcr) {
        const up = col.put, dn = col.call;
        const s = chart.addSeries(BaselineSeries, {
          baseValue: { type: 'price', price: 1 }, lineWidth: 2, lineType: 1,
          topLineColor: up, topFillColor1: hexA(up, 0.22), topFillColor2: hexA(up, 0.03),
          bottomLineColor: dn, bottomFillColor1: hexA(dn, 0.03), bottomFillColor2: hexA(dn, 0.22),
          priceScaleId: 'fnoPcr', lastValueVisible: true, priceLineVisible: false, crosshairMarkerVisible: false, title: '',
          priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
        } as any, paneIdx);
        const T = 'rgba(0,0,0,0)';
        const pts = gapLine(bars.map(b => ({ time: b.time, value: fv.pcr!.line && b.s ? b.s.pcr : null })), '#000')
          .map((p: any) => (p.color === T ? { time: p.time, value: p.value, topLineColor: T, bottomLineColor: T, topFillColor1: T, topFillColor2: T, bottomFillColor1: T, bottomFillColor2: T } : { time: p.time, value: p.value }));
        s.setData(pts);
        try { s.createPriceLine({ price: 1, color: isDark ? 'rgba(201,209,217,0.45)' : 'rgba(51,51,51,0.4)', lineWidth: 1, lineStyle: 2, axisLabelVisible: false, title: '' }); } catch { /* ignore */ }
        cfgScale(s, true, true, { top: 0.1, bottom: 0.1 }, up);
        overlaySeriesRefs.current.push(s as any);
        if (fv.pcr.ema && fv.pcr.ema.length >= 2) {
          const e = chart.addSeries(LineSeries, { color: col.pcrEma, lineWidth: 1, lineType: 0, priceScaleId: 'fnoPcr', lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false, title: '' }, paneIdx);
          e.setData(gapLine(fv.pcr.ema, col.pcrEma));
          overlaySeriesRefs.current.push(e);
        }
        shadeGaps(s);
        const lg = new PaneLegendPrimitive('PCR (OI)', isDark); lg.leftInset = overlayLeftInset || 0;
        try { (s as any).attachPrimitive(lg); primitivesRef.current.push(lg as any); } catch { /* ignore */ }
        legends.push(i => {
          const v = bars[i]?.s?.pcr;
          lg.setItems(v == null ? [noData] : [
            { text: v.toFixed(2), color: v >= 1 ? up : dn, bold: true },
            { text: v >= 1 ? 'put-heavy · support below' : 'call-heavy · cap above' },
          ]);
        });
        fnoPaneSeriesRef.current.pcr = s;
        paneIdx++;
      }

      // ATM IV % — the options market's volatility price for the selected expiry.
      if (fv.iv) {
        const s = chart.addSeries(LineSeries, {
          color: col.iv, lineWidth: 2, lineType: 1, priceScaleId: 'fnoIv',
          lastValueVisible: true, priceLineVisible: false, crosshairMarkerVisible: false, title: '',
          priceFormat: { type: 'price', precision: 1, minMove: 0.1 },
        }, paneIdx);
        s.setData(gapLine(bars.map(b => ({ time: b.time, value: fv.iv!.line && b.s?.iv != null ? b.s.iv * 100 : null })), col.iv));
        cfgScale(s, true, true, { top: 0.1, bottom: 0.1 }, col.iv);
        overlaySeriesRefs.current.push(s as any);
        if (fv.iv.ema && fv.iv.ema.length >= 2) {
          const e = chart.addSeries(LineSeries, { color: col.ivEma, lineWidth: 1, lineType: 0, priceScaleId: 'fnoIv', lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false, title: '' }, paneIdx);
          e.setData(gapLine(fv.iv.ema, col.ivEma));
          overlaySeriesRefs.current.push(e);
        }
        shadeGaps(s);
        const lg = new PaneLegendPrimitive('ATM IV', isDark); lg.leftInset = overlayLeftInset || 0;
        try { (s as any).attachPrimitive(lg); primitivesRef.current.push(lg as any); } catch { /* ignore */ }
        legends.push(i => {
          const b = bars[i];
          const v = b?.s?.iv;
          if (v == null) { lg.setItems([noData]); return; }
          // change over the last 12 bars with data, so a rising / falling vol regime is obvious
          let k = i - 12; while (k < i && !(bars[k]?.s?.iv != null)) k++;
          const ref = k < i ? bars[k]!.s!.iv! : null;
          const ch = ref != null ? (v - ref) * 100 : null;
          lg.setItems([
            { text: `${(v * 100).toFixed(1)}%`, color: col.iv, bold: true },
            ...(ch != null && Math.abs(ch) >= 0.05 ? [{ text: `${ch > 0 ? '▲' : '▼'} ${Math.abs(ch).toFixed(1)} pts / 12 bars` }] : []),
          ]);
        });
        fnoPaneSeriesRef.current.iv = s;
        paneIdx++;
      }

      // OI — NET change per candle (puts added minus calls added): green = writers adding
      // SUPPORT under price, red = writers adding RESISTANCE over it. 'total' = both totals.
      if (fv.oi) {
        let host: ISeriesApi<any>;
        if (fv.oi.mode === 'total') {
          const ce = chart.addSeries(LineSeries, { color: col.call, lineWidth: 2, lineType: 1, priceScaleId: 'fnoOi', lastValueVisible: true, priceLineVisible: false, crosshairMarkerVisible: false, title: '', priceFormat: { type: 'volume' } }, paneIdx);
          ce.setData(gapLine(bars.map(b => ({ time: b.time, value: b.s?.ce ?? null })), col.call));
          const pe = chart.addSeries(LineSeries, { color: col.put, lineWidth: 2, lineType: 1, priceScaleId: 'fnoOi', lastValueVisible: true, priceLineVisible: false, crosshairMarkerVisible: false, title: '', priceFormat: { type: 'volume' } }, paneIdx);
          pe.setData(gapLine(bars.map(b => ({ time: b.time, value: b.s?.pe ?? null })), col.put));
          cfgScale(ce, true, true, { top: 0.1, bottom: 0.1 }, col.put);
          overlaySeriesRefs.current.push(ce, pe);
          host = ce;
        } else {
          const h = chart.addSeries(HistogramSeries, { priceScaleId: 'fnoOi', lastValueVisible: false, priceLineVisible: false, title: '', base: 0, priceFormat: { type: 'volume' } } as any, paneIdx);
          h.setData(bars.map(b => {
            if (b.dce == null || b.dpe == null) return { time: b.time as Time };
            const v = b.dpe - b.dce;
            return { time: b.time as Time, value: v, color: v >= 0 ? hexA(col.put, 0.8) : hexA(col.call, 0.8) };
          }) as any);
          cfgScale(h, true, true, { top: 0.1, bottom: 0.1 }, col.put);
          try { h.createPriceLine({ price: 0, color: isDark ? 'rgba(201,209,217,0.35)' : 'rgba(51,51,51,0.3)', lineWidth: 1, lineStyle: 0, axisLabelVisible: false, title: '' }); } catch { /* ignore */ }
          overlaySeriesRefs.current.push(h as any);
          host = h;
        }
        shadeGaps(host);
        const lg = new PaneLegendPrimitive(fv.oi.mode === 'total' ? 'Open interest' : 'OI change', isDark); lg.leftInset = overlayLeftInset || 0;
        try { (host as any).attachPrimitive(lg); primitivesRef.current.push(lg as any); } catch { /* ignore */ }
        legends.push(i => {
          const b = bars[i];
          if (!b?.s || b.s.ce == null || b.s.pe == null) { lg.setItems([noData]); return; }
          if (fv.oi!.mode === 'total') {
            lg.setItems([{ text: `Calls ${fmtK(b.s.ce)}`, color: col.call }, { text: `Puts ${fmtK(b.s.pe)}`, color: col.put }]);
            return;
          }
          if (b.dce == null || b.dpe == null) { lg.setItems([{ text: b.roll ? `rolled to ${fmtExpiry(b.roll)}` : 'no change data' }]); return; }
          const net = b.dpe - b.dce;
          lg.setItems([
            { text: `net ${signed(net)}`, color: net >= 0 ? col.put : col.call, bold: true },
            { text: `puts ${signed(b.dpe)} · calls ${signed(b.dce)}` },
          ]);
        });
        fnoPaneSeriesRef.current.oi = host;
        paneIdx++;
      }

      // Price pane: collector gaps, roll markers, bands, γ-regime tint.
      if (gapSet.size) {
        const z = new VerticalZonesPrimitive(gapZones());
        try { (cs as any).attachPrimitive(z); primitivesRef.current.push(z); } catch { /* ignore */ }
      }
      // A payoff zone (your position's profit / stop region) owns the background: the γ-regime
      // tint (same teal) and the call-wall↔put-wall fill would mix with it into an unreadable
      // shade, so both step aside while a zone is drawn — the two wall LINES still mark the range.
      const zonesOwnFill = !!priceZones?.length;
      if (fv.regime && fv.regime.length && !zonesOwnFill) {
        const pos = isDark ? 'rgba(38,166,154,0.06)' : 'rgba(38,166,154,0.07)';
        const neg = isDark ? 'rgba(255,152,0,0.07)' : 'rgba(255,152,0,0.08)';
        const bySign = new Map(fv.regime.map(r => [r.time, r.sign]));
        const z = new VerticalZonesPrimitive(bars.map(b => ({ time: b.time, color: bySign.get(b.time) === 1 ? pos : bySign.get(b.time) === -1 ? neg : 'rgba(0,0,0,0)' })));
        try { (cs as any).attachPrimitive(z); primitivesRef.current.push(z); } catch { /* ignore */ }
      }
      for (const band of fv.bands || []) {
        if (band.points.length < 2) continue;
        if (zonesOwnFill && band.key.startsWith('oiRange')) continue;
        const anchor = chart.addSeries(LineSeries, { color: 'rgba(0,0,0,0)', lineWidth: 1, lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false, autoscaleInfoProvider: () => null } as any);
        anchor.setData(band.points.map(p => ({ time: p.time as Time, value: p.upper })));
        const bp = new BandPrimitive(band.points, { fillColor: band.color, step: true, zOrder: 'bottom' });
        try { (anchor as any).attachPrimitive(bp); primitivesRef.current.push(bp); } catch { /* ignore */ }
        overlaySeriesRefs.current.push(anchor);
      }
      if (fv.rolls && fv.rolls.length) {
        const vl = new VerticalLinePrimitive(fv.rolls.map(r => r.time), { color: isDark ? 'rgba(201,209,217,0.35)' : 'rgba(51,51,51,0.3)', width: 1, style: 'dotted' });
        try { (cs as any).attachPrimitive(vl); primitivesRef.current.push(vl); } catch { /* ignore */ }
        // Labels: an invisible host on the price pane carrying each roll bar's high, with a
        // marker above it ("D0 → 04 Nov").
        const highBy = new Map<number, number>();
        for (const c of candles) highBy.set(c.time as number, Number(c.high));
        const pts = fv.rolls.filter(r => highBy.has(r.time)).map(r => ({ time: r.time as Time, value: highBy.get(r.time)! }));
        if (pts.length) {
          const host = chart.addSeries(LineSeries, { color: 'rgba(0,0,0,0)', lineWidth: 1, lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false, autoscaleInfoProvider: () => null } as any);
          host.setData(pts);
          const lab = new Map(fv.rolls.map(r => [r.time, r.text]));
          try {
            createSeriesMarkers(host, pts.map(p => ({ time: p.time, position: 'aboveBar', shape: 'arrowDown', size: 0.6, color: isDark ? '#9aa4af' : '#5d6670', text: lab.get(p.time as number) || '' })) as any);
          } catch { /* ignore */ }
          overlaySeriesRefs.current.push(host);
        }
      }
      if (legends.length) {
        const latest = bars.length - 1;
        const update = (t: number | null) => {
          const i = t == null ? latest : (idxByTime.get(t) ?? latest);
          for (const fn of legends) fn(i);
        };
        update(null);
        fnoPaneLegendRef.current = update;
      }
    }

    // Shaded horizontal payoff zones (options overlay) — behind candles.
    if (priceZones?.length) {
      const zprim = new HorizontalZonesPrimitive(priceZones);
      try { (cs as any).attachPrimitive(zprim); primitivesRef.current.push(zprim); } catch {}
    }

    // Live basket P&L headline — pinned bottom-left, on top of everything.
    pnlBadgePrimRef.current = null;
    if (pnlBadge && Number.isFinite(pnlBadge.value)) {
      const pprim = new PnlBadgePrimitive(pnlBadge);
      try { (cs as any).attachPrimitive(pprim); primitivesRef.current.push(pprim); pnlBadgePrimRef.current = pprim; } catch {}
    }

    // Payoff levels (TP/SL/BE/Max-P) — lines with LEFT-pinned labels (right side
    // stays clear for the option-profile rail).
    if (leftLevels?.length) {
      const lprim = new LeftLabeledLevelsPrimitive(leftLevels);
      try { (cs as any).attachPrimitive(lprim); primitivesRef.current.push(lprim); } catch {}
    }

    // Strategy + signal-indicator markers
    if (markersPluginRef.current) {
      try { markersPluginRef.current.detach(); } catch {}
      markersPluginRef.current = null;
    }
    if (candles.length) {
      const candleTimes = new Set<number>(candles.map(c => c.time as number));
      const minT = candles[0].time as number;
      const maxT = candles[candles.length - 1].time as number;
      const inRange = (t: number) => Number.isFinite(t) && t >= minT && t <= maxT && candleTimes.has(t);

      // Display-time array (sorted) for signal-time → display-bar mapping.
      // For Regular / HA / CSL / CLH the raw signal time matches a display
      // bar time exactly (those transforms preserve source time). For any future
      // the display series is range-based and times don't match raw times,
      // so we snap each signal time to the smallest display bar time ≥ it
      // (the range candle that was forming when the raw signal fired).
      //
      // Round 25 fix — when the raw signal time is older than the first
      // visible bar (i.e. the user is looking at a sub-window of history
      // because the chart hasn't lazy-loaded older bars yet), the previous
      // binary-search returned displayTimes[0] for ALL such old signals.
      // Lightweight-charts then stacked every one of them on bar 0,
      // producing the vertical pile of markers the user reported. Now we
      // explicitly drop signals whose raw time predates the visible
      // window — they will reappear naturally if/when the user scrolls
      // left and the chart fetches the older bars (signal block re-runs).
      const displayTimes: number[] = candles.map(c => c.time as number);
      const firstDisplayT = displayTimes[0];
      const snapToDisplay = (t: number): number | null => {
        if (t < firstDisplayT) return null;
        if (candleTimes.has(t)) return t; // common case (Regular/HA/CSL/CLH)
        // Binary search for smallest display time ≥ t.
        let lo = 0, hi = displayTimes.length;
        while (lo < hi) {
          const mid = (lo + hi) >> 1;
          if (displayTimes[mid] < t) lo = mid + 1; else hi = mid;
        }
        if (lo >= displayTimes.length) return null;
        return displayTimes[lo];
      };

      type MK = { time: Time; position: 'aboveBar' | 'belowBar' | 'inBar'; color: string; shape: 'arrowUp' | 'arrowDown' | 'circle' | 'square'; text: string; size?: number };
      const all: MK[] = [];

      // 1) Existing strategy markers — R115 honours an optional `size` field
      //    (RockBand now emits size 2 on BUY/SHORT arrows so the new short B/S
      //    labels read clearly against the candle stream).
      if (strategyOverlay?.markers?.length) {
        for (const m of strategyOverlay.markers) {
          if (!inRange(m.time as number)) continue;
          all.push({
            time: m.time as Time,
            position: m.position as 'aboveBar' | 'belowBar' | 'inBar',
            color: m.color,
            shape: m.shape as 'arrowUp' | 'arrowDown' | 'circle' | 'square',
            text: m.text,
            size: (m as any).size,
          });
        }
      }

      // 2) Signal indicators — computed on raw OHLC so positions & values
      //    are stable across display candle types. `latestCandle` (forming)
      //    is excluded so signals are repaint-safe. Each signal marker is
      //    snapped to the closest display bar time and uses size:2 to read
      //    clearly against the candle stream.
      const sigSrc: CandleRow[] = (rawCandles && rawCandles.length) ? rawCandles : candles;
      const closed = (latestCandle && sigSrc.length && sigSrc[sigSrc.length - 1].time === latestCandle.time)
        ? sigSrc.slice(0, -1)
        : sigSrc.slice();

      const SIG_BUY  = '#26a69a';
      const SIG_SHORT = '#ef5350';
      const SIG_SIZE = 2;

      const pushSignal = (
        rawTime: number,
        side: 'BUY' | 'SHORT',
        shape: 'arrowUp' | 'arrowDown' | 'circle' | 'square',
        text: string,
        // Exit marks are neither a BUY nor a SHORT: they close one. They need
        // their own colour (blue for the level exit, amber for a stop) so the
        // outcome is readable without hovering, which the side-derived palette
        // cannot express.
        colorOverride?: string,
      ) => {
        const dt = snapToDisplay(rawTime);
        if (dt == null) return;
        all.push({
          time: dt as Time,
          position: side === 'BUY' ? 'belowBar' : 'aboveBar',
          color: colorOverride || (side === 'BUY' ? SIG_BUY : SIG_SHORT),
          shape,
          text,
          size: SIG_SIZE,
        });
      };

      // BBR x Wave. Computed up in the oscillator block (that is where both series
      // live); pushed here because pushSignal and the shared marker array are here.
      //
      // 'BW' = the long conjunction, never confused with Wave's own 'W'.
      // 'BD' = the momentum-BreakDown short, a different rule from a different
      //        mechanism, so it gets a different label rather than a mirrored one.
      // 'X'  = the paired exit, coloured by WHY it closed.
      try {
        if (bbrWaveSignals) {
          const bw = (settings as any)?.bbrWave || {};
          for (const t of bbrWaveSignals.buys)   pushSignal(t, 'BUY',   'arrowUp',   'BW');
          for (const t of bbrWaveSignals.shorts) pushSignal(t, 'SHORT', 'arrowDown', 'BD');
          /* Exit colours are theme-aware. The stored defaults are tuned for the
           * light chart, and on the dark chart a blue-800 square reads as a dim
           * smudge against #0D1117 - the dark shot showed exactly that. A value
           * still sitting at its default is lifted to the dark counterpart; a
           * colour the user actually PICKED is left as picked, because silently
           * overriding a deliberate choice is worse than a dim square. */
          const DARK_EXIT: Record<string, string> = {
            '#1565c0': '#42a5f5', '#ef6c00': '#ffa726', '#607d8b': '#b0bec5',
          };
          const themed = (c: string) =>
            (isDark && DARK_EXIT[String(c).toLowerCase()]) || c;
          for (const e of bbrWaveSignals.exits) {
            // An exit sits on the opposite side of the bar from its entry, so the
            // pair never collides on the same candle.
            pushSignal(
              e.time, e.side === 'BUY' ? 'SHORT' : 'BUY', 'square', 'X',
              themed(e.why === 'stop' ? (bw.stopColor || '#ef6c00')
                : e.why === 'time' ? (bw.timeColor || '#607d8b')
                : (bw.exitColor || '#1565c0')),
            );
          }
        }
      } catch { /* skip */ }

      try {
        if (overlays.fllhh && closed.length >= 6) {
          const sig = computeFLLHHSignals(closed);
          for (const t of sig.buys)   pushSignal(t, 'BUY',   'arrowUp',   'F');
          for (const t of sig.shorts) pushSignal(t, 'SHORT', 'arrowDown', 'F');
        }
      } catch { /* skip on error */ }

      try {
        if (overlays.fbo && closed.length >= 25) {
          const period = settings.fbo?.period || 20;
          const sig = computeFBOSignals(closed, period);
          for (const t of sig.buys)   pushSignal(t, 'BUY',   'circle', 'B');
          for (const t of sig.shorts) pushSignal(t, 'SHORT', 'circle', 'B');
        }
      } catch { /* skip */ }

      try {
        if (overlays.wave && closed.length >= 30) {
          const cl = settings.wave?.channelLength || 10;
          const al = settings.wave?.averageLength || 21;
          /* Wave's own arrows read the same source as Wave's curve. `closed` is
           * already RAW (sigSrc) and spans the full history rather than the
           * drawn window, so it is transformed directly rather than joined to
           * the window above. */
          const wSrc = bwSrcType === 'Regular' ? closed
            : indicatorSourceCandles(closed, bwSrcType, bwClnLen);
          const sig = computeWaveSignals(wSrc, cl, al);
          for (const t of sig.buys)   pushSignal(t, 'BUY',   'square', 'W');
          for (const t of sig.shorts) pushSignal(t, 'SHORT', 'square', 'W');
        }
      } catch { /* skip */ }

      // LRC — render via the same createSeriesMarkers pipeline as the
      // other signal indicators so BUY/SHORT arrows are equally visible.
      // The marker is anchored to the candle's high/low (above/below) at
      // the cross bar — same convention as FLLHH/FBO/Wave.
      try {
        if (overlays.lrc && closed.length >= 5) {
          const period: CprPeriod = auxCandles?.cprPeriod || 'D';
          // LRC uses its OWN candle type (Chart Settings → LRC), independent of
          // the chart's display candle type. C-POC cross = ★, H-POC cross = ★★.
          const lrcType = (settings as any)?.lrc?.candleType || 'Regular';
          const sig = computeLRCSignals(closed, period, auxCandles?.exchange, lrcType);
          for (const m of sig.buys)   pushSignal(m.time, 'BUY',   'arrowUp',   m.double ? '★★' : '★');
          for (const m of sig.shorts) pushSignal(m.time, 'SHORT', 'arrowDown', m.double ? '★★' : '★');
        }
      } catch { /* skip */ }

      try {
        // ASH v3.1 — needs 2*Length + Smooth + 2 = 51 closed bars before any
        // signal can fire; gate matches the warmup inside computeASHSignals.
        if (overlays.as && closed.length >= 51) {
          const sig = computeASHSignals(closed);
          for (const t of sig.buys)   pushSignal(t, 'BUY',   'arrowUp',   'A');
          for (const t of sig.shorts) pushSignal(t, 'SHORT', 'arrowDown', 'A');
        }
      } catch { /* skip */ }

      // Blast — fresh close-cross of the C-POC band while an Inside/Engulfing
      // band (Daily ≤1D, Weekly ≤1W) is active. Windows are rebuilt from the
      // Daily/Weekly aux candles (same detector that draws the bands above).
      try {
        // Blast signals are user-toggleable (Chart Settings → Blast → Show signals).
        if (overlays.blast && (settings as any)?.blast?.showSignals !== false && closed.length >= 5) {
          const ltfMin = intervalMinutes || 1;
          const windows: BlastWindow[] = [];
          if (ltfMin <= 1440 && auxCandles?.daily?.length) {
            windows.push(...blastActiveWindows(detectBlastEvents(closedPeriodCandles(auxCandles.daily, 86400)), 86400, 2));
          }
          if (ltfMin <= 10080 && auxCandles?.weekly?.length) {
            windows.push(...blastActiveWindows(detectBlastEvents(closedPeriodCandles(auxCandles.weekly, 604800)), 604800, 2));
          }
          if (windows.length) {
            const period: CprPeriod = auxCandles?.cprPeriod || 'D';
            const sig = computeBlastSignals(closed, windows, period, auxCandles?.exchange);
            for (const t of sig.buys)   pushSignal(t, 'BUY',   'arrowUp',   'BL');
            for (const t of sig.shorts) pushSignal(t, 'SHORT', 'arrowDown', 'BL');
          }
        }
      } catch { /* skip */ }

      // VOB retests — small crosses (rendered as circles) at OB retests, coloured
      // by the configured signal colours. vobRetests was computed in the VOB band
      // block above (closed raw candles).
      try {
        if (overlays.vob && vobRetests) {
          const V = (settings as any)?.vob || {};
          const bullSig = V.bullSigColor || '#00ffcc';
          const bearSig = V.bearSigColor || '#ff007f';
          for (const t of vobRetests.buys)   { const dt = snapToDisplay(t); if (dt != null) all.push({ time: dt as Time, position: 'belowBar', color: bullSig, shape: 'circle', text: '', size: 1 }); }
          for (const t of vobRetests.shorts) { const dt = snapToDisplay(t); if (dt != null) all.push({ time: dt as Time, position: 'aboveBar', color: bearSig, shape: 'circle', text: '', size: 1 }); }
        }
      } catch { /* skip */ }

      // VPA — Volume Spread Analysis markers. Each VSA signal carries its own
      // shape / colour / above-or-below placement and label (UT1, NS, BC…), so
      // it bypasses pushSignal (BUY/SHORT only) and pushes directly. Times are
      // snapped to display bars so markers stay put across all candle types.
      try {
        if (overlays.vpa && closed.length >= VPA_MIN_BARS) {
          const vToggles = (settings as any)?.vpa || VPA_TOGGLE_DEFAULTS;
          for (const m of computeVpaMarkers(closed, vToggles)) {
            const dt = snapToDisplay(m.time);
            if (dt == null) continue;
            all.push({ time: dt as Time, position: m.position, color: m.color, shape: m.shape, text: m.text, size: 1 });
          }
        }
      } catch { /* skip */ }

      // Flow markers (triangles + absorption stars) were removed in R67h —
      // user reported they cluttered the chart. All per-bar bias info is
      // conveyed by the coloured ribbon at the bottom + the hover tooltip.

      // S/R Break / Respect markers (R140) — CLH body vs the HTF S/R lines.
      // Gated on the S/R overlay + the HLDiff method's HTF stream (the markers
      // are defined against the HTF levels) + the markers toggle.
      try {
        const scfg = (settings as any)?.sr || {};
        const mk = scfg.markers || {};
        if (overlays.sr && mk.enabled
          && scfg.methods?.hlDiff?.enabled !== false && scfg.methods?.hlDiff?.useHtf !== false
          && auxCandles?.srHtf && auxCandles.srHtf.length && closed.length > 10) {
          const hcfg = (settings as any)?.hlDiff || {};
          const events = computeSrBreakRespectEvents(
            closed as any,
            auxCandles.srHtf as any,
            { candleType: String(hcfg.candleType || 'HA'), emaPeriod: Math.max(1, Math.min(500, Math.floor(Number(hcfg.emaPeriod) || 21))) },
            auxCandles.srHtfMinutes || 0,
            {
              bufFrac: Number(mk.bufFrac ?? 0.12), window: Math.floor(Number(mk.window ?? 3)),
              holds: Math.floor(Number(mk.holds ?? 2)), cooldown: Math.floor(Number(mk.cooldown ?? 2)),
              testLookback: Math.floor(Number(mk.testLookback ?? 3)),
              showConfirmed: mk.showConfirmed !== false, showFalse: mk.showFalse !== false, showRespected: mk.showRespected !== false,
            },
          );
          const TRAP = '#ff9800';
          for (const ev of events) {
            const dt = snapToDisplay(ev.time);
            if (dt == null) continue;
            switch (ev.kind) {
              case 'CONF_UP':   all.push({ time: dt as Time, position: 'belowBar', color: SIG_BUY,   shape: 'arrowUp',   text: 'Brk↑', size: 2 }); break;
              case 'CONF_DOWN': all.push({ time: dt as Time, position: 'aboveBar', color: SIG_SHORT, shape: 'arrowDown', text: 'Brk↓', size: 2 }); break;
              case 'FALSE_UP':  all.push({ time: dt as Time, position: 'aboveBar', color: TRAP,      shape: 'arrowDown', text: 'Trap', size: 2 }); break;
              case 'FALSE_DOWN':all.push({ time: dt as Time, position: 'belowBar', color: TRAP,      shape: 'arrowUp',   text: 'Trap', size: 2 }); break;
              case 'REJECT':    all.push({ time: dt as Time, position: 'aboveBar', color: SIG_SHORT, shape: 'circle',    text: 'Rej',  size: 1 }); break;
              case 'BOUNCE':    all.push({ time: dt as Time, position: 'belowBar', color: SIG_BUY,   shape: 'circle',    text: 'Bnc',  size: 1 }); break;
            }
          }
        }
      } catch { /* skip */ }

      if (all.length) {
        // Defensive de-dupe by (time, position, color, shape, text). If a
        // computation runs twice (e.g. a parent re-render briefly fed the
        // same signals through the strategy-overlay markers AND the
        // signal-indicator pipeline), this prevents stacked duplicates.
        const seen = new Set<string>();
        const dedup: typeof all = [];
        for (const m of all) {
          const k = `${m.time}|${m.position}|${m.color}|${m.shape}|${m.text}`;
          if (seen.has(k)) continue;
          seen.add(k);
          dedup.push(m);
        }
        dedup.sort((a, b) => (a.time as number) - (b.time as number));
        markersPluginRef.current = createSeriesMarkers(cs, dedup);
      }
    }

    // Strategy overlay: extra lines
    if (strategyOverlay?.lines?.length) {
      for (const line of strategyOverlay.lines) {
        const ls = chart.addSeries(LineSeries, {
          color: line.color,
          lineWidth: line.lineWidth as 1 | 2 | 3 | 4,
          lineStyle: line.lineStyle ?? 0,
          lastValueVisible: false,
          priceLineVisible: false,
        });
        // Non-finite values are WHITESPACE points → the line breaks instead of
        // connecting across flat stretches (Bhairav's trail gaps while flat).
        ls.setData(line.data.map(d => (Number.isFinite(d.value)
          ? { time: d.time as Time, value: d.value }
          : ({ time: d.time as Time } as any))));
        overlaySeriesRefs.current.push(ls);
      }
    }

    /* Strategy trade levels (Gandiv): its band as the rule saw it, and each
     * trade's Entry and SL from the entry bar to the exit bar. Drawn with
     * BarLevelPrimitive so a level spans the bar it governed — a WithSteps line
     * puts the left half of every candle under the PREVIOUS level, and these
     * lines exist to check a close against a level by eye.
     *
     * One host series per KIND, not per trade: a long history holds hundreds of
     * trades, and a series each would be hundreds of series. The host's own line
     * is hidden and it is kept out of the autoscale — a chained SL can sit far
     * from price and must not squash the candles. The OPEN trade gets its own
     * small series so its Entry and SL carry axis labels. */
    if (strategyOverlay?.tradeLevels?.length) {
      const g = (settings as any)?.gandiv || {};
      const gw = Math.max(1, Math.min(4, Math.floor(Number(g.lineWidth) || 2)));
      const col = (v: unknown, d: string): string => (typeof v === 'string' && v ? v : d);
      const bandUp = col(g.bandUpColor, GANDIV_DEFAULTS.bandUpColor);
      const bandDn = col(g.bandDownColor, GANDIV_DEFAULTS.bandDownColor);
      const longC = col(g.longColor, GANDIV_DEFAULTS.longColor);
      const shortC = col(g.shortColor, GANDIV_DEFAULTS.shortColor);
      const slC = col(g.slColor, GANDIV_DEFAULTS.slColor);
      const showLabels = g.showLabels !== false;
      const lastShown = candles.length ? Number(candles[candles.length - 1].time) : 0;
      const labelBg = isDark ? 'rgba(13,17,23,0.72)' : 'rgba(255,255,255,0.78)';
      const host = (pts: BarLevelPoint[], color: string, width: number, style: number) => {
        if (!pts.length) return;
        const ser = chart.addSeries(LineSeries, {
          color, lineWidth: 1, lineVisible: false, lastValueVisible: false, priceLineVisible: false,
          crosshairMarkerVisible: false, autoscaleInfoProvider: () => null,
        });
        // One point per time is all a series accepts; the primitive owns the
        // real (possibly overlapping) points.
        const seen = new Set<number>();
        const data: LineData<Time>[] = [];
        for (const p of pts) if (!seen.has(p.time)) { seen.add(p.time); data.push({ time: p.time as Time, value: p.value }); }
        data.sort((a, b) => Number(a.time) - Number(b.time));
        ser.setData(data);
        (ser as any).attachPrimitive(new BarLevelPrimitive(pts, { color, width, style, labelBg }));
        overlaySeriesRefs.current.push(ser);
      };
      const bandPts: Record<'bandUpper' | 'bandLower', BarLevelPoint[]> = { bandUpper: [], bandLower: [] };
      const entryPts: BarLevelPoint[] = [];
      const slPts: BarLevelPoint[] = [];
      const openLabels: Array<{ value: number; color: string; title: string }> = [];
      for (const seg of strategyOverlay.tradeLevels) {
        if (seg.kind === 'bandUpper' || seg.kind === 'bandLower') {
          if (g.showBand === false) continue;
          for (const p of seg.points) bandPts[seg.kind].push({ time: p.time, value: p.value, color: p.up ? bandUp : bandDn });
          continue;
        }
        const isEntry = seg.kind === 'entry';
        if (isEntry ? g.showEntry === false : g.showSl === false) continue;
        const color = isEntry ? (seg.side === 'SHORT' ? shortC : longC) : slC;
        const out = isEntry ? entryPts : slPts;
        const pts = seg.points.slice();
        // The position still held runs on through the forming bar, whose level
        // is already fixed — carrying it one bar is exact, not an extrapolation.
        if (seg.open && pts.length && lastShown > pts[pts.length - 1].time) {
          pts.push({ time: lastShown, value: pts[pts.length - 1].value });
        }
        pts.forEach((p, i) => out.push({
          time: p.time, value: p.value, color,
          ...(i === 0 ? { newSeg: true } : {}),
          ...(i === 0 && showLabels && seg.label ? { label: seg.label, labelBelow: !!seg.labelBelow } : {}),
        }));
        if (seg.open && pts.length) {
          openLabels.push({ value: pts[0].value, color, title: isEntry ? 'Entry' : 'SL' });
        }
      }
      // The band FILL, coloured by direction like the HTF Band chip, spanning each
      // bar exactly as the edge lines do (BarBandPrimitive, not BandPrimitive,
      // which interpolates between points and would sit half a bar off).
      const bandFillOp = Number.isFinite(Number(g.bandOpacity)) ? Math.max(0, Math.min(0.4, Number(g.bandOpacity))) : 0.12;
      if (g.showBand !== false && bandFillOp > 0) {
        const upSeg = strategyOverlay.tradeLevels.find(x => x.kind === 'bandUpper');
        const loSeg = strategyOverlay.tradeLevels.find(x => x.kind === 'bandLower');
        if (upSeg && loSeg && upSeg.points.length === loSeg.points.length && upSeg.points.length) {
          const fillPts = upSeg.points.map((pu, k) => ({
            time: pu.time, upper: pu.value, lower: loSeg.points[k].value,
            color: withAlpha(pu.up ? bandUp : bandDn, bandFillOp),
          }));
          const fser = chart.addSeries(LineSeries, {
            color: bandUp, lineWidth: 1, lineVisible: false, lastValueVisible: false, priceLineVisible: false,
            crosshairMarkerVisible: false, autoscaleInfoProvider: () => null,
          });
          fser.setData(fillPts.map(q => ({ time: q.time as Time, value: q.upper })));
          (fser as any).attachPrimitive(new BarBandPrimitive(fillPts));
          overlaySeriesRefs.current.push(fser);
        }
      }
      host(bandPts.bandUpper, bandUp, 1, 0);
      host(bandPts.bandLower, bandUp, 1, 0);
      host(entryPts, longC, gw, 0);
      host(slPts, slC, gw, 2);
      for (const l of openLabels) {
        const ser = chart.addSeries(LineSeries, {
          color: l.color, lineWidth: 1, lineVisible: false, lastValueVisible: true, priceLineVisible: false,
          crosshairMarkerVisible: false, title: l.title,
        });
        if (lastShown) ser.setData([{ time: lastShown as Time, value: l.value }]);
        overlaySeriesRefs.current.push(ser);
      }
    }

    // First-load zoom: prefer setVisibleLogicalRange over fitContent so the
    // chart shows up to `defaultVisibleBars` worth of recent bars at a normal
    // bar-width. fitContent() stretches whatever is loaded across the pane,
    // making 2-bar histories (recently-listed Delta tokens, weekly/monthly
    // charts) look like bands rather than candles. With a logical range from
    // (length - defaultVisibleBars) to (length + a few rightOffset bars) the
    // newest bars sit at the right edge and any unfilled space lives to the
    // left — same UX as TradingView.
    // Mark the range-sets below as programmatic so the "remember zoom"
    // subscriber ignores them (it must capture genuine user scroll-zoom only).
    programmaticRangeRef.current = true;
    // A verified first-fit re-applies across several frames, so the guard can no
    // longer be released on a fixed timer — it is released when the fit settles.
    // The token makes that release a no-op if a newer applyData pass has since
    // taken ownership, which would otherwise re-open the window mid-retry and
    // let our own programmatic sets be captured as the user's zoom.
    const fitToken = ++programmaticTokenRef.current;
    const settleFit = () => {
      if (programmaticTokenRef.current !== fitToken) return;
      programmaticRangeRef.current = false;
      fitSettledRef.current = true;
    };
    let fitPending = false;
    if (!firstFitDoneRef.current && candles.length > 0) {
      fitPending = true;
      fitSettledRef.current = false;
      const ts = chart.timeScale();
      const { want, gap } = plannedFit(candles.length, defaultVisibleBars);
      // Show the LAST `want` candles with `gap` empty slots after the newest
      // one. The last candle sits at index length-1, so `to` is length-1+gap —
      // and the whitespace series above materialises exactly those slots, which
      // is what keeps the crosshair's time label alive out there.
      const target = { from: candles.length - want, to: candles.length - 1 + gap };

      // MEASURED 2026-09-04, and the reason this is not a plain set:
      // a setVisibleLogicalRange issued in the SAME synchronous block as
      // cs.setData() is silently dropped by lightweight-charts. Instrumented on
      // the live app, a 504-bar 4H chart asked for {354,524} and read back
      // {265,503} immediately, on the next frame, and 900ms later — the library
      // default barSpacing 6 with the last candle flush against the right edge.
      // The SAME call re-issued on a later pass sticks: a 34-bar monthly chart
      // (which never locks first-fit, so the block re-runs) had its first set
      // dropped and its second honoured. That is the entire bug — dense charts
      // locked on the one pass whose set was lost, so the app's zoom and its
      // right-side gap never reached the chart at all, on any interval with
      // >= 50 bars.
      //
      // So: set it, then PROVE it landed on the next frame, and re-apply until
      // the chart reports what we asked for. See applyRangeVerified above — the
      // continuation branch below now goes through the same helper, which it did not
      // before, and that was the drift the owner was seeing on the lower-timeframe pane.
      // Round 44 — belt-and-braces: only LOCK first-fit once the dataset is
      // substantial OR has clearly settled. A premature lock on a tiny
      // batch (e.g. a 3-4 bar live-poll batch that lands before the full
      // historical fetch) would pin the chart to a few-candle zoom and the
      // later full batch would be wrongly treated as a "continuation". We
      // still POSITION the chart each pass (above) so the user always sees
      // something sensible; we just don't commit firstFitDoneRef until:
      //   • we have at least `fitFloor` bars (enough to fill a real view), OR
      //   • the candle count is unchanged from the previous run (settled —
      //     covers genuinely short-history symbols that will never reach
      //     fitFloor).
      // fitFloor must NOT be derived from defaultVisibleBars. That value is the
      // REMEMBERED ZOOM, and on a sparse series (a monthly chart with 34 bars)
      // the remembered zoom is small precisely BECAUSE the series is sparse — so
      // deriving the floor from it made "do we have enough bars to fill a view?"
      // answer itself yes on the very first batch. First-fit then locked on
      // whatever partial batch arrived first, and every later pass took the
      // continuation branch — which is why the same 1M chart rendered fitted on
      // a fresh open and squeezed into the right third on the next one. An
      // absolute floor breaks the circularity; a genuinely short history still
      // locks via countSettled on the following pass.
      const fitFloor = 50;
      const countSettled = prevCandleCountRef.current === candles.length;
      const dataReady = candles.length >= fitFloor || countSettled;

      /* AND it must have actually LANDED.
       *
       * This lock used to fire synchronously, in the same block that STARTED the verified
       * fit. So if anything re-ran this function before the retry loop finished — a live
       * poll is enough, and it re-runs on every one — the retry was cancelled with the
       * chart still on lightweight-charts' default, and first-fit was already marked done.
       * Every later pass then took the continuation branch and dutifully restored that
       * default as if it were the user's chosen zoom.
       *
       * MEASURED on the live app, split 8H/15m, 2026-09-10, identical runs apart from the
       * poll cadence:
       *   liveUpdateSec 15 -> pane 0 opened at pxPerBar 4.096 with a 15-bar (9%) gap
       *   liveUpdateSec  5 -> pane 0 opened at pxPerBar 5.97  with a  2-bar (1.8%) gap
       * 5.97 is the library default of 6, and 2 is MIN_GAP_BARS — i.e. at the faster
       * cadence the app's zoom and gap never reached the chart at all. Tripling the poll
       * rate did not create this bug, it just made it likely enough to notice.
       *
       * `fitFailsRef` stops a range the chart genuinely cannot honour from re-fitting
       * forever: after a few failed passes we lock anyway and let the user's own scroll
       * take over. */
      if (dataReady) {
        const settleToken = fitToken;
        pendingFitLockRef.current = (landed: boolean) => {
          if (programmaticTokenRef.current !== settleToken) return;   // superseded — the newer pass owns it
          if (landed) { fitFailsRef.current = 0; firstFitDoneRef.current = true; return; }
          if (++fitFailsRef.current >= 3) firstFitDoneRef.current = true;
        };
      } else {
        pendingFitLockRef.current = null;
      }
      applyRangeVerified(ts, target, fitRafRef, (landed) => {
        const lock = pendingFitLockRef.current;
        pendingFitLockRef.current = null;
        if (lock) lock(landed);
        settleFit();
      });
    } else if (savedLogical) {
      // Restore the user's pan/zoom captured at the top of applyData. Only runs
      // for genuine continuations, so a fresh symbol open is NOT pinned to the
      // previous chart's stale range. We shift the saved LOGICAL range by the
      // number of bars added (delta) so the same data bars — AND any right-side
      // whitespace the user panned in (to > last index) — stay exactly in view.
      // This is what makes a left-drag survive auto-refresh instead of snapping
      // back to the right.
      try {
        const ts = chart.timeScale();
        const savedFrom = Number((savedLogical as any).from);
        const savedTo = Number((savedLogical as any).to);
        if (Number.isFinite(savedFrom) && Number.isFinite(savedTo)) {
          const delta = candles.length - prevCandleCountRef.current;
          let target: { from: number; to: number } | null = null;
          if (replayCut != null) {
            // Bar Replay: follow the playhead ONLY when the user was already
            // parked at the right edge. This used to re-anchor unconditionally,
            // so panning back to study history was undone on the next reveal —
            // and, before the memoisation above, on the next mouse move.
            const prevLastIdx = Math.max(0, prevCandleCountRef.current - 1);
            const wasAtRightEdge = savedTo >= prevLastIdx - 1;
            if (wasAtRightEdge) {
              const span = Math.max(20, savedTo - savedFrom);
              const newTo = candles.length - 1 + baseOffset;
              target = { from: Math.max(0, newTo - span), to: newTo };
            } else {
              // Same rule as the live chart: shift by the bars just revealed so
              // the user keeps looking at exactly the bars they were looking at.
              target = { from: savedFrom + delta, to: savedTo + delta };
            }
          } else {
            target = { from: savedFrom + delta, to: savedTo + delta };
          }

          /* RE-ASSERT IT, EVERY PASS.
           *
           * `savedLogical` was snapshotted at the TOP of this function, before setData ran.
           * Restoring it at the bottom is not busywork: something inside a data pass nudges
           * the view left, and this is what holds it. MEASURED — an earlier version of this
           * fix skipped the restore whenever delta was 0, on the reasoning that a price tick
           * changes no bar indices and so needs no compensation. Deployed, the chart then
           * crept LEFT at ~0.6 bars per second in BOTH panes of a split, ~87 bars in two
           * minutes with nobody touching it, until the newest candle was off screen and the
           * jump-to-latest chevron appeared on an idle chart. That is the owner's "the charts
           * are shifting and I have to click Jump to last candle" — the old code was holding
           * the view against that nudge all along, and skipping the restore removed the
           * correction rather than the cause.
           *
           * It does not fight the user either: the snapshot IS wherever the user has just
           * dragged to, so re-applying it puts the view back where it already is. The
           * genuinely-nothing-to-do case is skipped inside applyRangeVerified, which compares
           * against the chart's real range instead of against our own arithmetic. */
          applyRangeVerified(ts, target, fitRafRef, () => settleFit());
          fitPending = true;               // settleFit is owned by the retry loop now
        }
      } catch { /* ignore — keep whatever range the chart settled on */ }
    }
    // Release the programmatic guard after the range-change events from the
    // sets above have flushed, so subsequent user zooms are captured again.
    // The first-fit branch owns its own release (settleFit, several frames
    // later, once the range has proven it landed); every other path can release
    // on the original short timer.
    if (!fitPending) setTimeout(() => settleFit(), 80);

    // R105 — Reconcile the lower indicator pane after all series are placed.
    // Prune any sub-pane left empty (e.g. the last pane indicator was turned
    // off or switched back to a price overlay) so no blank strip lingers, then
    // restore the user's remembered height on the active lower pane (seeding a
    // sensible ~30%-of-chart default the first time it appears).
    try {
      const ps = chart.panes();
      for (let i = ps.length - 1; i >= 1; i--) {
        try { if (ps[i].getSeries().length === 0) chart.removePane(i); } catch { /* ignore */ }
      }
      // Restore the lower pane's STRETCH factor (relative weight) so its size
      // survives the rebuild. Stretch round-trips exactly, so re-applying the
      // remembered value every refresh neither drifts (the creep we saw with
      // setHeight) nor springs back to a default when the pane was recreated.
      // First appearance seeds a sensible ~30%-of-chart default.
      {
        const ps = chart.panes();
        // The shared indicator pane is pane 1 only when it holds a NON-FNO series; FNO
        // panes keep their own remembered stretch (default: ~16% of the price pane each).
        const fnoSet = new Set(Object.values(fnoPaneSeriesRef.current));
        const fnoPaneIdx = new Map<number, string>();
        for (const [k, s] of Object.entries(fnoPaneSeriesRef.current)) { try { fnoPaneIdx.set(s.getPane().paneIndex(), k); } catch { /* ignore */ } }
        const p0sf = (() => { try { const v = ps[0]?.getStretchFactor(); return Number.isFinite(v) && v! > 0 ? v! : 1; } catch { return 1; } })();
        for (const [idx, k] of Array.from(fnoPaneIdx)) {
          const pane = ps[idx];
          if (!pane) continue;
          // Default share scales with the chart: a short chart (Workspace strip, quad) must
          // keep most of its height for the candles. A dragged height is remembered instead.
          const hPx = chartContainerRef.current?.clientHeight || 600;
          const target = fnoPaneStretchRef.current[k] ?? p0sf * (hPx < 450 ? 0.1 : hPx < 650 ? 0.14 : 0.2);
          try { const cur = pane.getStretchFactor(); if (!Number.isFinite(cur) || Math.abs(cur - target) > 1e-4) pane.setStretchFactor(target); } catch { /* ignore */ }
          fnoPaneStretchRef.current[k] = target;
        }
        const genericHasOwn = (() => { try { return !!ps[1] && ps[1].getSeries().some(s => !fnoSet.has(s as any)); } catch { return false; } })();
        const lower = genericHasOwn ? ps[1] : undefined;
        if (lower) {
          let target = lowerPaneStretchRef.current;
          if (target == null || !Number.isFinite(target) || target <= 0) {
            // Default: lower pane ≈ 30% of the chart → stretch = priceStretch × 0.30/0.70.
            const p0 = (() => { try { const v = ps[0]?.getStretchFactor(); return Number.isFinite(v) && v! > 0 ? v! : 1; } catch { return 1; } })();
            target = p0 * (0.30 / 0.70);
          }
          let cur = NaN;
          try { cur = lower.getStretchFactor(); } catch { /* ignore */ }
          if (!Number.isFinite(cur) || Math.abs(cur - (target as number)) > 1e-4) {
            try { lower.setStretchFactor(target as number); } catch { /* ignore */ }
          }
          lowerPaneStretchRef.current = target;
        }
      }
    } catch { /* ignore */ }

    // Crosshair: keep it free. The mode is already Normal (no magnet), but
    // lightweight-charts still draws a per-series crosshair MARKER dot that
    // tracks each line's value at the hovered bar — across ~10 indicators that
    // reads as the crosshair "snapping to indicators". Hide the marker on every
    // overlay series so only the free crosshair lines remain, letting the user
    // point cleanly at any price level.
    try {
      for (const s of overlaySeriesRefs.current) {
        try { (s as ISeriesApi<'Line'>).applyOptions({ crosshairMarkerVisible: false }); } catch { /* series type w/o marker — ignore */ }
      }
    } catch { /* ignore */ }

    // Round 42/44 — remember this run's last-candle time + count for the
    // continuation / settled tests at the top of the next applyData.
    prevLastCandleTimeRef.current = newLastTime;
    prevFirstCandleTimeRef.current = newFirstTime;
    prevCandleCountRef.current = candles.length;
    };
    applyDataRef.current = applyData;
    applyData();
  }, [candles, overlays, C, isDark, priceLevels, fnoLevels, fnoView, overlayLeftInset, synthiya, priceZones, pnlBadge, leftLevels, strategyOverlay, auxCandles, benchmarkCandles, benchmarkLabel, intervalMinutes, settings, defaultVisibleBars, hideCandles, rawCandles, bwSource, bwRealBars, bwSrcType, bwClnLen, flowCandles, fundingSeries, replayMaxTime, replayCut, showTrendBand, showBkground, overviewTf, overviewHtf, lrGhost, clnDirByTime, clnUp, clnDown, fraction, profitZones]);

  /* Live update — incrementally update last candle without full redraw.
   * Guards: lightweight-charts v5 throws "Cannot update oldest data" if the
   * update time is strictly less than the last point on the series. Skip the
   * call in that case (the next setData cycle will reconcile). Also skip when
   * the series is empty — setData([...history]) will run in the companion
   * effect and fold the live bar in. */
  useEffect(() => {
    const lc = latestCandle;
    if (!lc || !candleSeriesRef.current) return;
    if (!candles.length) return;
    const lastSeriesTime = candles[candles.length - 1].time;
    if (lc.time < lastSeriesTime) return;
    try {
      const TRANSPARENT = 'rgba(0,0,0,0)';
      // Re-apply the per-bar colour on the LIVE/forming bar. A bare update()
      // makes lightweight-charts repaint the bar with the series DEFAULT up/down
      // colour, wiping the per-bar colour that setData applied — that's why the
      // forming candle flashed red under RockBand even when the OverView gauge
      // (and the bar's strategy colour) was bullish. Look up the strategy colour
      // for this bar's time and keep it on every tick.
      let colorProps: Record<string, string> = {};
      if (hideCandles) {
        colorProps = { color: TRANSPARENT, borderColor: TRANSPARENT, wickColor: TRANSPARENT };
      } else if (lrGhost) {
        // Same invariant as the closed bars above — LR direction, not the real
        // bar's own direction, and never the strategy's colour.
        const up = clnDirByTime.get(lc.time as number);
        const hue = (up === undefined ? lc.close >= lc.open : up) ? clnUp : clnDown;
        const edge = withAlpha(hue, CLN_REAL_EDGE_ALPHA);
        colorProps = { color: withAlpha(hue, CLN_REAL_BODY_ALPHA), borderColor: edge, wickColor: edge };
      } else {
        const ccs = strategyOverlay?.candleColors;
        // Exact match for the forming bar's time, else CONTINUE the most-recent
        // strategy colour. The overlay only covers CLOSED bars, so the forming
        // bar would otherwise fall back to the series default up/down colour —
        // which is display-candle-type dependent (e.g. green in Heikin-Ashi but
        // red in CLH for the same Shaved-Doji state). The strategy state is
        // candle-type independent, so the live bar must carry it through too.
        const sc = ccs?.find(cc => cc.time === (lc.time as number)) ?? (ccs?.length ? ccs[ccs.length - 1] : undefined);
        if (sc) {
          const c = lc.close >= lc.open ? sc.upColor : sc.downColor;
          colorProps = { color: c, borderColor: c, wickColor: c };
        }
      }
      candleSeriesRef.current.update({
        time: lc.time as Time,
        open: lc.open, high: lc.high,
        low: lc.low, close: lc.close,
        ...colorProps,
      });
    } catch { /* race between setData and update — next render reconciles */ }
  }, [latestCandle, candles, hideCandles, strategyOverlay, lrGhost, clnDirByTime, clnUp, clnDown]);

  /* Dedicated LTP price line — anchored to the ACTUAL last-traded price (or
     latestCandle.close for Regular candles). Replaces the built-in last-value
     line so HA / CSH / CSL / CLH transforms don't drag the LTP indicator off
     the real market price. The right-axis label and dashed line use the
     user-configured ltp.lineColor / lineWidth from ChartSettings. */
  useEffect(() => {
    const cs = candleSeriesRef.current;
    if (!cs) return;
    // In Bar Replay there is no live LTP (actualLtp / latestCandle are not fed
    // while replaying). Anchor the line to the ACTUAL close of the LAST replayed
    // (visible) bar — taken from rawCandles, NOT the displayed candles, because
    // for HA / CSH / CSL / CLH the displayed close is a SMOOTHED value, not the
    // real market close (same reason live mode uses actualLtp over latestCandle).
    // Falls back to the displayed close only if raw candles aren't available.
    const inReplay = replayMaxTime != null && Number.isFinite(replayMaxTime);
    const replayClose = (rawCandles && rawCandles.length)
      ? (rawCandles[rawCandles.length - 1].close as number)
      : (candles.length ? (candles[candles.length - 1].close as number) : null);
    const price = inReplay
      ? replayClose
      : ((typeof actualLtp === 'number' && Number.isFinite(actualLtp))
          ? actualLtp
          : (latestCandle?.close ?? null));
    // Recreate the line when the price changes — lightweight-charts has no
    // updatePriceLine API; remove + add is the documented pattern. The label
    // jitter is invisible at typical poll cadences (≥3s).
    if (ltpPriceLineRef.current) {
      try { cs.removePriceLine(ltpPriceLineRef.current); } catch { /* ignore */ }
      ltpPriceLineRef.current = null;
    }
    if (price == null || !Number.isFinite(price) || price <= 0) return;
    try {
      ltpPriceLineRef.current = cs.createPriceLine({
        price,
        color: settings.ltp.lineColor,
        lineWidth: ((settings.ltp.lineWidth as 1 | 2 | 3 | 4) || 1),
        lineStyle: 2,
        axisLabelVisible: true,
        title: '',
      });
    } catch { /* ignore */ }
  }, [actualLtp, latestCandle, settings.ltp.lineColor, settings.ltp.lineWidth, replayMaxTime, candles, rawCandles]);

  /* Entry price line — shown when an OPEN position exists for the symbol. A
     non-draggable solid line at the trade's average entry price with a
     "Entry" axis label so the user can immediately see the level at which
     the position was taken. Different colour from SL / TSL / Target so it
     reads at a glance. */
  const entryLineRef = useRef<any>(null);
  useEffect(() => {
    const cs = candleSeriesRef.current;
    if (!cs) return;
    if (entryLineRef.current) {
      try { cs.removePriceLine(entryLineRef.current); } catch { /* ignore */ }
      entryLineRef.current = null;
    }
    const entry = positionPnl?.entryPrice;
    if (!entry || !Number.isFinite(entry) || entry <= 0) return;
    const isLong = positionPnl?.side === 'LONG';
    try {
      entryLineRef.current = cs.createPriceLine({
        price: entry,
        color: isLong ? C.bull : C.bear,
        lineWidth: 1,
        lineStyle: 0, // solid — distinguishes from dashed LTP / dotted Mark
        axisLabelVisible: true,
        title: isLong ? 'Entry L' : 'Entry S',
      });
    } catch { /* ignore */ }
  }, [positionPnl?.entryPrice, positionPnl?.side, C.bull, C.bear]);

  /* Mark price line — Delta perpetuals only. Maintained as a single dotted
     horizontal price line on the candle series; cleared when markPrice is null. */
  useEffect(() => {
    const cs = candleSeriesRef.current;
    if (!cs) return;
    if (markPriceLineRef.current) {
      try { cs.removePriceLine(markPriceLineRef.current); } catch {}
      markPriceLineRef.current = null;
    }
    if (markPrice == null || !Number.isFinite(markPrice) || markPrice <= 0) return;
    try {
      // Suppress the right-axis label on the Mark line — the dedicated LTP
      // price line above already shows the same value (we overlay markPrice
      // into the live candle close for Delta), so two labels stack on top of
      // each other and read as a "duplicate" on the price axis. Keep the
      // dotted line for visual context but only the LTP line carries a label.
      markPriceLineRef.current = cs.createPriceLine({
        price: markPrice,
        color: markPriceColor || '#ffb74d',
        lineWidth: 1,
        lineStyle: 2,
        axisLabelVisible: false,
        title: '',
      });
    } catch {}
  }, [markPrice, markPriceColor]);

  /* Snap a continuous price (from coordinateToPrice) to a valid exchange tick.
   *
   * BUG-FIX (TSTUSD calibration): the cleanup-rounding factor must accommodate
   * the tick precision. If decimalPrecision is coarser than the tick (e.g.
   * user-set decimalPrecision=2 but tickSize=0.0001), the old code first
   * snapped to 0.0018 and then re-rounded to 0.00 — landing the line far from
   * the click. We now use max(decimalPrecision, tickDerivedPrecision) for the
   * floating-point cleanup so tick-snapping is preserved.
   *
   * Without tickSize, fall back to decimalPrecision-only (legacy path). */
  const snapPrice = useCallback((p: number): number => {
    if (!Number.isFinite(p)) return p;
    if (tickSize && tickSize > 0) {
      const snapped = Math.round(p / tickSize) * tickSize;
      /* tickDecimals, not ceil(-log10(tick)): the approximation returns 3 for a 0.0025 tick that
       * needs 4, which would re-round a snapped currency-derivative price off its own tick. */
      const tickPrec = tickDecimals(tickSize) ?? 0;
      const cleanupPrec = Math.max(decimalPrecision, tickPrec);
      const factor = Math.pow(10, cleanupPrec);
      return Math.round(snapped * factor) / factor;
    }
    const factor = Math.pow(10, decimalPrecision);
    return Math.round(p * factor) / factor;
  }, [tickSize, decimalPrecision]);

  /* Effective display precision — what the dragged-line title and click-handler
   * formatting should use. Same logic as snapPrice cleanup. */
  const effectivePrecision = useMemo(() => {
    const tickPrec = tickDecimals(tickSize);
    if (tickPrec != null) return Math.max(decimalPrecision, tickPrec);
    return decimalPrecision;
  }, [tickSize, decimalPrecision]);

  /* Chart click handler — for price capture (trigger placement) and, when
     bar replay is on (R68b), to let the user seek the playhead with a click. */
  useEffect(() => {
    const chart = chartRef.current;
    const cs = candleSeriesRef.current;
    if (!chart || !cs) return;
    if (!onChartClick && !onChartTimeClick && !onPnlBadgeClick) return;
    const handler = (param: any) => {
      // Bottom-left P&L badge hit-test (the badge is canvas-drawn; its box is published
      // by the primitive in CSS px) → open the basket adjustment dialog. Takes priority.
      if (onPnlBadgeClick && pnlBadge && param.point) {
        const r = pnlBadgePrimRef.current?.lastRect;
        if (r && param.point.x >= r.x && param.point.x <= r.x + r.w && param.point.y >= r.y && param.point.y <= r.y + r.h) {
          onPnlBadgeClick();
          return;
        }
      }
      if (param.point) {
        if (onChartClick) {
          const price = cs.coordinateToPrice(param.point.y);
          if (price != null) onChartClick(snapPrice(Number(price)));
        }
      }
      if (onChartTimeClick && param.time != null) {
        onChartTimeClick(Number(param.time));
      }
    };
    chart.subscribeClick(handler);
    return () => { try { chart.unsubscribeClick(handler); } catch {} };
  }, [onChartClick, onChartTimeClick, onPnlBadgeClick, pnlBadge, snapPrice]);

  /* ── Rectangle zones (item 3): attach/update the primitive on pane 0. The big
     build effect recreates the candle series, so re-attach whenever the current
     series differs from the one we attached to. */
  useEffect(() => {
    const cs = candleSeriesRef.current;
    if (!cs) return;
    if (rectAttachedToRef.current !== cs) {
      // Old series was destroyed with its chart — just drop the stale primitive.
      rectPrimRef.current = null;
      if (rectZones !== undefined) {
        const prim = new RectangleZonePrimitive(rectZones || []);
        try { (cs as any).attachPrimitive(prim); rectPrimRef.current = prim; rectAttachedToRef.current = cs; } catch {}
      }
    }
    // Drag in-flight → the mouse handlers own the primitive's data. This effect
    // has NO deps (runs every render) — resetting here snapped the zone back to
    // its original position/size mid-drag and erased the pending commit.
    if (rectLiveRef.current) return;
    rectPrimRef.current?.setData(rectZones || [], selectedRectId ?? null);
  });

  /* Rect draw mode — 2-click create via subscribeClick (corner 1 → corner 2), Esc
     cancels. Prioritized by construction: the parent disarms other click tools
     while draw mode is on.
     The draft corner lives in a ref and is reset ONLY when draw mode toggles —
     NOT in the effect body: live ticks / crosshair renders re-run this effect
     (deps include parent callbacks), and an in-body reset silently wiped the
     first corner between the two clicks, making the tool look dead. */
  useEffect(() => { rectDraftRef.current = null; }, [rectDrawMode]);
  useEffect(() => {
    const chart = chartRef.current;
    const cs = candleSeriesRef.current;
    const wrapper = wrapperRef.current;
    if (!rectDrawMode || !chart || !cs || !onRectZonesChange) return;
    if (wrapper) wrapper.style.cursor = 'crosshair';
    const handler = (param: any) => {
      if (!param.point || param.time == null) return; // clicks over data only (v1)
      const price = cs.coordinateToPrice(param.point.y);
      if (price == null) return;
      const draft = rectDraftRef.current;
      if (!draft) { rectDraftRef.current = { time: Number(param.time), price: Number(price) }; return; }
      // Second corner — normalize (t1<t2, p1>p2) + minimum-size guard.
      const t1 = Math.min(draft.time, Number(param.time));
      const t2 = Math.max(draft.time, Number(param.time));
      const p1 = Math.max(draft.price, Number(price));
      const p2 = Math.min(draft.price, Number(price));
      rectDraftRef.current = null;
      const minP = Math.abs(p1) * 0.0005;
      if (t2 - t1 < (intervalMinutes || 1) * 30 || p1 - p2 < minP) { onRectDrawComplete?.(); return; }
      const z: RectZone = {
        id: `rz${Date.now().toString(36)}${Math.floor(Math.random() * 1e4).toString(36)}`,
        t1, t2, p1, p2, color: '#2196f3', opacity: 0.18,
      };
      onRectZonesChange([...(rectZones || []), z]);
      onRectDrawComplete?.();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { rectDraftRef.current = null; onRectDrawComplete?.(); }
    };
    chart.subscribeClick(handler);
    window.addEventListener('keydown', onKey);
    return () => {
      try { chart.unsubscribeClick(handler); } catch {}
      window.removeEventListener('keydown', onKey);
      if (wrapper) wrapper.style.cursor = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rectDrawMode, rectZones, onRectZonesChange, onRectDrawComplete, intervalMinutes]);

  /* Rect drag/resize + select + cursor feedback. Capture-phase mousedown wins over
     the chart's own pan handling; pan/scale are disabled for the drag and restored
     on mouseup; redraw freezes ride the existing onDragStateChange contract. */
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || rectDrawMode || !onRectZonesChange || !(rectZones && rectZones.length)) return;
    const hover = (ev: MouseEvent) => {
      if (rectLiveRef.current) return; // mid-drag — cursor already set
      const rect = wrapper.getBoundingClientRect();
      const hit = rectPrimRef.current?.hitTestZone(ev.clientX - rect.left, ev.clientY - rect.top);
      wrapper.style.cursor = hit
        ? (hit.part === 'body' ? 'move' : (hit.part === 'left' || hit.part === 'right') ? 'ew-resize' : 'ns-resize')
        : '';
    };
    const down = (ev: MouseEvent) => {
      if (ev.button !== 0) return;
      const chart = chartRef.current;
      const cs = candleSeriesRef.current;
      const prim = rectPrimRef.current;
      if (!chart || !cs || !prim) return;
      const rect = wrapper.getBoundingClientRect();
      const x0 = ev.clientX - rect.left, y0 = ev.clientY - rect.top;
      const hit = prim.hitTestZone(x0, y0);
      if (!hit) { onRectZoneSelect?.(null); return; }
      ev.preventDefault();
      ev.stopPropagation();
      try { chart.applyOptions({ handleScroll: false, handleScale: false }); } catch {}
      if (onDragStateChange) try { onDragStateChange(true); } catch {}
      const base = (rectZones || []).map(z => ({ ...z }));
      const ts = chart.timeScale();
      const t0 = ts.coordinateToTime(x0);
      const price0 = cs.coordinateToPrice(y0);
      let moved = false;
      const onMove = (mv: MouseEvent) => {
        const x = mv.clientX - rect.left, y = mv.clientY - rect.top;
        if (Math.abs(mv.clientX - ev.clientX) + Math.abs(mv.clientY - ev.clientY) > 3) moved = true;
        const p = cs.coordinateToPrice(y);
        if (p == null || price0 == null) return;
        const dp = Number(p) - Number(price0);
        const t = ts.coordinateToTime(x);
        const dt = (t != null && t0 != null) ? Number(t) - Number(t0) : 0;
        const live = base.map(z => {
          if (z.id !== hit.id) return z;
          if (hit.part === 'body') return { ...z, t1: z.t1 + dt, t2: z.t2 + dt, p1: z.p1 + dp, p2: z.p2 + dp };
          if (hit.part === 'top') return { ...z, p1: Math.max(z.p2, z.p1 + dp) };
          if (hit.part === 'bottom') return { ...z, p2: Math.min(z.p1, z.p2 + dp) };
          if (hit.part === 'left') return { ...z, t1: Math.min(z.t2, z.t1 + dt) };
          return { ...z, t2: Math.max(z.t1, z.t2 + dt) }; // right
        });
        rectLiveRef.current = live;
        prim.setData(live, hit.id);
      };
      const onUp = (uv: MouseEvent) => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        try { chart.applyOptions({ handleScroll: true, handleScale: true }); } catch {}
        if (onDragStateChange) try { onDragStateChange(false); } catch {}
        const live = rectLiveRef.current;
        rectLiveRef.current = null;
        if (!moved) { onRectZoneSelect?.(hit.id, { x: uv.clientX, y: uv.clientY }); return; }
        if (live) {
          // Normalize on commit (a top-edge dragged past the bottom flips p1/p2).
          onRectZonesChange(live.map(z => ({
            ...z, t1: Math.min(z.t1, z.t2), t2: Math.max(z.t1, z.t2),
            p1: Math.max(z.p1, z.p2), p2: Math.min(z.p1, z.p2),
          })));
        }
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    };
    wrapper.addEventListener('mousemove', hover);
    wrapper.addEventListener('mousedown', down, true); // capture — beats chart pan
    return () => {
      wrapper.removeEventListener('mousemove', hover);
      wrapper.removeEventListener('mousedown', down, true);
      wrapper.style.cursor = '';
    };
  }, [rectZones, rectDrawMode, onRectZonesChange, onRectZoneSelect, onDragStateChange]);

  /* Crosshair move — emit to parent for cross-pane sync AND track the
     hovered candle locally so we can render a TradingView-style OHLC legend.
     Only emits to parent when triggered by user interaction (sourceEvent
     present) so programmatic setCrosshairPosition from other panes doesn't
     loop. The local OHLC legend tracks any move (incl. sync) to stay useful. */
  const [hoverCandle, setHoverCandle] = useState<CandleRow | null>(null);
  /** Hover state for the Order Flow ribbon — drives the tooltip overlay
   *  that explains the current bar's flow regime. Populated by the same
   *  crosshair handler so it tracks the user's mouse without a second
   *  subscription. */
  // Round 96 — the crosshair tooltip summarises the hovered candle's OverView
  // (replaces the old Flow regime tooltip). Shows the chart-TF score (exact
  // bar) and the HTF score (the HTF bar containing the hovered time).
  type OvPoint = { time: number; score: number; zone: OverviewZone; up: number; down: number; na: number };
  // R115 Stage 2 — the two separate OverView + BOLev tooltips are replaced by a
  // single CrosshairInfoBox driven by `crosshairCtx`. hoverOverview / hoverBolev
  // state is retired.
  const ovTfByTime = useMemo(() => {
    const m = new Map<number, OvPoint>();
    for (const p of (overviewTf || [])) m.set(p.time, p as OvPoint);
    return m;
  }, [overviewTf]);
  const ovHtfSorted = useMemo(() => (overviewHtf || []).slice().sort((a, b) => a.time - b.time) as OvPoint[], [overviewHtf]);
  const ovActive = !!(overviewTf && overviewTf.length);

  // R131 — the tooltip's OHLC + Change% always read the RAW (Normal) candles
  // regardless of the selected display candle type. (The BOLev_D/W tooltip
  // sections from R113/R115 are retired per user request — the BOLev overlay
  // lines/markers on the chart itself are unaffected.)
  const rawByTime = useMemo(() => {
    const m = new Map<number, CandleRow>();
    const src = (rawCandles && rawCandles.length) ? rawCandles : candles;
    for (const c of src) m.set(c.time as number, c);
    // The forming/live bar lives outside `rawCandles` (closed bars only). Add its RAW
    // OHLC so the OHLC legend + change% read regular-candle values for the CURRENT bar
    // too — otherwise they'd fall back to the transformed display bar and flip with the
    // candle type. Overwrites any transformed entry for that time.
    if (rawLatestCandle && Number.isFinite(rawLatestCandle.time as number)) m.set(rawLatestCandle.time as number, rawLatestCandle);
    return m;
  }, [rawCandles, candles, rawLatestCandle]);
  // "Important fraction": the fraction bar per time, for the legend's second price set.
  const fracByTime = useMemo(() => {
    if (!fraction) return null;
    const m = new Map<number, CandleRow>();
    for (const c of fractionBarsCut || []) m.set(c.time as number, c);
    if (fractionLatest && Number.isFinite(fractionLatest.time as number)) m.set(fractionLatest.time as number, fractionLatest);
    return m;
  }, [fraction, fractionBarsCut, fractionLatest]);

  // R131 — VPA signals per bar for the crosshair tooltip (ONLY when the VPA
  // indicator is on). Mirrors the marker pipeline: raw CLOSED candles, same
  // toggles — so the tooltip describes exactly the markers on the chart.
  const vpaByTime = useMemo(() => {
    const m = new Map<number, VpaMarker[]>();
    if (!overlays.vpa) return m;
    const src: CandleRow[] = (rawCandles && rawCandles.length) ? rawCandles : candles;
    const closed = (latestCandle && src.length && src[src.length - 1].time === latestCandle.time)
      ? src.slice(0, -1) : src;
    if (closed.length < VPA_MIN_BARS) return m;
    try {
      const toggles = (settings as any)?.vpa || VPA_TOGGLE_DEFAULTS;
      for (const mk of computeVpaMarkers(closed, toggles)) {
        const arr = m.get(mk.time) || [];
        arr.push(mk);
        m.set(mk.time, arr);
      }
    } catch { /* tooltip-only — ignore */ }
    return m;
  }, [overlays.vpa, rawCandles, candles, latestCandle, settings]);
  // R115 Stage 2 — unified crosshair context (drives <CrosshairInfoBox>).
  const [crosshairCtx, setCrosshairCtx] = useState<CrosshairCtx | null>(null);
  /** Is the pointer over THIS pane? While it is, this chart owns its crosshair
   *  and ignores the synced position coming back from the parent. */
  const pointerInsideRef = useRef(false);
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const enter = () => { pointerInsideRef.current = true; };
    const leave = () => { pointerInsideRef.current = false; };
    wrapper.addEventListener('mouseenter', enter);
    wrapper.addEventListener('mouseleave', leave);
    return () => {
      wrapper.removeEventListener('mouseenter', enter);
      wrapper.removeEventListener('mouseleave', leave);
    };
  }, []);
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const handler = (param: any) => {
      const t = param?.time != null ? Number(param.time) : null;
      try { fnoPaneLegendRef.current?.(t); } catch { /* ignore */ }
      if (param?.sourceEvent && crosshairHandleRef.current) {
        // Emit the hovered PRICE too (not just time) so the other pane can
        // mirror the exact horizontal level. Price ← candle series' inverse
        // of the cursor's y pixel.
        let price: number | null = null;
        const csForPrice = candleSeriesRef.current;
        if (csForPrice && param.point && typeof param.point.y === 'number') {
          const p = csForPrice.coordinateToPrice(param.point.y);
          if (p != null && Number.isFinite(Number(p))) price = Number(p);
        }
        // Straight to the other panes. No setState, so no page re-render, so no
        // rebuild of this chart's series mid-drag.
        crosshairHandleRef.current.publish({ time: t, price });
      }
      // Binary-search the hovered bar (drives the OHLC legend)
      if (t == null || !candles.length) { setHoverCandle(null); setCrosshairCtx(null); return; }
      let lo = 0, hi = candles.length - 1, found: CandleRow | null = null;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (candles[mid].time === t) { found = candles[mid]; break; }
        if (candles[mid].time < t) lo = mid + 1; else hi = mid - 1;
      }
      if (!found && candles.length) {
        const a = candles[Math.max(hi, 0)];
        const b = candles[Math.min(lo, candles.length - 1)];
        found = Math.abs(a.time - t) <= Math.abs(b.time - t) ? a : b;
      }
      setHoverCandle(found);

      // R115 Stage 2 — populate the unified crosshair context. Always shows OHLC;
      // OverView scores / BOLev_D / BOLev_W / RockBand sections appear when their
      // underlying data is available.
      if (!param?.point || !found) { setCrosshairCtx(null); return; }
      const tfP = ovTfByTime.get(t) || null;
      let htfP: OvPoint | null = null;
      {
        const arr = ovHtfSorted;
        let l = 0, r = arr.length - 1;
        while (l <= r) { const m = (l + r) >> 1; if (arr[m].time <= t) { htfP = arr[m]; l = m + 1; } else r = m - 1; }
      }
      // R131 — OHLC + Change% read RAW (Normal) candles regardless of the
      // selected display candle type.
      const rawFound = rawByTime.get(t) || found;
      let pct: number | null = null;
      const idx = candles.indexOf(found);
      if (idx > 0) {
        const prev = candles[idx - 1];
        // Change vs the PREVIOUS candle's RAW (Normal) close. Compute ONLY from
        // regular-candle values — if either bar is missing from the raw map (e.g.
        // a forming bar on an HA/CLH chart), skip rather than fall back to the
        // transformed close (which would show a misleading %).
        const curRaw = rawByTime.get(t);
        const prevRaw = rawByTime.get(prev.time as number);
        if (curRaw && prevRaw && prevRaw.close) pct = ((curRaw.close - prevRaw.close) / prevRaw.close) * 100;
      }
      const W = wrapperRef.current?.clientWidth || 600;
      const H = wrapperRef.current?.clientHeight || 400;
      // R131 — strategy-generic: any applied strategy that emits
      // signalsByTime/positionByTime feeds the tooltip (RockBand, Sniper, …).
      const stratId = activeStrategy && activeStrategy !== 'none' ? activeStrategy : undefined;
      const sSig = stratId ? (strategyOverlay?.signalsByTime?.get(t) || null) : null;
      const sPos = stratId && strategyOverlay?.positionByTime ? (strategyOverlay.positionByTime.get(t) || 'FLAT') : undefined;
      const vpaMarks = vpaByTime.get(t);
      // Informational, non-trade marks (OM_Simple's W / C). A separate ARRAY
      // channel from signalsByTime, which holds one TRADE per bar.
      const sNotes = stratId ? strategyOverlay?.notesByTime?.get(t) : undefined;
      // FNO read at the hovered candle's CLOSE (option symbols, FNO on) — step
      // candle-by-candle to test the option picture against the actual price action.
      // Bull/Bear rank comes from the same close. Futures/PCR lines are price-
      // independent context.
      let fnoLines: ReturnType<typeof interpretFnoAtPrice> | undefined;
      let fnoPrice: number | undefined;
      let fnoScore: CrosshairCtx['fnoScore'] | undefined;
      if (fnoInterp && rawFound) {
        const cp = Number(rawFound.close);
        if (Number.isFinite(cp) && cp > 0) {
          fnoPrice = cp;
          const lines = interpretFnoAtPrice(cp, fnoInterp);
          if (lines.length) fnoLines = lines;
          const sc = scoreFno(cp, fnoInterp);
          const zone = zoneForScore(sc);
          fnoScore = { score: sc, zone, label: ZONE_LABELS[zone] };
        }
      }
      // Was a fourth inline copy of the label rules, and it rendered every
      // interval above a week as "1W" and every multi-day one as "1D".
      const intervalLabel = intervalMinutes != null ? catalogIntervalLabel(intervalMinutes) : '';
      setCrosshairCtx({
        x: param.point.x, y: param.point.y,
        containerW: W, containerH: H,
        isDark,
        symbol: symbol || '',
        intervalLabel,
        ohlc: { time: found.time as number, open: rawFound.open, high: rawFound.high, low: rawFound.low, close: rawFound.close },
        pctChange: pct,
        decimalPrecision,
        tfScore:  tfP  ? { score: Math.round(tfP.score),  zone: tfP.zone  } : undefined,
        htfScore: htfP ? { score: Math.round(htfP.score), zone: htfP.zone } : undefined,
        tfLabel:  overviewTfLabel,
        htfLabel: overviewHtfLabel,
        strategyId: stratId,
        strategyPos: sPos,
        strategySignal: sSig
          ? { signal: sSig.signal, strength: sSig.strength, reason: (sSig as any).reason ?? null }
          : null,
        strategyNotes: sNotes && sNotes.length ? sNotes : undefined,
        vpaSignals: vpaMarks && vpaMarks.length
          ? vpaMarks.map(mk => ({
              text: mk.text, color: mk.color,
              defs: vpaDefsForMarkerText(mk.text).map(d => ({ code: d.code, label: d.label, tip: d.tip })),
            }))
          : undefined,
        fno: fnoLines,
        fnoPrice,
        fnoScore,
      });
    };
    chart.subscribeCrosshairMove(handler);
    return () => { try { chart.unsubscribeCrosshairMove(handler); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candles, ovTfByTime, ovHtfSorted, ovActive, rawByTime, vpaByTime, strategyOverlay, symbol, activeStrategy, intervalMinutes, overviewTfLabel, overviewHtfLabel, isDark, decimalPrecision, settings, fnoInterp]);

  /* Receive external crosshair position — reflect BOTH the time (vertical line)
     and the price (horizontal line) on this chart. Same symbol across the
     split, so the same price maps to the same level even if this pane is zoomed
     differently. Falls back to the candle close only if no price was sent.

     This is a plain subscription, not a prop + effect. Two things follow, and both are the
     point of the change:
       • Applying a mirrored crosshair is imperative, so nothing re-renders. Previously the
         position round-tripped through ChartsPage state at pointer rate.
       • The subscription is created ONCE per group instead of being re-keyed on `candles`.
         The old effect listed `candles` as a dependency, so it tore down and re-subscribed
         every time the data identity changed — which, on a live chart, is every poll. */
  const candlesRef = useRef<CandleRow[]>(candles);
  useEffect(() => { candlesRef.current = candles; }, [candles]);
  useEffect(() => {
    // A pane with no group (single layout) has nothing to mirror and must NEVER touch the
    // crosshair. It used to fall through to clearCrosshairPosition(), and since that effect
    // was keyed on `candles` — which changed identity on every render while replaying — the
    // crosshair was wiped after every mouse move. That was the whole of "the crosshair
    // doesn't track during replay"; keep the opt-out.
    const handle = joinCrosshairGroup(crosshairGroup, (pos: CrosshairPos) => {
      const chart = chartRef.current;
      const cs = candleSeriesRef.current;
      if (!chart || !cs) return;
      const syncTime = pos?.time ?? null;
      if (syncTime == null) {
        // Clear only on a real null-transition, so a repeat with nothing to do leaves the
        // user's own crosshair alone.
        if (lastSyncTimeRef.current != null) {
          lastSyncTimeRef.current = null;
          try { chart.clearCrosshairPosition(); } catch { /* chart disposed */ }
        }
        return;
      }
      lastSyncTimeRef.current = syncTime;
      // Don't echo the sync onto the pane the pointer is actually over — re-applying it
      // there snaps the crosshair to the nearest bar centre instead of letting it track the
      // mouse freely. (The store already skips the publisher; this also covers the case
      // where the pointer is over a pane that is not currently publishing.)
      if (pointerInsideRef.current) return;
      const rows = candlesRef.current;
      if (!rows || !rows.length) return;
      // Find nearest candle by time — binary search since candles are ascending
      let lo = 0, hi = rows.length - 1, nearest: CandleRow | null = null;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (rows[mid].time === syncTime) { nearest = rows[mid]; break; }
        if (rows[mid].time < syncTime) lo = mid + 1; else hi = mid - 1;
      }
      if (!nearest) {
        const a = rows[Math.max(hi, 0)];
        const b = rows[Math.min(lo, rows.length - 1)];
        nearest = Math.abs(a.time - syncTime) <= Math.abs(b.time - syncTime) ? a : b;
      }
      if (!nearest) return;
      const price = (pos?.price != null && Number.isFinite(pos.price)) ? pos.price : nearest.close;
      try { chart.setCrosshairPosition(price, nearest.time as Time, cs); } catch { /* chart disposed */ }
    });
    crosshairHandleRef.current = handle;
    return () => { crosshairHandleRef.current = null; handle?.leave(); };
  }, [crosshairGroup]);

  /* Draggable axis handles — track the Y position of each price level so we
     can render a small draggable chip on the right axis. rAF loop keeps
     positions fresh as the chart auto-scales or the user scrolls/zooms. */
  type HandlePos = { type: string; id?: string | number; price: number; color: string; y: number; deletable?: boolean };
  const [handles, setHandles] = useState<HandlePos[]>([]);
  const draggingTypeRef = useRef<string | null>(null);

  useEffect(() => {
    if (!priceLevels?.length || !onPriceLevelChange) { setHandles([]); return; }
    // Only FNO-origin levels pass draggable:false — everything else defaults to draggable.
    const draggables = priceLevels.filter(pl => pl.draggable !== false);
    if (!draggables.length) { setHandles([]); return; }
    let rafId = 0;
    const loop = () => {
      const cs = candleSeriesRef.current;
      if (cs) {
        const next: HandlePos[] = [];
        for (const pl of draggables) {
          const y = cs.priceToCoordinate(pl.price);
          if (y != null) next.push({ type: pl.type, id: pl.id, price: pl.price, color: pl.color, y: Number(y), deletable: pl.deletable });
        }
        setHandles(prev => {
          if (prev.length !== next.length) return next;
          for (let i = 0; i < prev.length; i++) {
            const a = prev[i], b = next[i];
            if (a.type !== b.type || a.id !== b.id || a.price !== b.price || a.color !== b.color || Math.abs(a.y - b.y) > 0.5) return next;
          }
          return prev;
        });
      }
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafId);
  }, [priceLevels, onPriceLevelChange]);

  /* Candle close countdown — ticks once per second while intervalMinutes is set.
     Positions a small label at the LTP Y coordinate showing mm:ss (or Hh Mm for
     daily/weekly). lastCandle.time is in IST-shifted seconds (UTC+19800). */
  /* Lazy-load older candles — fires onLoadMore as the user pans / wheels
     toward the leftmost bar. Two-tier trigger:
       • range.from <= 10 bars from start  → request a backfill
       • OR range.from goes negative       → user has clearly scrolled into
         the empty area before the oldest bar
     The previous threshold was a strict `< 5` which often missed wheel-zoom
     gestures that landed at index 6-10 and never produced more bars.
     Debounced via a ref flag (cleared after 1500 ms). */
  const loadMoreBusyRef = useRef(false);
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !onLoadMore) return;
    const handler = (range: any) => {
      if (!range || loadMoreBusyRef.current) return;
      if (candles.length === 0) return;
      const from = range.from;
      // Trigger on either: panned into negative space (from < 0) OR within
      // the leftmost 10 bars when the range covers a meaningful slice (to-from
      // > 5 — guards against transient zero-width ranges right after setData).
      const pannedPastEdge = from != null && from < 0;
      const inLeftZone = pannedPastEdge || (from != null && from <= 10 && (range.to - from) > 5);
      if (!inLeftZone) return;
      loadMoreBusyRef.current = true;
      const oldest = candles[0].time;
      onLoadMore(oldest, pannedPastEdge ? 'pan' : 'edge');
      // Cooldown sized to interval — Zerodha rate-limits historical-data
      // at ~3 req/s, and low-interval scrolls fire many backfills in a
      // burst. Bumping the cooldown for ≤15m intervals keeps NSE charts
      // out of 429 territory; higher intervals retain the snappy 1.2s
      // cadence (their bar density is much lower so 429 isn't a concern).
      const cooldownMs = (intervalMinutes && intervalMinutes <= 15) ? 4000 : 1200;
      window.setTimeout(() => { loadMoreBusyRef.current = false; }, cooldownMs);
    };
    const ts = chart.timeScale();
    ts.subscribeVisibleLogicalRangeChange(handler);
    return () => { try { ts.unsubscribeVisibleLogicalRangeChange(handler); } catch {} };
  }, [candles, onLoadMore]);

  /* Remember-zoom — report the user's chosen visible-candle COUNT (debounced)
     so the parent can persist it per layout and reuse it on the next open.
     Captures genuine scroll-zoom only. Three things must all be true before a
     span is believed to be the user's choice: no programmatic set is in flight,
     first-fit has locked, AND the fit has PROVEN it landed (fitSettledRef).
     That last gate is not defensive padding — without it this subscriber was
     measured writing lightweight-charts' own default view (238 bars) into
     chart_visibleBars_v1 on charts nobody had zoomed, quietly replacing the
     user's real remembered zoom with the library default.
     The count excludes the right-side gap so it matches `defaultVisibleBars`
     semantics (number of REAL candles in view). Pan-independent — the span (bar
     width) only changes on zoom, not on a left-drag. */
  const visBarsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !onVisibleBarsChange) return;

    const handler = (range: any) => {
      if (!range || programmaticRangeRef.current || !firstFitDoneRef.current) return;
      if (!fitSettledRef.current) return;
      const span = Number(range.to) - Number(range.from);
      if (!Number.isFinite(span) || span <= 0) return;
      // Invert the same geometry the fit uses, so re-opening a chart reproduces
      // the zoom exactly instead of shedding a bar on every round-trip.
      const bars = realBarsFromSpan(Math.round(span));
      if (bars < 20) return;
      if (visBarsTimerRef.current) clearTimeout(visBarsTimerRef.current);
      visBarsTimerRef.current = setTimeout(() => onVisibleBarsChange(Math.min(2000, bars)), 400);
    };
    const ts = chart.timeScale();
    ts.subscribeVisibleLogicalRangeChange(handler);
    return () => {
      try { ts.unsubscribeVisibleLogicalRangeChange(handler); } catch {}
      if (visBarsTimerRef.current) clearTimeout(visBarsTimerRef.current);
    };
  }, [candles, onVisibleBarsChange]);

  const [countdown, setCountdown] = useState<{ y: number; text: string } | null>(null);

  // Y-coordinate tracker for the SL / Target amount pills. Re-evaluates each
  // animation frame so the pills follow the lines through zoom / pan / scale
  // changes. Built like the handle tracker further down.
  const [tradePillPositions, setTradePillPositions] = useState<Array<{ type: 'SL' | 'TARGET'; y: number; price: number; amountInr: number; profitLocked?: boolean }>>([]);
  useEffect(() => {
    if (!tradeAmountPills || tradeAmountPills.length === 0) {
      setTradePillPositions([]);
      return;
    }
    let raf = 0;
    const loop = () => {
      const cs = candleSeriesRef.current;
      if (cs) {
        const next: Array<{ type: 'SL' | 'TARGET'; y: number; price: number; amountInr: number; profitLocked?: boolean }> = [];
        for (const p of tradeAmountPills) {
          const y = cs.priceToCoordinate(p.price);
          if (y != null) next.push({ ...p, y: Number(y) });
        }
        setTradePillPositions(prev => {
          if (prev.length !== next.length) return next;
          for (let i = 0; i < prev.length; i++) {
            const a = prev[i]; const b = next[i];
            if (a.type !== b.type || a.price !== b.price || a.amountInr !== b.amountInr || Math.abs(a.y - b.y) > 0.5) return next;
          }
          return prev;
        });
      }
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => { cancelAnimationFrame(raf); };
  }, [tradeAmountPills]);

  /* Auto-scroll-to-latest — show a floating chevron when the visible range's
     right edge sits more than a few bars before the last candle. */
  const [showScrollLatest, setShowScrollLatest] = useState(false);
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const ts = chart.timeScale();
    const onRange = (range: any) => {
      if (!range || candles.length === 0) { setShowScrollLatest(false); return; }
      // range.to is a logical index; when the rightmost visible bar is more
      // than 3 bars behind the tail we consider the user "scrolled back".
      const tail = candles.length - 1;
      const needs = range.to != null && range.to < tail - 3;
      setShowScrollLatest(needs);
    };
    ts.subscribeVisibleLogicalRangeChange(onRange);
    return () => { try { ts.unsubscribeVisibleLogicalRangeChange(onRange); } catch {} };
  }, [candles]);
  /* Jump to the newest candle — in ONE click.
   *
   * This used to call `scrollToRealTime()`, which ANIMATES. Any visible-range set landing
   * during that animation cancels it wherever it had got to, and the data effect was issuing
   * exactly such sets on every live tick and (via the old crosshair state) on every mouse
   * move. That is why the owner had to click the chevron repeatedly to reach the tail.
   *
   * Set the destination geometry directly instead. It is the SAME geometry the first fit
   * uses — the last `want` candles plus the 10%-of-view gap — so a jump lands the chart
   * exactly where a fresh open would, with no animation to interrupt, and it keeps the
   * user's current zoom (span) rather than silently re-zooming them. */
  const scrollToLatest = useCallback(() => {
    const chart = chartRef.current;
    if (!chart) return;
    try {
      const ts = chart.timeScale();
      const rows = candlesRef.current;
      const len = rows?.length || 0;
      if (!len) { try { ts.scrollToRealTime(); } catch { /* ignore */ } return; }
      // Keep whatever zoom the user is on; only the POSITION changes.
      let span = 0;
      try {
        const cur = ts.getVisibleLogicalRange();
        if (cur) span = Number(cur.to) - Number(cur.from);
      } catch { /* fall through to the planned span */ }
      const planned = plannedFit(len, defaultVisibleBars);
      if (!Number.isFinite(span) || span <= 0) span = planned.want + planned.gap - 1;
      const gap = gapBarsFor(realBarsFromSpan(Math.round(span)));
      const to = len - 1 + gap;
      // Held as programmatic so the remember-zoom subscriber does not record the jump as a
      // deliberate re-zoom by the user.
      programmaticRangeRef.current = true;
      applyRangeVerified(ts, { from: to - span, to }, fitRafRef, () => {
        programmaticRangeRef.current = false;
      });
    } catch { /* chart disposed */ }
  }, [defaultVisibleBars]);
  useEffect(() => {
    if (!intervalMinutes || !candles.length) { setCountdown(null); return; }
    // Bar Replay: there is no bar forming. The playhead's bar closed months ago,
    // so the "roll forward past now" branch below produced a live countdown to
    // the NEXT REAL-WORLD boundary, ticking away next to a frozen historical
    // price. Nothing to count down to — hide it.
    if (replayCut != null) { setCountdown(null); return; }
    const last = candles[candles.length - 1];
    // Prefer latestCandle's time if newer — handles the case where live polling
    // has advanced into a new bar that hasn't yet been merged into `candles`.
    const openIst = Math.max(last.time, latestCandle?.time ?? last.time);
    // LTP is the ACTUAL last-traded price. When the chart is rendering a
    // synthetic candle type (HA / CSH / CSL / CLH), `latestCandle.close` is a
    // SMOOTHED value, not the real LTP — anchoring the LTP line to it would
    // visibly drift the indicator away from market price. We always prefer the
    // explicit `actualLtp` prop when it's provided.
    const ltp = (typeof actualLtp === 'number' && Number.isFinite(actualLtp))
      ? actualLtp
      : (latestCandle?.close ?? last.close);
    const periodSec = intervalMinutes * 60;
    const tick = () => {
      const cs = candleSeriesRef.current;
      if (!cs) return;
      const y = cs.priceToCoordinate(ltp);
      if (y == null) return;
      const nowIst = Math.floor(Date.now() / 1000) + 19800;
      // Next close = open + interval. If the last bar is stale (market closed /
      // feed paused), roll forward to the next aligned boundary past now so the
      // countdown is always positive rather than stuck at 00:00.
      let nextClose = openIst + periodSec;
      if (nextClose <= nowIst) {
        const k = Math.ceil((nowIst - openIst) / periodSec);
        nextClose = openIst + k * periodSec;
        if (nextClose <= nowIst) nextClose += periodSec;
      }
      let secs = nextClose - nowIst;
      if (secs < 0) secs = 0;
      // Always HH:mm:ss (hours can exceed 24 for daily/weekly candles) so the readout
      // never collapses to a large minutes-only value.
      const h = Math.floor(secs / 3600);
      const m = Math.floor((secs % 3600) / 60);
      const s = secs % 60;
      const text = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      setCountdown({ y: Number(y), text });
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [candles, latestCandle, intervalMinutes, actualLtp, replayCut]);

  const onHandleMouseDown = useCallback((e: React.MouseEvent, h: HandlePos) => {
    e.preventDefault();
    e.stopPropagation();
    const wrapper = wrapperRef.current;
    const cs = candleSeriesRef.current;
    if (!wrapper || !cs || !onPriceLevelChange) return;
    draggingTypeRef.current = h.type;
    // Notify the parent so it can pause state replacements that would
    // otherwise re-render the chart mid-drag (round 25 fix for the
    // "target snaps back during drag" bug).
    if (onDragStateChange) try { onDragStateChange(true); } catch {}
    // Match on (type, id) so multiple HVN rows can drag independently. For
    // single-instance types (NBUY/etc) `id` is undefined on both sides.
    const lineIdx = priceLevels?.findIndex(pl => pl.type === h.type && pl.id === h.id) ?? -1;
    const line = lineIdx >= 0 ? priceLinesRef.current[lineIdx] : null;

    const onMove = (ev: MouseEvent) => {
      const rect = wrapper.getBoundingClientRect();
      const localY = ev.clientY - rect.top;
      const price = cs.coordinateToPrice(localY);
      if (price == null) return;
      const p = snapPrice(Number(price));
      if (line) { try { line.applyOptions({ price: p, title: `${h.type} @ ${p.toFixed(effectivePrecision)}` }); } catch {} }
      // Visually move the chip too
      setHandles(prev => prev.map(x => (x.type === h.type && x.id === h.id) ? { ...x, y: localY, price: p } : x));
    };
    const onUp = (ev: MouseEvent) => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      draggingTypeRef.current = null;
      if (onDragStateChange) try { onDragStateChange(false); } catch {}
      const rect = wrapper.getBoundingClientRect();
      const localY = ev.clientY - rect.top;
      const price = cs.coordinateToPrice(localY);
      if (price != null) onPriceLevelChange(h.type, snapPrice(Number(price)), h.id);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [priceLevels, onPriceLevelChange, snapPrice, effectivePrecision, onDragStateChange]);

  /* ── ALWAYS render the container — overlay loading/empty states on top ── */
  return (
    <Box ref={wrapperRef} data-chart-snapshot="true" sx={{ width: '100%', height: isFillHeight ? '100%' : height, position: 'relative' }}>
      <Box ref={chartContainerRef} sx={{ width: '100%', height: '100%' }} />
      {/* Three distinct states, because they mean different things and a single
          centred "Loading..." served none of them well:
            first load  -> a skeleton, so the pane has shape while it fills
            refreshing  -> a quiet spinner OVER the existing chart, which must
                           stay visible (a skeleton here would blank a chart the
                           user is reading, on every poll)
            no data     -> a real empty state, not one line of grey text        */}
      {loading && !candles.length && (
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 2, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <CardSkeleton rows={4} />
        </Box>
      )}
      {loading && !!candles.length && (
        <Box sx={{
          position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 2,
          display: 'flex', alignItems: 'center', gap: 0.8, px: 1.2, py: 0.4,
          ...chOverlaySurface(isDark), borderRadius: 999, pointerEvents: 'none',
        }}>
          <CircularProgress size={12} />
          <Typography sx={{ color: 'text.secondary', fontSize: CH_TYPE.micro, fontWeight: 600 }}>Updating…</Typography>
        </Box>
      )}
      {!candles.length && !loading && (
        <Box sx={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, px: 2,
        }}>
          <EmptyState kind="chart" size="sm"
            title={replayEmptiedPane ? 'Nothing here yet at the playhead'
              : !symbol ? 'Select a symbol'
              : 'No candles for this selection'}
            description={replayEmptiedPane
              ? 'Every bar this pane has is still in the future at the playhead. Step forward, or load a deeper window with Data: Broker / Free on the replay bar. (Option contracts often only exist for the last few days.)'
              : !symbol
                ? 'Pick a symbol from the list to chart it.'
                : 'Nothing came back for this symbol, timeframe and range.'} />
        </Box>
      )}
      {/* TradingView-style OHLC legend — top-left overlay that follows the
          crosshair. Shows the hovered candle's O/H/L/C plus change vs. its
          open (absolute and %). Falls back to the latest candle when the
          crosshair is not active so the legend is always informative.
          R81 — ALWAYS reads RAW OHLC (regular candle values), regardless of
          the chart's display candle type. Hovering an HA/CSL/CSH bar
          looks up the matching raw bar by time so users see the actual price
          values + accurate change %, not the smoothed transform values. */}
      {(() => {
        const hovered = hoverCandle || (candles.length ? candles[candles.length - 1] : null);
        if (!hovered) return null;
        // OHLC values always read the RAW (Normal) bar by time (reuses the shared
        // rawByTime map), regardless of the display candle type.
        const c = rawByTime.get(hovered.time as number) || hovered;
        // Change% = vs the PREVIOUS candle's RAW close (consistent with the
        // crosshair info box). Only computed from regular-candle values; falls
        // back to intrabar (open→close) when no previous raw bar is available.
        const hIdx = candles.indexOf(hovered);
        const prevRawClose = hIdx > 0 ? rawByTime.get(candles[hIdx - 1].time as number)?.close : undefined;
        const base = (prevRawClose != null && prevRawClose > 0) ? prevRawClose : c.open;
        const chg = c.close - base;
        const chgPct = base ? (chg / base) * 100 : 0;
        const up = chg >= 0;
        const col = up ? C.bull : C.bear;
        const bg = isDark ? 'rgba(20,24,31,0.75)' : 'rgba(255,255,255,0.85)';
        /* The instrument's precision, not the number's magnitude.
         *
         * This used to be a pure magnitude ladder -- >=1000 got 2 decimals, sub-1 got 5 -- which
         * knew nothing about the instrument and so disagreed with the price axis beside it. On
         * AKEUSD (tick 1e-7) the axis read `0.02` while this legend read `0.01624`: one price,
         * two answers, on the same chart, and neither was the 0.0162400 the venue actually
         * quotes. effectivePrecision is tick-derived, so the legend, the axis, the LTP badge and
         * a dragged line now all agree by construction. */
        const fmt = (n: number) => n.toFixed(effectivePrecision);
        // Candle START time — chart times are IST-shifted UTC seconds, so getUTC* gives
        // IST wall-clock. Intraday → "DD Mon HH:MM"; daily+ → "DD Mon YYYY".
        const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const ct = new Date((c.time as number) * 1000);
        const startStr = (intervalMinutes && intervalMinutes < 1440)
          ? `${String(ct.getUTCDate()).padStart(2, '0')} ${MON[ct.getUTCMonth()]} ${String(ct.getUTCHours()).padStart(2, '0')}:${String(ct.getUTCMinutes()).padStart(2, '0')}`
          : `${String(ct.getUTCDate()).padStart(2, '0')} ${MON[ct.getUTCMonth()]} ${ct.getUTCFullYear()}`;
        return (
          <Box sx={{
            position: 'absolute', left: 6, top: 4,
            bgcolor: bg, px: 0.8, py: 0.3, borderRadius: 0.5,
            fontFamily: 'monospace', fontSize: CH_TYPE.small, lineHeight: 1.3,
            color: 'text.primary', pointerEvents: 'none', zIndex: 5,
            boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
            backdropFilter: 'blur(2px)', whiteSpace: 'nowrap',
            /* A cap so the account rail below has something to wrap AGAINST. Without it the
               nowrap line simply runs off a narrow quad pane. */
            maxWidth: 'calc(100% - 12px)',
          }}>
            {/* Line 1 stays exactly as it was -- one nowrap run of symbol / time / OHLC /
                change. The account rail is a SIBLING flex item, so at full width it reads as
                the tail of the title and on a narrow pane it wraps to its own row instead of
                pushing the price values off the edge. */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', rowGap: 0.2 }}>
            <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
            {symbol && <Box component="span" sx={{ fontWeight: 800, mr: 0.8, fontFamily: (t) => t.typography.fontFamily }}>{symbol}</Box>}
            {/* alias charts show the ACTUAL contract being charted (IDXNGOLD → GOLD26AUGFUT · MCX) */}
            {symbolNote && <Box component="span" sx={{ color: 'info.main', fontWeight: 700, mr: 0.8 }}>{symbolNote}</Box>}
            <Box component="span" sx={{ color: 'text.secondary', mr: 0.8 }}>{startStr}</Box>
            <Box component="span" sx={{ mr: 0.6 }}>O<b style={{ marginLeft: 2 }}>{fmt(c.open)}</b></Box>
            <Box component="span" sx={{ mr: 0.6 }}>H<b style={{ marginLeft: 2 }}>{fmt(c.high)}</b></Box>
            <Box component="span" sx={{ mr: 0.6 }}>L<b style={{ marginLeft: 2 }}>{fmt(c.low)}</b></Box>
            <Box component="span" sx={{ mr: 0.6 }}>C<b style={{ marginLeft: 2 }}>{fmt(c.close)}</b></Box>
            <Box component="span" sx={{ color: col, fontWeight: 700 }}>
              {up ? '+' : ''}{fmt(chg)} ({up ? '+' : ''}{chgPct.toFixed(2)}%)
            </Box>
            </Box>
            {showAccountStats && <ChartTitleAccountStats />}
            </Box>
            {/* FNO row — the expiry switch + the option market's state AS OF the hovered
                candle's close, each value in the colour of its line / pane. */}
            {(fnoSelect || fnoView) && (() => {
              const col = fnoView?.colors;
              // The live forming candle can be newer than the last projected bar: it then
              // shows the newest bar's state when that sample is under 20 min old.
              let b = fnoBarByTime.get(hovered.time as number) ?? null;
              if (!b && fnoView?.bars.length) {
                const lb = fnoView.bars[fnoView.bars.length - 1];
                if ((hovered.time as number) > lb.time && lb.s && Math.floor(lb.s.t / 1000) + 19800 >= (hovered.time as number) - 1200) b = lb;
              }
              const s = b?.s ?? null;
              const px = Number(hovered.close);
              const lvl = (v: number | null | undefined) => (v == null || !Number.isFinite(v) ? null : Math.round(v).toLocaleString('en-IN'));
              const rel = (v: number | null | undefined) => (v == null || !(px > 0) ? '' : ` (${v >= px ? '+' : '−'}${Math.abs((v / px - 1) * 100).toFixed(1)}%)`);
              const cw = s?.res?.[0]?.[0] ?? null;
              const pw = s?.sup?.[0]?.[0] ?? null;
              const Item = ({ k, v, c, t }: { k: string; v: string; c?: string; t?: string }) => (
                <Box component="span" title={t} sx={{ mr: 0.8, whiteSpace: 'nowrap' }}>
                  <Box component="span" sx={{ color: c || 'text.secondary', fontWeight: 700 }}>{k}</Box>{' '}<b>{v}</b>
                </Box>
              );
              return (
                <Box data-testid="fno-legend-row" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', rowGap: 0.2, mt: 0.2 }}>
                  {fnoSelect && <FnoExpiryMenu {...fnoSelect} />}
                  {fnoView && !s && (
                    <Box component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      {fnoGapSet.has(hovered.time as number) ? 'no FNO data — collector was down' : 'no FNO data for this candle'}
                    </Box>
                  )}
                  {fnoView && s && col && (
                    <>
                      {cw != null && <Item k="Call wall" v={`${lvl(cw)}${rel(cw)}`} c={col.call} t="Strike with the most call open interest — option writers' ceiling" />}
                      {pw != null && <Item k="Put wall" v={`${lvl(pw)}${rel(pw)}`} c={col.put} t="Strike with the most put open interest — option writers' floor" />}
                      {s.mp != null && <Item k="Max pain" v={lvl(s.mp)!} c={col.maxPain} t="Settlement price at which option buyers collectively lose the most" />}
                      {s.gf != null && <Item k="γ flip" v={lvl(s.gf)!} c={col.gexFlip} t="Price where dealer gamma changes sign: above it moves tend to be damped, below it amplified" />}
                      {s.ng != null && s.ng !== 0 && (
                        <Item k={s.ng > 0 ? 'γ+' : 'γ−'} v={s.ng > 0 ? 'pinning' : 'trending'} c={s.ng > 0 ? '#26a69a' : '#ff9800'}
                          t={s.ng > 0 ? 'Dealers net long gamma: they sell rallies and buy dips, so ranges tend to hold' : 'Dealers net short gamma: they chase moves, so breakouts tend to run'} />
                      )}
                      {s.pcr != null && <Item k="PCR" v={s.pcr.toFixed(2)} c={s.pcr >= 1 ? col.put : col.call} t="Put / call open-interest ratio. Above 1 = more puts written (support below), under 1 = more calls (cap above)" />}
                      {s.iv != null && <Item k="IV" v={`${(s.iv * 100).toFixed(1)}%`} c={col.iv} t="At-the-money implied volatility of this expiry" />}
                      {s.st != null && s.sp != null && <Item k="±move" v={`${lvl(s.st)} (${((s.st / s.sp) * 100).toFixed(1)}%)`} t="ATM straddle premium — the move to expiry the options market is pricing" />}
                      {b && b.dce != null && b.dpe != null && (
                        <Item k="ΔOI" v={`P ${b.dpe >= 0 ? '+' : '−'}${Math.abs(Math.round(b.dpe)).toLocaleString('en-IN')} · C ${b.dce >= 0 ? '+' : '−'}${Math.abs(Math.round(b.dce)).toLocaleString('en-IN')}`}
                          c={b.dpe - b.dce >= 0 ? col.put : col.call} t="Open interest added (+) or closed (−) on this candle: puts · calls" />
                      )}
                      <Item k="exp" v={fmtExpiry(s.x)} t="The expiry this candle's values come from" />
                    </>
                  )}
                </Box>
              );
            })()}
            {/* "Important fraction" — the ONE marker the owner chose (legend only): the
                fraction, the hovered bar's fraction O/H/L next to the real prices above
                (the close is the same), and any note about how the chart was built. */}
            {/* P-Zone — the zones in force on the hovered bar (DOM, so a probe can read it). */}
            {overlays.profitZone && profitZones && (() => {
              const z = profitZones.byTime.get(hovered.time as number)
                ?? profitZones.points[profitZones.points.length - 1];
              const zc = ((settings as any).profitZone?.color as string) || '#f5a623';
              const htfLbl = auxCandles?.srHtfMinutes ? catalogIntervalLabel(Number(auxCandles.srHtfMinutes)) : '';
              return (
                <Box data-testid="pzone-legend" sx={{ mt: 0.2, whiteSpace: 'normal', display: 'flex', flexWrap: 'wrap', gap: '0 8px', alignItems: 'baseline' }}>
                  <Box component="span" sx={{ color: zc, fontWeight: 800 }}>P-Zone{z && typeof z !== 'string' ? (z.dir > 0 ? ' ▲' : ' ▼') : ''}</Box>
                  {!z || z === 'noHtf'
                    ? <Box component="span" sx={{ color: 'text.secondary' }}>no higher timeframe for this chart</Box>
                    : z === 'warmup'
                      ? <Box component="span" sx={{ color: 'text.secondary' }}>warming up (needs more higher-timeframe history)</Box>
                      : z === 'neutral'
                        ? <Box component="span" sx={{ color: 'text.secondary' }}>HTF Band mixed: no zones</Box>
                        : (
                          <Box component="span">
                            Z1<b style={{ marginLeft: 2, marginRight: 6 }}>{fmt(z.z1)}</b>
                            Z2<b style={{ marginLeft: 2, marginRight: 6 }}>{fmt(z.z2)}</b>
                            <Box component="span" sx={{ color: 'text.secondary' }}>(set at {htfLbl} close, ATR {fmt(z.scale)} · reference, not a tested signal)</Box>
                          </Box>
                        )}
                </Box>
              );
            })()}
            {fraction && (() => {
              const f = fracByTime?.get(hovered.time as number);
              const same = !f || (f.open === c.open && f.high === c.high && f.low === c.low);
              const missing = fraction.missing.has(hovered.time as number);
              const forming = !!fractionLatest && hovered.time === fractionLatest.time;
              return (
                <Box data-testid="fraction-legend" sx={{ mt: 0.2, whiteSpace: 'normal', display: 'flex', flexWrap: 'wrap', gap: '0 8px', alignItems: 'baseline' }}>
                  <Box component="span" sx={{ color: 'info.main', fontWeight: 800 }}>Fraction {fraction.label}</Box>
                  {fraction.inactive ? null : missing
                    ? <Box component="span" sx={{ color: 'text.secondary' }}>this bar: normal candle (no finer data)</Box>
                    : same && forming
                      ? <Box component="span" sx={{ color: 'text.secondary' }}>forming bar: normal until its last {fraction.label} starts</Box>
                    : same
                      ? <Box component="span" sx={{ color: 'text.secondary' }}>this bar: same as the real bar</Box>
                      : (
                        <Box component="span">
                          O<b style={{ marginLeft: 2, marginRight: 6 }}>{fmt(f!.open)}</b>
                          H<b style={{ marginLeft: 2, marginRight: 6 }}>{fmt(f!.high)}</b>
                          L<b style={{ marginLeft: 2 }}>{fmt(f!.low)}</b>
                        </Box>
                      )}
                  {fraction.notes.map((n, i) => (
                    <Box component="span" key={i} sx={{ color: 'warning.main' }}>{n}</Box>
                  ))}
                </Box>
              );
            })()}
            {/* Synthetic-symbol constituents — the actual option contracts being traded, so the
                title makes it unambiguous what this combined-premium series is built from. */}
            {syntheticLegs && syntheticLegs.some(l => l.symbol) && (
              <Box sx={{ mt: 0.3, whiteSpace: 'normal', display: 'flex', flexWrap: 'wrap', gap: '2px 8px' }}>
                {syntheticLegs.filter(l => l.symbol).map((l, i) => {
                  const mark = l.symbol ? syntheticLegMarks?.[l.symbol] : undefined;
                  return (
                    <Box component="span" key={i} sx={{ color: 'text.secondary' }}>
                      <Box component="span" sx={{ fontWeight: 700, color: l.action === 'SELL' ? C.bear : C.bull }}>
                        {l.action === 'SELL' ? 'S' : 'B'}
                      </Box>
                      {' '}{l.symbol}{(l.ratio && l.ratio !== 1) ? ` ×${l.ratio}` : ''}
                      {/* live per-leg LTP — how much this leg contributes to the combined premium */}
                      {mark != null && Number.isFinite(mark) && (
                        <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}> @{fmt(mark)}</Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        );
      })()}

      {/* R115 Stage 2 — single always-on crosshair tooltip. Replaces the
          separate OverView (R96) and BOLev (R113) tooltips with one aggregated
          info box that shows OHLC + TF/HTF scores + BOLev_D/W state + RockBand
          position, and a bold accent row naming the firing condition (C1/C2/C3)
          on signal candles. */}
      <CrosshairInfoBox ctx={(settings as any)?.crosshairTooltip === false ? null : crosshairCtx} />

      {/* LTP overlay strip — a single, professional, left-anchored pill that
          rides ON the LTP line and bundles three live readouts into one row:
            • Countdown to next candle close
            • Intra-bar % change (current bar's open → live close)
            • Open-position P&L (when an OPEN trade exists for this symbol)
          Anchored on the LEFT (next to the candle area) so the right-axis
          stays uncluttered. The vertical position tracks the LTP price's
          y-coordinate so the strip visually attaches to the LTP indicator. */}
      {countdown && candles.length > 0 && (() => {
        const last = candles[candles.length - 1];
        if (!last) return null;
        const lc = (latestCandle && latestCandle.time >= last.time) ? latestCandle : last;
        // Bar % change uses ACTUAL close vs bar open — never the candle-type
        // transform (HA close would mislead the user).
        const realClose = (typeof actualLtp === 'number' && Number.isFinite(actualLtp)) ? actualLtp : lc.close;
        const chgPct = lc.open ? ((realClose - lc.open) / lc.open) * 100 : 0;
        const up = chgPct >= 0;
        // Position P&L — prefer the explicit unrealizedPnl when supplied
        // (matches Trades Management exactly); otherwise estimate from
        // realClose vs entry, lot-aware via positionPnl.quantity.
        const hasPos = !!(positionPnl && positionPnl.entryPrice && positionPnl.entryPrice > 0);
        let pnlText: string | null = null;
        let pnlCol = up ? C.bull : C.bear;
        if (hasPos) {
          const explicit = (typeof unrealizedPnl === 'number' && Number.isFinite(unrealizedPnl)) ? unrealizedPnl : null;
          const dir = positionPnl!.side === 'LONG' ? 1 : -1;
          const qty = positionPnl!.quantity ?? 1;
          const estimated = (realClose - positionPnl!.entryPrice) * dir * qty;
          const pnl = explicit != null ? explicit : estimated;
          pnlCol = pnl >= 0 ? C.bull : C.bear;
          const cur = pnlCurrency === 'USD' ? '$' : '₹';
          const absStr = Math.abs(pnl) >= 1000
            ? Math.abs(pnl).toLocaleString('en-IN', { maximumFractionDigits: 0 })
            : Math.abs(pnl).toFixed(2);
          pnlText = `${pnl >= 0 ? '+' : '−'}${cur}${absStr}`;
        }
        // Compose a divider-separated single row.
        const bg = isDark ? 'rgba(15,18,24,0.86)' : 'rgba(255,255,255,0.92)';
        const border = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)';
        const subtle = isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)';
        const sideTag = hasPos ? (positionPnl!.side === 'LONG' ? 'LONG' : 'SHORT') : '';
        const sideTagCol = hasPos ? (positionPnl!.side === 'LONG' ? C.bull : C.bear) : subtle;
        return (
          <Box sx={{
            position: 'absolute',
            left: overlayLeftInset ?? 6,
            top: Math.max(2, countdown.y - 11),
            bgcolor: bg,
            border: `1px solid ${border}`,
            color: 'text.primary',
            borderRadius: 0.6,
            px: 0.8, py: 0.25,
            fontFamily: 'monospace', fontSize: CH_TYPE.small, lineHeight: 1.25,
            boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
            backdropFilter: 'blur(2px)',
            // Allow pointer events ONLY when a close button needs to be clickable.
            // Without a button the pill stays click-through so it doesn't block
            // the candle area.
            pointerEvents: hasPos && onCloseTrade ? 'auto' : 'none',
            zIndex: 5,
            whiteSpace: 'nowrap',
            display: 'flex', alignItems: 'center', gap: 0.7,
            fontWeight: 600,
          }}>
            <span style={{ color: subtle }}>⏱</span>
            <span>{countdown.text}</span>
            <span style={{ color: border, fontWeight: 400 }}>│</span>
            <span style={{ color: up ? C.bull : C.bear, fontWeight: 700 }}>{up ? '+' : ''}{chgPct.toFixed(2)}%</span>
            {hasPos && pnlText && (
              <>
                <span style={{ color: border, fontWeight: 400 }}>│</span>
                {positionPnl!.paper && (
                  <span data-testid="position-paper-tag" title="PAPER trade — simulated, not real money"
                    style={{ color: '#fff', background: '#546e7a', borderRadius: 3, padding: '0 4px', fontWeight: 800, fontSize: CH_TYPE.micro }}>PAPER</span>
                )}
                <span style={{ color: sideTagCol, fontWeight: 700, fontSize: CH_TYPE.micro }}>{sideTag}</span>
                <span style={{ color: pnlCol, fontWeight: 700 }}>{pnlText}</span>
                {onCloseTrade && (
                  <span
                    role="button"
                    title={positionPnl!.paper ? 'Close this PAPER position (simulated)' : 'Close this position'}
                    aria-label="Close position"
                    onClick={(e) => { e.stopPropagation(); onCloseTrade(); }}
                    style={{
                      cursor: 'pointer',
                      marginLeft: 4,
                      width: 16, height: 16,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      borderRadius: 3,
                      background: pnlCol,
                      color: '#fff',
                      fontWeight: 800, fontSize: CH_TYPE.body, lineHeight: 1,
                      userSelect: 'none',
                    }}
                  >
                    ✕
                  </span>
                )}
              </>
            )}
          </Box>
        );
      })()}
      {/* SL / Target amount pills — left-anchored, ride along their respective
          lines on the price scale. Numbers match the Trades Management section
          to the rupee (same INR formula). Profit-locked SL pill (negative risk
          when TSL has moved beyond entry) flips to bullish color with a 🔒. */}
      {tradePillPositions.map((p) => {
        const isSL = p.type === 'SL';
        const profitLocked = !!p.profitLocked;
        const col = isSL
          ? (profitLocked ? C.bull : C.bear)
          : C.bull;
        const bg = isDark ? 'rgba(15,18,24,0.9)' : 'rgba(255,255,255,0.95)';
        const border = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)';
        const subtle = isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)';
        const abs = Math.abs(p.amountInr);
        const amountStr = abs >= 1000
          ? abs.toLocaleString('en-IN', { maximumFractionDigits: 0 })
          : abs.toFixed(2);
        const sign = isSL ? '' : (p.amountInr >= 0 ? '+' : '−');
        const label = isSL ? 'SL Risk' : 'Target Gain';
        return (
          <Box key={p.type} sx={{
            position: 'absolute',
            left: 6,
            top: Math.max(2, p.y - 11),
            bgcolor: bg,
            border: `1px solid ${border}`,
            color: 'text.primary',
            borderRadius: 0.6,
            px: 0.8, py: 0.25,
            fontFamily: 'monospace', fontSize: CH_TYPE.small, lineHeight: 1.25,
            boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
            backdropFilter: 'blur(2px)',
            pointerEvents: 'none', zIndex: 4,
            whiteSpace: 'nowrap',
            display: 'flex', alignItems: 'center', gap: 0.5,
            fontWeight: 600,
          }}>
            <span style={{ color: subtle, fontSize: CH_TYPE.micro, fontWeight: 700 }}>{label}</span>
            <span style={{ color: col, fontWeight: 800 }}>
              {sign}₹{amountStr}{profitLocked ? ' 🔒' : ''}
            </span>
          </Box>
        );
      })}

      {/* Floating "scroll to latest" chevron when user has panned away from the tail. */}
      {showScrollLatest && (
        <IconButton
          size="small"
          onClick={scrollToLatest}
          title="Jump to latest candle"
          sx={{
            position: 'absolute',
            right: 60,
            /* Clear of the Bar Replay panel.
             *
             * That panel is centred at bottom:36 with zIndex 10; this chevron sat at
             * bottom:30 with zIndex 6. In a 2-SPLIT the left pane's right edge is the middle
             * of the screen, which is exactly where the panel sits - so the panel covered
             * this button and ATE THE CLICK. Playwright named it outright: "<div>bar 456/501
             * </div> from <div data-replay-panel> subtree intercepts pointer events". Anyone
             * trying to get back to the newest candle while replaying would click and click
             * and see nothing happen. Lift it above the panel while replay is active, and
             * out-rank the panel so a user who drags the panel over it cannot re-bury it. */
            bottom: replayCut != null ? 120 : 30,
            zIndex: replayCut != null ? 11 : 6,
            bgcolor: isDark ? 'rgba(33,33,33,0.85)' : 'rgba(255,255,255,0.9)',
            color: C.accent,
            border: `1px solid ${isDark ? '#455a64' : '#cfd8dc'}`,
            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
            '&:hover': { bgcolor: isDark ? 'rgba(55,55,55,0.95)' : 'rgba(245,245,245,1)' },
          }}
        >
          <KeyboardDoubleArrowRightIcon fontSize="small" />
        </IconButton>
      )}
      {/* Draggable axis handles — one per price level. Chip sits on the right axis.
          Deletable handles (HVN) reveal a small × button on hover that fires
          onPriceLevelDelete with (type, id). */}
      {handles.map(h => (
        <Box
          key={`${h.type}_${h.id ?? '_'}`}
          data-trigger-chart="true"
          sx={{
            position: 'absolute',
            right: 4,
            top: h.y - 7,
            zIndex: 4,
            display: 'flex', alignItems: 'center',
            '&:hover .hvn-del': { opacity: 1, pointerEvents: 'auto' },
          }}
        >
          {h.deletable && onPriceLevelDelete && (
            <Box
              className="hvn-del"
              onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); onPriceLevelDelete(h.type, h.id); }}
              title={`Delete ${h.type}`}
              sx={{
                width: 14, height: 14, mr: 0.4,
                borderRadius: '50%',
                bgcolor: '#fff', color: h.color,
                border: `1.5px solid ${h.color}`,
                fontSize: 10, fontWeight: 800, lineHeight: '11px', textAlign: 'center',
                cursor: 'pointer',
                opacity: 0, pointerEvents: 'none', transition: 'opacity 120ms',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.25)',
                '&:hover': { bgcolor: h.color, color: '#fff' },
              }}
            >×</Box>
          )}
          <Box
            onMouseDown={(e) => onHandleMouseDown(e, h)}
            title={`Drag to move ${h.type}`}
            sx={{
              width: 14, height: 14, borderRadius: '50%',
              bgcolor: h.color,
              border: '2px solid #fff',
              boxShadow: '0 0 0 1px rgba(0,0,0,0.3)',
              cursor: 'ns-resize',
              '&:hover': { transform: 'scale(1.2)' },
            }}
          />
        </Box>
      ))}
    </Box>
  );
};

export default TradingChart;
